import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const C = {
  primary: '#1a3a5c',
  primaryDark: '#142d47',
  accent: '#3a7ca5',
  border: '#dde2ea',
  bg: '#f4f6fa',
  white: '#ffffff',
  text: '#2c3e50',
  muted: '#6c7a89',
  danger: '#c0392b',
  success: '#1e8449',   // dunkler als vorher (#27ae60), damit grüne Schrift auf Weiß/Hellgrün lesbar ist
};

const api = axios.create({ baseURL: '/api' });

const S = {
  loginPage: { minHeight: '100vh', background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif" },
  loginCard: { background: C.white, padding: 40, borderRadius: 8, boxShadow: '0 10px 40px rgba(0,0,0,0.25)', width: 360 },
  loginTitle: { margin: '0 0 8px', color: C.primary, fontSize: 22 },
  loginSub: { margin: '0 0 24px', color: C.muted, fontSize: 13 },
  label: { display: 'block', fontSize: 12, color: C.muted, marginBottom: 4, marginTop: 12 },
  input: { width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 14, boxSizing: 'border-box', background: C.white, fontFamily: 'inherit' },
  btnPrimary: { background: C.primary, color: C.white, border: 'none', padding: '10px 16px', borderRadius: 4, fontSize: 14, cursor: 'pointer', marginTop: 16, fontFamily: 'inherit' },
  btnGhost: { background: 'transparent', color: C.white, border: '1px solid rgba(255,255,255,0.4)', padding: '6px 12px', borderRadius: 4, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  btnSmall: { background: C.accent, color: C.white, border: 'none', padding: '4px 10px', borderRadius: 3, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  btnDanger: { background: C.danger, color: C.white, border: 'none', padding: '4px 10px', borderRadius: 3, fontSize: 12, cursor: 'pointer', marginLeft: 6, fontFamily: 'inherit' },
  err: { color: C.danger, fontSize: 13, marginTop: 12, minHeight: 18 },
  app: { minHeight: '100vh', background: C.bg, colorScheme: 'light', fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif", color: C.text },
  header: { background: C.primary, color: C.white, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 },
  brand: { fontSize: 17, fontWeight: 600, letterSpacing: 0.3 },
  tabs: { display: 'flex', gap: 4, alignItems: 'center' },
  tab: (active) => ({ background: active ? C.primaryDark : 'transparent', color: C.white, border: 'none', padding: '8px 16px', borderRadius: 4, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }),
  userBox: { display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 },
  main: { padding: 24, maxWidth: 1680, margin: '0 auto' },
  layout: { display: 'flex', minHeight: '100vh', alignItems: 'flex-start', background: C.bg, colorScheme: 'light', fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif", color: C.text },
  sidebar: { width: 220, minWidth: 220, background: C.primary, color: C.white, position: 'sticky', top: 0, height: '100vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' },
  sidebarBrand: { fontSize: 16, fontWeight: 600, padding: '18px 16px', borderBottom: '1px solid rgba(255,255,255,0.15)', lineHeight: 1.3 },
  sidebarMandant: { padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  sidebarLabel: { fontSize: 11, color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: 4 },
  sidebarSelect: { width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.3)', background: C.white, color: C.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  navBtn: (active) => ({ display: 'block', width: '100%', textAlign: 'left', background: active ? C.primaryDark : 'transparent', color: C.white, border: 'none', borderLeft: active ? `3px solid ${C.accent}` : '3px solid transparent', padding: '11px 16px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }),
  sidebarUser: { marginTop: 'auto', padding: 14, borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: 12 },
  content: { flex: 1, padding: 24, maxWidth: 1500, boxSizing: 'border-box', minWidth: 0 },
  card: { background: C.white, padding: 20, borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 20 },
  cardTitle: { margin: '0 0 14px', fontSize: 16, color: C.primary, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: C.bg, color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { padding: '10px', borderBottom: `1px solid ${C.border}` },
  row: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' },
  field: { flex: '1 1 180px' },
  textarea: { width: '100%', padding: 10, border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 14, fontFamily: 'inherit', minHeight: 80, boxSizing: 'border-box', resize: 'vertical' },
  msg: { padding: 12, background: C.bg, borderRadius: 4, marginBottom: 8, borderLeft: `3px solid ${C.accent}` },
  msgMeta: { fontSize: 11, color: C.muted, marginBottom: 4 },
  msgBody: { fontSize: 14, whiteSpace: 'pre-wrap' },
  dirTag: (dir) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, background: dir === 'from_client' ? '#e8f4ea' : '#e8eef5', color: dir === 'from_client' ? C.success : C.accent }),
  empty: { padding: 20, textAlign: 'center', color: C.muted, fontSize: 13 },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 14, marginBottom: 14 },
  crumbLink: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 14, padding: 0, fontFamily: 'inherit' },
  crumbCur: { color: C.text, fontWeight: 600 },
  crumbSep: { color: C.muted },
  folderItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 4, marginBottom: 6, background: C.bg },
  folderLeft: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, flex: 1 },
  hint: { padding: 14, background: '#fff7e6', border: '1px solid #ffe1a8', borderRadius: 4, color: '#8a6d3b', fontSize: 13 },
  pathInfo: { fontSize: 12, color: C.muted, marginBottom: 8 },
  dropZone: { border: `2px dashed ${C.border}`, borderRadius: 8, minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: C.muted, cursor: 'pointer', background: C.bg, padding: 16, boxSizing: 'border-box', transition: 'border-color 0.15s, background 0.15s' },
  dropZoneActive: { border: `2px dashed ${C.primary}`, borderRadius: 8, minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: C.primary, cursor: 'pointer', background: '#eaf1f8', padding: 16, boxSizing: 'border-box', transition: 'border-color 0.15s, background 0.15s' },
  fileRow: { display: 'flex', alignItems: 'center', padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: 4, marginBottom: 6, fontSize: 13, background: C.white },
  fileRemove: { background: 'transparent', color: C.danger, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', lineHeight: 1 },
  okMsg: { color: C.success, fontSize: 13, marginTop: 10 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto', zIndex: 1000 },
  modalCard: { background: C.white, borderRadius: 8, padding: 24, width: 560, maxWidth: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' },
  docChip: { display: 'inline-block', fontSize: 11, color: C.accent, cursor: 'pointer', textDecoration: 'underline', marginRight: 6, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' },
  uploadMini: { background: C.bg, color: C.primary, border: `1px solid ${C.border}`, borderRadius: 3, fontSize: 12, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#e8eef5', color: C.accent },
};

const MA_STATUS = ["Standard", "Minijob", "Werkstudent", "SV-freies Pflichtpraktikum", "SV-pflichtiges Praktikum"];
const MA_DOC_TYPES = [
  { key: "personalfragebogen", label: "Personalfragebogen" },
  { key: "arbeitsvertrag", label: "Arbeitsvertrag" },
  { key: "personalausweis", label: "Personalausweis" },
  { key: "immatrikulation", label: "Immatrikulation" },
  { key: "sonstige", label: "Sonstige" },
];

const KRANKENKASSEN = [
  "Techniker Krankenkasse", "BARMER", "DAK-Gesundheit", "KKH Kaufmännische Krankenkasse",
  "HEK-Hanseatische Krankenkasse", "hkk", "KNAPPSCHAFT",
  "AOK Baden-Württemberg", "AOK Bayern", "AOK Bremen/Bremerhaven", "AOK Hessen", "AOK Niedersachsen",
  "AOK Nordost", "AOK NordWest", "AOK PLUS", "AOK Rheinland-Pfalz/Saarland", "AOK Rheinland/Hamburg",
  "AOK Sachsen-Anhalt", "IKK classic", "IKK - Die Innovationskasse", "IKK Brandenburg und Berlin",
  "IKK gesund plus", "IKK Südwest", "BIG direkt gesund", "Audi BKK", "BAHN-BKK", "BERGISCHE KRANKENKASSE",
  "Bertelsmann BKK", "BKK Akzo Nobel Bayern", "BKK Diakonie", "BKK DürkoppAdler", "BKK EUREGIO",
  "BKK exklusiv", "BKK Faber-Castell & Partner", "BKK firmus", "BKK Freudenberg",
  "BKK GILDEMEISTER SEIDENSTICKER", "BKK Herkules", "BKK Linde", "bkk melitta hmr", "BKK mkk",
  "BKK PFAFF", "BKK Pfalz", "BKK ProVita", "BKK Public", "BKK SBH", "BKK Scheufelen", "BKK Technoform",
  "BKK VDN", "BKK VerbundPlus", "BKK WERRA-MEISSNER", "BKK Wirtschaft & Finanzen", "BKK24", "BOSCH BKK",
  "Continentale Betriebskrankenkasse", "Debeka BKK", "energie-BKK", "Heimat Krankenkasse", "mhplus BKK",
  "Mobil Krankenkasse", "novitas bkk", "Pronova BKK", "R+V Betriebskrankenkasse", "Salus BKK", "SBK",
  "SECURVITA Krankenkasse", "SKD BKK", "TUI BKK", "VIACTIV Krankenkasse", "vivida bkk", "WMF BKK", "ZF BKK",
];

// Backend liefert naive UTC-Zeitstempel (…T07:19:54 ohne Zone). Als UTC kennzeichnen,
// damit der Browser korrekt in die lokale Zeit (z.B. Berlin) umrechnet – sonst 2h zu früh.
function _asUtc(iso) {
  if (typeof iso === 'string' && iso.includes('T') && !/[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso)) return iso + 'Z';
  return iso;
}

function fmtDateDE(iso) {
  if (!iso) return '—';
  try { return new Date(_asUtc(iso)).toLocaleDateString('de-DE'); } catch { return iso; }
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(_asUtc(iso)).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

// Lohndokumente je Mitarbeiter und Monat: werden dem MA zugeordnet und vom Mandanten für den MA-Zugang freigegeben
const LOHN_DOK_TYPEN = ['lohnabrechnung', 'meldebescheinigung', 'lohnsteuerbescheinigung'];
const LOHN_DOK_LABEL = { lohnabrechnung: 'Lohnabrechnung', meldebescheinigung: 'Meldebescheinigung', lohnsteuerbescheinigung: 'Lohnsteuerbescheinigung' };
const LOHN_DOK_FARBE = { lohnabrechnung: ['#e8eef5', '#3a7ca5'], meldebescheinigung: ['#f3e8f7', '#7d3c98'], lohnsteuerbescheinigung: ['#fdf2e3', '#b9770e'] };
const istLohnDok = (d) => LOHN_DOK_TYPEN.includes(d && d.doc_type);
const AUSW_TYPEN = [
  ['lohnabrechnung', 'Lohnabrechnung (je MA)'],
  ['meldebescheinigung', 'Meldebescheinigung (je MA)'],
  ['lohnsteuerbescheinigung', 'Lohnsteuerbescheinigung (je MA)'],
  ['zahlungsliste', 'Zahlungsliste'],
  ['buchungsbeleg', 'Buchungsbeleg (PDF)'],
  ['buchungsbeleg_ascii', 'Buchungsbeleg (ASCII)'],
  ['sv_meldung', 'SV-Meldung'],
  ['lohnjournal', 'Lohnjournal'],
  ['lohnkonto', 'Lohnkonto'],
  ['beitragsnachweis', 'Beitragsnachweis'],
  ['lohnsteueranmeldung', 'Lohnsteueranmeldung'],
];
const _MON_NAMEN = { januar: 1, jan: 1, februar: 2, feb: 2, 'märz': 3, maerz: 3, mrz: 3, april: 4, apr: 4, mai: 5, juni: 6, jun: 6, juli: 7, jul: 7, august: 8, aug: 8, september: 9, sep: 9, sept: 9, oktober: 10, okt: 10, november: 11, nov: 11, dezember: 12, dez: 12 };
function detectMonat(fn, defaultJahr) {
  const s = fn.toLowerCase();
  const pad = n => String(n).padStart(2, '0');
  let m = s.match(/(20\d\d)[-_.]?(0[1-9]|1[0-2])(?!\d)/); if (m) return `${m[1]}-${m[2]}`;
  m = s.match(/(?<!\d)(0[1-9]|1[0-2])[-_.](20\d\d)/); if (m) return `${m[2]}-${m[1]}`;
  m = s.match(/(?<!\d)(0[1-9]|1[0-2])(20\d\d)(?!\d)/); if (m) return `${m[2]}-${m[1]}`;
  const names = Object.keys(_MON_NAMEN).sort((a, b) => b.length - a.length);
  for (const name of names) { const mm = s.match(new RegExp(`(?<![a-zäöü])${name}[-_. ]*(20\\d\\d)`)); if (mm) return `${mm[1]}-${pad(_MON_NAMEN[name])}`; }
  for (const name of names) { const mm = s.match(new RegExp(`(20\\d\\d)[-_. ]*${name}(?![a-zäöü])`)); if (mm) return `${mm[1]}-${pad(_MON_NAMEN[name])}`; }
  const jahr = defaultJahr || new Date().getFullYear();
  for (const name of names) { if (new RegExp(`(?<![a-zäöü])${name}(?![a-zäöü])`).test(s)) return `${jahr}-${pad(_MON_NAMEN[name])}`; }
  return '';
}
function detectTyp(fn) {
  const s = fn.toLowerCase();
  const isText = /\.(txt|csv|asc)$/.test(s);
  if (/(lohnsteuer[-_ ]?bescheinigung|lst[-_ ]?bescheinigung|\blstb\b)/.test(s)) return 'lohnsteuerbescheinigung';
  if (/(buchungsbeleg|buchung)/.test(s)) return isText ? 'buchungsbeleg_ascii' : 'buchungsbeleg';
  if (/ascii/.test(s)) return 'buchungsbeleg_ascii';
  if (/(meldebescheinigung|meldebesch|de[uü]e?v)/.test(s)) return 'meldebescheinigung';
  if (/(beitragsnachweis|\bbnw\b)/.test(s)) return 'beitragsnachweis';
  if (/(lohnsteueranmeldung|lohnsteuer|\blst\b|lstanm)/.test(s)) return 'lohnsteueranmeldung';
  if (/(sv[-_ ]?meldung|sozialversicherung)/.test(s)) return 'sv_meldung';
  if (/(lohnjournal|journal)/.test(s)) return 'lohnjournal';
  if (/(lohnkonto)/.test(s)) return 'lohnkonto';
  if (/(zahlungsliste|zahlung)/.test(s)) return 'zahlungsliste';
  if (/(lohnabrechnung|entgeltabrechnung|verdienstabrechnung|abrechnung)/.test(s)) return 'lohnabrechnung';
  return '';
}
function detectMa(fn, maList) {
  const cand = maList.filter(m => m.pers_nr && String(m.pers_nr).trim() && fn.includes(String(m.pers_nr).trim()));
  if (cand.length) return cand.sort((a, b) => String(b.pers_nr).length - String(a.pers_nr).length)[0].id;
  const low = ' ' + fn.toLowerCase().replace(/[._-]+/g, ' ') + ' ';
  const both = maList.filter(m => m.nachname && m.vorname && low.includes(m.nachname.toLowerCase()) && low.includes(m.vorname.toLowerCase()));
  if (both.length === 1) return both[0].id;
  if (both.length > 1) return '';
  const byName = maList.filter(m => m.nachname && low.includes(' ' + m.nachname.toLowerCase() + ' '));
  return byName.length === 1 ? byName[0].id : '';
}

// Inline-Vorschau eines Dokuments (PDF im iframe, Bild direkt) – ohne Download
function DocPreviewModal({ doc, onClose, canFreigabe, onChanged }) {
  const [url, setUrl] = useState(null);
  const [type, setType] = useState('');
  const [err, setErr] = useState('');
  const [freigegeben, setFreigegeben] = useState(!!doc.freigegeben);
  const [busy, setBusy] = useState(false);
  async function toggleFreigabe() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('freigegeben', freigegeben ? 'false' : 'true');
      await api.patch(`/documents/${doc.id}/freigabe`, fd);
      setFreigegeben(v => !v);
      if (onChanged) onChanged();
    } catch { setErr('Freigabe fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  useEffect(() => {
    let objUrl;
    (async () => {
      try {
        const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
        const ext = (doc.original_name || '').split('.').pop().toLowerCase();
        const mime = ext === 'pdf' ? 'application/pdf'
          : (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}` : (res.data.type || 'application/octet-stream'));
        objUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
        setUrl(objUrl); setType(mime);
      } catch { setErr('Vorschau konnte nicht geladen werden.'); }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [doc]);
  async function download() {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = u; a.download = doc.original_name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch { /* ignore */ }
  }
  const isPdf = type === 'application/pdf';
  const isImg = type.startsWith('image/');
  return (
    <div style={{ ...S.modalOverlay, zIndex: 1100 }} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 'min(940px, 96vw)', maxWidth: '96vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 15, color: C.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 200px' }}>
            {doc.bezeichnung || doc.original_name}
            {canFreigabe && <span style={{ ...S.badge, marginLeft: 8, background: freigegeben ? '#e8f4ea' : '#fdeccd', color: freigegeben ? C.success : '#8a6d3b' }}>{freigegeben ? '✓ freigegeben' : 'nicht freigegeben'}</span>}
          </h2>
          <div style={{ display: 'flex', gap: 8, flex: 'none', flexWrap: 'wrap' }}>
            {canFreigabe && <button style={{ ...S.btnSmall, background: freigegeben ? C.muted : C.success }} disabled={busy} onClick={toggleFreigabe}>{busy ? '…' : (freigegeben ? 'Freigabe zurückziehen' : '✓ Für Mitarbeiter freigeben')}</button>}
            <button style={S.btnSmall} onClick={download}>⬇ Download</button>
            <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button>
          </div>
        </div>
        {err ? <div style={S.err}>{err}</div>
          : !url ? <div style={S.empty}>Lade Vorschau…</div>
            : isPdf ? <iframe title="Vorschau" src={url} style={{ width: '100%', height: '80vh', border: `1px solid ${C.border}`, borderRadius: 6 }} />
              : isImg ? <div style={{ textAlign: 'center' }}><img alt="Vorschau" src={url} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 6 }} /></div>
                : <div style={S.hint}>Für diesen Dateityp ist keine Vorschau möglich – bitte herunterladen.</div>}
      </div>
    </div>
  );
}

function AuswertungUpload({ mandantId, onClose, onDone }) {
  const [maList, setMaList] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [bulkMonat, setBulkMonat] = useState('');

  useEffect(() => { (async () => {
    try { const { data } = await api.get('/mitarbeiter', { params: { mandant_id: mandantId } }); setMaList(data); }
    catch { /* ignore */ }
  })(); }, [mandantId]);

  const bulkJahr = bulkMonat ? Number(bulkMonat.slice(0, 4)) : undefined;
  function addFiles(list) {
    const arr = Array.from(list || []);
    setRows(prev => [...prev, ...arr.map(file => {
      const typ = detectTyp(file.name) || 'zahlungsliste';
      let monat = bulkMonat || detectMonat(file.name, bulkJahr);
      if (!monat && typ === 'lohnsteuerbescheinigung') { const y = (file.name.match(/(20\d\d)/) || [])[1]; if (y) monat = `${y}-12`; }   // Jahresbescheinigung ohne Monat → Dezember
      return { file, typ, monat, mitarbeiter_id: LOHN_DOK_TYPEN.includes(typ) ? detectMa(file.name, maList) : '' };
    })]);
    setDone(null);
  }
  function monatFuerAlle() {
    if (!bulkMonat) return;
    setRows(rs => rs.map(r => ({ ...r, monat: bulkMonat })));
  }
  const upd = (i, k, v) => setRows(rs => rs.map((r, j) => {
    if (j !== i) return r;
    const n = { ...r, [k]: v };
    if (k === 'typ' && LOHN_DOK_TYPEN.includes(v) && !n.mitarbeiter_id) n.mitarbeiter_id = detectMa(r.file.name, maList);
    return n;
  }));
  const del = (i) => setRows(rs => rs.filter((_, j) => j !== i));

  async function uploadAll() {
    setErr('');
    for (const r of rows) {
      if (!r.monat) { setErr('Bitte für alle Dateien einen Monat angeben.'); return; }
      if (LOHN_DOK_TYPEN.includes(r.typ) && !r.mitarbeiter_id) { setErr(`${LOHN_DOK_LABEL[r.typ]} „${r.file.name}“ braucht einen Mitarbeiter.`); return; }
    }
    setBusy(true);
    let ok = 0; const fails = [];
    for (const r of rows) {
      try {
        const fd = new FormData();
        fd.append('file', r.file); fd.append('mandant_id', mandantId);
        fd.append('typ', r.typ); fd.append('monat', r.monat);
        if (r.mitarbeiter_id) fd.append('mitarbeiter_id', r.mitarbeiter_id);
        await api.post('/auswertung-upload', fd);
        ok++;
      } catch (ex) { fails.push(`${r.file.name}: ${ex.response?.data?.detail || 'Fehler'}`); }
    }
    setBusy(false); setDone({ ok, fails }); setRows([]);
    if (onDone) onDone();
  }

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 820 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Auswertungen & Lohnabrechnungen hochladen</h2>
        <div style={S.pathInfo}>Mehrere Dateien wählen — Typ und Monat werden aus dem Dateinamen erkannt und können vor dem Speichern korrigiert werden. Auswertungen landen automatisch im Ordner „Lohnauswertungen/&lt;Monat&gt;"; Lohnabrechnungen, Melde- und Lohnsteuerbescheinigungen beim jeweiligen Mitarbeiter. Beitragsnachweis/Lohnsteueranmeldung haken die Cockpit-Fälligkeit automatisch ab.</div>
        <input type="file" multiple style={S.input} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: C.muted }}>Übergreifender Monat:</span>
          <input type="month" style={{ ...S.input, width: 160 }} value={bulkMonat} onChange={e => setBulkMonat(e.target.value)} />
          <button style={{ ...S.btnGhost, color: C.accent, borderColor: C.accent, marginTop: 0 }} disabled={!bulkMonat || rows.length === 0} onClick={monatFuerAlle}>Für alle übernehmen</button>
          <span style={{ fontSize: 12, color: C.muted }}>(neue Dateien erhalten diesen Monat automatisch; sonst Erkennung aus dem Dateinamen)</span>
        </div>
        {rows.length > 0 && (
          <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 12 }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Datei</th><th style={S.th}>Typ</th><th style={S.th}>Monat</th><th style={S.th}>Mitarbeiter</th><th style={S.th}></th></tr></thead>
              <tbody>{rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.file.name}</td>
                  <td style={S.td}><select style={{ ...S.select, minWidth: 150 }} value={r.typ} onChange={e => upd(i, 'typ', e.target.value)}>{AUSW_TYPEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                  <td style={S.td}><input type="month" style={S.input} value={r.monat} onChange={e => upd(i, 'monat', e.target.value)} /></td>
                  <td style={S.td}>{LOHN_DOK_TYPEN.includes(r.typ)
                    ? <select style={S.select} value={r.mitarbeiter_id} onChange={e => upd(i, 'mitarbeiter_id', e.target.value)}><option value="">— MA —</option>{maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}</select>
                    : <span style={{ color: C.muted }}>—</span>}</td>
                  <td style={S.td}><button style={S.fileRemove} onClick={() => del(i)}>✕</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {err && <div style={S.err}>{err}</div>}
        {done && <div style={S.okMsg}>{done.ok} Datei(en) hochgeladen.{done.fails.length ? ` Fehlgeschlagen: ${done.fails.join('; ')}` : ''}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || rows.length === 0} onClick={uploadAll}>{busy ? 'Lädt…' : `Hochladen${rows.length ? ` (${rows.length})` : ''}`}</button>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

function Documents({ user, mandanten, mandantId }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [auswOpen, setAuswOpen] = useState(false);

  const [currentFolderId, setCurrentFolderId] = useState(null); // null = Wurzel
  const [folderPath, setFolderPath] = useState([]); // [{id, name}]
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [payslips, setPayslips] = useState([]);   // Lohnabrechnungen des aktuellen Monatsordners (liegen an folder_id=None)
  const [maMap, setMaMap] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [direction, setDirection] = useState('to_client');
  const [newFolderName, setNewFolderName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const ready = isStaff ? !!mandantId : true;

  const load = useCallback(async () => {
    if (!ready) { setFolders([]); setDocs([]); setPayslips([]); return; }
    setLoading(true); setErr('');
    try {
      const fParams = {};
      const dParams = { folder_id: currentFolderId === null ? 0 : currentFolderId };
      if (isStaff) { fParams.mandant_id = mandantId; dParams.mandant_id = mandantId; }
      const [fRes, dRes] = await Promise.all([
        api.get('/folders', { params: fParams }),
        api.get('/documents', { params: dParams }),
      ]);
      setFolders(fRes.data);
      setDocs(dRes.data);
      // Monatsordner unter „Lohnauswertungen"? → zugehörige MA-Lohnabrechnungen (liegen an folder_id=None) dazuladen
      const cur = fRes.data.find(f => f.id === currentFolderId);
      const parent = cur ? fRes.data.find(f => f.id === (cur.parent_id ?? null)) : null;
      const isMonth = !!(cur && parent && parent.name === 'Lohnauswertungen' && /^\d{4}-(0[1-9]|1[0-2])$/.test(cur.name));
      const isLohn = !!(cur && cur.name === 'Lohnabrechnungen');
      if (isMonth || isLohn) {
        const pParams = { doc_type: LOHN_DOK_TYPEN.join(',') };
        if (isMonth) pParams.monat = cur.name;   // im Ordner „Lohnabrechnungen": alle Monate
        if (isStaff) pParams.mandant_id = mandantId;
        const [pRes, mRes] = await Promise.all([
          api.get('/documents', { params: pParams }),
          api.get('/mitarbeiter', { params: isStaff ? { mandant_id: mandantId } : {} }).catch(() => ({ data: [] })),
        ]);
        setPayslips(pRes.data);
        const mm = {}; mRes.data.forEach(m => { mm[m.id] = m; }); setMaMap(mm);
      } else { setPayslips([]); }
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId, currentFolderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentFolderId(null); setFolderPath([]); }, [mandantId]);

  const subFolders = folders.filter(f => (f.parent_id ?? null) === currentFolderId);

  function openFolder(f) {
    setFolderPath(p => [...p, { id: f.id, name: f.name }]);
    setCurrentFolderId(f.id);
  }

  function goTo(index) {
    if (index < 0) { setFolderPath([]); setCurrentFolderId(null); }
    else {
      const np = folderPath.slice(0, index + 1);
      setFolderPath(np);
      setCurrentFolderId(np[np.length - 1].id);
    }
  }

  async function createFolder() {
    const name = newFolderName.trim();
    if (!name) return;
    if (isStaff && !mandantId) { setErr('Bitte Mandant wählen'); return; }
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('mandant_id', isStaff ? mandantId : (user.mandant_id ?? 0));
      if (currentFolderId !== null) fd.append('parent_id', currentFolderId);
      await api.post('/folders', fd);
      setNewFolderName('');
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Ordner anlegen fehlgeschlagen'); }
  }

  async function renameFolder(f) {
    const name = window.prompt('Neuer Name für Ordner', f.name);
    if (!name || !name.trim()) return;
    try {
      const fd = new FormData(); fd.append('name', name.trim());
      await api.patch(`/folders/${f.id}`, fd);
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Umbenennen fehlgeschlagen'); }
  }

  async function deleteFolder(f) {
    if (!window.confirm(`Ordner "${f.name}" löschen?`)) return;
    try { await api.delete(`/folders/${f.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  function fmtSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function addFiles(list) {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setOkMsg(''); setErr('');
    setFiles(prev => [...prev, ...arr]);
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  async function upload() {
    setErr(''); setOkMsg('');
    if (!files.length) { setErr('Bitte mindestens eine Datei wählen'); return; }
    if (isStaff && !mandantId) { setErr('Bitte Mandant wählen'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      fd.append('mandant_id', isStaff ? mandantId : (user.mandant_id ?? 0));
      fd.append('direction', isStaff ? direction : 'from_client');
      if (currentFolderId !== null) fd.append('folder_id', currentFolderId);
      const count = files.length;
      await api.post('/documents/upload', fd);
      setFiles([]);
      const input = document.getElementById('doc-file-input');
      if (input) input.value = '';
      setOkMsg(`${count} Datei(en) erfolgreich hochgeladen.`);
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  async function download(doc) {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.original_name; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  async function removeDoc(doc) {
    if (!window.confirm(`Dokument "${doc.original_name}" löschen?`)) return;
    try { await api.delete(`/documents/${doc.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  async function pushLexware(doc) {
    if (!window.confirm(`„${doc.original_name}" an Lexware Office übertragen?`)) return;
    setErr('');
    try { await api.post(`/documents/${doc.id}/lexware-push`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Lexware-Übertragung fehlgeschlagen'); }
  }

  async function toggleFreigabe(d) {
    try {
      const fd = new FormData(); fd.append('freigegeben', d.freigegeben ? 'false' : 'true');
      await api.patch(`/documents/${d.id}/freigabe`, fd); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Freigabe fehlgeschlagen'); }
  }
  const maLabel = (id) => { const m = maMap[id]; return m ? `${m.nachname}, ${m.vorname}` : (id ? `MA #${id}` : '—'); };

  const mandantName = (id) => mandanten.find(m => m.id === id)?.name || `#${id ?? '—'}`;
  const currentName = folderPath.length ? folderPath[folderPath.length - 1].name : 'Start';
  // Sind wir in einem Monatsordner unter „Lohnauswertungen"? → dann Lohnabrechnungs-Sektion zeigen
  const curFolderObj = folders.find(f => f.id === currentFolderId);
  const curParentObj = curFolderObj ? folders.find(f => f.id === (curFolderObj.parent_id ?? null)) : null;
  const isMonthFolder = !!(curFolderObj && curParentObj && curParentObj.name === 'Lohnauswertungen' && /^\d{4}-(0[1-9]|1[0-2])$/.test(curFolderObj.name));
  const isLohnFolder = !!(curFolderObj && curFolderObj.name === 'Lohnabrechnungen');
  const showPayslips = isMonthFolder || isLohnFolder;
  const payslipMonths = [...new Set(payslips.map(d => d.monat || '—'))].sort((a, b) => String(b).localeCompare(String(a)));
  const visibleDocs = docs.filter(d => !istLohnDok(d));  // Lohnabrechnungen/Meldebescheinigungen erscheinen in der eigenen Sektion

  return (
    <div>
      {!ready ? (
        <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen, um Ordner und Dokumente anzuzeigen.</div></div>
      ) : (
        <>
          {isStaff && (
            <div style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Auswertungen & Lohnabrechnungen</h2>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Mehrere Dateien auf einmal — automatische Zuordnung nach Typ &amp; Monat (Lohnauswertungen/&lt;Monat&gt;).</div>
                </div>
                <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => setAuswOpen(true)}>⬆ Massen-Upload</button>
              </div>
            </div>
          )}
          <div style={S.card}>
            <h2 style={S.cardTitle}>Ordner</h2>
            <div style={S.breadcrumb}>
              <button style={S.crumbLink} onClick={() => goTo(-1)}>📁 Start</button>
              {folderPath.map((c, i) => (
                <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={S.crumbSep}>/</span>
                  {i === folderPath.length - 1
                    ? <span style={S.crumbCur}>{c.name}</span>
                    : <button style={S.crumbLink} onClick={() => goTo(i)}>{c.name}</button>}
                </span>
              ))}
            </div>

            {isStaff && (
              <div style={{ ...S.row, marginBottom: 14 }}>
                <div style={S.field}>
                  <input style={S.input} placeholder="Neuer Ordnername" value={newFolderName}
                         onChange={e => setNewFolderName(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createFolder(); } }} />
                </div>
                <div><button type="button" style={{ ...S.btnPrimary, marginTop: 0 }} onClick={createFolder}>+ Neuer Ordner</button></div>
              </div>
            )}

            {subFolders.length === 0 ? <div style={S.empty}>Keine Unterordner</div> : subFolders.map(f => (
              <div key={f.id} style={S.folderItem}>
                <div style={S.folderLeft} onClick={() => openFolder(f)}>
                  <span>📁</span><span>{f.name}</span>
                </div>
                {isStaff && (
                  <div>
                    <button style={S.btnSmall} onClick={() => renameFolder(f)}>Umbenennen</button>
                    <button style={S.btnDanger} onClick={() => deleteFolder(f)}>Löschen</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={S.card}>
            <h2 style={S.cardTitle}>Dokumente hochladen</h2>
            <div style={S.pathInfo}>Ziel-Ordner: <strong>{currentName}</strong></div>

            <input id="doc-file-input" type="file" multiple style={{ display: 'none' }}
                   onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

            <div
              style={dragOver ? S.dropZoneActive : S.dropZone}
              onClick={() => { const el = document.getElementById('doc-file-input'); if (el) el.click(); }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
              onDrop={onDrop}
            >
              <div style={{ fontSize: 30, marginBottom: 8 }}>⬆</div>
              <div style={{ fontSize: 15 }}>Dateien hierher ziehen oder <strong>klicken zum Auswählen</strong></div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Mehrere Dateien gleichzeitig möglich</div>
            </div>

            {files.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {files.map((f, i) => (
                  <div key={i} style={S.fileRow}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ color: C.muted, fontSize: 12, marginRight: 12 }}>{fmtSize(f.size)}</span>
                    <button type="button" style={S.fileRemove} title="Entfernen" onClick={() => removeFile(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ ...S.row, marginTop: 12 }}>
              {isStaff && (
                <div style={S.field}>
                  <label style={S.label}>Richtung</label>
                  <select style={S.select} value={direction} onChange={e => setDirection(e.target.value)}>
                    <option value="to_client">→ An Mandant</option>
                    <option value="from_client">← Vom Mandant</option>
                  </select>
                </div>
              )}
              <div>
                <button type="button" style={{ ...S.btnPrimary, marginTop: 0, opacity: (busy || files.length === 0) ? 0.6 : 1 }}
                        disabled={busy || files.length === 0} onClick={upload}>
                  {busy ? 'Lädt hoch…' : `Hochladen${files.length ? ` (${files.length})` : ''}`}
                </button>
              </div>
            </div>
            {okMsg && <div style={S.okMsg}>{okMsg}</div>}
          </div>

          {!(isLohnFolder && visibleDocs.length === 0) && (
          <div style={S.card}>
            <h2 style={S.cardTitle}>Dokumente in „{currentName}"</h2>
            {loading ? <div style={S.empty}>Lade…</div> : visibleDocs.length === 0 ? <div style={S.empty}>Keine Dokumente in diesem Ordner</div> : (
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Richtung</th>
                  <th style={S.th}>Datum</th>
                  <th style={S.th}>Mandant</th>
                  <th style={S.th}>Aktionen</th>
                </tr></thead>
                <tbody>
                  {visibleDocs.map(d => (
                    <tr key={d.id}>
                      <td style={S.td}>{d.original_name}</td>
                      <td style={S.td}><span style={S.dirTag(d.direction)}>{d.direction === 'from_client' ? '← vom Mandant' : '→ an Mandant'}</span></td>
                      <td style={S.td}>{fmtDate(d.created_at)}</td>
                      <td style={S.td}>{mandantName(d.mandant_id)}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        <button style={{ ...S.btnSmall, background: C.accent }} onClick={() => setPreviewDoc(d)}>Ansehen</button>
                        <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => download(d)}>Download</button>
                        {isStaff && (d.lexware_uebertragen_am
                          ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8f4ea', color: C.success }} title={`übertragen am ${fmtDate(d.lexware_uebertragen_am)}`}>Lexware ✓</span>
                          : <button style={{ ...S.btnSmall, marginLeft: 6, background: C.accent }} onClick={() => pushLexware(d)}>→ Lexware</button>)}
                        {isStaff && <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => removeDoc(d)}>Löschen</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}

          {showPayslips && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>Lohndokumente der Mitarbeiter{isMonthFolder ? ` — ${currentName}` : ''}</h2>
              <div style={S.pathInfo}>{isStaff
                ? 'Die Abrechnungen liegen bei den Mitarbeitern; hier zusätzlich gebündelt. Freigabe schaltet sie für den jeweiligen Mitarbeiter sichtbar.'
                : 'Geben Sie die Abrechnungen frei, damit Ihre Mitarbeiter sie in ihrem eigenen Zugang sehen. (Gleiche Freigabe wie im Tab „Mitarbeiter".)'}</div>
              {payslips.length === 0 ? <div style={S.empty}>Hier sind noch keine Lohndokumente hochgeladen.</div> : payslipMonths.map(mon => {
                const rows = payslips.filter(d => (d.monat || '—') === mon).sort((a, b) => maLabel(a.mitarbeiter_id).localeCompare(maLabel(b.mitarbeiter_id)) || String(a.doc_type).localeCompare(String(b.doc_type)));
                return (
                  <div key={mon} style={{ marginBottom: 14 }}>
                    {isLohnFolder && <div style={{ fontWeight: 600, color: C.primary, margin: '4px 0 6px', fontSize: 14 }}>📅 {mon === '—' ? 'Ohne Monat' : mon}</div>}
                    <table style={S.table}>
                      <thead><tr><th style={S.th}>Mitarbeiter</th><th style={S.th}>Art</th><th style={S.th}>Datei</th><th style={S.th}>Freigabe</th><th style={S.th}>Aktionen</th></tr></thead>
                      <tbody>
                        {rows.map(d => (
                          <tr key={d.id}>
                            <td style={S.td}>{maLabel(d.mitarbeiter_id)}</td>
                            <td style={S.td}><span style={{ ...S.badge, background: (LOHN_DOK_FARBE[d.doc_type] || LOHN_DOK_FARBE.lohnabrechnung)[0], color: (LOHN_DOK_FARBE[d.doc_type] || LOHN_DOK_FARBE.lohnabrechnung)[1] }}>{LOHN_DOK_LABEL[d.doc_type] || d.doc_type}</span></td>
                            <td style={S.td}>{d.original_name}</td>
                            <td style={S.td}>{d.freigegeben
                              ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>✓ freigegeben</span>
                              : <span style={{ ...S.badge, background: '#fdeccd', color: '#8a6d3b' }}>nicht freigegeben</span>}</td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                              <button style={{ ...S.btnSmall, background: C.accent }} onClick={() => setPreviewDoc(d)}>Ansehen</button>
                              <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => download(d)}>Download</button>
                              <button style={{ ...S.btnSmall, marginLeft: 6, background: d.freigegeben ? C.muted : C.success }} onClick={() => toggleFreigabe(d)}>{d.freigegeben ? 'Freigabe zurückziehen' : 'Freigeben'}</button>
                              {isStaff && <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => removeDoc(d)}>Löschen</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {err && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{err}</div>}
      {auswOpen && <AuswertungUpload mandantId={mandantId} onClose={() => setAuswOpen(false)} onDone={load} />}
      {previewDoc && <DocPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} canFreigabe={istLohnDok(previewDoc)} onChanged={load} />}
    </div>
  );
}

const CHAT_REQ_TYPES = [
  { v: 'arbeitsvertrag', l: 'Arbeitsvertrag' },
  { v: 'personalfragebogen', l: 'Personalfragebogen' },
  { v: 'personalausweis', l: 'Personalausweis' },
  { v: 'immatrikulation', l: 'Immatrikulation' },
  { v: 'sonstige', l: 'Sonstige' },
];

// Kleines Ablagefeld für Anhänge: Dateien hineinziehen oder klicken, mehrere nacheinander sammeln, einzeln entfernen
function AnhangFeld({ files, onChange }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const schluessel = (x) => `${x.name}|${x.size}|${x.lastModified}`;
  function add(list) {
    const neu = Array.from(list || []);
    if (!neu.length) return;
    const da = new Set(files.map(schluessel));
    onChange([...files, ...neu.filter(x => !da.has(schluessel(x)))]);
  }
  return (
    <div style={{ flex: '1 1 100%' }}>
      <div role="button" tabIndex={0} onClick={() => inputRef.current && inputRef.current.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (inputRef.current) inputRef.current.click(); } }}
        onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        style={{ border: `1.5px dashed ${over ? C.primary : C.border}`, background: over ? '#eaf1f8' : C.bg, borderRadius: 8, padding: '7px 12px', fontSize: 12.5, color: over ? C.primary : C.muted, cursor: 'pointer', textAlign: 'center' }}>
        📎 Anhänge hierher ziehen oder klicken – mehrere möglich
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { add(e.target.files); e.target.value = ''; }} />
      </div>
      {files.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {files.map((x, i) => (
            <span key={schluessel(x) + i} style={{ ...S.badge, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 280 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.name}>{x.name}</span>
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} title="Anhang entfernen"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.danger, fontWeight: 700, padding: 0, fontSize: 12 }}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Messages({ user, mandanten, mandantId, onOpenTicket, onRead }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [msgs, setMsgs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [maList, setMaList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [anforderungen, setAnforderungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [maId, setMaId] = useState('');
  const [attach, setAttach] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reqOpen, setReqOpen] = useState(false);
  const [reqTitel, setReqTitel] = useState('');
  const [reqMa, setReqMa] = useState('');
  const [reqType, setReqType] = useState('arbeitsvertrag');
  const [showAllTickets, setShowAllTickets] = useState(false);
  const endRef = useRef(null);
  const taRef = useRef(null);

  const ready = isStaff ? !!mandantId : true;
  const effectiveMandant = isStaff ? mandantId : (user.mandant_id ?? '');

  const load = useCallback(async () => {
    if (!ready) { setMsgs([]); return; }
    setLoading(true); setErr('');
    try {
      const params = isStaff ? { mandant_id: mandantId } : {};
      const [mRes, tRes, maRes, dRes, aRes] = await Promise.all([
        api.get('/messages', { params }),
        api.get('/tickets', { params }).catch(() => ({ data: [] })),
        api.get('/mitarbeiter', { params }).catch(() => ({ data: [] })),
        api.get('/documents', { params }).catch(() => ({ data: [] })),
        api.get('/anforderungen', { params }).catch(() => ({ data: [] })),
      ]);
      const data = mRes.data; data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMsgs(data); setTickets(tRes.data); setMaList(maRes.data); setDocs(dRes.data); setAnforderungen(aRes.data);
      // Chat ist offen → Nachrichten der Gegenseite gelten als gelesen (rote Zahl verschwindet)
      const gf = new FormData(); if (isStaff) gf.append('mandant_id', mandantId);
      api.post('/messages/gelesen', gf).then(() => { if (onRead) onRead(); }).catch(() => {});
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId, onRead]);
  useEffect(() => { load(); }, [load]);

  const ticketTitle = (id) => tickets.find(t => t.id === id)?.titel || `#${id}`;
  const maName = (id) => { const m = maList.find(x => x.id === id); return m ? `${m.vorname} ${m.nachname}` : `MA #${id}`; };
  const docsForMsg = (id) => docs.filter(d => d.message_id === id);

  // Aussagekräftiges Label für die Ticket-Bezug-Auswahl
  const ticketLabel = (t) => {
    const parts = [];
    if (t.mitarbeiter_id) parts.push(maName(t.mitarbeiter_id));
    parts.push((t.titel || '').length > 45 ? (t.titel || '').slice(0, 45) + '…' : (t.titel || ''));
    parts.push(t.status === 'offen' ? '[offen]' : '[erledigt]');
    return parts.join(' · ');
  };
  const ticketOptions = [...tickets]
    .filter(t => showAllTickets || t.status === 'offen')
    .sort((a, b) => (a.status === b.status ? 0 : a.status === 'offen' ? -1 : 1));

  // Beim Laden / neuer Nachricht ans Ende scrollen
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: 'end' }); }, [msgs, loading]);

  // Textfeld automatisch mitwachsen lassen (min ~3, max ~12 Zeilen)
  function autoGrow(el) {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 280) + 'px';
  }
  useEffect(() => { autoGrow(taRef.current); }, [content]);

  async function download(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = d.original_name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  async function send(e) {
    if (e) e.preventDefault();
    setErr('');
    if (!content.trim() && attach.length === 0) return;
    if (isStaff && !mandantId) { setErr('Bitte Mandant wählen'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('content', content.trim() || '(Anhang)');
      fd.append('mandant_id', effectiveMandant);
      if (ticketId) fd.append('ticket_id', ticketId);
      if (maId) fd.append('mitarbeiter_id', maId);
      const { data } = await api.post('/messages', fd);
      if (attach.length) {
        const ufd = new FormData();
        attach.forEach(f => ufd.append('files', f));
        ufd.append('mandant_id', effectiveMandant);
        ufd.append('message_id', data.id);
        if (maId) { ufd.append('mitarbeiter_id', maId); ufd.append('doc_type', 'sonstige'); }
        ufd.append('direction', isStaff ? 'to_client' : 'from_client');
        await api.post('/documents/upload', ufd);
      }
      setContent(''); setTicketId(''); setMaId(''); setAttach([]);
      const inp = document.getElementById('chat-attach'); if (inp) inp.value = '';
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  async function createRequest() {
    if (!reqTitel.trim()) { setErr('Bitte Bezeichnung angeben'); return; }
    try {
      const fd = new FormData();
      fd.append('titel', reqTitel);
      fd.append('mandant_id', effectiveMandant);
      fd.append('doc_type', reqType);
      if (reqMa) fd.append('mitarbeiter_id', reqMa);
      await api.post('/anforderungen', fd);
      setReqTitel(''); setReqMa(''); setReqOpen(false);
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Anforderung fehlgeschlagen'); }
  }

  function fulfillRequest(a) {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async () => {
      if (!input.files.length) return;
      try {
        const fd = new FormData();
        fd.append('files', input.files[0]);
        fd.append('mandant_id', effectiveMandant);
        fd.append('anforderung_id', a.id);
        fd.append('direction', isStaff ? 'to_client' : 'from_client');
        await api.post('/documents/upload', fd);
        await load();
      } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    };
    input.click();
  }

  const timeline = [
    ...msgs.map(m => ({ kind: 'msg', at: m.created_at, data: m })),
    ...anforderungen.map(a => ({ kind: 'req', at: a.created_at, data: a })),
  ].sort((x, y) => new Date(x.at) - new Date(y.at));

  if (!ready) return <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen, um den Chat anzuzeigen.</div></div>;

  return (
    <div>
      <div style={{ ...S.card, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)', marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}`, paddingBottom: 8, marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Chat mit {isStaff ? 'Mandant' : 'Lohnbüro'}</h2>
          {isStaff && <button style={S.btnSmall} onClick={() => setReqOpen(o => !o)}>📎 Dokument anfordern</button>}
        </div>

        {isStaff && reqOpen && (
          <div style={{ ...S.row, marginBottom: 10, background: C.bg, padding: 10, borderRadius: 6 }}>
            <div style={S.field}><label style={S.label}>Bezeichnung</label><input style={S.input} value={reqTitel} onChange={e => setReqTitel(e.target.value)} placeholder="z.B. Arbeitsvertrag" /></div>
            <div style={S.field}><label style={S.label}>Ziel-Ablage</label><select style={S.select} value={reqType} onChange={e => setReqType(e.target.value)}>{CHAT_REQ_TYPES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}</select></div>
            <div style={S.field}><label style={S.label}>Mitarbeiter</label><select style={S.select} value={reqMa} onChange={e => setReqMa(e.target.value)}><option value="">— optional —</option>{maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}</select></div>
            <div><button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={createRequest}>Anfordern</button></div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 2px' }}>
          {loading ? <div style={S.empty}>Lade…</div> : timeline.length === 0 ? <div style={S.empty}>Noch keine Nachrichten</div> : timeline.map((item) => {
            if (item.kind === 'req') {
              const a = item.data;
              return (
                <div key={`r${a.id}`} style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                  <div style={{ background: '#fff7e6', border: '1px solid #ffe1a8', borderRadius: 8, padding: '8px 12px', fontSize: 13, maxWidth: '85%' }}>
                    📎 <strong>Dokument angefordert:</strong> {a.titel}{a.mitarbeiter_id ? ` · ${maName(a.mitarbeiter_id)}` : ''} — {a.status === 'erfuellt' ? <span style={{ color: C.success }}>erledigt ✓</span> : <span style={{ color: '#8a6d3b' }}>offen</span>}
                    {a.status !== 'erfuellt' && <button style={{ ...S.btnSmall, marginLeft: 10 }} onClick={() => fulfillRequest(a)}>Hochladen</button>}
                  </div>
                </div>
              );
            }
            const m = item.data;
            const left = m.sender_seite !== 'mandant';
            const atts = docsForMsg(m.id);
            return (
              <div key={`m${m.id}`} style={{ display: 'flex', justifyContent: left ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
                <div style={{ maxWidth: '72%', background: left ? C.white : '#eaf1f8', border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px' }}>
                  <div style={S.msgMeta}><strong>{m.sender_name || (m.sender_seite === 'mandant' ? 'Mandant' : 'Lohnbüro')}</strong> <span style={{ ...S.badge, background: m.sender_seite === 'mandant' ? '#e8f4ea' : '#e8eef5', color: m.sender_seite === 'mandant' ? C.success : C.accent, fontSize: 10, padding: '1px 6px' }}>{m.sender_seite === 'mandant' ? 'Mandant' : 'Lohnbüro'}</span> · {fmtDate(m.created_at)}{m.mitarbeiter_id ? ` · 👤 ${maName(m.mitarbeiter_id)}` : ''}</div>
                  <div style={S.msgBody}>{m.content}</div>
                  {isStaff && m.ticket_id ? <div style={{ marginTop: 4 }}><span style={{ ...S.badge, cursor: 'pointer' }} onClick={() => onOpenTicket && onOpenTicket(m.ticket_id)}>🎫 #{m.ticket_id}: {ticketTitle(m.ticket_id)}</span></div> : null}
                  {atts.map(d => <div key={d.id} style={{ marginTop: 4, fontSize: 13 }}><span style={{ cursor: 'pointer', color: C.accent }} onClick={() => download(d)}>📎 {d.bezeichnung || d.original_name}</span></div>)}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
            <select style={{ ...S.select, width: 'auto', flex: '1 1 160px' }} value={maId} onChange={e => setMaId(e.target.value)}><option value="">MA-Label (optional)</option>{maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}</select>
            {isStaff && <>
              <select style={{ ...S.select, width: 'auto', flex: '1 1 220px' }} value={ticketId} onChange={e => setTicketId(e.target.value)}>
                <option value="">Ticket-Bezug (optional)</option>
                {ticketOptions.map(t => <option key={t.id} value={t.id}>{ticketLabel(t)}</option>)}
              </select>
              <label style={{ fontSize: 12, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} title="Auch erledigte Tickets anzeigen">
                <input type="checkbox" checked={showAllTickets} onChange={e => setShowAllTickets(e.target.checked)} /> auch erledigte
              </label>
            </>}
            <AnhangFeld files={attach} onChange={setAttach} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea ref={taRef} rows={3} style={{ ...S.textarea, minHeight: 76, maxHeight: 280, flex: 1, overflowY: 'auto' }} value={content}
                      onChange={e => setContent(e.target.value)}
                      onInput={e => autoGrow(e.target)}
                      placeholder="Nachricht… (Enter = neue Zeile, Strg/⌘+Enter sendet)"
                      onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}
                      onDragOver={e => { if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) e.preventDefault(); }}
                      onDrop={e => { const fl = e.dataTransfer && e.dataTransfer.files; if (fl && fl.length) { e.preventDefault(); setAttach(a => [...a, ...Array.from(fl)]); } }} />
            <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={send}>{busy ? '…' : 'Senden'}</button>
          </div>
          {err && <div style={S.err}>{err}</div>}
        </div>
      </div>
    </div>
  );
}

function OnboardingVorlagenCard() {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { const { data } = await api.get('/onboarding-vorlagen'); setRows(data); } catch { /* ignore */ } }, []);
  useEffect(() => { load(); }, [load]);
  function upload(typ) {
    const input = document.createElement('input'); input.type = 'file'; input.accept = 'application/pdf';
    input.onchange = async () => {
      if (!input.files.length) return;
      setErr(''); setMsg('');
      try { const fd = new FormData(); fd.append('typ', typ); fd.append('file', input.files[0]); await api.post('/onboarding-vorlagen', fd); setMsg('Vorlage hochgeladen.'); await load(); }
      catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    };
    input.click();
  }
  async function view(typ) {
    try { const res = await api.get(`/onboarding-vorlagen/${typ}/download`, { responseType: 'blob' }); window.open(URL.createObjectURL(res.data), '_blank'); }
    catch { setErr('PDF konnte nicht geöffnet werden'); }
  }
  return (
    <div style={S.card}>
      <h2 style={S.cardTitle}>Onboarding-Dokumente (Vorlagen)</h2>
      <div style={S.pathInfo}>Vollmacht und Dienstvertrag (inkl. Auftragsverarbeitung) erzeugt das Portal automatisch mit den Stammblatt-Daten des Mandanten; die Unterschrift steht direkt im Unterschriftsfeld. Der Vertrag übernimmt die Preise aus dem zuletzt versendeten Angebot, sonst die Standardpreise. Eine Datenschutzerklärung erscheint nur, wenn hier eine Vorlage hinterlegt ist.</div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>Dokument</th><th style={S.th}>Status</th><th style={S.th}>Aktion</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.typ}>
            <td style={S.td}>{r.label}</td>
            <td style={S.td}>{r.automatisch ? <span style={{ ...S.badge, background: '#e8eef5', color: C.accent }}>automatisch ausgefüllt</span> : r.vorhanden ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>hinterlegt</span> : <span style={{ ...S.badge }}>keine Vorlage – wird nicht abgefragt</span>}</td>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
              {!r.automatisch && <button style={S.btnSmall} onClick={() => upload(r.typ)}>{r.vorhanden ? 'Ersetzen' : 'Hochladen'}</button>}
              {r.vorhanden && <button style={{ ...S.btnSmall, marginLeft: r.automatisch ? 0 : 6 }} onClick={() => view(r.typ)}>{r.automatisch ? 'Muster ansehen' : 'Ansehen'}</button>}
            </td>
          </tr>
        ))}</tbody>
      </table>
      {msg && <div style={S.okMsg}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}
    </div>
  );
}

function MandantBearbeiterCard({ mandanten, users }) {
  const staffUsers = users.filter(u => u.role === 'admin' || u.role === 'staff');
  const [selMandant, setSelMandant] = useState('');
  const [assigned, setAssigned] = useState([]);
  const [addId, setAddId] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!selMandant) { setAssigned([]); return; }
    try { const { data } = await api.get(`/mandanten/${selMandant}/bearbeiter`); setAssigned(data); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
  }, [selMandant]);
  useEffect(() => { load(); }, [load]);

  const userLabel = (id) => { const u = staffUsers.find(x => x.id === id); return u ? (u.name || u.username) : `#${id}`; };
  const nichtZugeordnet = staffUsers.filter(u => !assigned.some(a => a.user_id === u.id));

  async function add() {
    if (!addId) return;
    setErr('');
    try { const fd = new FormData(); fd.append('user_id', addId); await api.post(`/mandanten/${selMandant}/bearbeiter`, fd); setAddId(''); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Zuordnen fehlgeschlagen'); }
  }
  async function remove(uid) {
    setErr('');
    try { await api.delete(`/mandanten/${selMandant}/bearbeiter/${uid}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Entfernen fehlgeschlagen'); }
  }
  async function setHaupt(uid, val) {
    setErr('');
    try { const fd = new FormData(); fd.append('ist_hauptbearbeiter', val ? 'true' : 'false'); await api.patch(`/mandanten/${selMandant}/bearbeiter/${uid}`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  return (
    <div style={S.card}>
      <h2 style={S.cardTitle}>Mandanten ↔ Bearbeiter</h2>
      <div style={S.pathInfo}>Ordnen Sie jedem Mandanten einen oder mehrere Bearbeiter (Lohnbüro) zu. Mit dem Stern markieren Sie den Hauptbearbeiter. Bearbeiter sehen im Cockpit nur ihre zugeordneten Mandanten.</div>
      <div style={S.row}>
        <div style={{ ...S.field, flex: '2 1 260px' }}>
          <label style={S.label}>Mandant</label>
          <select style={S.select} value={selMandant} onChange={e => setSelMandant(e.target.value)}>
            <option value="">— wählen —</option>
            {mandanten.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>
      {selMandant && (
        <>
          {assigned.length === 0 ? <div style={S.empty}>Noch kein Bearbeiter zugeordnet</div> : (
            <table style={{ ...S.table, marginTop: 10 }}>
              <thead><tr><th style={S.th}>Bearbeiter</th><th style={S.th}>Hauptbearbeiter</th><th style={S.th}></th></tr></thead>
              <tbody>{assigned.map(a => (
                <tr key={a.user_id}>
                  <td style={S.td}>{a.name || a.username || userLabel(a.user_id)}</td>
                  <td style={S.td}>
                    <button style={{ ...S.btnSmall, background: a.ist_hauptbearbeiter ? '#e0a800' : C.muted }} onClick={() => setHaupt(a.user_id, !a.ist_hauptbearbeiter)}>
                      {a.ist_hauptbearbeiter ? '★ Hauptbearbeiter' : '☆ als Haupt setzen'}
                    </button>
                  </td>
                  <td style={S.td}><button style={S.btnDanger} onClick={() => remove(a.user_id)}>Entfernen</button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
          <div style={{ ...S.row, marginTop: 12 }}>
            <div style={{ ...S.field, flex: '2 1 260px' }}>
              <label style={S.label}>Bearbeiter hinzufügen</label>
              <select style={S.select} value={addId} onChange={e => setAddId(e.target.value)}>
                <option value="">— wählen —</option>
                {nichtZugeordnet.map(u => <option key={u.id} value={u.id}>{u.name || u.username} ({u.role})</option>)}
              </select>
            </div>
            <div><button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={add} disabled={!addId}>Zuordnen</button></div>
          </div>
        </>
      )}
      {err && <div style={S.err}>{err}</div>}
    </div>
  );
}

function Admin({ user, mandanten, reloadMandanten }) {
  const [users, setUsers] = useState([]);
  const [alleMandanten, setAlleMandanten] = useState([]);
  const loadAlle = useCallback(async () => {
    try { const { data } = await api.get('/mandanten', { params: { alle: 1 } }); setAlleMandanten(data); } catch { /* */ }
  }, []);
  useEffect(() => { loadAlle(); }, [loadAlle]);
  const [auditRows, setAuditRows] = useState([]);
  const [newMandant, setNewMandant] = useState('');
  const [newMandantEigen, setNewMandantEigen] = useState(false);
  const [newMandantOnbEmail, setNewMandantOnbEmail] = useState('');
  const [mandantMsg, setMandantMsg] = useState('');
  const [nu, setNu] = useState({ username: '', name: '', email: '', password: '', role: 'client_user', mandant_id: '' });
  const [err, setErr] = useState('');

  const loadUsers = useCallback(async () => {
    try { const { data } = await api.get('/users'); setUsers(data); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Benutzer konnten nicht geladen werden'); }
  }, []);

  const loadAudit = useCallback(async () => {
    try { const { data } = await api.get('/audit', { params: { limit: 200 } }); setAuditRows(data); }
    catch { /* ignore */ }
  }, []);

  const [kanzlei, setKanzlei] = useState(null);
  const [kanzleiMsg, setKanzleiMsg] = useState('');
  const [lexKanzleiKey, setLexKanzleiKey] = useState('');
  const loadKanzlei = useCallback(async () => {
    try { const { data } = await api.get('/kanzlei'); setKanzlei(data); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { loadUsers(); loadAudit(); loadKanzlei(); }, [loadUsers, loadAudit, loadKanzlei]);

  async function saveKanzlei(e) {
    e.preventDefault(); setKanzleiMsg(''); setErr('');
    try {
      const fd = new FormData();
      ['name', 'anschrift', 'steuernummer', 'ust_idnr', 'iban', 'bic', 'bank', 'email', 'telefon', 'rechnung_prefix', 'fusszeile'].forEach(k => fd.append(k, kanzlei[k] ?? ''));
      fd.append('kleinunternehmer', kanzlei.kleinunternehmer ? 'true' : 'false');
      fd.append('ust_satz', String(kanzlei.ust_satz ?? 19).replace(',', '.'));
      fd.append('zahlungsziel_tage', String(kanzlei.zahlungsziel_tage ?? 14));
      if (lexKanzleiKey.trim()) fd.append('lexware_api_key', lexKanzleiKey.trim());
      await api.put('/kanzlei', fd);
      setLexKanzleiKey('');
      setKanzleiMsg('Kanzlei-Stammdaten gespeichert.');
      await loadKanzlei();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  }
  const setK = (key, val) => setKanzlei(p => ({ ...(p || {}), [key]: val }));

  async function createMandant(e) {
    e.preventDefault(); setErr('');
    if (!newMandant.trim()) return;
    try {
      const fd = new FormData(); fd.append('name', newMandant);
      if (newMandantEigen) fd.append('ist_eigenes_lohnbuero', 'true');
      if (newMandantOnbEmail.trim()) fd.append('onboarding_email', newMandantOnbEmail.trim());
      const { data } = await api.post('/mandanten', fd);
      setMandantMsg(data.onboarding_gesendet ? `Mandant angelegt – Onboarding-Einladung an ${newMandantOnbEmail.trim()} gesendet.` : 'Mandant angelegt.');
      setNewMandant(''); setNewMandantEigen(false); setNewMandantOnbEmail(''); await reloadMandanten(); await loadAlle();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
  }

  async function createUser(e) {
    e.preventDefault(); setErr('');
    try {
      const fd = new FormData();
      fd.append('username', nu.username);
      if (nu.name) fd.append('name', nu.name);
      fd.append('email', nu.email);
      fd.append('password', nu.password);
      fd.append('role', nu.role);
      if (nu.mandant_id) fd.append('mandant_id', nu.mandant_id);
      await api.post('/users', fd);
      setNu({ username: '', name: '', email: '', password: '', role: 'client_user', mandant_id: '' });
      await loadUsers();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Benutzer anlegen fehlgeschlagen'); }
  }

  async function changePassword(u) {
    const pw = window.prompt(`Neues Passwort für „${u.username}":`);
    if (!pw || !pw.trim()) return;
    try { const fd = new FormData(); fd.append('password', pw); await api.patch(`/users/${u.id}`, fd); setErr(''); window.alert('Passwort geändert.'); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Passwort ändern fehlgeschlagen'); }
  }

  async function toggleActive(u) {
    try { const fd = new FormData(); fd.append('is_active', u.is_active ? 'false' : 'true'); await api.patch(`/users/${u.id}`, fd); await loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  async function changeEmail(u) {
    const v = window.prompt(`E-Mail-Adresse für „${u.name || u.username}":\n\nAn diese Adresse gehen die Portal-Benachrichtigungen (z.B. „Neue Nachricht von …", neue Tickets/Dokumente vom Mandanten).`, u.email || '');
    if (v === null) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) { setErr('Bitte eine gültige E-Mail-Adresse angeben.'); return; }
    try { const fd = new FormData(); fd.append('email', v.trim()); await api.patch(`/users/${u.id}`, fd); setErr(''); await loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'E-Mail ändern fehlgeschlagen'); }
  }

  async function setKostensatz(u) {
    const cur = u.kostensatz != null ? String(u.kostensatz).replace('.', ',') : '';
    const v = window.prompt(`Interner Kostensatz (€/Stunde) für „${u.name || u.username}":\n(leer = Standard 75 €)`, cur);
    if (v === null) return;
    try { const fd = new FormData(); fd.append('kostensatz', v.trim()); await api.patch(`/users/${u.id}`, fd); await loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Kostensatz speichern fehlgeschlagen'); }
  }
  async function setAgBrutto(u) {
    const cur = u.ag_brutto_monat != null ? String(u.ag_brutto_monat).replace('.', ',') : '';
    const v = window.prompt(`Monatliche AG-Kosten (Brutto inkl. AG-Anteil) für „${u.name || u.username}":\n(für die Deckungsbeitrags-Auswertung; leer = keine)`, cur);
    if (v === null) return;
    try { const fd = new FormData(); fd.append('ag_brutto_monat', v.trim()); await api.patch(`/users/${u.id}`, fd); await loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  }

  async function deleteUser(u) {
    if (!window.confirm(`Benutzer „${u.username}" wirklich löschen?`)) return;
    try { await api.delete(`/users/${u.id}`); await loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  const mandantName = (id) => alleMandanten.find(m => m.id === id)?.name || (id ? `#${id}` : '—');

  return (
    <div>
      {err && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{err}</div>}

      {kanzlei && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>Kanzlei-Stammdaten (für Rechnungen)</h2>
          <div style={S.pathInfo}>Diese Angaben erscheinen auf jeder Rechnung. Pflicht für rechtssichere Rechnungen: Name, Anschrift und Steuernummer oder USt-IdNr.</div>
          <form onSubmit={saveKanzlei}>
            <div style={S.row}>
              <div style={{ ...S.field, flex: '2 1 280px' }}><label style={S.label}>Kanzlei-Name</label><input style={S.input} value={kanzlei.name || ''} onChange={e => setK('name', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>E-Mail</label><input style={S.input} value={kanzlei.email || ''} onChange={e => setK('email', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>Telefon</label><input style={S.input} value={kanzlei.telefon || ''} onChange={e => setK('telefon', e.target.value)} /></div>
            </div>
            <label style={S.label}>Anschrift (Straße, PLZ Ort — mehrzeilig)</label>
            <textarea style={{ ...S.textarea, minHeight: 60 }} value={kanzlei.anschrift || ''} onChange={e => setK('anschrift', e.target.value)} placeholder={'Die Lohnkanzlei GmbH\nMusterstraße 1\n12345 Musterstadt'} />
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Steuernummer</label><input style={S.input} value={kanzlei.steuernummer || ''} onChange={e => setK('steuernummer', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>USt-IdNr.</label><input style={S.input} value={kanzlei.ust_idnr || ''} onChange={e => setK('ust_idnr', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Bank</label><input style={S.input} value={kanzlei.bank || ''} onChange={e => setK('bank', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>IBAN</label><input style={S.input} value={kanzlei.iban || ''} onChange={e => setK('iban', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>BIC</label><input style={S.input} value={kanzlei.bic || ''} onChange={e => setK('bic', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Rechnungsnr.-Präfix</label><input style={S.input} value={kanzlei.rechnung_prefix || ''} onChange={e => setK('rechnung_prefix', e.target.value)} placeholder="RE-" /></div>
              <div style={S.field}><label style={S.label}>Zahlungsziel (Tage)</label><input style={S.input} value={kanzlei.zahlungsziel_tage ?? 14} onChange={e => setK('zahlungsziel_tage', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>USt-Satz (%)</label><input style={S.input} value={kanzlei.ust_satz ?? 19} onChange={e => setK('ust_satz', e.target.value)} disabled={kanzlei.kleinunternehmer} /></div>
              <div style={S.field}><label style={{ ...S.label, marginTop: 22 }}><input type="checkbox" checked={!!kanzlei.kleinunternehmer} onChange={e => setK('kleinunternehmer', e.target.checked)} /> Kleinunternehmer (§19 UStG)</label></div>
            </div>
            <label style={S.label}>Fußzeile (optional, z.B. Geschäftsführer / Registergericht)</label>
            <textarea style={{ ...S.textarea, minHeight: 44 }} value={kanzlei.fusszeile || ''} onChange={e => setK('fusszeile', e.target.value)} />
            <label style={{ ...S.label, marginTop: 12 }}>Lexware Office API-Key (für Ausgangsrechnungen) — Status: {kanzlei.lexware_konfiguriert ? <span style={{ color: C.success }}>hinterlegt ✓</span> : <span style={{ color: C.muted }}>nicht hinterlegt</span>}</label>
            <input style={S.input} type="password" value={lexKanzleiKey} onChange={e => setLexKanzleiKey(e.target.value)} placeholder={kanzlei.lexware_konfiguriert ? 'Zum Ändern neuen Key eingeben…' : 'Lexware API-Key des Büro-Kontos'} autoComplete="off" />
            <div style={S.pathInfo}>Damit werden im Tab „Leistungen" erzeugte Rechnungen als Ausgangsrechnung an das Lexware Office des Büros übertragen.</div>
            <div style={{ marginTop: 10 }}><button style={{ ...S.btnPrimary, marginTop: 0 }}>Kanzlei-Stammdaten speichern</button></div>
            {kanzleiMsg && <div style={S.okMsg}>{kanzleiMsg}</div>}
          </form>
        </div>
      )}

      <div style={S.card}>
        <h2 style={S.cardTitle}>Neuen Mandanten anlegen</h2>
        <form onSubmit={createMandant}>
          <div style={S.row}>
            <div style={S.field}>
              <label style={S.label}>Name</label>
              <input style={S.input} value={newMandant} onChange={e => setNewMandant(e.target.value)} placeholder="Firmenname" />
            </div>
            <div style={S.field}>
              <label style={S.label}>Onboarding-E-Mail (optional)</label>
              <input style={S.input} value={newMandantOnbEmail} onChange={e => setNewMandantOnbEmail(e.target.value)} placeholder="kontakt@firma.de – Einladung sofort senden" />
            </div>
            <div><button style={S.btnPrimary}>Anlegen</button></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={newMandantEigen} onChange={e => setNewMandantEigen(e.target.checked)} />
            Eigenes Lohnbüro (eigene Mitarbeiter verwalten – ohne Rechnungstellung &amp; Leistungserfassung)
          </label>
          <div style={S.pathInfo}>Mit Onboarding-E-Mail erhält der Mandant sofort einen Einladungslink (Konto, Stammblatt, Unterlagen).</div>
          {mandantMsg && <div style={S.okMsg}>{mandantMsg}</div>}
        </form>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Mandanten</h2>
        <div style={S.pathInfo}>Löschen entfernt den Mandanten mit allen Daten (Mitarbeiter, Dokumente, Logins, Tickets) – gedacht für Test-/Fehlanlagen. Mandanten mit Rechnungen sind aus Aufbewahrungsgründen gesperrt.</div>
        {alleMandanten.length === 0 ? <div style={S.empty}>Keine Mandanten</div> : (
          <table style={S.table}>
            <thead><tr><th style={S.th}>ID</th><th style={S.th}>Mandanten-Nr.</th><th style={S.th}>Name</th><th style={S.th}></th></tr></thead>
            <tbody>{alleMandanten.map(m => (
              <tr key={m.id}>
                <td style={S.td}>{m.id}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  {m.kundennummer ? <strong>{m.kundennummer}</strong> : <span style={{ color: C.muted }}>—</span>}
                  {!m.ist_eigenes_lohnbuero && (
                    <button style={{ ...S.btnSmall, marginLeft: 8 }} title="Mandantennummer festlegen (erscheint auf den Rechnungen)" onClick={async () => {
                      const v = window.prompt(`Mandantennummer für „${m.name}":\n\nWie im bisherigen System vergeben – erscheint auf allen neuen Rechnungen.`, m.kundennummer || '');
                      if (v === null) return;
                      try {
                        const fd = new FormData(); fd.append('mandant_id', m.id); fd.append('kundennummer', v.trim());
                        await api.put('/mandant-settings', fd);
                        await loadAlle(); if (reloadMandanten) reloadMandanten();
                      } catch (ex) { window.alert(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
                    }}>{m.kundennummer ? 'ändern' : 'festlegen'}</button>
                  )}
                </td>
                <td style={S.td}>{m.name}{m.ist_dummy ? <span style={{ ...S.badge, marginLeft: 6 }}>Test</span> : null}{m.ist_eigenes_lohnbuero ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8eef5', color: C.accent }}>eigenes Büro</span> : null}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>
                  {!m.ist_eigenes_lohnbuero && (
                    <button style={{ ...S.btnSmall, background: C.danger }} onClick={async () => {
                      const eingabe = window.prompt(`Mandant „${m.name}" ENDGÜLTIG löschen?\n\nAlle zugehörigen Daten werden entfernt: Mitarbeiter, Dokumente, Logins, Tickets, Nachrichten, Zeiten.\n\nZur Bestätigung den Mandantennamen exakt eingeben:`);
                      if (eingabe === null) return;
                      if (eingabe.trim() !== m.name) { window.alert('Name stimmt nicht überein – es wurde nichts gelöscht.'); return; }
                      try {
                        await api.delete(`/mandanten/${m.id}`);
                        window.alert(`Mandant „${m.name}" wurde gelöscht.`);
                        await loadAlle(); if (reloadMandanten) reloadMandanten();
                      } catch (ex) { window.alert(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
                    }}>Löschen</button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Neuen Benutzer anlegen</h2>
        <form onSubmit={createUser}>
          <div style={S.row}>
            <div style={S.field}>
              <label style={S.label}>Benutzername</label>
              <input style={S.input} value={nu.username} onChange={e => setNu({ ...nu, username: e.target.value })} required />
            </div>
            <div style={S.field}>
              <label style={S.label}>Name (Realname)</label>
              <input style={S.input} value={nu.name} onChange={e => setNu({ ...nu, name: e.target.value })} placeholder="z.B. Max Mustermann" />
            </div>
            <div style={S.field}>
              <label style={S.label}>E-Mail</label>
              <input style={S.input} type="email" value={nu.email} onChange={e => setNu({ ...nu, email: e.target.value })} required />
            </div>
            <div style={S.field}>
              <label style={S.label}>Passwort</label>
              <input style={S.input} type="password" value={nu.password} onChange={e => setNu({ ...nu, password: e.target.value })} required />
            </div>
          </div>
          <div style={S.row}>
            <div style={S.field}>
              <label style={S.label}>Rolle</label>
              <select style={S.select} value={nu.role} onChange={e => setNu({ ...nu, role: e.target.value })}>
                <option value="admin">admin</option>
                <option value="staff">staff</option>
                <option value="client_admin">client_admin</option>
                <option value="client_user">client_user</option>
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Mandant (optional)</label>
              <select style={S.select} value={nu.mandant_id} onChange={e => setNu({ ...nu, mandant_id: e.target.value })}>
                <option value="">— kein Mandant —</option>
                {alleMandanten.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div><button style={S.btnPrimary}>Anlegen</button></div>
          </div>
        </form>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Benutzer</h2>
        {users.length === 0 ? <div style={S.empty}>Keine Benutzer</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>ID</th><th style={S.th}>Benutzername</th><th style={S.th}>Name</th><th style={S.th}>E-Mail</th>
              <th style={S.th}>Rolle</th><th style={S.th}>Mandant</th><th style={S.th}>Kostensatz</th><th style={S.th}>Aktiv</th><th style={S.th}>Aktionen</th>
            </tr></thead>
            <tbody>{users.map(u => (
              <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                <td style={S.td}>{u.id}</td>
                <td style={S.td}>{u.username}</td>
                <td style={S.td}>{u.name || '—'}</td>
                <td style={S.td}>{u.email}</td>
                <td style={S.td}>{u.role}</td>
                <td style={S.td}>{mandantName(u.mandant_id)}</td>
                <td style={S.td}>{(u.role === 'staff' || u.role === 'admin') ? (u.kostensatz != null ? `${String(u.kostensatz).replace('.', ',')} €/h` : <span style={{ color: C.muted }}>Standard (75 €)</span>) : '—'}</td>
                <td style={S.td}>{u.is_active ? 'Ja' : 'Nein'}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  {(u.role === 'staff' || u.role === 'admin') && <button style={{ ...S.btnSmall, marginRight: 6 }} onClick={() => setKostensatz(u)}>Kostensatz</button>}
                  {(u.role === 'staff' || u.role === 'admin') && <button style={{ ...S.btnSmall, marginRight: 6 }} onClick={() => setAgBrutto(u)}>AG-Kosten/Mon.</button>}
                  <button style={{ ...S.btnSmall, marginRight: 6 }} onClick={() => changeEmail(u)}>E-Mail</button>
                  <button style={S.btnSmall} onClick={() => changePassword(u)}>Passwort</button>
                  {user && u.id !== user.id && <button style={{ ...S.btnSmall, marginLeft: 6, background: u.is_active ? C.muted : C.success }} onClick={() => toggleActive(u)}>{u.is_active ? 'Deaktivieren' : 'Aktivieren'}</button>}
                  {user && u.id !== user.id && <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => deleteUser(u)}>Löschen</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <MandantBearbeiterCard mandanten={alleMandanten} users={users} />

      <OnboardingVorlagenCard />

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Audit-Log (Zugriffsprotokoll)</h2>
          <button style={S.btnSmall} onClick={loadAudit}>Aktualisieren</button>
        </div>
        {auditRows.length === 0 ? <div style={S.empty}>Keine Einträge</div> : (
          <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Zeit</th><th style={S.th}>Benutzer</th><th style={S.th}>Aktion</th>
                <th style={S.th}>Objekt</th><th style={S.th}>Detail</th><th style={S.th}>IP</th>
              </tr></thead>
              <tbody>{auditRows.map(a => (
                <tr key={a.id}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                  <td style={S.td}>{a.username || '—'}</td>
                  <td style={S.td}>{a.action}</td>
                  <td style={S.td}>{a.object_type ? `${a.object_type} #${a.object_id}` : '—'}</td>
                  <td style={S.td}>{a.detail || '—'}</td>
                  <td style={S.td}>{a.ip || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TwoFactor({ mode, tmpToken, onSuccess }) {
  const [uri, setUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode !== 'setup') return;
    (async () => {
      try {
        const fd = new FormData(); fd.append('tmp_token', tmpToken);
        const { data } = await axios.post('/api/2fa/setup', fd);
        setUri(data.otpauth_uri || '');
        const m = /secret=([A-Z2-7]+)/i.exec(data.otpauth_uri || '');
        setSecret(m ? m[1] : '');
      } catch (ex) { setErr(ex.response?.data?.detail || '2FA-Setup fehlgeschlagen'); }
    })();
  }, [mode, tmpToken]);

  async function confirm(e) {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const fd = new FormData(); fd.append('tmp_token', tmpToken); fd.append('code', code.trim());
      const { data } = await axios.post('/api/2fa/confirm', fd);
      onSuccess(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Code ungültig'); setBusy(false); }
  }

  return (
    <div style={S.loginPage}>
      <form style={{ ...S.loginCard, width: 400 }} onSubmit={confirm}>
        <h1 style={S.loginTitle}>Zwei-Faktor-Authentifizierung</h1>
        {mode === 'setup' ? (
          <>
            <p style={S.loginSub}>Scannen Sie den QR-Code mit einer Authenticator-App (Google/Microsoft Authenticator), dann geben Sie den 6-stelligen Code ein.</p>
            {uri ? <div style={{ background: '#fff', padding: 12, borderRadius: 6, display: 'flex', justifyContent: 'center' }}><QRCodeSVG value={uri} size={200} /></div> : <div style={S.empty}>Lade…</div>}
            {secret ? <div style={{ fontSize: 11, color: C.muted, marginTop: 6, wordBreak: 'break-all' }}>Manueller Schlüssel: {secret}</div> : null}
          </>
        ) : (
          <p style={S.loginSub}>Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.</p>
        )}
        <label style={S.label}>Code</label>
        <input style={S.input} value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoFocus placeholder="123456" />
        <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy}>{busy ? 'Prüfe…' : 'Bestätigen'}</button>
        <div style={S.err}>{err}</div>
      </form>
    </div>
  );
}

// Impressum/Datenschutz der Website – auf allen öffentlich erreichbaren Portalseiten
function RechtsLinks() {
  const st = { color: C.muted, textDecoration: 'none' };
  return (
    <div style={{ marginTop: 18, textAlign: 'center', fontSize: 12 }}>
      <a href="https://die-lohnkanzlei.de/impressum/" target="_blank" rel="noopener noreferrer" style={st}>Impressum</a>
      <span style={{ color: C.muted, margin: '0 8px' }}>·</span>
      <a href="https://die-lohnkanzlei.de/datenschutz/" target="_blank" rel="noopener noreferrer" style={st}>Datenschutz</a>
    </div>
  );
}

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('username', username);
      fd.append('password', password);
      const { data } = await axios.post('/api/auth/login', fd);
      if (data.access_token) { onLogin(data); return; }
      if (data.twofa_setup) { setTwofa({ mode: 'setup', tmpToken: data.tmp_token }); return; }
      if (data.twofa) { setTwofa({ mode: 'login', tmpToken: data.tmp_token }); return; }
      setErr('Unerwartete Server-Antwort');
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Login fehlgeschlagen');
    } finally { setBusy(false); }
  }

  if (twofa) return <TwoFactor mode={twofa.mode} tmpToken={twofa.tmpToken} onSuccess={onLogin} />;

  return (
    <div style={S.loginPage}>
      <form style={S.loginCard} onSubmit={submit}>
        <img src="/logo.png" alt="Die Lohnkanzlei" style={{ height: 72, display: 'block', margin: '0 auto 14px' }} />
        <h1 style={S.loginTitle}>Die Lohnkanzlei</h1>
        <p style={S.loginSub}>Portal — bitte anmelden</p>
        <label style={S.label}>Benutzername</label>
        <input style={S.input} value={username} onChange={e => setUsername(e.target.value)} autoFocus required />
        <label style={S.label}>Passwort</label>
        <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
        <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy}>{busy ? 'Anmelden…' : 'Anmelden'}</button>
        <div style={S.err}>{err}</div>
        <RechtsLinks />
      </form>
    </div>
  );
}

function RvBefreiungPage({ token }) {
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const canvasRef = useRef(null); const drawing = useRef(false); const hasSig = useRef(false);

  useEffect(() => { (async () => {
    try { const { data } = await axios.get(`/api/rv/${token}`); setInfo(data); }
    catch (ex) { setFatal(ex.response?.data?.detail || 'Link ungültig oder abgelaufen'); }
  })(); }, [token]);

  function pos(e) { const c = canvasRef.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; }
  function start(e) { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }
  function move(e) { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = C.primary; ctx.stroke(); hasSig.current = true; }
  function end() { drawing.current = false; }
  function clearSig() { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); hasSig.current = false; }

  async function submit(e) {
    e.preventDefault(); setErr('');
    if (!consent) { setErr('Bitte bestätigen.'); return; }
    if (!hasSig.current) { setErr('Bitte unterschreiben.'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('signatur', canvasRef.current.toDataURL('image/png')); fd.append('einwilligung', 'true');
      await axios.post(`/api/rv/${token}`, fd); setDone(true);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen'); setBusy(false); }
  }

  if (fatal) return <div style={S.loginPage}><div style={S.loginCard}><h1 style={S.loginTitle}>RV-Befreiung</h1><div style={S.err}>{fatal}</div></div></div>;
  if (done) return <div style={S.loginPage}><div style={S.loginCard}><h1 style={S.loginTitle}>Vielen Dank!</h1><p style={S.loginSub}>Ihr Antrag wurde übermittelt und signiert. Sie können dieses Fenster schließen.</p></div></div>;
  if (!info) return <div style={S.loginPage}><div style={{ color: C.white }}>Lade…</div></div>;
  const Z = (l, v) => <div style={{ fontSize: 13, marginBottom: 4 }}><span style={{ color: C.muted }}>{l}: </span>{v || '—'}</div>;

  return (
    <div style={{ ...S.loginPage, alignItems: 'flex-start', padding: 24, overflowY: 'auto' }}>
      <form style={{ ...S.loginCard, width: 560, maxWidth: '100%' }} onSubmit={submit}>
        <h1 style={S.loginTitle}>Befreiung von der Rentenversicherungspflicht</h1>
        <p style={S.loginSub}>Minijob bei {info.arbeitgeber || 'Ihrem Arbeitgeber'}. Bitte Angaben prüfen und unterschreiben.</p>
        {Z('Name', `${info.vorname} ${info.nachname}`)}
        {Z('Geburtsdatum', info.geburtsdatum ? fmtDateDE(info.geburtsdatum) : '')}
        {Z('SV-Nummer', info.sv_nummer)}
        {Z('Arbeitgeber', info.arbeitgeber)}
        {Z('Betriebsnummer', info.betriebsnummer)}
        {Z('Beschäftigung seit', info.eintritt ? fmtDateDE(info.eintritt) : '')}
        <div style={{ ...S.pathInfo, marginTop: 8 }}>Die Befreiung ist für die gesamte Dauer der Beschäftigung bindend und nicht widerrufbar.</div>
        <label style={S.label}>Unterschrift</label>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, background: '#fff', display: 'inline-block', touchAction: 'none' }}>
          <canvas ref={canvasRef} width={500} height={150} style={{ display: 'block', maxWidth: '100%', cursor: 'crosshair' }}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>
        <div><button type="button" style={{ ...S.btnSmall, background: C.muted, marginTop: 6 }} onClick={clearSig}>Löschen</button></div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
          <span>Ich beantrage die Befreiung von der Rentenversicherungspflicht und bestätige meine Angaben.</span>
        </label>
        {err && <div style={S.err}>{err}</div>}
        <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy}>{busy ? 'Wird übermittelt…' : 'Antrag absenden & signieren'}</button>
        <RechtsLinks />
      </form>
    </div>
  );
}

function OnboardingPage({ token, onLogin }) {
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState('');
  const [f, setF] = useState({ name: '', username: '', password: '', password2: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { (async () => {
    try {
      const { data } = await axios.get(`/api/onboarding/${token}`); setInfo(data);
      setF(p => ({ ...p, name: p.name || data.name || '', username: p.username || data.benutzername || '' }));
    }
    catch (ex) { setFatal(ex.response?.data?.detail || 'Einladung ungültig oder abgelaufen'); }
  })(); }, [token]);

  async function submit(e) {
    e.preventDefault(); setErr('');
    if (!f.username.trim()) { setErr('Benutzername erforderlich'); return; }
    if (f.password.length < 8) { setErr('Passwort mindestens 8 Zeichen'); return; }
    if (f.password !== f.password2) { setErr('Passwörter stimmen nicht überein'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', f.name); fd.append('username', f.username); fd.append('password', f.password);
      const { data } = await axios.post(`/api/onboarding/${token}/konto`, fd);
      onLogin(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Fehlgeschlagen'); setBusy(false); }
  }

  return (
    <div style={S.loginPage}>
      <form style={{ ...S.loginCard, width: 420 }} onSubmit={submit}>
        <h1 style={S.loginTitle}>Willkommen{info?.mandant_name ? ` – ${info.mandant_name}` : ''}</h1>
        {fatal ? <div style={S.err}>{fatal}</div> : (
          <>
            <p style={S.loginSub}>Schritt 1 von 4: Richten Sie Ihren Zugang ein. Danach prüfen Sie Ihr Arbeitgeber-Stammblatt (bereits vorausgefüllt) und unterschreiben Vollmacht und Dienstvertrag – beide werden automatisch mit Ihren Daten ausgefüllt.</p>
            <label style={S.label}>Ihr Name</label>
            <input style={S.input} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Vor- und Nachname" />
            <label style={S.label}>Benutzername</label>
            <input style={S.input} value={f.username} onChange={e => setF({ ...f, username: e.target.value })} autoCapitalize="none" />
            <label style={S.label}>Passwort (mind. 8 Zeichen)</label>
            <input style={S.input} type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} />
            <label style={S.label}>Passwort wiederholen</label>
            <input style={S.input} type="password" value={f.password2} onChange={e => setF({ ...f, password2: e.target.value })} />
            <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy || !info}>{busy ? '…' : 'Konto einrichten & weiter'}</button>
            <div style={{ ...S.pathInfo, marginTop: 10 }}>Eine Zwei-Faktor-Authentifizierung können Sie nach dem Login jederzeit unter „🔒 Sicherheit" aktivieren.</div>
            <div style={S.err}>{err}</div>
          </>
        )}
        <RechtsLinks />
      </form>
    </div>
  );
}

function InvitePage({ token, onLogin }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [fatal, setFatal] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`/api/invite/${token}`); setInfo(data); }
      catch (ex) { setFatal(ex.response?.data?.detail || 'Einladung ungültig oder abgelaufen'); }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault(); setErr('');
    if (pw.length < 8) { setErr('Passwort mindestens 8 Zeichen'); return; }
    if (pw !== pw2) { setErr('Passwörter stimmen nicht überein'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('password', pw);
      const { data } = await axios.post(`/api/invite/${token}/set-password`, fd);
      setTwofa({ mode: 'setup', tmpToken: data.tmp_token });
    } catch (ex) { setErr(ex.response?.data?.detail || 'Fehlgeschlagen'); setBusy(false); }
  }

  if (twofa) return <TwoFactor mode="setup" tmpToken={twofa.tmpToken} onSuccess={onLogin} />;

  return (
    <div style={S.loginPage}>
      <form style={S.loginCard} onSubmit={submit}>
        <h1 style={S.loginTitle}>Mitarbeiter-Zugang einrichten</h1>
        {fatal ? <div style={S.err}>{fatal}</div> : (
          <>
            <p style={S.loginSub}>{info ? `Konto: ${info.email}` : 'Lade…'} — bitte Passwort festlegen.</p>
            <label style={S.label}>Passwort (mind. 8 Zeichen)</label>
            <input style={S.input} type="password" value={pw} onChange={e => setPw(e.target.value)} />
            <label style={S.label}>Passwort wiederholen</label>
            <input style={S.input} type="password" value={pw2} onChange={e => setPw2(e.target.value)} />
            <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy || !info}>{busy ? '…' : 'Weiter zur 2-Faktor-Einrichtung'}</button>
            <div style={S.err}>{err}</div>
          </>
        )}
      </form>
    </div>
  );
}

function PersonalfragebogenPage({ token }) {
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState('');
  const [vals, setVals] = useState({});
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasSig = useRef(false);

  useEffect(() => { (async () => {
    try { const { data } = await axios.get(`/api/pfb/${token}`); setInfo(data); setVals(data.werte || {}); }
    catch (ex) { setFatal(ex.response?.data?.detail || 'Link ungültig oder abgelaufen'); }
  })(); }, [token]);

  function pos(e) {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  }
  function start(e) { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }
  function move(e) { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = C.primary; ctx.stroke(); hasSig.current = true; }
  function end() { drawing.current = false; }
  function clearSig() { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); hasSig.current = false; }

  async function submit(e) {
    e.preventDefault(); setErr('');
    if (!consent) { setErr('Bitte bestätigen Sie die Richtigkeit Ihrer Angaben.'); return; }
    if (!hasSig.current) { setErr('Bitte unterschreiben Sie im Unterschriftfeld.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      info.felder.forEach(f => fd.append(f.key, vals[f.key] ?? ''));
      fd.append('signatur', canvasRef.current.toDataURL('image/png'));
      fd.append('einwilligung', 'true');
      await axios.post(`/api/pfb/${token}`, fd);
      setDone(true);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen'); setBusy(false); }
  }

  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));

  if (fatal) return <div style={S.loginPage}><div style={S.loginCard}><h1 style={S.loginTitle}>Personalfragebogen</h1><div style={S.err}>{fatal}</div></div></div>;
  if (done) return <div style={S.loginPage}><div style={S.loginCard}><h1 style={S.loginTitle}>Vielen Dank!</h1><p style={S.loginSub}>Ihr Personalfragebogen wurde übermittelt und signiert. Sie können dieses Fenster schließen.</p></div></div>;
  if (!info) return <div style={S.loginPage}><div style={{ color: C.white }}>Lade…</div></div>;

  return (
    <div style={{ ...S.loginPage, alignItems: 'flex-start', padding: 24, overflowY: 'auto' }}>
      <form style={{ ...S.loginCard, width: 640, maxWidth: '100%' }} onSubmit={submit}>
        <h1 style={S.loginTitle}>Personalfragebogen</h1>
        <p style={S.loginSub}>{info.vorname} {info.nachname}{info.mandant_name ? ` · ${info.mandant_name}` : ''} — bitte ausfüllen und unten unterschreiben.</p>
        {info.felder.map(f => (
          <div key={f.key} style={{ marginBottom: 10 }}>
            <label style={S.label}>{f.label}</label>
            {f.is_date
              ? <input type="date" style={S.input} value={vals[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
              : f.key === 'anschrift'
                ? <textarea style={S.textarea} value={vals[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder={'Straße Nr.\nPLZ Ort'} />
                : <input style={S.input} value={vals[f.key] || ''} onChange={e => set(f.key, e.target.value)} />}
          </div>
        ))}
        <label style={S.label}>Unterschrift (mit Maus oder Finger zeichnen)</label>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, background: '#fff', display: 'inline-block', touchAction: 'none' }}>
          <canvas ref={canvasRef} width={580} height={170} style={{ display: 'block', maxWidth: '100%', cursor: 'crosshair' }}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>
        <div><button type="button" style={{ ...S.btnSmall, background: C.muted, marginTop: 6 }} onClick={clearSig}>Unterschrift löschen</button></div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
          <span>Ich bestätige, dass meine Angaben vollständig und richtig sind, und unterschreibe diesen Personalfragebogen elektronisch.</span>
        </label>
        {err && <div style={S.err}>{err}</div>}
        <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy}>{busy ? 'Wird übermittelt…' : 'Absenden & signieren'}</button>
      </form>
    </div>
  );
}

function EmployeeApp({ user, onLogout }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [secOpen, setSecOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/meine-lohnabrechnungen'); setDocs(data); }
      catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
      finally { setLoading(false); }
    })();
  }, []);

  async function download(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = d.original_name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  return (
    <div style={{ ...S.app, fontFamily: "'Inter', system-ui, sans-serif" }}>
      {secOpen && <SecurityModal onClose={() => setSecOpen(false)} onLoggedOut={() => { setSecOpen(false); onLogout(); }} />}
      <header style={S.header}>
        <div style={S.brand}>Die Lohnkanzlei – Mitarbeiterportal</div>
        <div style={S.userBox}><span>{user.username}</span><button style={{ ...S.btnGhost, marginRight: 6 }} onClick={() => setSecOpen(true)}>🔒 Sicherheit</button><button style={S.btnGhost} onClick={onLogout}>Abmelden</button></div>
      </header>
      <main style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
        <div style={S.card}>
          <h2 style={S.cardTitle}>Meine Lohnabrechnungen & Bescheinigungen</h2>
          {loading ? <div style={S.empty}>Lade…</div> : docs.length === 0 ? <div style={S.empty}>Noch keine freigegebenen Dokumente vorhanden.</div> : (
            <table style={S.table}>
              <thead><tr><th style={S.th}>Monat</th><th style={S.th}>Bezeichnung</th><th style={S.th}>Bereitgestellt</th><th style={S.th}></th></tr></thead>
              <tbody>{docs.map(d => (
                <tr key={d.id}>
                  <td style={S.td}>{d.monat || '—'}</td>
                  <td style={S.td}>{d.bezeichnung}</td>
                  <td style={S.td}>{fmtDate(d.created_at)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button style={{ ...S.btnSmall, background: C.accent }} onClick={() => setPreviewDoc(d)}>Ansehen</button>
                    <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => download(d)}>Download</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {err && <div style={S.err}>{err}</div>}
        </div>
      </main>
      {previewDoc && <DocPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />}
    </div>
  );
}

function AenderungsHistorie({ maId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/mitarbeiter/${maId}/changes`); setRows(data); } catch { /* ignore */ } })(); }, [maId]);
  if (!rows.length) return <div style={S.empty}>Noch keine Änderungen erfasst.</div>;
  const todayS = new Date().toISOString().slice(0, 10);
  const sorted = [...rows].sort((a, b) => (b.gueltig_ab || '').localeCompare(a.gueltig_ab || ''));
  return (
    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
      <table style={S.table}>
        <thead><tr><th style={S.th}>Gültig ab</th><th style={S.th}>Änderung</th></tr></thead>
        <tbody>{sorted.map(c => (
          <tr key={c.id}>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDateDE(c.gueltig_ab)}{c.gueltig_ab && c.gueltig_ab > todayS ? <span style={{ ...S.badge, marginLeft: 6, background: '#fff7e6', color: '#8a6d3b' }}>geplant</span> : null}</td>
            <td style={S.td}>{c.beschreibung}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function GueltigAbModal({ maId, onConfirm, onClose, busy }) {
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 560 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Änderung gültig ab</h2>
        <div style={S.pathInfo}>Ab welchem Datum gilt die Änderung? Unten siehst du die Historie – auch geplante (zukünftige) Änderungen.</div>
        <label style={S.label}>Gültig ab</label>
        <input type="date" style={S.input} value={datum} onChange={e => setDatum(e.target.value)} />
        <h3 style={{ fontSize: 14, color: C.primary, margin: '14px 0 6px' }}>Historie</h3>
        <AenderungsHistorie maId={maId} />
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || !datum} onClick={() => onConfirm(datum)}>{busy ? 'Speichert…' : 'Speichern'}</button>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

function MitarbeiterForm({ isStaff, initial, onClose, onSubmit }) {
  const [f, setF] = useState(() => ({
    pers_nr: initial?.pers_nr || '',
    nachname: initial?.nachname || '',
    vorname: initial?.vorname || '',
    email: initial?.email || '',
    status: initial?.status || 'Standard',
    wochenstunden: initial?.wochenstunden || '',
    eintritt: initial?.eintritt || '',
    austritt: initial?.austritt || '',
    immatrikulation_gueltig_bis: initial?.immatrikulation_gueltig_bis || '',
    stundenlohn: initial?.stundenlohn || '',
    monatslohn: initial?.monatslohn || '',
  }));
  const [pfb, setPfb] = useState(null);
  const [pfbMode, setPfbMode] = useState('upload'); // upload | link | spaeter
  const [ausweis, setAusweis] = useState([]);
  const [imma, setImma] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [gueltigAb, setGueltigAb] = useState(() => new Date().toISOString().slice(0, 10));
  const isNew = !initial;
  const isWerk = f.status === 'Werkstudent';

  async function submit(e) {
    e.preventDefault(); setErr('');
    if (!f.nachname.trim() || !f.vorname.trim()) { setErr('Nach- und Vorname erforderlich'); return; }
    if (!f.eintritt) { setErr('Beschäftigungsbeginn erforderlich'); return; }
    if (isNew && !f.stundenlohn.trim() && !f.monatslohn.trim()) { setErr('Bitte Stundenlohn oder Monatslohn angeben (mindestens eins).'); return; }
    if (isNew && pfbMode === 'upload' && !pfb) { setErr('Bitte Personalfragebogen hochladen – oder eine andere Option wählen.'); return; }
    if (isNew && pfbMode === 'link' && !f.email.trim()) { setErr('Für den Link-Versand ist eine Kontakt-E-Mail erforderlich.'); return; }
    if (isWerk && !f.immatrikulation_gueltig_bis) { setErr('Bitte „Immatrikulation gültig bis" angeben'); return; }
    if (isNew && isWerk && !imma) { setErr('Immatrikulationsbescheinigung erforderlich (Werkstudent)'); return; }
    setBusy(true);
    try {
      await onSubmit(f, { pfb, ausweis, imma, isWerk, pfbMode, gueltig_ab: gueltigAb });
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  }

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <form style={S.modalCard} onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2 style={S.cardTitle}>{isNew ? 'Neuer Mitarbeiter' : `${initial.vorname} ${initial.nachname} bearbeiten`}</h2>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Nachname</label><input style={S.input} value={f.nachname} onChange={e => setF({ ...f, nachname: e.target.value })} /></div>
          <div style={S.field}><label style={S.label}>Vorname</label><input style={S.input} value={f.vorname} onChange={e => setF({ ...f, vorname: e.target.value })} /></div>
        </div>
        <div style={S.row}>
          {isStaff && <div style={S.field}><label style={S.label}>Pers.Nr</label><input style={S.input} value={f.pers_nr} onChange={e => setF({ ...f, pers_nr: e.target.value })} /></div>}
          <div style={S.field}><label style={S.label}>Kontakt-E-Mail</label><input style={S.input} value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></div>
        </div>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>MA-Status</label>
            <select style={S.select} value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
              {MA_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={S.field}><label style={S.label}>Wochenstunden</label><input style={S.input} value={f.wochenstunden} onChange={e => setF({ ...f, wochenstunden: e.target.value })} /></div>
        </div>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Beschäftigungsbeginn</label><input type="date" style={S.input} value={f.eintritt} onChange={e => setF({ ...f, eintritt: e.target.value })} /></div>
          <div style={S.field}><label style={S.label}>Austritt (optional)</label><input type="date" style={S.input} value={f.austritt} onChange={e => setF({ ...f, austritt: e.target.value })} /></div>
        </div>
        {initial && initial.austritt && <div style={{ ...S.pathInfo, marginTop: 4 }}>Kommt der Mitarbeiter nach dem Austritt zurück? Tragen Sie einfach das neue Eintrittsdatum ein (bei Befristung auch das neue Ende) – die bisherige Beschäftigung bleibt als Zeitraum erhalten, und das Lohnbüro bekommt eine Meldung „Wiedereintritt“.</div>}
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Stundenlohn</label><input style={S.input} value={f.stundenlohn} onChange={e => setF({ ...f, stundenlohn: e.target.value })} placeholder="z.B. 13,50" /></div>
          <div style={S.field}><label style={S.label}>Monatslohn</label><input style={S.input} value={f.monatslohn} onChange={e => setF({ ...f, monatslohn: e.target.value })} placeholder="z.B. 2.000,00" /></div>
        </div>
        <div style={S.pathInfo}>Mindestens eins von Stundenlohn / Monatslohn ausfüllen. Weitere Akte-Felder nach dem Anlegen über die Detailseite (Klick auf den Namen).</div>
        {isWerk && (
          <div style={S.row}>
            <div style={S.field}><label style={S.label}>Immatrikulation gültig bis</label><input type="date" style={S.input} value={f.immatrikulation_gueltig_bis} onChange={e => setF({ ...f, immatrikulation_gueltig_bis: e.target.value })} /></div>
          </div>
        )}
        {isNew && (
          <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
            <label style={S.label}>Personalfragebogen</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="pfbmode" checked={pfbMode === 'upload'} onChange={() => setPfbMode('upload')} /> Ausgefüllten Personalfragebogen jetzt hochladen</label>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="pfbmode" checked={pfbMode === 'link'} onChange={() => setPfbMode('link')} /> Link zum digitalen Ausfüllen jetzt an den MA senden (E-Mail nötig)</label>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="pfbmode" checked={pfbMode === 'spaeter'} onChange={() => setPfbMode('spaeter')} /> Später senden (Erinnerung erscheint im Dashboard)</label>
            </div>
            {pfbMode === 'upload' && <input type="file" style={S.input} onChange={e => setPfb(e.target.files[0] || null)} />}
            <label style={{ ...S.label, marginTop: 8 }}>Personalausweis (1–2 Dateien)</label>
            <input type="file" multiple style={S.input} onChange={e => setAusweis(Array.from(e.target.files || []))} />
            {isWerk && (
              <>
                <label style={S.label}>Immatrikulationsbescheinigung</label>
                <input type="file" style={S.input} onChange={e => setImma(e.target.files[0] || null)} />
              </>
            )}
          </div>
        )}
        {!isNew && (
          <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
            <label style={S.label}>Änderung gültig ab</label>
            <input type="date" style={S.input} value={gueltigAb} onChange={e => setGueltigAb(e.target.value)} />
            <h3 style={{ fontSize: 13, color: C.primary, margin: '12px 0 4px' }}>Bisherige & geplante Änderungen</h3>
            <AenderungsHistorie maId={initial.id} />
          </div>
        )}
        {err && <div style={S.err}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="submit" style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy}>{busy ? 'Speichert…' : (isNew ? 'Anlegen' : 'Speichern')}</button>
          <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}

const WOCHENTAGE = [['1', 'Mo'], ['2', 'Di'], ['3', 'Mi'], ['4', 'Do'], ['5', 'Fr'], ['6', 'Sa'], ['7', 'So']];

function ArbeitszeitenEditor({ ma, onSaved }) {
  const initV = () => {
    const o = {}; WOCHENTAGE.forEach(([k]) => { o[k] = ''; });
    try { const d = ma.arbeitszeiten ? JSON.parse(ma.arbeitszeiten) : {}; WOCHENTAGE.forEach(([k]) => { if (d[k] != null && Number(d[k]) > 0) o[k] = String(d[k]).replace('.', ','); }); } catch { /* ignore */ }
    return o;
  };
  const [v, setV] = useState(initV);
  const [aktiv, setAktiv] = useState(!!ma.arbeitszeiten);
  const [msg, setMsg] = useState(''); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const summe = WOCHENTAGE.reduce((a, [k]) => a + (parseFloat(String(v[k]).replace(',', '.')) || 0), 0);
  async function save() {
    setBusy(true); setErr(''); setMsg('');
    try {
      const obj = {}; WOCHENTAGE.forEach(([k]) => { const n = parseFloat(String(v[k]).replace(',', '.')); obj[k] = (n > 0 ? n : 0); });
      const fd = new FormData(); fd.append('arbeitszeiten', aktiv ? JSON.stringify(obj) : '');
      await api.patch(`/mitarbeiter/${ma.id}`, fd);
      setMsg('Gespeichert.'); if (onSaved) onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  return (
    <div style={S.card}>
      <h2 style={S.cardTitle}>Individuelle Arbeitszeiten (Spezial)</h2>
      <div style={S.pathInfo}>Für Teilzeit-/Schicht-Kräfte mit festen Wochentagen. Wenn gesetzt, werden Fehlzeiten tagesgenau nach diesen Stunden berechnet (auch Sa/So/Feiertag). Ohne Angabe wird die Wochenarbeitszeit gleichmäßig auf Mo–Fr verteilt.</div>
      <label style={{ fontSize: 13, cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <input type="checkbox" checked={aktiv} onChange={e => setAktiv(e.target.checked)} /> Individuelle Wochenarbeitszeit verwenden
      </label>
      {aktiv && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'flex-end' }}>
          {WOCHENTAGE.map(([k, l]) => (
            <div key={k} style={{ width: 70 }}>
              <label style={{ ...S.label, marginTop: 0 }}>{l} (h)</label>
              <input style={{ ...S.input, textAlign: 'center' }} value={v[k]} onChange={e => setV(s => ({ ...s, [k]: e.target.value }))} placeholder="0" />
            </div>
          ))}
          <div style={{ fontSize: 13, color: C.muted, paddingBottom: 10 }}>Summe/Woche: <strong style={{ color: C.text }}>{summe.toFixed(2).replace('.', ',')} h</strong></div>
        </div>
      )}
      {err && <div style={S.err}>{err}</div>}{msg && <div style={S.okMsg}>{msg}</div>}
      <button style={{ ...S.btnPrimary, marginTop: 4 }} disabled={busy} onClick={save}>{busy ? 'Speichert…' : 'Speichern'}</button>
    </div>
  );
}

// Wiedereintritt: ehemaliger Mitarbeiter kommt zurück – neuer Beschäftigungszeitraum statt Überschreiben der Daten
function WiedereintrittForm({ ma, onDone, onCancel }) {
  const [ein, setEin] = useState('');
  const [aus, setAus] = useState('');
  const [status, setStatus] = useState(ma.status || 'Standard');
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function speichern() {
    if (!ein) { setErr('Bitte das Datum des Wiedereintritts angeben.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('eintritt', ein); if (aus) fd.append('austritt', aus);
      if (status) fd.append('status', status); if (notiz.trim()) fd.append('notiz', notiz.trim());
      const { data } = await api.post(`/mitarbeiter/${ma.id}/wiedereintritt`, fd);
      onDone(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Wiedereintritt konnte nicht gemeldet werden.'); setBusy(false); }
  }
  return (
    <div style={{ border: `1px solid ${C.accent}`, background: '#eef4fb', borderRadius: 8, padding: '10px 14px', marginTop: 10 }}>
      <div style={{ fontSize: 13, color: C.primary, fontWeight: 600 }}>Wiedereintritt: {ma.vorname} {ma.nachname}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{ma.austritt ? `Bisherige Beschäftigung bis ${fmtDateDE(ma.austritt)}. ` : ''}Die bisherigen Daten bleiben erhalten – es wird ein neuer Beschäftigungszeitraum angelegt.</div>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Wiedereintritt am</label><input type="date" style={S.input} value={ein} onChange={e => setEin(e.target.value)} /></div>
        <div style={S.field}><label style={S.label}>Befristet bis (optional)</label><input type="date" style={S.input} value={aus} onChange={e => setAus(e.target.value)} /></div>
        <div style={S.field}><label style={S.label}>Beschäftigungsart</label><select style={S.select} value={status} onChange={e => setStatus(e.target.value)}>{MA_STATUS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
      </div>
      <label style={S.label}>Hinweis ans Lohnbüro (optional)</label>
      <input style={S.input} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z. B. neue Wochenstunden, neues Gehalt" />
      {err && <div style={S.err}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={speichern}>{busy ? 'Meldet…' : 'Wiedereintritt melden'}</button>
        {onCancel && <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={onCancel}>Abbrechen</button>}
      </div>
    </div>
  );
}

function WiedereintrittModal({ maList, onClose, onDone }) {
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  const ehemalige = maList.filter(m => m.austritt && (new Date(m.austritt) < heute || !m.aktiv))
    .sort((a, b) => `${a.nachname} ${a.vorname}`.localeCompare(`${b.nachname} ${b.vorname}`));
  const [maId, setMaId] = useState('');
  const ma = ehemalige.find(m => String(m.id) === String(maId));
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 640 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Wiedereintritt melden</h2>
        <div style={S.pathInfo}>Ein ehemaliger Mitarbeiter kommt zurück? Bitte keine neue Akte anlegen – hier wird ein neuer Beschäftigungszeitraum in der bestehenden Akte gemeldet.</div>
        {ehemalige.length === 0 ? <div style={S.empty}>Keine ausgeschiedenen Mitarbeiter vorhanden.</div> : (
          <>
            <label style={S.label}>Mitarbeiter</label>
            <select style={S.select} value={maId} onChange={e => setMaId(e.target.value)}>
              <option value="">— bitte wählen —</option>
              {ehemalige.map(m => <option key={m.id} value={m.id}>{m.nachname}, {m.vorname} (ausgeschieden zum {fmtDateDE(m.austritt)})</option>)}
            </select>
            {ma && <WiedereintrittForm key={ma.id} ma={ma} onDone={onDone} />}
          </>
        )}
        <div style={{ marginTop: 14 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button></div>
      </div>
    </div>
  );
}

function MitarbeiterDetail({ ma, list, isStaff, onBack, onSwitch, onChanged }) {
  const [wiederOffen, setWiederOffen] = useState(false);
  const [f, setF] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [np, setNp] = useState({ eintritt: '', austritt: '', notiz: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [gueltigOpen, setGueltigOpen] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [d, z] = await Promise.all([
        api.get(`/mitarbeiter/${ma.id}`),
        api.get(`/mitarbeiter/${ma.id}/zeitraeume`),
      ]);
      setF(d.data); setPeriods(z.data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
  }, [ma.id]);
  useEffect(() => { load(); }, [load]);

  function set(k, v) { setF(prev => ({ ...prev, [k]: v })); }

  async function doSave(gueltig) {
    setErr(''); setOkMsg('');
    if (!gueltig) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('gueltig_ab', gueltig);
      const fields = ['pers_nr', 'nachname', 'vorname', 'email', 'status', 'wochenstunden', 'eintritt', 'austritt',
        'immatrikulation_gueltig_bis', 'stundenlohn', 'monatslohn', 'sv_nummer', 'steuer_id', 'anschrift',
        'geschlecht', 'geburtsdatum', 'familienstand', 'iban', 'staatsangehoerigkeit', 'telefon',
        'steuerklasse', 'konfession', 'kinderfreibetraege', 'krankenkasse'];
      fields.forEach(k => { if (!isStaff && k === 'pers_nr') return; fd.append(k, f[k] ?? ''); });
      await api.patch(`/mitarbeiter/${ma.id}`, fd);
      setGueltigOpen(false);
      setOkMsg('Akte gespeichert.'); if (onChanged) onChanged(); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  async function addPeriod() {
    if (!np.eintritt && !np.austritt && !np.notiz.trim()) return;
    try {
      const fd = new FormData();
      if (np.eintritt) fd.append('eintritt', np.eintritt);
      if (np.austritt) fd.append('austritt', np.austritt);
      if (np.notiz) fd.append('notiz', np.notiz);
      await api.post(`/mitarbeiter/${ma.id}/zeitraeume`, fd);
      setNp({ eintritt: '', austritt: '', notiz: '' });
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Zeitraum konnte nicht angelegt werden'); }
  }

  async function delPeriod(z) {
    if (!window.confirm('Zeitraum löschen?')) return;
    try { await api.delete(`/zeitraeume/${z.id}`); await load(); }
    catch { setErr('Löschen fehlgeschlagen'); }
  }

  const [inviteMsg, setInviteMsg] = useState('');
  async function inviteMa() {
    setErr(''); setInviteMsg('');
    if (!f.email || !f.email.trim()) { setErr('Bitte zuerst eine E-Mail in den Stammdaten speichern.'); return; }
    if (!window.confirm(`Einladung zum Mitarbeiter-Portal an ${f.email} senden?`)) return;
    try { await api.post(`/mitarbeiter/${ma.id}/invite`); setInviteMsg(`Einladung an ${f.email} gesendet.`); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Einladung fehlgeschlagen'); }
  }

  const [pfbMsg, setPfbMsg] = useState('');
  async function requestPfb() {
    setErr(''); setPfbMsg('');
    if (!f.email || !f.email.trim()) { setErr('Bitte zuerst eine E-Mail in den Stammdaten speichern.'); return; }
    if (!window.confirm(`Personalfragebogen-Link an ${f.email} senden?`)) return;
    try { await api.post(`/mitarbeiter/${ma.id}/pfb-anfordern`); setPfbMsg(`Personalfragebogen-Link an ${f.email} gesendet.`); if (onChanged) onChanged(); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Anfordern fehlgeschlagen'); }
  }
  async function togglePfbExtern() {
    setErr(''); setPfbMsg('');
    const neu = !(f.pfb_status === 'extern');
    if (neu && !window.confirm('Personalfragebogen als bereits vorhanden markieren (ohne Upload)? Nur wählen, wenn er außerhalb des Portals vorliegt.')) return;
    try { const fd = new FormData(); fd.append('wert', neu ? 'true' : 'false'); await api.post(`/mitarbeiter/${ma.id}/pfb-extern`, fd); setPfbMsg(neu ? 'Als vorhanden markiert.' : 'Markierung entfernt.'); if (onChanged) onChanged(); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  const [rvMsg, setRvMsg] = useState('');
  async function requestRv() {
    setErr(''); setRvMsg('');
    if (!f.email || !f.email.trim()) { setErr('Bitte zuerst eine E-Mail in den Stammdaten speichern.'); return; }
    if (!window.confirm(`RV-Befreiungsantrag-Link an ${f.email} senden?`)) return;
    try { await api.post(`/mitarbeiter/${ma.id}/rv-anfordern`); setRvMsg(`RV-Befreiungs-Link an ${f.email} gesendet.`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Anfordern fehlgeschlagen'); }
  }

  async function deleteMa() {
    const eingabe = window.prompt(`Achtung: Dieser Mitarbeiter wird mit ALLEN Dokumenten, Tickets und Fristen unwiderruflich gelöscht.\n\nZum Bestätigen den Nachnamen „${ma.nachname}" eintippen:`);
    if (eingabe === null) return;
    if (eingabe.trim() !== ma.nachname) { setErr('Name stimmt nicht überein — Löschen abgebrochen.'); return; }
    if (!window.confirm('Wirklich endgültig löschen?')) return;
    try { await api.delete(`/mitarbeiter/${ma.id}`); onBack(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  if (!f) return <div style={S.card}><div style={S.empty}>Lade…</div></div>;

  const field = (label, key, type = 'text') => (
    <div style={S.field}>
      <label style={S.label}>{label}</label>
      <input style={S.input} type={type} value={f[key] ?? ''} onChange={e => set(key, e.target.value)} />
    </div>
  );

  return (
    <div>
      <datalist id="kk-list">{KRANKENKASSEN.map(k => <option key={k} value={k} />)}</datalist>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onBack}>← Zurück zur Liste</button>
          <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>{f.vorname} {f.nachname} — Personalakte</h2>
          <select style={{ ...S.select, width: 'auto' }} value={ma.id} onChange={e => { const m = list.find(x => String(x.id) === e.target.value); if (m) onSwitch(m); }}>
            {list.map(m => <option key={m.id} value={m.id}>{m.nachname}, {m.vorname}</option>)}
          </select>
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Stammdaten</h2>
        <div style={S.row}>
          {isStaff && field('Pers.Nr', 'pers_nr')}
          {field('Nachname', 'nachname')}
          {field('Vorname', 'vorname')}
        </div>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Status</label>
            <select style={S.select} value={f.status || ''} onChange={e => set('status', e.target.value)}>
              {MA_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={S.field}><label style={S.label}>Geschlecht</label>
            <select style={S.select} value={f.geschlecht || ''} onChange={e => set('geschlecht', e.target.value)}>
              <option value="">—</option><option>männlich</option><option>weiblich</option><option>divers</option>
            </select>
          </div>
          {field('Geburtsdatum', 'geburtsdatum', 'date')}
        </div>
        <div style={S.row}>
          {field('Telefon', 'telefon')}
          {field('E-Mail', 'email')}
          {field('Staatsangehörigkeit', 'staatsangehoerigkeit')}
        </div>
        <label style={S.label}>Anschrift</label>
        <textarea style={S.textarea} value={f.anschrift || ''} onChange={e => set('anschrift', e.target.value)} />
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Familienstand</label>
            <select style={S.select} value={f.familienstand || ''} onChange={e => set('familienstand', e.target.value)}>
              <option value="">—</option><option>ledig</option><option>verheiratet</option><option>geschieden</option><option>verwitwet</option><option>eingetragene Lebenspartnerschaft</option>
            </select>
          </div>
          {field('IBAN', 'iban')}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Steuer & Sozialversicherung</h2>
        <div style={S.row}>
          {field('SV-Nummer', 'sv_nummer')}
          {field('Steuer-ID', 'steuer_id')}
          <div style={S.field}><label style={S.label}>Steuerklasse</label>
            <select style={S.select} value={f.steuerklasse || ''} onChange={e => set('steuerklasse', e.target.value)}>
              <option value="">—</option>{['I', 'II', 'III', 'IV', 'V', 'VI'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div style={S.row}>
          {field('Konfession', 'konfession')}
          {field('Kinderfreibeträge', 'kinderfreibetraege')}
          <div style={S.field}><label style={S.label}>Krankenkasse</label>
            <input style={S.input} list="kk-list" value={f.krankenkasse || ''} onChange={e => set('krankenkasse', e.target.value)} placeholder="z.B. Techniker…" />
          </div>
        </div>
        <div style={S.row}>
          {field('Stundenlohn', 'stundenlohn')}
          {field('Monatslohn', 'monatslohn')}
          {field('Wochenstunden', 'wochenstunden')}
        </div>
        {f.status === 'Werkstudent' && (
          <div style={S.row}>{field('Immatrikulation gültig bis', 'immatrikulation_gueltig_bis', 'date')}</div>
        )}
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}`, paddingBottom: 8, marginBottom: 10 }}>
          <h2 style={{ ...S.cardTitle, border: 'none', margin: 0, padding: 0 }}>Beschäftigungszeiträume</h2>
          {(f.austritt || (periods.length > 0 && periods[periods.length - 1].austritt)) && !wiederOffen && <button style={S.btnSmall} onClick={() => setWiederOffen(true)}>↩ Wiedereintritt melden</button>}
        </div>
        <div style={S.pathInfo}>Jede Beschäftigung bleibt hier erhalten. Kommt der Mitarbeiter nach einem Austritt zurück, bitte „Wiedereintritt melden“ nutzen – keine neue Akte anlegen und keine Daten überschreiben.</div>
        {wiederOffen && <WiedereintrittForm ma={f} onCancel={() => setWiederOffen(false)} onDone={async () => { setWiederOffen(false); setOkMsg('Wiedereintritt gemeldet – das Lohnbüro ist informiert.'); if (onChanged) onChanged(); await load(); }} />}
        <table style={{ ...S.table, marginTop: 10 }}>
          <thead><tr><th style={S.th}>Eintritt</th><th style={S.th}>Austritt</th><th style={S.th}>Notiz</th><th style={S.th}></th></tr></thead>
          <tbody>
            {periods.map(z => (
              <tr key={z.id}>
                <td style={S.td}>{fmtDateDE(z.eintritt)}</td>
                <td style={S.td}>{fmtDateDE(z.austritt)}</td>
                <td style={S.td}>{z.notiz || '—'}</td>
                <td style={S.td}>{isStaff && <button style={S.btnDanger} onClick={() => delPeriod(z)}>✕</button>}</td>
              </tr>
            ))}
            {periods.length === 0 && <tr><td style={S.td} colSpan={4}><span style={{ color: C.muted }}>Noch kein Zeitraum erfasst.</span></td></tr>}
            {isStaff && <tr>
              <td style={S.td}><input type="date" style={S.input} value={np.eintritt} onChange={e => setNp({ ...np, eintritt: e.target.value })} /></td>
              <td style={S.td}><input type="date" style={S.input} value={np.austritt} onChange={e => setNp({ ...np, austritt: e.target.value })} /></td>
              <td style={S.td}><input style={S.input} placeholder="z.B. Wechsel Werkstudent → Normalbeschäftigung" value={np.notiz} onChange={e => setNp({ ...np, notiz: e.target.value })} /></td>
              <td style={S.td}><button style={S.btnSmall} onClick={addPeriod}>+ Hinzufügen</button></td>
            </tr>}
          </tbody>
        </table>
      </div>

      <div style={S.card}>
        <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={() => { setErr(''); setOkMsg(''); setGueltigOpen(true); }}>Akte speichern</button>
        {okMsg && <div style={S.okMsg}>{okMsg}</div>}
        {err && <div style={S.err}>{err}</div>}
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Mitarbeiter-Zugang</h2>
        <div style={S.pathInfo}>Lädt den Mitarbeiter per E-Mail ein, sein eigenes Portal einzurichten (Passwort + 2-Faktor-App). Er sieht dort ausschließlich seine freigegebenen Lohnabrechnungen und Meldebescheinigungen. Status aller Zugänge und Entfernen: Mitarbeiter-Liste › „Mitarbeiter-Zugänge“.</div>
        <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={inviteMa}>Zum Mitarbeiter-Portal einladen</button>
        {inviteMsg && <div style={S.okMsg}>{inviteMsg}</div>}
      </div>

      <ArbeitszeitenEditor ma={ma} onSaved={() => { if (onChanged) onChanged(); }} />

      <div style={S.card}>
        <h2 style={S.cardTitle}>Digitaler Personalfragebogen</h2>
        <div style={S.pathInfo}>Sendet dem Mitarbeiter einen Link, um seinen Personalfragebogen selbst auszufüllen und online zu unterschreiben. Das signierte PDF landet anschließend automatisch in der Personalakte (Dokumente).</div>
        <div style={{ marginBottom: 8, fontSize: 13 }}>Status: {
          f.pfb_status === 'eingereicht'
            ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>eingereicht{f.pfb_eingereicht_am ? ` am ${fmtDateDE(f.pfb_eingereicht_am)}` : ''}</span>
            : f.pfb_status === 'angefordert'
              ? <span style={{ ...S.badge, background: '#fdeccd', color: '#8a6d3b' }}>angefordert – wartet auf Mitarbeiter</span>
              : f.pfb_status === 'extern'
                ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>liegt extern vor (kein Upload)</span>
                : <span style={{ ...S.badge }}>noch nicht angefordert</span>
        }</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={requestPfb}>{f.pfb_status === 'angefordert' ? 'Link erneut senden' : 'Personalfragebogen anfordern'}</button>
          <button style={{ ...S.btnGhost, marginTop: 0, color: C.text, borderColor: C.border }} onClick={togglePfbExtern}>{f.pfb_status === 'extern' ? 'Markierung „liegt vor" entfernen' : 'Liegt bereits vor (ohne Upload)'}</button>
        </div>
        {pfbMsg && <div style={S.okMsg}>{pfbMsg}</div>}
      </div>

      {(f.status === 'Minijob') && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>Minijob: Befreiung von der Rentenversicherungspflicht</h2>
          <div style={S.pathInfo}>Sendet dem Minijobber einen Link, um den RV-Befreiungsantrag (vorausgefüllt mit Betriebsnummer) online zu unterschreiben. Das signierte PDF landet in der Personalakte (Aufbewahrung beim Arbeitgeber). Voraussetzung: Betriebsnummer im Arbeitgeber-Stammblatt.</div>
          <div style={{ marginBottom: 8, fontSize: 13 }}>Status: {
            f.rv_status === 'eingereicht'
              ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>signiert{f.rv_eingereicht_am ? ` am ${fmtDateDE(f.rv_eingereicht_am)}` : ''}</span>
              : f.rv_status === 'angefordert'
                ? <span style={{ ...S.badge, background: '#fdeccd', color: '#8a6d3b' }}>angefordert – wartet auf Mitarbeiter</span>
                : <span style={{ ...S.badge }}>noch nicht angefordert</span>
          }</div>
          <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={requestRv}>{f.rv_status === 'angefordert' ? 'Link erneut senden' : 'RV-Befreiung anfordern'}</button>
          {rvMsg && <div style={S.okMsg}>{rvMsg}</div>}
        </div>
      )}

      <div style={{ ...S.card, border: `1px solid ${C.danger}` }}>
        <h2 style={{ ...S.cardTitle, color: C.danger, borderColor: '#f3c6c2' }}>Gefahrenzone</h2>
        <div style={S.pathInfo}>Mitarbeiter inkl. aller Dokumente, Tickets und Fristen unwiderruflich löschen.</div>
        <button style={{ ...S.btnDanger, marginLeft: 0 }} onClick={deleteMa}>Mitarbeiter löschen</button>
      </div>

      {gueltigOpen && <GueltigAbModal maId={ma.id} busy={busy} onConfirm={doSave} onClose={() => setGueltigOpen(false)} />}
    </div>
  );
}

const LEX_FELDER = [
  ['pers_nr', 'Personalnummer'], ['nachname', 'Nachname'], ['vorname', 'Vorname'], ['email', 'E-Mail'],
  ['geburtsdatum', 'Geburtsdatum'], ['geschlecht', 'Geschlecht'],
  ['sv_nummer', 'SV-Nummer'], ['steuer_id', 'Steuer-ID'], ['steuerklasse', 'Steuerklasse'],
  ['kinderfreibetraege', 'Kinderfreibeträge'], ['konfession', 'Konfession'], ['krankenkasse', 'Krankenkasse'],
  ['iban', 'IBAN'], ['staatsangehoerigkeit', 'Staatsangehörigkeit'], ['telefon', 'Telefon'], ['familienstand', 'Familienstand'],
  ['eintritt', 'Eintrittsdatum'], ['austritt', 'Austrittsdatum'], ['wochenstunden', 'Wochenstunden'],
  ['stundenlohn', 'Stundenlohn'], ['monatslohn', 'Monatslohn'],
  ['strasse', 'Straße'], ['plz', 'PLZ'], ['ort', 'Ort'],
];

function LexwareImport({ mandantId, onClose, onDone }) {
  const [step, setStep] = useState('upload'); // upload | map | done
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [map, setMap] = useState({});
  const [updateExisting, setUpdateExisting] = useState(true);
  const [defaultStatus, setDefaultStatus] = useState('Standard');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);

  async function doPreview(f) {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('mandant_id', mandantId); fd.append('file', f);
      const { data } = await api.post('/lexware-import/preview', fd);
      setPrev(data); setMap(data.mapping || {}); setFile(f); setStep('map');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Vorschau fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  async function doImport() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('mandant_id', mandantId); fd.append('file', file);
      fd.append('mapping', JSON.stringify(map));
      if (prev.delimiter) fd.append('delimiter', prev.delimiter);
      if (prev.encoding) fd.append('encoding', prev.encoding);
      fd.append('update_existing', updateExisting ? 'true' : 'false');
      fd.append('default_status', defaultStatus);
      const { data } = await api.post('/lexware-import', fd);
      setResult(data); setStep('done');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Import fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  const mappedCount = Object.values(map).filter(Boolean).length;
  const canImport = !!map.nachname && !!map.vorname;
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 780 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Mitarbeiter importieren (Lexware-Export, CSV oder Excel)</h2>
        {step === 'upload' && (
          <>
            <div style={S.pathInfo}>Lexware-Export als .txt/.csv oder eine Mitarbeiterliste als Excel (.xlsx) bzw. CSV mit Kopfzeile – z. B. die Liste, die ein Mandant im Portal an uns geschickt hat (Ordner „Mitarbeiterliste“). Spalten werden automatisch erkannt und im nächsten Schritt zur Kontrolle angezeigt. PDF eignet sich nicht für den Import.</div>
            <input type="file" accept=".txt,.csv,.tsv,.xlsx,text/plain,text/csv" style={S.input} onChange={e => { const f = e.target.files[0]; if (f) doPreview(f); }} />
            {busy && <div style={S.empty}>Lese Datei…</div>}
            {err && <div style={S.err}>{err}</div>}
          </>
        )}
        {step === 'map' && prev && (
          <>
            <div style={S.pathInfo}>Erkannt: {prev.total} Datenzeile(n) · Trennzeichen „{prev.delimiter === '\t' ? 'Tab' : prev.delimiter}" · Kodierung {prev.encoding} · {mappedCount} Felder automatisch zugeordnet. Bitte prüfen und ggf. korrigieren:</div>
            <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 6, padding: 8 }}>
              {LEX_FELDER.map(([f, l]) => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <div style={{ width: 180, fontSize: 13 }}>{l}{(f === 'nachname' || f === 'vorname') ? <span style={{ color: C.danger }}> *</span> : ''}</div>
                  <select style={{ ...S.select, flex: 1 }} value={map[f] || ''} onChange={e => setMap(m => ({ ...m, [f]: e.target.value }))}>
                    <option value="">— nicht importieren —</option>
                    {prev.columns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ ...S.row, marginTop: 12 }}>
              <label style={{ fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} /> Bestehende (gleiche Personalnummer) aktualisieren</label>
              <div style={S.field}><label style={S.label}>Status für neue Mitarbeiter</label><select style={S.select} value={defaultStatus} onChange={e => setDefaultStatus(e.target.value)}>{MA_STATUS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            </div>
            {prev.preview && prev.preview.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: C.accent }}>Vorschau (erste {prev.preview.length} Zeilen)</summary>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...S.table, marginTop: 6 }}>
                    <thead><tr>{prev.columns.map(c => <th key={c} style={S.th}>{c}</th>)}</tr></thead>
                    <tbody>{prev.preview.map((r, i) => <tr key={i}>{prev.columns.map(c => <td key={c} style={S.td}>{r[c]}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              </details>
            )}
            {err && <div style={S.err}>{err}</div>}
            {!canImport && <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>Vor- und Nachname müssen zugeordnet sein.</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || !canImport} onClick={doImport}>{busy ? 'Importiere…' : `Importieren (${prev.total})`}</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setStep('upload')}>Andere Datei</button>
            </div>
          </>
        )}
        {step === 'done' && result && (
          <>
            <div style={{ ...S.okMsg, fontSize: 15 }}>✓ Import abgeschlossen: {result.angelegt} neu angelegt, {result.aktualisiert} aktualisiert.</div>
            {result.fehler && result.fehler.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 13, color: C.danger, fontWeight: 600 }}>Übersprungen ({result.fehler.length}):</div>
                <ul style={{ fontSize: 12, color: C.muted, maxHeight: 160, overflowY: 'auto' }}>{result.fehler.map((f, i) => <li key={i}>{f}</li>)}</ul>
              </div>
            )}
            <div style={{ marginTop: 14 }}><button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={onDone}>Fertig</button></div>
          </>
        )}
      </div>
    </div>
  );
}

function Mitarbeiter({ user, mandanten, mandantId, focusMaId, clearMaFocus, startNewSignal, onStartNewConsumed }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [list, setList] = useState([]);
  const [docsByMa, setDocsByMa] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lohnMa, setLohnMa] = useState(null);
  const [docsMa, setDocsMa] = useState(null);
  const [detailMa, setDetailMa] = useState(null);
  const [suche, setSuche] = useState('');
  const [statusFilter, setStatusFilter] = useState('aktiv'); // aktiv | raus | alle
  const [sortKey, setSortKey] = useState('id');   // Standard: Reihenfolge der Anlage (ID)
  const [sortDir, setSortDir] = useState(1);      // 1 = aufsteigend, -1 = absteigend
  const [importOpen, setImportOpen] = useState(false);
  const [zugaengeOpen, setZugaengeOpen] = useState(false);

  const ready = isStaff ? !!mandantId : true;
  const effectiveMandant = isStaff ? mandantId : (user.mandant_id ?? '');

  const load = useCallback(async () => {
    if (!ready) { setList([]); setDocsByMa({}); return; }
    setLoading(true); setErr('');
    try {
      const params = isStaff ? { mandant_id: mandantId } : {};
      const [maRes, docRes] = await Promise.all([
        api.get('/mitarbeiter', { params }),
        api.get('/documents', { params }),
      ]);
      setList(maRes.data);
      const map = {};
      docRes.data.filter(d => d.mitarbeiter_id).forEach(d => {
        map[d.mitarbeiter_id] = map[d.mitarbeiter_id] || {};
        (map[d.mitarbeiter_id][d.doc_type] = map[d.mitarbeiter_id][d.doc_type] || []).push(d);
      });
      setDocsByMa(map);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId]);

  useEffect(() => { load(); }, [load]);

  // Vom Dashboard verlinkter Mitarbeiter → direkt Personalakte öffnen
  useEffect(() => {
    if (focusMaId && list.length) {
      const m = list.find(x => x.id === focusMaId);
      if (m) { setDetailMa(m); }
      if (clearMaFocus) clearMaFocus();
    }
  }, [focusMaId, list, clearMaFocus]);

  // Aus dem Erfassen-Hub „Neuer Mitarbeiter" → Formular einmalig öffnen (Signal danach zurücksetzen)
  useEffect(() => {
    if (startNewSignal) { setDetailMa(null); setEditing(null); setFormOpen(true); if (onStartNewConsumed) onStartNewConsumed(); }
  }, [startNewSignal, onStartNewConsumed]);

  async function download(doc) {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.original_name; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  async function uploadDocs(maId, docType, fileList) {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const fd = new FormData();
    arr.forEach(f => fd.append('files', f));
    fd.append('mandant_id', effectiveMandant);
    fd.append('mitarbeiter_id', maId);
    fd.append('doc_type', docType);
    fd.append('direction', isStaff ? 'to_client' : 'from_client');
    await api.post('/documents/upload', fd);
  }

  function pickAndUpload(maId, docType, multiple) {
    const input = document.createElement('input');
    input.type = 'file';
    if (multiple) input.multiple = true;
    input.onchange = async () => {
      try { await uploadDocs(maId, docType, input.files); await load(); }
      catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    };
    input.click();
  }

  async function handleSubmit(formVals, extra) {
    if (editing) {
      const gueltig = extra.gueltig_ab || new Date().toISOString().slice(0, 10);
      const fd = new FormData();
      fd.append('gueltig_ab', gueltig);
      Object.entries(formVals).forEach(([k, v]) => {
        if (!isStaff && k === 'pers_nr') return; // Mandant ändert die Pers.Nr nicht
        fd.append(k, v ?? '');
      });
      await api.patch(`/mitarbeiter/${editing.id}`, fd);
    } else {
      const norm = (s) => (s || '').trim().toLowerCase();
      const gleich = list.find(m => norm(m.vorname) === norm(formVals.vorname) && norm(m.nachname) === norm(formVals.nachname) && m.austritt);
      if (gleich && formVals.eintritt && window.confirm(`${gleich.vorname} ${gleich.nachname} ist bereits angelegt (ausgeschieden zum ${fmtDateDE(gleich.austritt)}).\n\nStatt einer zweiten Akte einen Wiedereintritt ab ${fmtDateDE(formVals.eintritt)} melden? Die bisherige Beschäftigung bleibt erhalten.\n\nOK = Wiedereintritt melden · Abbrechen = trotzdem neu anlegen`)) {
        const wfd = new FormData(); wfd.append('eintritt', formVals.eintritt);
        if (formVals.austritt) wfd.append('austritt', formVals.austritt);
        if (formVals.status) wfd.append('status', formVals.status);
        await api.post(`/mitarbeiter/${gleich.id}/wiedereintritt`, wfd);
        await load();
        return;
      }
      const fd = new FormData();
      fd.append('mandant_id', effectiveMandant);
      Object.entries(formVals).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      const { data } = await api.post('/mitarbeiter', fd);
      const maId = data.id;
      if (extra.pfbMode === 'upload' && extra.pfb) await uploadDocs(maId, 'personalfragebogen', [extra.pfb]);
      if (extra.ausweis && extra.ausweis.length) await uploadDocs(maId, 'personalausweis', extra.ausweis);
      if (extra.isWerk && extra.imma) await uploadDocs(maId, 'immatrikulation', [extra.imma]);
      if (extra.pfbMode === 'link') {
        try { await api.post(`/mitarbeiter/${maId}/pfb-anfordern`); }
        catch (ex) { /* MA ist angelegt; Link kann später aus der Akte gesendet werden */ }
      }
    }
    await load();
  }

  function openNew() { setEditing(null); setFormOpen(true); }
  function openEdit(m) { setEditing(m); setFormOpen(true); }

  async function deleteDoc(d) {
    if (!window.confirm(`Dokument „${d.original_name}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    try { await api.delete(`/documents/${d.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  const docCount = (m) => Object.values(docsByMa[m.id] || {}).reduce((a, arr) => a + arr.length, 0);
  const hasUnreleased = (m) => LOHN_DOK_TYPEN.some(t => (docsByMa[m.id]?.[t] || []).some(d => !d.freigegeben));
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const istRaus = (m) => !m.aktiv || (m.austritt && new Date(m.austritt) < today0);
  const aktivCount = list.filter(m => !istRaus(m)).length;
  const rausCount = list.length - aktivCount;
  const sucheLc = suche.trim().toLowerCase();
  const SORT_GET = {
    id: m => m.id,
    pers_nr: m => m.pers_nr,
    name: m => `${m.nachname || ''}, ${m.vorname || ''}`,
    status: m => m.status,
    eintritt: m => m.eintritt,
    austritt: m => m.austritt,
    std: m => { const v = parseFloat(String(m.wochenstunden || '').replace(',', '.')); return isNaN(v) ? null : v; },
    docs: m => docCount(m),
  };
  function toggleSort(k) {
    if (sortKey === k) {
      if (sortDir === 1) setSortDir(-1);
      else { setSortKey('id'); setSortDir(1); }   // 3. Klick: zurück zur Standard-Sortierung (ID)
    } else { setSortKey(k); setSortDir(1); }
  }
  const filtered = list
    .filter(m => statusFilter === 'alle' || (statusFilter === 'aktiv' ? !istRaus(m) : istRaus(m)))
    .filter(m => !sucheLc || `${m.vorname} ${m.nachname} ${m.pers_nr || ''} ${m.email || ''} ${m.status || ''}`.toLowerCase().includes(sucheLc))
    .sort((a, b) => {
      const get = SORT_GET[sortKey] || SORT_GET.id;
      const va = get(a), vb = get(b);
      const leerA = va == null || va === '', leerB = vb == null || vb === '';
      if (leerA && leerB) return a.id - b.id;
      if (leerA) return 1;                        // fehlende Werte immer ans Ende
      if (leerB) return -1;
      const r = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb), 'de', { numeric: true, sensitivity: 'base' });
      return (r * sortDir) || (a.id - b.id);
    });
  const thSort = (k, label) => (
    <th style={{ ...S.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => toggleSort(k)} title="Klicken zum Sortieren">
      {label}{sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}
    </th>
  );

  if (detailMa) {
    return (
      <MitarbeiterDetail
        ma={detailMa}
        list={list}
        isStaff={isStaff}
        onBack={() => { setDetailMa(null); load(); }}
        onSwitch={(m) => setDetailMa(m)}
        onChanged={load}
      />
    );
  }

  return (
    <div>
      {!ready ? (
        <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen, um Mitarbeiter anzuzeigen.</div></div>
      ) : (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Mitarbeiter <span style={{ fontSize: 13, color: C.muted, fontWeight: 400 }}>({aktivCount} aktiv{rausCount ? `, ${rausCount} ausgeschieden` : ''})</span></h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={() => setZugaengeOpen(true)}>👥 Mitarbeiter-Zugänge</button>
              {isStaff && <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={() => setImportOpen(true)}>⬆ Import (Lexware/CSV/Excel)</button>}
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={openNew}>+ Neuer Mitarbeiter</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <input style={{ ...S.input, flex: '1 1 240px', maxWidth: 360 }} placeholder="🔎 Suche: Name, Pers.-Nr. oder E-Mail" value={suche} onChange={e => setSuche(e.target.value)} />
            <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
              {[['aktiv', `Aktive (${aktivCount})`], ['raus', `Ausgeschieden (${rausCount})`], ['alle', `Alle (${list.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setStatusFilter(k)} style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: '8px 12px', background: statusFilter === k ? C.primary : C.white, color: statusFilter === k ? C.white : C.text }}>{l}</button>
              ))}
            </div>
          </div>
          {err && <div style={S.err}>{err}</div>}
          {loading ? <div style={S.empty}>Lade…</div> : filtered.length === 0 ? <div style={S.empty}>{list.length === 0 ? 'Keine Mitarbeiter' : 'Keine Treffer'}</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead><tr>
                  {thSort('pers_nr', 'Pers.Nr')}
                  {thSort('name', 'Name')}
                  {thSort('status', 'Status')}
                  {thSort('eintritt', 'Eintritt')}
                  {thSort('austritt', 'Austritt')}
                  {thSort('std', 'Std/W')}
                  {thSort('docs', 'Dokumente')}
                  <th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {filtered.map(m => { const raus = istRaus(m); return (
                    <tr key={m.id} style={{ opacity: raus ? 0.55 : 1, background: raus ? '#fafbfc' : 'transparent' }}>
                      <td style={S.td}>{m.pers_nr || '—'}</td>
                      <td style={S.td}>
                        <span style={{ cursor: 'pointer', color: raus ? C.muted : C.accent, fontWeight: 600 }} onClick={() => setDetailMa(m)} title="Personalakte öffnen">{m.nachname}, {m.vorname}</span>
                        {raus && <span style={{ ...S.badge, marginLeft: 6, background: '#eceff1', color: C.muted }}>ausgeschieden</span>}
                        {hasUnreleased(m) && <span style={{ ...S.badge, marginLeft: 6, background: '#fdeccd', color: '#8a6d3b' }} title="Nicht freigegebene Lohndokumente">⚠ Freigabe offen</span>}
                        {m.email ? <div style={{ fontSize: 11, color: C.muted }}>{m.email}</div> : null}
                      </td>
                      <td style={S.td}><span style={S.badge}>{m.status}</span></td>
                      <td style={S.td}>{fmtDateDE(m.eintritt)}</td>
                      <td style={S.td}>{fmtDateDE(m.austritt)}</td>
                      <td style={S.td}>{m.wochenstunden || '—'}</td>
                      <td style={S.td}>
                        <button style={S.btnSmall} onClick={() => setDocsMa(m)}>Dokumente ({docCount(m)})</button>
                      </td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        <button style={S.btnSmall} onClick={() => setDetailMa(m)}>Akte</button>
                        <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => openEdit(m)}>Bearbeiten</button>
                        <button style={{ ...S.btnSmall, marginLeft: 6, background: C.primary }} onClick={() => setLohnMa(m)}>Lohnabr.</button>
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {zugaengeOpen && <MaZugaenge isStaff={isStaff} mandantId={effectiveMandant} onClose={() => { setZugaengeOpen(false); load(); }} />}
      {importOpen && <LexwareImport mandantId={effectiveMandant} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}

      {docsMa && (
        <div style={S.modalOverlay} onClick={() => setDocsMa(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <h2 style={S.cardTitle}>Dokumente — {docsMa.vorname} {docsMa.nachname}</h2>
            {MA_DOC_TYPES.map(dt => (
              <div key={dt.key} style={{ marginBottom: 12, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 13 }}>{dt.label}</strong>
                  <button style={S.uploadMini} onClick={() => pickAndUpload(docsMa.id, dt.key, dt.key === 'personalausweis')}>+ Hochladen</button>
                </div>
                {(docsByMa[docsMa.id]?.[dt.key] || []).length === 0
                  ? <div style={{ fontSize: 12, color: C.muted }}>—</div>
                  : (docsByMa[docsMa.id]?.[dt.key] || []).map(d => (
                    <div key={d.id} style={{ fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ cursor: 'pointer', color: C.accent }} onClick={() => download(d)}>⬇ {d.original_name}</span>
                      <button style={{ ...S.fileRemove, fontSize: 12 }} title="Löschen" onClick={() => deleteDoc(d)}>✕ Löschen</button>
                    </div>
                  ))}
              </div>
            ))}
            <div style={{ marginTop: 8 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setDocsMa(null)}>Schließen</button></div>
          </div>
        </div>
      )}

      {formOpen && (
        <MitarbeiterForm
          isStaff={isStaff}
          initial={editing}
          onClose={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
      {lohnMa && (
        <LohnModal ma={lohnMa} isStaff={isStaff} effectiveMandant={effectiveMandant} onClose={() => setLohnMa(null)} />
      )}
    </div>
  );
}

// Mitarbeiter-Zugänge: wer hat Zugang zum Mitarbeiterportal, Einladung annehmen/registriert, Entfernen mit Mindestabruffrist
const ZUGANG_STATUS = {
  kein_zugang: ['Kein Zugang', '#eceff3', '#6c7a89'],
  eingeladen: ['Eingeladen – noch nicht angenommen', '#fff7e6', '#b8860b'],
  einladung_abgelaufen: ['Einladung abgelaufen', '#fdecea', '#c0392b'],
  registriert_ohne_2fa: ['Registriert – 2-Faktor-Anmeldung noch offen', '#e8eef5', '#3a7ca5'],
  aktiv: ['Registriert ✓', '#e8f4ea', '#27ae60'],
  entfernt: ['Zugang entfernt', '#eceff3', '#6c7a89'],
};
function MaZugaenge({ isStaff, mandantId, onClose }) {
  const [d, setD] = useState(null);
  const [alle, setAlle] = useState(false);
  const [mails, setMails] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');
  const heute = new Date().toISOString().slice(0, 10);
  const load = useCallback(async () => {
    try { const { data } = await api.get('/mitarbeiter-zugaenge', { params: isStaff ? { mandant_id: mandantId } : {} }); setD(data); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Übersicht konnte nicht geladen werden.'); }
  }, [isStaff, mandantId]);
  useEffect(() => { load(); }, [load]);
  async function einladen(r, leise) {
    const fd = new FormData();
    const neu = (mails[r.ma_id] || '').trim();
    if (!r.email && !neu) { setErr(`Bitte für ${r.vorname} ${r.nachname} eine E-Mail-Adresse eintragen.`); return false; }
    if (neu) fd.append('email', neu);
    try {
      const { data } = await api.post(`/mitarbeiter/${r.ma_id}/invite`, fd);
      if (!leise) { setMsg(`Einladung an ${data.email} gesendet.`); setErr(''); await load(); }
      return true;
    } catch (ex) { setErr(`${r.vorname} ${r.nachname}: ${ex.response?.data?.detail || 'Einladung fehlgeschlagen.'}`); return false; }
  }
  async function alleEinladen(liste) {
    if (!window.confirm(`${liste.length} Mitarbeiter per E-Mail zum Mitarbeiterportal einladen?`)) return;
    setBusy(true); setMsg(''); setErr('');
    let ok = 0;
    for (const r of liste) { if (await einladen(r, true)) ok++; }
    setBusy(false); setMsg(`${ok} von ${liste.length} Einladungen gesendet.`); await load();
  }
  async function entfernen(r) {
    const offen = r.status === 'eingeladen' || r.status === 'einladung_abgelaufen';
    const frist = !offen && r.entfernen_frei_ab && r.entfernen_frei_ab > heute;
    const text = offen ? `Einladung für ${r.vorname} ${r.nachname} zurückziehen? Der Link funktioniert danach nicht mehr.`
      : frist ? `Zugang von ${r.vorname} ${r.nachname} beenden?\n\nWegen der Mindestabruffrist bleibt der Zugang bis ${datum(r.entfernen_frei_ab)} bestehen, damit die freigegebenen Abrechnungen noch abgerufen werden können. Danach endet er automatisch.`
        : `Zugang von ${r.vorname} ${r.nachname} jetzt entfernen?`;
    if (!window.confirm(text)) return;
    try {
      const { data } = await api.post(`/mitarbeiter/${r.ma_id}/zugang-entfernen`, new FormData());
      setMsg(data.status === 'endet' ? `Zugang von ${r.vorname} ${r.nachname} endet am ${datum(data.zugang_bis)}.` : `Zugang von ${r.vorname} ${r.nachname} entfernt.`);
      setErr(''); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Entfernen fehlgeschlagen.'); }
  }
  async function sofortSperren(r) {
    if (!window.confirm(`Zugang von ${r.vorname} ${r.nachname} SOFORT sperren – trotz Mindestabruffrist?\n\nNur im Ausnahmefall (z. B. Verdacht auf Missbrauch). Der Mitarbeiter kann seine Dokumente dann nicht mehr selbst abrufen.`)) return;
    try { const fd = new FormData(); fd.append('sofort', 'true'); await api.post(`/mitarbeiter/${r.ma_id}/zugang-entfernen`, fd); setMsg('Zugang gesperrt.'); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Sperren fehlgeschlagen.'); }
  }
  async function behalten(r) {
    try { await api.post(`/mitarbeiter/${r.ma_id}/zugang-behalten`, new FormData()); setMsg(`Zugang von ${r.vorname} ${r.nachname} bleibt bestehen.`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Fehlgeschlagen.'); }
  }
  const rows = d ? d.mitarbeiter.filter(r => alle || r.aktiv) : [];
  const zaehl = (st) => rows.filter(r => st.includes(r.status)).length;
  const einladbar = rows.filter(r => r.aktiv && r.email && ['kein_zugang', 'einladung_abgelaufen'].includes(r.status));
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 'min(1100px, 96vw)', maxWidth: '96vw' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Mitarbeiter-Zugänge</h2>
        <div style={S.pathInfo}>Mitarbeiter mit Zugang sehen in ihrem eigenen Portal ausschließlich ihre freigegebenen Lohnabrechnungen, Melde- und Lohnsteuerbescheinigungen. Die Anmeldung ist mit Passwort und Zwei-Faktor-App geschützt, der Einladungslink ist 7 Tage gültig.{d ? ` Beim Entfernen gilt eine Mindestabruffrist: Der Zugang bleibt bis ${d.abruffrist_monate} Monate nach der letzten Freigabe bestehen und endet dann automatisch.` : ''}</div>
        {!d ? <div style={S.empty}>{err || 'Lade…'}</div> : (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '6px 0 12px' }}>
              <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>{zaehl(['aktiv', 'registriert_ohne_2fa'])} registriert</span>
              <span style={{ ...S.badge, background: '#fff7e6', color: '#b8860b' }}>{zaehl(['eingeladen'])} eingeladen</span>
              <span style={{ ...S.badge }}>{zaehl(['kein_zugang', 'einladung_abgelaufen', 'entfernt'])} ohne Zugang</span>
              <span style={{ flex: 1 }} />
              <label style={{ fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={alle} onChange={e => setAlle(e.target.checked)} /> ausgeschiedene Mitarbeiter anzeigen</label>
              {einladbar.length > 0 && <button style={S.btnSmall} disabled={busy} onClick={() => alleEinladen(einladbar)}>{busy ? 'Lädt ein…' : `Alle ohne Zugang einladen (${einladbar.length})`}</button>}
            </div>
            {msg && <div style={{ background: '#e8f4ea', color: C.success, padding: 8, borderRadius: 4, marginBottom: 8, fontSize: 13 }}>{msg}</div>}
            {err && <div style={{ ...S.err, marginTop: 0, marginBottom: 8 }}>{err}</div>}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Mitarbeiter</th><th style={S.th}>E-Mail</th><th style={S.th}>Status</th><th style={S.th}>Details</th><th style={S.th}></th></tr></thead>
                <tbody>{rows.map(r => {
                  const [lbl, bg, col] = ZUGANG_STATUS[r.status] || ZUGANG_STATUS.kein_zugang;
                  const ohneZugang = ['kein_zugang', 'einladung_abgelaufen', 'entfernt'].includes(r.status);
                  const registriert = ['aktiv', 'registriert_ohne_2fa'].includes(r.status);
                  const details = [];
                  if (r.status === 'eingeladen') details.push(`eingeladen am ${datum(r.eingeladen_am)} · Link gültig bis ${datum(r.einladung_gueltig_bis)}`);
                  if (r.status === 'einladung_abgelaufen') details.push(`eingeladen am ${datum(r.eingeladen_am)} · Link abgelaufen`);
                  if (registriert) details.push(r.registriert_am ? `registriert am ${datum(r.registriert_am)}` : 'registriert');
                  if (r.letzte_anmeldung) details.push(`zuletzt angemeldet ${datum(r.letzte_anmeldung)}`);
                  if (r.dokumente_freigegeben) details.push(`${r.dokumente_freigegeben} Dokument(e) freigegeben, zuletzt ${datum(r.letzte_freigabe)}`);
                  return (
                    <tr key={r.ma_id} style={{ opacity: r.aktiv ? 1 : 0.7 }}>
                      <td style={S.td}><b>{r.nachname}, {r.vorname}</b>{r.pers_nr ? <span style={{ color: C.muted, fontSize: 12 }}> · {r.pers_nr}</span> : null}{!r.aktiv && <div style={{ fontSize: 11, color: C.muted }}>ausgeschieden{r.austritt ? ` zum ${datum(r.austritt)}` : ''}</div>}</td>
                      <td style={S.td}>{r.email || (ohneZugang
                        ? <input style={{ ...S.input, padding: '5px 8px', minWidth: 190 }} placeholder="E-Mail eintragen" value={mails[r.ma_id] || ''} onChange={e => setMails(m => ({ ...m, [r.ma_id]: e.target.value }))} />
                        : <span style={{ color: C.muted }}>—</span>)}</td>
                      <td style={S.td}><span style={{ ...S.badge, background: bg, color: col, whiteSpace: 'nowrap' }}>{lbl}</span>
                        {r.zugang_bis && <div style={{ fontSize: 11, color: '#b8860b', marginTop: 3 }}>endet am {datum(r.zugang_bis)}</div>}</td>
                      <td style={{ ...S.td, fontSize: 12, color: C.muted }}>{details.join(' · ') || '—'}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        {ohneZugang && <button style={S.btnSmall} onClick={() => einladen(r)}>{r.status === 'kein_zugang' ? 'Einladen' : 'Erneut einladen'}</button>}
                        {r.status === 'eingeladen' && <>
                          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => einladen(r)}>Link erneut senden</button>
                          <button style={{ ...S.btnDanger }} onClick={() => entfernen(r)}>Zurückziehen</button>
                        </>}
                        {registriert && !r.zugang_bis && <button style={{ ...S.btnDanger, marginLeft: 0 }} onClick={() => entfernen(r)}>Zugang entfernen</button>}
                        {registriert && r.zugang_bis && <>
                          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => behalten(r)}>Doch behalten</button>
                          {isStaff && <button style={S.btnDanger} onClick={() => sofortSperren(r)}>Sofort sperren</button>}
                        </>}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td style={S.td} colSpan={5}><span style={{ color: C.muted }}>Keine Mitarbeiter.</span></td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div style={{ marginTop: 14 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button></div>
      </div>
    </div>
  );
}

function LohnMassenUpload({ effectiveMandant, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [standardMonat, setStandardMonat] = useState('');
  const [erzwingen, setErzwingen] = useState(false);
  const [bezeichnung, setBezeichnung] = useState('Lohnabrechnung');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [rows, setRows] = useState(null);
  const [maList, setMaList] = useState([]);

  useEffect(() => {
    api.get('/mitarbeiter', { params: { mandant_id: effectiveMandant } })
      .then(({ data }) => setMaList(data)).catch(() => {});
  }, [effectiveMandant]);

  async function upload() {
    if (!files.length) { setErr('Bitte Dateien wählen'); return; }
    if (erzwingen && !standardMonat) { setErr('Bitte einen Monat wählen, der erzwungen werden soll'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      fd.append('mandant_id', effectiveMandant);
      if (standardMonat) fd.append('standard_monat', standardMonat);
      fd.append('monat_erzwingen', erzwingen ? 'true' : 'false');
      fd.append('bezeichnung', bezeichnung || 'Lohnabrechnung');
      const { data } = await api.post('/lohnabrechnungen/massen-upload', fd);
      setRows(data.results.map(r => ({ ...r, dirty: false })));
      if (onDone) onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  function setRow(i, patch) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch, dirty: true } : r));
  }
  async function saveCorrections() {
    setBusy(true); setMsg(''); setErr('');
    try {
      const dirty = rows.filter(r => r.dirty);
      for (const r of dirty) {
        const fd = new FormData();
        fd.append('mitarbeiter_id', r.mitarbeiter_id == null ? '' : r.mitarbeiter_id);
        fd.append('monat', r.monat || '');
        await api.patch(`/documents/${r.doc_id}`, fd);
      }
      setRows(rs => rs.map(r => ({ ...r, dirty: false, status: (r.mitarbeiter_id && r.monat) ? 'ok' : 'pruefen' })));
      setMsg(`${dirty.length} Korrektur(en) gespeichert.`);
      if (onDone) onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  const offen = rows ? rows.filter(r => !(r.mitarbeiter_id && r.monat)).length : 0;
  const dirtyN = rows ? rows.filter(r => r.dirty).length : 0;

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 820 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Lohnabrechnungen – Massen-Upload</h2>
        <div style={S.pathInfo}>Mehrere PDFs auf einmal. Automatische Erkennung über <strong>Personalnummer</strong>, <strong>Name</strong> (Vor-/Nachname) und <strong>Monat</strong> im Dateinamen – auch Monatsnamen wie <code>Juli</code>. Mit dem Schalter unten kannst du <strong>einen Monat für alle erzwingen</strong>. Alles wird gespeichert; falsch oder nicht Erkanntes korrigierst du direkt in der Übersicht.</div>
        <div style={S.row}>
          <div style={{ ...S.field, flex: '2 1 280px' }}><label style={S.label}>Dateien</label>
            <input type="file" multiple style={S.input} onChange={e => { setFiles(Array.from(e.target.files || [])); setRows(null); }} />
          </div>
          <div style={S.field}><label style={S.label}>Monat {erzwingen ? '(für alle)' : '(Fallback)'}</label>
            <input type="month" style={S.input} value={standardMonat} onChange={e => setStandardMonat(e.target.value)} />
          </div>
          <div style={S.field}><label style={S.label}>Bezeichnung</label>
            <input style={S.input} value={bezeichnung} onChange={e => setBezeichnung(e.target.value)} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', margin: '4px 0 8px' }}>
          <input type="checkbox" checked={erzwingen} onChange={e => setErzwingen(e.target.checked)} />
          Monat für <strong>alle</strong> Dokumente erzwingen (Erkennung im Dateinamen ignorieren)
        </label>
        {files.length > 0 && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{files.length} Datei(en) gewählt</div>}
        {err && <div style={S.err}>{err}</div>}
        {msg && <div style={{ background: '#e8f4ea', color: C.success, padding: 8, borderRadius: 4, marginBottom: 8, fontSize: 13 }}>{msg}</div>}
        {rows && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>{rows.length} hochgeladen · {offen > 0 ? <span style={{ color: C.danger }}>{offen} zu prüfen</span> : <span style={{ color: C.success }}>alle zugeordnet</span>}. Du kannst Mitarbeiter und Monat hier anpassen:</div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Datei</th><th style={S.th}>Mitarbeiter</th><th style={S.th}>Monat</th><th style={S.th}>Status</th></tr></thead>
                <tbody>{rows.map((r, i) => {
                  const ok = r.mitarbeiter_id && r.monat;
                  return (
                    <tr key={r.doc_id} style={{ background: ok ? 'transparent' : '#fff7f6' }}>
                      <td style={{ ...S.td, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.filename}>{r.filename}</td>
                      <td style={S.td}>
                        <select style={{ ...S.select, minWidth: 170 }} value={r.mitarbeiter_id || ''} onChange={e => setRow(i, { mitarbeiter_id: e.target.value ? Number(e.target.value) : null })}>
                          <option value="">— wählen —</option>
                          {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}{m.pers_nr ? ` (${m.pers_nr})` : ''}</option>)}
                        </select>
                      </td>
                      <td style={S.td}><input type="month" style={{ ...S.input, width: 150 }} value={r.monat || ''} onChange={e => setRow(i, { monat: e.target.value })} /></td>
                      <td style={S.td}>{ok
                        ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>{r.dirty ? 'geändert' : 'ok'}</span>
                        : <span style={{ ...S.badge, background: '#fdecea', color: C.danger }}>prüfen</span>}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            <div style={S.pathInfo}>Zugeordnete Abrechnungen liegen beim jeweiligen MA (Tab „Lohnabr.") und müssen dort noch <strong>freigegeben</strong> werden.</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          {!rows && <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || !files.length} onClick={upload}>{busy ? 'Lädt…' : 'Hochladen & erkennen'}</button>}
          {rows && <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || dirtyN === 0} onClick={saveCorrections}>{busy ? 'Speichert…' : `Korrekturen speichern${dirtyN ? ` (${dirtyN})` : ''}`}</button>}
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

function LohnModal({ ma, isStaff, effectiveMandant, onClose }) {
  const [docs, setDocs] = useState([]);
  const [monat, setMonat] = useState('');
  const [art, setArt] = useState('lohnabrechnung');
  const [bezeichnung, setBezeichnung] = useState('Lohnabrechnung');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { mitarbeiter_id: ma.id };
      if (isStaff) params.mandant_id = effectiveMandant;
      const { data } = await api.get('/documents', { params });
      setDocs(data.filter(istLohnDok).sort((a, b) => (b.monat || '').localeCompare(a.monat || '') || String(a.doc_type).localeCompare(String(b.doc_type))));
    } catch { setErr('Laden fehlgeschlagen'); }
  }, [ma.id, isStaff, effectiveMandant]);
  useEffect(() => { load(); }, [load]);

  async function download(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = d.original_name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  async function upload() {
    if (!file || !monat) { setErr('Monat und Datei erforderlich'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('files', file);
      fd.append('mandant_id', effectiveMandant);
      fd.append('mitarbeiter_id', ma.id);
      fd.append('doc_type', art);
      fd.append('monat', monat);
      fd.append('bezeichnung', bezeichnung || LOHN_DOK_LABEL[art]);
      fd.append('direction', 'to_client');
      await api.post('/documents/upload', fd);
      setFile(null); setMonat(''); setBezeichnung(LOHN_DOK_LABEL[art]);
      const inp = document.getElementById('lohn-file'); if (inp) inp.value = '';
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  async function toggleFreigabe(d) {
    try {
      const fd = new FormData(); fd.append('freigegeben', d.freigegeben ? 'false' : 'true');
      await api.patch(`/documents/${d.id}/freigabe`, fd); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Freigabe fehlgeschlagen'); }
  }

  async function deleteDoc(d) {
    if (!window.confirm(`„${d.bezeichnung || d.original_name}" wirklich löschen?`)) return;
    try { await api.delete(`/documents/${d.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 720 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Lohnabrechnungen & Bescheinigungen — {ma.vorname} {ma.nachname}</h2>
        {isStaff && (
          <div style={{ ...S.row, marginBottom: 12 }}>
            <div style={S.field}><label style={S.label}>Art</label>
              <select style={S.select} value={art} onChange={e => { const v = e.target.value; setArt(v); if (!bezeichnung || Object.values(LOHN_DOK_LABEL).includes(bezeichnung)) setBezeichnung(LOHN_DOK_LABEL[v]); }}>
                <option value="lohnabrechnung">Lohnabrechnung</option><option value="meldebescheinigung">Meldebescheinigung</option><option value="lohnsteuerbescheinigung">Lohnsteuerbescheinigung</option>
              </select></div>
            <div style={S.field}><label style={S.label}>Monat</label><input type="month" style={S.input} value={monat} onChange={e => setMonat(e.target.value)} /></div>
            <div style={S.field}><label style={S.label}>Bezeichnung</label><input style={S.input} list="lohn-bez" value={bezeichnung} onChange={e => setBezeichnung(e.target.value)} placeholder="z.B. Lohnsteuerbescheinigung 2025" />
              <datalist id="lohn-bez"><option value="Lohnabrechnung" /><option value="Meldebescheinigung" /><option value="Lohnsteuerbescheinigung 2025" /><option value="SV-Meldung" /><option value="Sozialversicherungsnachweis" /></datalist>
            </div>
            <div style={S.field}><label style={S.label}>Datei</label><input id="lohn-file" type="file" style={S.input} onChange={e => setFile(e.target.files[0] || null)} /></div>
            <div><button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={upload}>{busy ? 'Lädt…' : 'Hochladen'}</button></div>
          </div>
        )}
        {err && <div style={S.err}>{err}</div>}
        {docs.length === 0 ? <div style={S.empty}>Noch keine Lohndokumente</div> : (
          <table style={S.table}>
            <thead><tr><th style={S.th}>Monat</th><th style={S.th}>Art</th><th style={S.th}>Bezeichnung</th><th style={S.th}>MA-Freigabe</th><th style={S.th}></th></tr></thead>
            <tbody>{docs.map(d => (
              <tr key={d.id}>
                <td style={S.td}>{d.monat || '—'}</td>
                <td style={S.td}>{LOHN_DOK_LABEL[d.doc_type] || d.doc_type}</td>
                <td style={S.td}>{d.bezeichnung || d.original_name}</td>
                <td style={S.td}>
                  <button style={{ ...S.btnSmall, background: d.freigegeben ? C.success : C.muted }} onClick={() => toggleFreigabe(d)}>
                    {d.freigegeben ? 'Freigegeben ✓' : 'Freigeben'}
                  </button>
                </td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  <button style={{ ...S.btnSmall, background: C.accent }} onClick={() => setPreviewDoc(d)}>Ansehen</button>
                  <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => download(d)}>Download</button>
                  {isStaff && <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => deleteDoc(d)}>✕</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
        <div style={S.pathInfo}>„Ansehen" öffnet die Abrechnung direkt im Portal. „Freigeben" macht das Dokument für den Mitarbeiter sichtbar (sobald MA-Logins aktiv sind).</div>
        <div style={{ marginTop: 12 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button></div>
      </div>
      {previewDoc && <DocPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} canFreigabe onChanged={load} />}
    </div>
  );
}

function NewTicketModal({ isStaff, maList, effectiveMandant, onClose, onCreated }) {
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [maId, setMaId] = useState('');
  const [empfaenger, setEmpfaenger] = useState('mandant');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault(); setErr('');
    if (!titel.trim()) { setErr('Titel erforderlich'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('titel', titel);
      fd.append('mandant_id', effectiveMandant);
      if (beschreibung) fd.append('beschreibung', beschreibung);
      if (maId) fd.append('mitarbeiter_id', maId);
      if (isStaff) fd.append('empfaenger', empfaenger);
      await api.post('/tickets', fd);
      onCreated(); onClose();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Anlegen fehlgeschlagen'); setBusy(false); }
  }

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <form style={S.modalCard} onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2 style={S.cardTitle}>Neues Ticket</h2>
        <label style={S.label}>Titel</label>
        <input style={S.input} value={titel} onChange={e => setTitel(e.target.value)} autoFocus />
        <label style={S.label}>Beschreibung</label>
        <textarea style={S.textarea} value={beschreibung} onChange={e => setBeschreibung(e.target.value)} />
        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Bezug Mitarbeiter (optional)</label>
            <select style={S.select} value={maId} onChange={e => setMaId(e.target.value)}>
              <option value="">— kein MA-Bezug —</option>
              {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
            </select>
          </div>
          {isStaff && (
            <div style={S.field}>
              <label style={S.label}>Empfänger</label>
              <select style={S.select} value={empfaenger} onChange={e => setEmpfaenger(e.target.value)}>
                <option value="mandant">an Mandant</option>
                <option value="lohnbuero">interne Notiz (Lohnbüro)</option>
              </select>
            </div>
          )}
        </div>
        {err && <div style={S.err}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="submit" style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy}>{busy ? 'Erstellt…' : 'Ticket anlegen'}</button>
          <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}

function TicketDetailModal({ ticket, maName, onClose, onChanged }) {
  const [comments, setComments] = useState([]);
  const [anhaenge, setAnhaenge] = useState([]);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticket.id}/comments`); setComments(data);
      const a = await api.get(`/tickets/${ticket.id}/anhaenge`); setAnhaenge(a.data);
    }
    catch { setErr('Inhalte konnten nicht geladen werden'); }
  }, [ticket.id]);
  useEffect(() => { load(); }, [load]);

  async function uploadAnhang(fileList) {
    const files = Array.from(fileList || []); if (!files.length) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      await api.post(`/tickets/${ticket.id}/anhaenge`, fd); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  async function downloadAnhang(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a');
      a.href = url; a.download = d.original_name; a.click(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  async function addComment(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('content', text);
      await api.post(`/tickets/${ticket.id}/comments`, fd);
      setText(''); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  async function toggle() {
    try {
      const fd = new FormData(); fd.append('status', ticket.status === 'offen' ? 'erledigt' : 'offen');
      await api.patch(`/tickets/${ticket.id}`, fd);
      onChanged(); onClose();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalCard} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>{ticket.titel}</h2>
        <div style={S.pathInfo}>
          Status: <strong>{ticket.status}</strong> · von {ticket.erstellt_von_seite === 'mandant' ? 'Mandant' : 'Lohnbüro'}
          {ticket.mitarbeiter_id ? ` · MA: ${maName(ticket.mitarbeiter_id)}` : ''} · {fmtDate(ticket.created_at)}
          {ticket.quelle === 'ma_aenderung' ? ' · (autom. MA-Änderung)' : ''}
        </div>
        {ticket.beschreibung && <div style={{ ...S.msg, marginBottom: 14 }}><div style={S.msgBody}>{ticket.beschreibung}</div></div>}
        <h3 style={{ fontSize: 14, color: C.primary, margin: '8px 0' }}>Verlauf</h3>
        {comments.length === 0 ? <div style={S.empty}>Noch keine Kommentare</div> : comments.map(c => (
          <div key={c.id} style={S.msg}>
            <div style={S.msgMeta}>{fmtDate(c.created_at)} · {c.sender_name || `Absender #${c.sender_id}`}</div>
            <div style={S.msgBody}>{c.content}</div>
          </div>
        ))}
        <h3 style={{ fontSize: 14, color: C.primary, margin: '14px 0 8px' }}>Anhänge</h3>
        {anhaenge.length === 0 ? <div style={{ ...S.empty, marginBottom: 8 }}>Keine Anhänge</div> : (
          <div style={{ marginBottom: 8 }}>{anhaenge.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, borderBottom: `1px solid ${C.bg}` }}>
              <span>📎</span>
              <button onClick={() => downloadAnhang(d)} style={{ border: 'none', background: 'transparent', color: C.accent, cursor: 'pointer', padding: 0, textAlign: 'left', flex: 1 }}>{d.original_name}</button>
              <span style={{ color: C.muted, fontSize: 11 }}>{d.uploaded_by || ''} · {fmtDate(d.created_at)}</span>
            </div>
          ))}</div>
        )}
        <label style={{ ...S.btnSmall, display: 'inline-block', cursor: 'pointer', marginBottom: 12 }}>
          📎 Datei anhängen
          <input type="file" multiple style={{ display: 'none' }} onChange={e => { uploadAnhang(e.target.files); e.target.value = ''; }} />
        </label>
        <form onSubmit={addComment}>
          <textarea style={S.textarea} value={text} onChange={e => setText(e.target.value)} placeholder="Kommentar…" />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button type="submit" style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy}>Kommentar senden</button>
            <button type="button" style={{ ...S.btnSmall, background: ticket.status === 'offen' ? C.success : C.accent }} onClick={toggle}>
              {ticket.status === 'offen' ? 'Als erledigt markieren' : 'Wieder öffnen'}
            </button>
            <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button>
          </div>
        </form>
        {err && <div style={S.err}>{err}</div>}
      </div>
    </div>
  );
}

function Tickets({ user, mandanten, mandantId, focusId, clearFocus }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [tickets, setTickets] = useState([]);
  const [maList, setMaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filterStatus, setFilterStatus] = useState('offen');
  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const ready = isStaff ? !!mandantId : true;
  const effectiveMandant = isStaff ? mandantId : (user.mandant_id ?? '');

  const load = useCallback(async () => {
    if (!ready) { setTickets([]); setMaList([]); return; }
    setLoading(true); setErr('');
    try {
      const params = {};
      if (isStaff) params.mandant_id = mandantId;
      if (filterStatus) params.status = filterStatus;
      const [tRes, maRes] = await Promise.all([
        api.get('/tickets', { params }),
        api.get('/mitarbeiter', { params: isStaff ? { mandant_id: mandantId } : {} }),
      ]);
      setTickets(tRes.data); setMaList(maRes.data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId, filterStatus]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (focusId) { setDetailId(focusId); if (clearFocus) clearFocus(); } }, [focusId, clearFocus]);

  const maName = (id) => { const m = maList.find(x => x.id === id); return m ? `${m.vorname} ${m.nachname}` : (id ? `MA #${id}` : '—'); };
  const detailTicket = tickets.find(t => t.id === detailId);

  // Thema aus dem Titel automatischer Tickets ("Adresse: Anna Schneider" → "Adresse")
  const themaVon = (t) => {
    if (t.quelle !== 'ma_aenderung') return null;
    const i = (t.titel || '').indexOf(':');
    return i > 0 ? t.titel.slice(0, i) : null;
  };
  // "Gültig ab" aus der Beschreibung (deutsches Format; ISO-Altbestand als Fallback)
  const gueltigAbVon = (t) => {
    const de = /Gültig ab (\d{2}\.\d{2}\.\d{4})/.exec(t.beschreibung || '');
    if (de) return de[1];
    const iso = /Gültig ab (\d{4})-(\d{2})-(\d{2})/.exec(t.beschreibung || '');
    return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : null;
  };
  // Tickets nach Mitarbeiter gruppieren; Tickets ohne MA-Bezug ans Ende
  const gruppen = (() => {
    const map = new Map();
    tickets.forEach(t => { const k = t.mitarbeiter_id || 0; if (!map.has(k)) map.set(k, []); map.get(k).push(t); });
    const arr = [...map.entries()].map(([k, items]) => ({
      key: k, name: k ? maName(k) : 'Allgemein (ohne Mitarbeiter-Bezug)',
      items: items.slice().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    }));
    arr.sort((a, b) => ((a.key === 0) - (b.key === 0)) || a.name.localeCompare(b.name, 'de'));
    return arr;
  })();

  async function setStatus(t, status) {
    try { const fd = new FormData(); fd.append('status', status); await api.patch(`/tickets/${t.id}`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  return (
    <div>
      {!ready ? (
        <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen, um Tickets anzuzeigen.</div></div>
      ) : (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Tickets</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select style={{ ...S.select, width: 'auto', padding: '6px 10px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="offen">Offen</option>
                <option value="erledigt">Erledigt</option>
                <option value="">Alle</option>
              </select>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => setShowNew(true)}>+ Neues Ticket</button>
            </div>
          </div>
          {err && <div style={S.err}>{err}</div>}
          {loading ? <div style={S.empty}>Lade…</div> : tickets.length === 0 ? <div style={S.empty}>Keine Tickets</div> : (
            gruppen.map(g => (
              <div key={g.key} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, background: C.bg, border: `1px solid ${C.border}`, borderBottom: 'none', borderRadius: '6px 6px 0 0', padding: '7px 10px' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.primary }}>{g.key ? `👤 ${g.name}` : `📋 ${g.name}`}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{g.items.filter(t => t.status === 'offen').length} offen · {g.items.length} gesamt</span>
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: '0 0 6px 6px', overflowX: 'auto' }}>
                  <table style={{ ...S.table, marginBottom: 0, tableLayout: 'fixed', minWidth: 700 }}>
                    <colgroup>
                      <col />{/* Thema/Titel: flexibel */}
                      <col style={{ width: 90 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 170 }} />
                    </colgroup>
                    <thead><tr>
                      <th style={S.th}>Thema / Titel</th><th style={S.th}>Von</th>
                      <th style={S.th}>Gültig ab</th>
                      <th style={S.th}>Status</th><th style={S.th}>Erstellt</th><th style={S.th}></th>
                    </tr></thead>
                    <tbody>
                      {g.items.map(t => { const thema = themaVon(t); const gab = gueltigAbVon(t); return (
                        <tr key={t.id}>
                          <td style={{ ...S.td, overflowWrap: 'break-word' }}>
                            {thema && <span style={{ ...S.badge, background: '#e8eef5', color: C.accent, marginRight: 6 }}>{thema}</span>}
                            <span style={{ cursor: 'pointer', color: C.accent }} onClick={() => setDetailId(t.id)}>{thema ? (t.titel.slice(thema.length + 1).trim() || t.titel) : t.titel}</span>
                            {t.quelle === 'ma_aenderung' && <span style={{ ...S.badge, marginLeft: 6 }}>auto</span>}
                          </td>
                          <td style={S.td}>{t.erstellt_von_seite === 'mandant' ? 'Mandant' : 'Lohnbüro'}</td>
                          <td style={{ ...S.td, fontVariantNumeric: 'tabular-nums' }}>{gab || '—'}</td>
                          <td style={S.td}><span style={{ ...S.badge, background: t.status === 'offen' ? '#fdeccd' : '#e8f4ea', color: t.status === 'offen' ? '#8a6d3b' : C.success }}>{t.status}</span></td>
                          <td style={{ ...S.td, fontVariantNumeric: 'tabular-nums' }}>{fmtDateDE(t.created_at)}</td>
                          <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                            <button style={S.btnSmall} onClick={() => setDetailId(t.id)}>Öffnen</button>
                            {t.status === 'offen'
                              ? <button style={{ ...S.btnSmall, background: C.success, marginLeft: 6 }} onClick={() => setStatus(t, 'erledigt')}>Erledigt</button>
                              : <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => setStatus(t, 'offen')}>Öffnen</button>}
                          </td>
                        </tr>
                      ); })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {showNew && <NewTicketModal isStaff={isStaff} maList={maList} effectiveMandant={effectiveMandant} onClose={() => setShowNew(false)} onCreated={load} />}
      {detailTicket && <TicketDetailModal ticket={detailTicket} maName={maName} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}

const URG_STYLE = {
  neutral:  { background: '#eef4ee', color: '#2f7a4f', border: '1px solid #cfe6d6' },
  warnung:  { background: '#fff7e6', color: '#8a6d3b', border: '1px solid #ffe1a8' },
  dringend: { background: '#ffe9d6', color: '#b35900', border: '1px solid #ffc999' },
  kritisch: { background: '#fdecea', color: '#c0392b', border: '1px solid #f3c6c2', fontWeight: 700 },
};
const ART_LABEL = { beitragsnachweis: 'Beitragsnachweis (SV)', lohnsteuer: 'Lohnsteueranmeldung' };

function tageText(t) {
  if (t < 0) return `überfällig seit ${-t} Tag(en)`;
  if (t === 0) return 'heute fällig';
  if (t === 1) return 'morgen fällig';
  return `in ${t} Tagen`;
}

function MandantBenachrichtigen({ mandantId }) {
  const [open, setOpen] = useState(false);
  const [typ, setTyp] = useState('abrechnung');
  const [monat, setMonat] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const jahr = new Date().getFullYear();
  const MON = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  async function send() {
    if (typ === 'text' && (!betreff.trim() || !text.trim())) { setErr('Betreff und Text erforderlich'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData(); fd.append('typ', typ);
      if (typ === 'abrechnung') fd.append('monat', monat);
      else { fd.append('betreff', betreff); fd.append('text', text); }
      const { data } = await api.post(`/mandanten/${mandantId}/benachrichtigung`, fd);
      setMsg(`Benachrichtigung an ${data.empfaenger} Empfänger gesendet.`); setOpen(false); setBetreff(''); setText('');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  return (
    <div style={S.card}>
      <h2 style={S.cardTitle}>Mandant benachrichtigen</h2>
      <div style={S.pathInfo}>Sendet dem Mandanten eine E-Mail im gebrandeten Design – z.B. „Abrechnung abgeschlossen" oder eine freie Mitteilung.</div>
      <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => { setErr(''); setMsg(''); setOpen(true); }}>📣 Benachrichtigung senden</button>
      {msg && <div style={S.okMsg}>{msg}</div>}
      {open && (
        <div style={S.modalOverlay} onClick={() => setOpen(false)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <h2 style={S.cardTitle}>Benachrichtigung an Mandant</h2>
            <div style={{ display: 'flex', gap: 16, margin: '4px 0 12px' }}>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="btyp" checked={typ === 'abrechnung'} onChange={() => setTyp('abrechnung')} /> Abrechnung abgeschlossen</label>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="btyp" checked={typ === 'text'} onChange={() => setTyp('text')} /> Freier Text</label>
            </div>
            {typ === 'abrechnung' ? (
              <>
                <label style={S.label}>Abrechnungsmonat</label>
                <select style={S.select} value={monat} onChange={e => setMonat(e.target.value)}>
                  <option value="">— Monat wählen (optional) —</option>
                  {MON.map((m, i) => <option key={i} value={`${m} ${jahr}`}>{m} {jahr}</option>)}
                  {MON.map((m, i) => <option key={'v' + i} value={`${m} ${jahr - 1}`}>{m} {jahr - 1}</option>)}
                </select>
                <div style={S.pathInfo}>Betreff &amp; Text werden automatisch erzeugt: „Lohnabrechnung {monat || '…'} abgeschlossen – Auswertungen online" mit Hinweis auf das Portal.</div>
              </>
            ) : (
              <>
                <label style={S.label}>Betreff</label>
                <input style={S.input} value={betreff} onChange={e => setBetreff(e.target.value)} />
                <label style={{ ...S.label, marginTop: 8 }}>Nachricht</label>
                <textarea style={{ ...S.textarea, minHeight: 160 }} value={text} onChange={e => setText(e.target.value)} />
              </>
            )}
            {err && <div style={S.err}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={send}>{busy ? 'Sendet…' : 'Senden'}</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setOpen(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Cockpit({ onGoMandant, isAdmin, onChanged }) {
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [mein, setMein] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [fRes, rRes, mRes] = await Promise.all([
        api.get('/faelligkeiten'),
        api.get('/reporting').catch(() => ({ data: null })),
        api.get('/mein-buero').catch(() => ({ data: null })),
      ]);
      setRows(fRes.data); setReport(rRes.data); setMein(mRes.data);
    }
    catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function meinBueroEinrichten() {
    try { await api.post('/mein-buero/einrichten'); await load(); if (onChanged) onChanged(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Einrichten fehlgeschlagen'); }
  }

  async function abhaken(mandant_id, art, periode) {
    try {
      const fd = new FormData();
      fd.append('mandant_id', mandant_id); fd.append('art', art); fd.append('periode', periode);
      const { data } = await api.post('/faelligkeiten/abhaken', fd);
      setRows(rs => rs.map(r => r.mandant_id === mandant_id ? data : r));
    } catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }
  async function undo(mandant_id, art, periode) {
    try {
      const { data } = await api.delete('/faelligkeiten/abhaken', { params: { mandant_id, art, periode } });
      setRows(rs => rs.map(r => r.mandant_id === mandant_id ? data : r));
    } catch (ex) { setErr(ex.response?.data?.detail || 'Rückgängig fehlgeschlagen'); }
  }

  const Zelle = ({ r, art }) => {
    const f = r[art];
    const st = URG_STYLE[f.urgency] || URG_STYLE.neutral;
    return (
      <div style={{ ...st, borderRadius: 6, padding: '8px 10px' }}>
        <div style={{ fontSize: 13 }}>{f.periode_label}{f.urgency === 'kritisch' ? ' · ÄUSSERST DRINGEND' : ''}</div>
        <div style={{ fontSize: 12 }}>fällig {fmtDateDE(f.faellig_am)} · {tageText(f.tage_bis)}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={{ ...S.btnSmall, background: C.success }} onClick={() => abhaken(r.mandant_id, art, f.periode)}>Abgesendet ✓</button>
          {f.zurueck_periode && <button style={{ ...S.btnSmall, background: C.muted }} title={`Zurück – „${f.zurueck_label}" wieder öffnen`} onClick={() => undo(r.mandant_id, art, f.zurueck_periode)}>↩ zurück</button>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>Cockpit – Fälligkeiten Ihrer Mandanten</h2>
        <button style={S.btnSmall} onClick={load}>Aktualisieren</button>
      </div>
      <div style={S.pathInfo}>Wählen Sie links einen Mandanten, um dessen Detail-Dashboard zu öffnen. „Abgesendet ✓" hakt die Periode ab – danach erscheint automatisch der nächste Termin (ohne Warnfarbe).</div>

      <div style={{ ...S.card, background: '#eef4fb', border: `1px solid ${C.accent}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>🏢 Mein Büro – eigene Lohnabrechnung</h2>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Eigene Mitarbeiter, SV-Fälligkeiten und Dokumente – ohne Rechnungstellung.</div>
          </div>
          {mein && mein.vorhanden
            ? <span style={{ fontSize: 13, color: C.accent }}>Über die Reiter links (Mitarbeiter, Dokumente, …) verwalten</span>
            : isAdmin
              ? <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={meinBueroEinrichten}>Mein Büro einrichten</button>
              : <span style={{ fontSize: 13, color: C.muted }}>noch nicht eingerichtet</span>}
        </div>
      </div>

      {report && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { label: 'Mandanten', val: report.summary.mandanten_anzahl, sub: '' },
            { label: 'Offene Rechnungen', val: fmtEur(report.summary.offene_betrag), sub: `${report.summary.offene_rechnungen} Stück` },
            { label: `Umsatz ${report.jahr}`, val: fmtEur(report.summary.umsatz_jahr), sub: 'abgerechnet' },
            { label: 'Dringende Fälligkeiten', val: report.summary.faellig_kritisch, sub: 'BN/Lohnsteuer' },
          ].map((k, i) => (
            <div key={i} style={{ ...S.card, flex: '1 1 180px', marginBottom: 0, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: C.primary, margin: '6px 0 2px' }}>{k.val}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{k.sub}</div>
            </div>
          ))}
        </div>
      )}
      {err && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{err}</div>}
      {loading ? <div style={S.card}><div style={S.empty}>Lade…</div></div>
        : rows.length === 0 ? <div style={S.card}><div style={S.empty}>Keine Mandanten mit aktiven Mitarbeitern zugeordnet.</div></div> : (
        <div style={S.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Mandant</th>
                <th style={S.th}>{ART_LABEL.beitragsnachweis}</th>
                <th style={S.th}>{ART_LABEL.lohnsteuer}</th>
                <th style={S.th}>Offene Tickets</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.mandant_id}>
                    <td style={S.td}>
                      <span style={{ cursor: 'pointer', color: C.accent, fontWeight: 600 }} onClick={() => onGoMandant(r.mandant_id, 'dashboard')}>{r.mandant_name}</span>
                      <div style={{ fontSize: 11, color: C.muted }}>{r.bundesland || 'Bundesland fehlt'} · LSt {r.lohnsteuer_zeitraum}</div>
                    </td>
                    <td style={{ ...S.td, minWidth: 200 }}><Zelle r={r} art="beitragsnachweis" /></td>
                    <td style={{ ...S.td, minWidth: 200 }}><Zelle r={r} art="lohnsteuer" /></td>
                    <td style={S.td}>
                      {r.offene_tickets > 0
                        ? <button style={S.btnSmall} onClick={() => onGoMandant(r.mandant_id, 'tickets')}>{r.offene_tickets} offen</button>
                        : <span style={{ color: C.muted }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Fehlzeiten / Erfassen (Mandanten-Bereich) =====
const FEHLZEIT_ARTEN = [
  { key: 'krankheit', label: 'Krankheit (AU/eAU)', icon: '🤒', color: '#c0392b' },
  { key: 'kind_krank', label: 'Kind krank', icon: '🧒', color: '#e67e22' },
  { key: 'urlaub', label: 'Urlaub', icon: '🌴', color: '#27ae60' },
  { key: 'mutterschutz', label: 'Mutterschutz / Schwangerschaft', icon: '🤰', color: '#d81b8c' },
  { key: 'elternzeit', label: 'Elternzeit', icon: '👶', color: '#8e44ad' },
  { key: 'freistellung', label: 'Unbezahlte Freistellung', icon: '⏸️', color: '#7f8c8d' },
  { key: 'kur', label: 'Kur / Reha', icon: '🩺', color: '#16a085' },
  { key: 'arbeitsunfall', label: 'Arbeitsunfall', icon: '⚠️', color: '#d35400' },
  { key: 'fortbildung', label: 'Fortbildung', icon: '🎓', color: '#2980b9' },
  { key: 'sonderurlaub', label: 'Sonderurlaub', icon: '🗓️', color: '#34495e' },
];
const FEHLZEIT_MAP = Object.fromEntries(FEHLZEIT_ARTEN.map(a => [a.key, a]));
const MONATE_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const _iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Kalender zum benutzerfreundlichen Markieren eines Zeitraums (1. Klick = Von, 2. Klick = Bis)
function RangeKalender({ von, bis, onChange, accent = C.accent }) {
  const startD = von ? new Date(von + 'T00:00:00') : new Date();
  const [view, setView] = useState({ y: startD.getFullYear(), m: startD.getMonth() });
  const todayIso = _iso(new Date());
  const first = new Date(view.y, view.m, 1);
  const startWd = (first.getDay() + 6) % 7; // Montag = 0
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(view.y, view.m, d));
  function pick(d) {
    const iso = _iso(d);
    if (!von || (von && bis)) onChange({ von: iso, bis: '' });
    else if (iso < von) onChange({ von: iso, bis: von });
    else onChange({ von, bis: iso });
  }
  const inRange = (d) => { const iso = _iso(d); if (von && bis) return iso >= von && iso <= bis; return iso === von; };
  const isEnd = (d) => { const iso = _iso(d); return iso === von || iso === bis; };
  const shift = (n) => { let m = view.m + n, y = view.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setView({ y, m }); };
  const cellBtn = { border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, height: 38, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', color: C.text };
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: C.white }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" onClick={() => shift(-1)} style={{ ...cellBtn, width: 34, background: C.bg }}>‹</button>
        <div style={{ fontWeight: 600, color: C.primary, fontSize: 14 }}>{MONATE_DE[view.m]} {view.y}</div>
        <button type="button" onClick={() => shift(1)} style={{ ...cellBtn, width: 34, background: C.bg }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, textAlign: 'center' }}>
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(w => <div key={w} style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: '2px 0' }}>{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const sel = inRange(d), end = isEnd(d), we = (d.getDay() === 0 || d.getDay() === 6);
          return (
            <button key={i} type="button" onClick={() => pick(d)}
              style={{ ...cellBtn, background: sel ? (end ? accent : accent + '33') : 'transparent', color: end ? '#fff' : (we ? C.muted : C.text), fontWeight: end ? 700 : 400, outline: _iso(d) === todayIso && !sel ? `1px solid ${accent}` : 'none' }}>
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: 12.5, color: C.muted }}>
        {von ? <>Gewählt: <strong style={{ color: C.text }}>{fmtDateDE(von)}{bis ? ` – ${fmtDateDE(bis)}` : ' (einzelner Tag – 2. Klick für Ende)'}</strong></> : 'Tippen Sie den ersten Tag an, dann den letzten.'}
        {von && <button type="button" onClick={() => onChange({ von: '', bis: '' })} style={{ ...S.crumbLink, marginLeft: 10 }}>zurücksetzen</button>}
      </div>
    </div>
  );
}

// Fehlzeit erfassen (Krankheit/eAU, Urlaub, Mutterschutz, … ) mit Kalender
function FehlzeitModal({ maList, effectiveMandant, onClose, onDone }) {
  const [typ, setTyp] = useState('krankheit');
  const [maId, setMaId] = useState('');
  const [range, setRange] = useState({ von: '', bis: '' });
  const [art, setArt] = useState('erst');
  const [notiz, setNotiz] = useState('');
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const cfg = FEHLZEIT_MAP[typ];
  async function submit(e) {
    e.preventDefault(); setErr('');
    if (!maId) { setErr('Bitte Mitarbeiter wählen'); return; }
    if (!range.von) { setErr('Bitte im Kalender einen Zeitraum wählen'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('mandant_id', effectiveMandant);
      fd.append('mitarbeiter_id', maId);
      fd.append('typ', typ);
      fd.append('von', range.von); if (range.bis) fd.append('bis', range.bis);
      if (typ === 'krankheit') fd.append('art', art);
      if (notiz.trim()) fd.append('notiz', notiz.trim());
      await api.post('/krankmeldungen', fd);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Erfassen fehlgeschlagen'); setBusy(false); }
  }
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <form style={{ ...S.modalCard, width: 620 }} onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2 style={S.cardTitle}>Fehlzeit erfassen</h2>
        <label style={S.label}>Art der Fehlzeit</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8, marginBottom: 12 }}>
          {FEHLZEIT_ARTEN.map(a => {
            const on = a.key === typ;
            return (
              <button type="button" key={a.key} onClick={() => setTyp(a.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                  border: `1.5px solid ${on ? a.color : C.border}`, background: on ? a.color + '18' : C.white, color: on ? a.color : C.text,
                  borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: on ? 700 : 500 }}>
                <span style={{ fontSize: 17 }}>{a.icon}</span> {a.label}
              </button>
            );
          })}
        </div>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Mitarbeiter</label>
            <select style={S.select} value={maId} onChange={e => setMaId(e.target.value)}>
              <option value="">— wählen —</option>
              {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
            </select>
          </div>
          {typ === 'krankheit' && (
            <div style={S.field}><label style={S.label}>Bescheinigung</label>
              <select style={S.select} value={art} onChange={e => setArt(e.target.value)}>
                <option value="erst">Erstbescheinigung</option>
                <option value="folge">Folgebescheinigung</option>
              </select>
            </div>
          )}
        </div>
        <label style={{ ...S.label, marginTop: 12 }}>Zeitraum ({cfg.label})</label>
        <RangeKalender von={range.von} bis={range.bis} onChange={setRange} accent={cfg.color} />
        <label style={{ ...S.label, marginTop: 12 }}>Notiz (optional)</label>
        <input style={S.input} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder={typ === 'mutterschutz' ? 'z.B. voraussichtlicher Entbindungstermin' : 'optional'} />
        {err && <div style={S.err}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="submit" style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy}>{busy ? 'Sendet…' : 'An das Lohnbüro senden'}</button>
          <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}

// Meldungen ans Lohnbüro: Austritt / Stammdaten / Entgelt / Arbeitszeit / Sonstiges
function MeldungModal({ kind, maList, effectiveMandant, onClose, onDone }) {
  const TITLES = { austritt: 'Austritt melden', stammdaten: 'Stammdatenänderung melden', entgelt: 'Entgelt / Einmalzahlung melden', arbeitszeit: 'Arbeitszeitänderung melden', sonstige: 'Sonstige Meldung ans Lohnbüro' };
  const needsMa = ['austritt', 'stammdaten', 'entgelt', 'arbeitszeit'].includes(kind);
  const [maId, setMaId] = useState('');
  const ma = maList.find(m => String(m.id) === String(maId));
  const [f, setF] = useState({});
  const [entgeltMode, setEntgeltMode] = useState('gehalt');
  const [file, setFile] = useState(null);
  const [gueltigAb, setGueltigAb] = useState(() => new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  useEffect(() => {
    if (!ma) { setF({}); return; }
    setF({
      nachname: ma.nachname || '', vorname: ma.vorname || '', email: ma.email || '', telefon: ma.telefon || '',
      anschrift: ma.anschrift || '', iban: ma.iban || '', steuerklasse: ma.steuerklasse || '', konfession: ma.konfession || '',
      kinderfreibetraege: ma.kinderfreibetraege || '', familienstand: ma.familienstand || '', krankenkasse: ma.krankenkasse || '',
      stundenlohn: ma.stundenlohn || '', monatslohn: ma.monatslohn || '', wochenstunden: ma.wochenstunden || '', austritt: ma.austritt || '',
    });
  }, [maId]);
  async function patchMa(fields, gab) {
    const fd = new FormData();
    fd.append('gueltig_ab', gab || gueltigAb || new Date().toISOString().slice(0, 10));
    let any = false;
    Object.entries(fields).forEach(([k, v]) => { if (v != null && String(v).trim() !== '') { fd.append(k, v); any = true; } });
    if (!any) throw new Error('Bitte mindestens ein Feld ändern/ausfüllen');
    await api.patch(`/mitarbeiter/${maId}`, fd);
  }
  async function postTicket(titel, beschreibung) {
    const fd = new FormData();
    fd.append('titel', titel); fd.append('mandant_id', effectiveMandant);
    if (beschreibung) fd.append('beschreibung', beschreibung);
    if (maId) fd.append('mitarbeiter_id', maId);
    await api.post('/tickets', fd);
  }
  async function submit(e) {
    e.preventDefault(); setErr('');
    if (needsMa && !maId) { setErr('Bitte Mitarbeiter wählen'); return; }
    setBusy(true);
    try {
      if (kind === 'austritt') {
        if (!f.austritt) throw new Error('Bitte Austrittsdatum angeben');
        await patchMa({ austritt: f.austritt }, f.austritt);
        if (f.bemerkung && f.bemerkung.trim()) await postTicket(`Austritt-Hinweis: ${ma.vorname} ${ma.nachname}`, f.bemerkung.trim());
      } else if (kind === 'stammdaten') {
        await patchMa({ nachname: f.nachname, vorname: f.vorname, email: f.email, telefon: f.telefon, anschrift: f.anschrift, iban: f.iban, steuerklasse: f.steuerklasse, konfession: f.konfession, kinderfreibetraege: f.kinderfreibetraege, familienstand: f.familienstand, krankenkasse: f.krankenkasse });
      } else if (kind === 'arbeitszeit') {
        if (!f.wochenstunden) throw new Error('Bitte neue Wochenstunden angeben');
        await patchMa({ wochenstunden: f.wochenstunden });
      } else if (kind === 'entgelt') {
        if (entgeltMode === 'gehalt') {
          if (!f.stundenlohn && !f.monatslohn) throw new Error('Bitte Stunden- oder Monatslohn angeben');
          await patchMa({ stundenlohn: f.stundenlohn, monatslohn: f.monatslohn });
        } else {
          if (!f.betrag) throw new Error('Bitte Betrag angeben');
          const bez = f.bezeichnung || 'Sonderzahlung';
          await postTicket(`Einmalzahlung: ${bez} – ${ma.vorname} ${ma.nachname}`, `${bez}: ${f.betrag} €${f.monat ? ` für ${f.monat}` : ''}${f.notiz ? ` — ${f.notiz}` : ''}`);
        }
      } else if (kind === 'sonstige') {
        if (!f.betreff || !f.betreff.trim()) throw new Error('Bitte Betreff angeben');
        await postTicket(f.betreff.trim(), f.text || '');
        if (file) {
          const ufd = new FormData(); ufd.append('files', file); ufd.append('mandant_id', effectiveMandant);
          ufd.append('direction', 'from_client'); ufd.append('doc_type', 'sonstige');
          await api.post('/documents/upload', ufd);
        }
      }
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Fehlgeschlagen'); setBusy(false); }
  }
  const showGueltig = ['stammdaten', 'arbeitszeit'].includes(kind) || (kind === 'entgelt' && entgeltMode === 'gehalt');
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <form style={S.modalCard} onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2 style={S.cardTitle}>{TITLES[kind]}</h2>
        <div style={S.pathInfo}>Ihre Meldung geht direkt an Ihr Lohnbüro und wird dort verarbeitet.</div>
        {needsMa && (
          <div style={S.field}><label style={S.label}>Mitarbeiter</label>
            <select style={S.select} value={maId} onChange={e => setMaId(e.target.value)}>
              <option value="">— wählen —</option>
              {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
            </select>
          </div>
        )}
        {kind === 'austritt' && (
          <>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Austrittsdatum (letzter Arbeitstag)</label><input type="date" style={S.input} value={f.austritt || ''} onChange={e => set('austritt', e.target.value)} /></div>
            </div>
            <label style={S.label}>Bemerkung (optional)</label>
            <input style={S.input} value={f.bemerkung || ''} onChange={e => set('bemerkung', e.target.value)} placeholder="z.B. Kündigung durch AN, Aufhebungsvertrag …" />
          </>
        )}
        {kind === 'stammdaten' && ma && (
          <>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Anschrift</label><input style={S.input} value={f.anschrift || ''} onChange={e => set('anschrift', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>IBAN</label><input style={S.input} value={f.iban || ''} onChange={e => set('iban', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>Telefon</label><input style={S.input} value={f.telefon || ''} onChange={e => set('telefon', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Steuerklasse</label><input style={S.input} value={f.steuerklasse || ''} onChange={e => set('steuerklasse', e.target.value)} placeholder="1–6" /></div>
              <div style={S.field}><label style={S.label}>Kinderfreibeträge</label><input style={S.input} value={f.kinderfreibetraege || ''} onChange={e => set('kinderfreibetraege', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Konfession</label><input style={S.input} value={f.konfession || ''} onChange={e => set('konfession', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>Familienstand</label><input style={S.input} value={f.familienstand || ''} onChange={e => set('familienstand', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={S.field}><label style={S.label}>Krankenkasse</label><input style={S.input} list="kk-list-meldung" value={f.krankenkasse || ''} onChange={e => set('krankenkasse', e.target.value)} /><datalist id="kk-list-meldung">{KRANKENKASSEN.map(k => <option key={k} value={k} />)}</datalist></div>
              <div style={S.field}><label style={S.label}>E-Mail</label><input style={S.input} value={f.email || ''} onChange={e => set('email', e.target.value)} /></div>
            </div>
            <div style={S.pathInfo}>Nur ausgefüllte Felder werden übernommen. Zum Leeren eines Feldes bitte im Mitarbeiter-Tab die Akte öffnen.</div>
          </>
        )}
        {kind === 'arbeitszeit' && (
          <div style={S.row}>
            <div style={S.field}><label style={S.label}>Neue Wochenstunden</label><input style={S.input} value={f.wochenstunden || ''} onChange={e => set('wochenstunden', e.target.value)} placeholder="z.B. 30" /></div>
          </div>
        )}
        {kind === 'entgelt' && (
          <>
            <div style={{ display: 'flex', gap: 16, margin: '4px 0 8px' }}>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="em" checked={entgeltMode === 'gehalt'} onChange={() => setEntgeltMode('gehalt')} /> Gehaltsänderung</label>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="radio" name="em" checked={entgeltMode === 'einmal'} onChange={() => setEntgeltMode('einmal')} /> Einmalzahlung</label>
            </div>
            {entgeltMode === 'gehalt' ? (
              <div style={S.row}>
                <div style={S.field}><label style={S.label}>Stundenlohn</label><input style={S.input} value={f.stundenlohn || ''} onChange={e => set('stundenlohn', e.target.value)} placeholder="z.B. 14,00" /></div>
                <div style={S.field}><label style={S.label}>Monatslohn</label><input style={S.input} value={f.monatslohn || ''} onChange={e => set('monatslohn', e.target.value)} placeholder="z.B. 2.400,00" /></div>
              </div>
            ) : (
              <>
                <div style={S.row}>
                  <div style={S.field}><label style={S.label}>Art</label>
                    <select style={S.select} value={f.bezeichnung || 'Bonus'} onChange={e => set('bezeichnung', e.target.value)}>
                      {['Bonus', 'Prämie', 'Sonderzahlung', 'Provision', 'Nachzahlung', 'Urlaubsgeld', 'Weihnachtsgeld'].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div style={S.field}><label style={S.label}>Betrag (€ brutto)</label><input style={S.input} value={f.betrag || ''} onChange={e => set('betrag', e.target.value)} placeholder="z.B. 500,00" /></div>
                </div>
                <div style={S.row}>
                  <div style={S.field}><label style={S.label}>Abrechnungsmonat</label><input type="month" style={S.input} value={f.monat || ''} onChange={e => set('monat', e.target.value)} /></div>
                </div>
                <label style={S.label}>Notiz (optional)</label>
                <input style={S.input} value={f.notiz || ''} onChange={e => set('notiz', e.target.value)} />
              </>
            )}
          </>
        )}
        {kind === 'sonstige' && (
          <>
            <label style={S.label}>Betreff</label>
            <input style={S.input} value={f.betreff || ''} onChange={e => set('betreff', e.target.value)} placeholder="z.B. Post von der Krankenkasse" />
            <label style={{ ...S.label, marginTop: 8 }}>Nachricht</label>
            <textarea style={S.textarea} value={f.text || ''} onChange={e => set('text', e.target.value)} />
            <label style={{ ...S.label, marginTop: 8 }}>Datei anhängen (optional)</label>
            <input type="file" style={S.input} onChange={e => setFile(e.target.files[0] || null)} />
          </>
        )}
        {showGueltig && (
          <div style={S.row}>
            <div style={S.field}><label style={S.label}>Gültig ab</label><input type="date" style={S.input} value={gueltigAb} onChange={e => setGueltigAb(e.target.value)} /></div>
          </div>
        )}
        {err && <div style={S.err}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="submit" style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy}>{busy ? 'Sendet…' : 'An das Lohnbüro senden'}</button>
          <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}

// Große Kachel fürs Mandanten-Dashboard
function DashKachel({ icon, title, sub, color, onClick, badge }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ position: 'relative', textAlign: 'left', cursor: 'pointer', background: C.white, border: `1px solid ${h ? color : C.border}`,
        borderRadius: 14, padding: '22px 22px 20px', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 158, fontFamily: 'inherit',
        boxShadow: h ? '0 12px 30px rgba(0,0,0,0.14)' : '0 2px 8px rgba(0,0,0,0.06)', transform: h ? 'translateY(-3px)' : 'none', transition: 'all .16s ease' }}>
      <div style={{ width: 54, height: 54, borderRadius: 15, background: color + '1f', color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 27 }}>{icon}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color: C.primary }}>{title}</div>
      <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.45 }}>{sub}</div>
      {badge > 0 && <span style={{ position: 'absolute', top: 16, right: 16, background: C.danger, color: '#fff', borderRadius: 999, fontSize: 12, fontWeight: 700, padding: '2px 9px' }}>{badge}</span>}
      <span style={{ marginTop: 'auto', color, fontSize: 13, fontWeight: 600 }}>Öffnen →</span>
    </button>
  );
}

// Mandanten-Startseite: 4 große Kacheln + wichtige Hinweise (keine Fristen/Tickets mehr)
function ClientHome({ user, onNav, onOpenMa }) {
  const [ungelesenChat] = useUngelesen(true);
  const [docs, setDocs] = useState([]);
  const [maList, setMaList] = useState([]);
  const load = useCallback(async () => {
    const [dRes, mRes] = await Promise.all([
      api.get('/documents').catch(() => ({ data: [] })),
      api.get('/mitarbeiter').catch(() => ({ data: [] })),
    ]);
    setDocs(dRes.data); setMaList(mRes.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const offeneFreigaben = docs.filter(d => istLohnDok(d) && !d.freigegeben).length;
  const pfbDocMaIds = new Set(docs.filter(d => d.doc_type === 'personalfragebogen' && d.mitarbeiter_id).map(d => d.mitarbeiter_id));
  const pendingPfb = maList.filter(m => m.aktiv && m.pfb_status == null && !pfbDocMaIds.has(m.id));
  async function markPfbExtern(m) {
    if (!window.confirm(`Personalfragebogen von ${m.vorname} ${m.nachname} als bereits vorhanden markieren?\n\nNur wählen, wenn der Fragebogen außerhalb des Portals vorliegt (z.B. Altsystem/Bestandsmitarbeiter). Es wird nichts hochgeladen.`)) return;
    const fd = new FormData(); fd.append('wert', 'true');
    try { await api.post(`/mitarbeiter/${m.id}/pfb-extern`, fd); await load(); } catch { /* ignore */ }
  }
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, color: C.primary, margin: '0 0 4px' }}>Willkommen zurück</h1>
        <div style={{ color: C.muted, fontSize: 14 }}>Was möchten Sie erledigen?</div>
      </div>
      {offeneFreigaben > 0 && (
        <div style={{ ...S.card, background: '#fff7e6', border: '1px solid #ffe1a8' }}>
          <div style={{ color: '#8a6d3b', fontSize: 14 }}>📄 Es {offeneFreigaben === 1 ? 'wartet' : 'warten'} <strong>{offeneFreigaben}</strong> Lohndokument{offeneFreigaben === 1 ? '' : 'e'} auf Ihre Freigabe. Öffnen Sie „Mitarbeiter" → Lohnabrechnung → „Freigeben". Damit Ihre Mitarbeiter ihre Dokumente selbst abrufen können, laden Sie sie unter „Mitarbeiter" → „Mitarbeiter-Zugänge" ein.</div>
        </div>
      )}
      {pendingPfb.length > 0 && (
        <div style={{ ...S.card, background: '#fdecea', border: `1px solid ${C.danger}` }}>
          <div style={{ color: C.danger, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>⚠ Personalfragebogen ausstehend</div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>Liegt der Fragebogen bereits vor (z.B. aus Ihrem bisherigen System), können Sie ihn ohne Upload als vorhanden markieren.</div>
          {pendingPfb.map(m => (
            <div key={m.id} style={{ ...S.folderItem, borderLeft: `3px solid ${C.danger}`, flexWrap: 'wrap', gap: 6 }}>
              <div style={{ flex: '1 1 160px', fontSize: 14 }}>{m.vorname} {m.nachname}</div>
              <button style={S.btnSmall} onClick={() => onOpenMa && onOpenMa(m.id)}>Jetzt erledigen →</button>
              <button style={{ ...S.btnSmall, background: C.muted }} onClick={() => markPfbExtern(m)}>Liegt bereits vor</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 20 }}>
        <DashKachel icon="✍️" title="Erfassen" sub="Neuer Mitarbeiter, Austritt, Fehlzeit & Meldungen ans Lohnbüro" color={C.accent} onClick={() => onNav('erfassen')} />
        <DashKachel icon="📊" title="Auswertungen" sub="Ihre monatlichen Lohnauswertungen & Dokumente" color="#2980b9" badge={offeneFreigaben} onClick={() => onNav('documents')} />
        <DashKachel icon="🧾" title="Rechnungen" sub="Rechnungen Ihrer Lohnkanzlei einsehen" color="#27ae60" onClick={() => onNav('rechnungen')} />
        <DashKachel icon="💬" title="Chat" sub="Direkt mit Ihrem Lohnbüro schreiben" color="#8e44ad" badge={ungelesenChat.gesamt} onClick={() => onNav('messages')} />
      </div>
    </div>
  );
}

// Erfassen-Hub: Aktionskacheln, die die passenden Erfassungs-Dialoge öffnen
function ErfassenHub({ user, onNeuerMa, onNav }) {
  const [maList, setMaList] = useState([]);
  const [modal, setModal] = useState(null); // 'fehlzeit' | 'austritt' | 'stammdaten' | 'entgelt' | 'arbeitszeit' | 'sonstige'
  const [ok, setOk] = useState('');
  const effectiveMandant = user.mandant_id ?? '';
  const load = useCallback(async () => {
    const r = await api.get('/mitarbeiter').catch(() => ({ data: [] }));
    setMaList(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = (msg) => { setModal(null); setOk(msg); load(); setTimeout(() => setOk(''), 5000); };
  const AKTIONEN = [
    { key: 'neuer_ma', icon: '➕', color: '#27ae60', title: 'Neuer Mitarbeiter', sub: 'Einstellung melden & Personalfragebogen' },
    { key: 'austritt', icon: '👋', color: '#c0392b', title: 'Austritt', sub: 'Beschäftigungsende melden' },
    { key: 'wiedereintritt', icon: '↩️', color: '#1f6f54', title: 'Wiedereintritt', sub: 'Ehemaliger Mitarbeiter kommt zurück' },
    { key: 'fehlzeit', icon: '📅', color: '#3a7ca5', title: 'Fehlzeit', sub: 'Krankheit, Urlaub, Mutterschutz, …' },
    { key: 'stammdaten', icon: '🏠', color: '#8e44ad', title: 'Stammdaten', sub: 'Adresse, IBAN, Steuerklasse …' },
    { key: 'entgelt', icon: '💶', color: '#16a085', title: 'Entgelt / Einmalzahlung', sub: 'Gehaltsänderung, Bonus, Prämie' },
    { key: 'arbeitszeit', icon: '⏱️', color: '#e67e22', title: 'Arbeitszeit', sub: 'Wochenstunden ändern' },
    { key: 'zeiten', icon: '🗓️', color: '#2980b9', title: 'Arbeitsschichten', sub: 'Geleistete Schichten/Stunden im Kalender erfassen' },
    { key: 'sonstige', icon: '✉️', color: '#34495e', title: 'Sonstige Meldung', sub: 'Freie Meldung + Dokument' },
    { key: 'ma_liste', icon: '📋', color: '#2c3e50', title: 'Mitarbeiterliste hochladen', sub: 'Viele Mitarbeiter auf einmal: CSV importieren oder Liste an uns senden' },
    { key: 'unterlagen', icon: '📁', color: '#7f8c8d', title: 'Unterlagen hochladen', sub: 'Bisherige Lohnunterlagen, Verträge, Bescheide' },
  ];
  function openAktion(key) {
    if (key === 'neuer_ma') { onNeuerMa(); return; }
    if (key === 'zeiten') { if (onNav) onNav('arbeitszeiten'); return; }
    setModal(key);
  }
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 23, color: C.primary, margin: '0 0 4px' }}>Erfassen</h1>
        <div style={{ color: C.muted, fontSize: 14 }}>Wählen Sie, was Sie an Ihr Lohnbüro melden möchten.</div>
      </div>
      {ok && <div style={{ ...S.card, background: '#e8f4ea', border: `1px solid ${C.success}`, color: C.success, fontSize: 14 }}>✓ {ok}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 18 }}>
        {AKTIONEN.map(a => <DashKachel key={a.key} icon={a.icon} title={a.title} sub={a.sub} color={a.color} onClick={() => openAktion(a.key)} />)}
      </div>
      {modal === 'wiedereintritt' && <WiedereintrittModal maList={maList} onClose={() => setModal(null)} onDone={() => done('Wiedereintritt an das Lohnbüro gemeldet.')} />}
      {modal === 'fehlzeit' && <FehlzeitModal maList={maList} effectiveMandant={effectiveMandant} onClose={() => setModal(null)} onDone={() => done('Fehlzeit an das Lohnbüro gesendet.')} />}
      {['austritt', 'stammdaten', 'entgelt', 'arbeitszeit', 'sonstige'].includes(modal) && (
        <MeldungModal kind={modal} maList={maList} effectiveMandant={effectiveMandant} onClose={() => setModal(null)} onDone={() => done('Meldung an das Lohnbüro gesendet.')} />
      )}
      {(modal === 'ma_liste' || modal === 'unterlagen') && (
        <div style={S.modalOverlay} onClick={() => setModal(null)}>
          <div style={{ ...S.modalCard, width: 780 }} onClick={e => e.stopPropagation()}>
            <h2 style={S.cardTitle}>{modal === 'ma_liste' ? 'Mitarbeiterliste hochladen' : 'Unterlagen hochladen'}</h2>
            {modal === 'ma_liste' ? <MaListeHochladen onDone={load} /> : <UnterlagenHochladen />}
            <div style={{ marginTop: 14 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setModal(null)}>Schließen</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mitarbeiterliste: selbst per CSV importieren ODER Excel/CSV an die Kanzlei schicken (wir übernehmen den Import)
function MaListeHochladen({ onDone }) {
  const [modus, setModus] = useState('');      // '' | 'selbst' | 'kanzlei'
  const [prev, setPrev] = useState(null);
  const [file, setFile] = useState(null);
  const [map, setMap] = useState({});
  const [pfb, setPfb] = useState(true);
  const [status, setStatus] = useState('Standard');
  const [res, setRes] = useState(null);
  const [kFiles, setKFiles] = useState([]);
  const [notiz, setNotiz] = useState('');
  const [gesendet, setGesendet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function vorlage() { setErr(''); try { await dlDatei('/mein-mitarbeiter-import/vorlage'); } catch { setErr('Vorlage konnte nicht geladen werden.'); } }
  async function vorschau(f) {
    setBusy(true); setErr(''); setRes(null);
    try { const fd = new FormData(); fd.append('file', f); const { data } = await api.post('/mein-mitarbeiter-import/preview', fd); setPrev(data); setMap(data.mapping || {}); setFile(f); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Die Datei konnte nicht gelesen werden.'); setPrev(null); }
    finally { setBusy(false); }
  }
  async function importieren() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('mapping', JSON.stringify(map));
      if (prev.delimiter) fd.append('delimiter', prev.delimiter);
      if (prev.encoding) fd.append('encoding', prev.encoding);
      fd.append('default_status', status); fd.append('pfb_vorhanden', pfb ? 'true' : 'false');
      const { data } = await api.post('/mein-mitarbeiter-import', fd);
      setRes(data); setPrev(null); setFile(null);
      if (onDone) onDone(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Import fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  async function senden() {
    if (!kFiles.length) { setErr('Bitte eine Excel- oder CSV-Datei auswählen.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); kFiles.forEach(f => fd.append('files', f)); if (notiz.trim()) fd.append('notiz', notiz.trim());
      const { data } = await api.post('/mein-mitarbeiter-liste/senden', fd);
      setGesendet(data.dateien || []); setKFiles([]); setNotiz('');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  const wahl = (k, icon, titel, text) => {
    const a = modus === k;
    return (
      <button type="button" onClick={() => { setModus(k); setErr(''); }} style={{ flex: '1 1 260px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', background: a ? '#eef4fb' : C.white, border: `1.5px solid ${a ? C.primary : C.border}`, borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>{icon} {titel}</div>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.45 }}>{text}</div>
      </button>
    );
  };
  const kannImport = !!map.vorname && !!map.nachname;
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {wahl('selbst', '📋', 'Selbst importieren (CSV)', 'Vorlage herunterladen, ausfüllen und hochladen – Ihre Mitarbeiter sind sofort im Portal angelegt.')}
        {wahl('kanzlei', '📨', 'Liste an die Kanzlei senden', 'Ihre vorhandene Liste als Excel oder CSV, in Ihrem eigenen Format – wir übernehmen den Import für Sie.')}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Nur wenige Mitarbeiter? Diese können Sie auch einzeln unter „Erfassen › Neuer Mitarbeiter“ anlegen.</div>

      {modus === 'selbst' && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={vorlage}>⬇ Vorlage herunterladen (CSV)</button>
            <span style={{ fontSize: 12, color: C.muted }}>Eine Zeile je Mitarbeiter. Pflicht sind nur Vor- und Nachname, alles Weitere ergänzen wir gemeinsam.</span>
          </div>
          <label style={S.label}>Ausgefüllte Liste</label>
          <input type="file" accept=".csv,.txt,.xlsx" style={S.input} onChange={e => { const f = e.target.files[0]; if (f) vorschau(f); e.target.value = ''; }} />
          {busy && !prev && <div style={S.empty}>Lese Datei…</div>}
          {prev && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13.5 }}><b>{prev.total}</b> Mitarbeiter in „{file?.name}“ erkannt{prev.doppelt ? <span style={{ color: '#8a6d3b' }}> · {prev.doppelt} davon sind bereits angelegt und werden übersprungen</span> : ''}.</div>
              <details style={{ marginTop: 8 }} open={!kannImport}>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: C.accent }}>Spaltenzuordnung prüfen ({Object.values(map).filter(Boolean).length} Spalten erkannt)</summary>
                <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 6, padding: 8, marginTop: 6 }}>
                  {LEX_FELDER.map(([fk, l]) => (
                    <div key={fk} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                      <div style={{ width: 170, fontSize: 13 }}>{l}{(fk === 'nachname' || fk === 'vorname') ? <span style={{ color: C.danger }}> *</span> : ''}</div>
                      <select style={{ ...S.select, flex: 1, padding: '6px 8px' }} value={map[fk] || ''} onChange={e => setMap(m => ({ ...m, [fk]: e.target.value }))}>
                        <option value="">— nicht übernehmen —</option>
                        {prev.columns.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </details>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
                <div style={{ minWidth: 220 }}><label style={S.label}>Beschäftigungsart (für alle)</label>
                  <select style={S.select} value={status} onChange={e => setStatus(e.target.value)}>{MA_STATUS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer', flex: '1 1 300px' }}>
                  <input type="checkbox" checked={pfb} onChange={e => setPfb(e.target.checked)} /> <span>Für diese Mitarbeiter liegen die Personalfragebögen bereits vor (bisherige Mitarbeiter)</span>
                </label>
              </div>
              {!kannImport && <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>Bitte die Spalten für Vor- und Nachname zuordnen.</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || !kannImport} onClick={importieren}>{busy ? 'Lege an…' : `${Math.max(0, prev.total - (prev.doppelt || 0))} Mitarbeiter anlegen`}</button>
                <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={() => { setPrev(null); setFile(null); }}>Andere Datei</button>
              </div>
            </div>
          )}
          {res && (
            <div style={{ marginTop: 10 }}>
              <div style={{ ...S.okMsg, fontSize: 14 }}>✓ {res.angelegt} Mitarbeiter angelegt. Ihr Lohnbüro ist informiert.</div>
              {res.fehler && res.fehler.length > 0 && <ul style={{ fontSize: 12, color: C.muted, maxHeight: 140, overflowY: 'auto' }}>{res.fehler.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            </div>
          )}
        </div>
      )}

      {modus === 'kanzlei' && (
        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Mitarbeiterliste (Excel oder CSV)</label>
          <input type="file" multiple accept=".xlsx,.xls,.csv,.ods,.txt" style={S.input} onChange={e => { setKFiles(Array.from(e.target.files || [])); setGesendet(null); }} />
          <label style={S.label}>Hinweis an uns (optional)</label>
          <textarea style={{ ...S.textarea, minHeight: 60 }} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z. B. Spalte „Gehalt“ ist der Monatslohn brutto" />
          <button style={{ ...S.btnPrimary, marginTop: 10 }} disabled={busy || !kFiles.length} onClick={senden}>{busy ? 'Sende…' : 'An die Kanzlei senden'}</button>
          {gesendet && <div style={{ ...S.okMsg, fontSize: 14 }}>✓ Danke! Wir haben {gesendet.join(', ')} erhalten und übernehmen den Import. Sie hören von uns, sobald alle Mitarbeiter angelegt sind.</div>}
        </div>
      )}
      {err && <div style={S.err}>{err}</div>}
    </div>
  );
}

// Bisherige Lohnunterlagen des Mandanten (nach Vertragsabschluss und jederzeit über „Erfassen“)
const UNTERLAGEN_VORSCHLAG = [
  'Lohnabrechnungen und Lohnjournal des letzten Monats', 'Lohnkonten des laufenden Jahres',
  'Beitragsnachweise und Lohnsteueranmeldungen der letzten Monate', 'Arbeitsverträge der Mitarbeiter',
  'Bescheid bzw. Mitgliedschaft der Berufsgenossenschaft', 'Besondere Unterlagen (z. B. Pfändungen, Schwerbehinderung, Immatrikulation)',
];
function UnterlagenHochladen() {
  const [liste, setListe] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => { try { const { data } = await api.get('/mein-onboarding/unterlagen'); setListe(data || []); } catch { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  async function hochladen() {
    if (!files.length) { setErr('Bitte Dateien auswählen.'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      const { data } = await api.post('/mein-onboarding/unterlagen', fd);
      setMsg(`${data.anzahl} Datei(en) hochgeladen – danke!`); setFiles([]);
      const inp = document.getElementById('onb-unterlagen'); if (inp) inp.value = '';
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Hochladen fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  return (
    <div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 6 }}>Hilfreich für den Start sind zum Beispiel:</div>
      <ul style={{ margin: '0 0 10px', paddingLeft: 20, fontSize: 13, color: C.text, lineHeight: 1.6 }}>{UNTERLAGEN_VORSCHLAG.map(u => <li key={u}>{u}</li>)}</ul>
      <input id="onb-unterlagen" type="file" multiple style={S.input} onChange={e => { setFiles(Array.from(e.target.files || [])); setMsg(''); }} />
      {files.length > 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{files.length} Datei(en) ausgewählt</div>}
      <button style={{ ...S.btnPrimary, marginTop: 10 }} disabled={busy || !files.length} onClick={hochladen}>{busy ? 'Lädt hoch…' : 'Hochladen'}</button>
      {msg && <div style={S.okMsg}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}
      {liste.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Bereits hochgeladen (Ordner „Unterlagen zur Einrichtung“):</div>
          {liste.map(d => <div key={d.id} style={{ fontSize: 13, padding: '3px 0' }}>📄 {d.original_name} <span style={{ color: C.muted, fontSize: 12 }}>· {fmtDateDE(d.created_at)}</span></div>)}
        </div>
      )}
    </div>
  );
}

const _fmtH = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} h`;
const _WD_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// Kalender-Tool zum Erfassen einzelner Arbeitsschichten (Datum + Stunden + optional Uhrzeit)
function SchichtKalenderModal({ mandantId, maList, initialMonat, onClose, onDone }) {
  const [maId, setMaId] = useState(maList[0] ? String(maList[0].id) : '');
  const [ym, setYm] = useState(() => { const p = (initialMonat || '').split('-'); return { y: +p[0] || new Date().getFullYear(), m: (+p[1] || 1) - 1 }; });
  const [shifts, setShifts] = useState([]);
  const [sel, setSel] = useState('');
  const [form, setForm] = useState({ stunden: '', von: '', bis: '', notiz: '' });
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const monatStr = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}`;
  const loadShifts = useCallback(async () => {
    if (!maId) { setShifts([]); return; }
    try { const { data } = await api.get('/arbeitsschichten', { params: { mandant_id: mandantId, mitarbeiter_id: maId, monat: monatStr } }); setShifts(data); }
    catch { setShifts([]); }
  }, [maId, mandantId, monatStr]);
  useEffect(() => { loadShifts(); }, [loadShifts]);
  const byDate = {}; shifts.forEach(s => { byDate[s.datum] = s; });
  const iso = (d) => `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const first = new Date(ym.y, ym.m, 1); const startWd = (first.getDay() + 6) % 7; const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = []; for (let i = 0; i < startWd; i++) cells.push(null); for (let d = 1; d <= days; d++) cells.push(d);
  function pick(d) { const i = iso(d); setSel(i); const ex = byDate[i]; setForm(ex ? { stunden: String(ex.stunden).replace('.', ','), von: ex.beginn || '', bis: ex.ende || '', notiz: ex.notiz || '' } : { stunden: '', von: '', bis: '', notiz: '' }); }
  const shiftM = (n) => { let m = ym.m + n, y = ym.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setYm({ y, m }); setSel(''); };
  async function add() {
    setErr('');
    if (!maId) { setErr('Bitte Mitarbeiter wählen'); return; }
    if (!sel) { setErr('Bitte einen Tag im Kalender antippen'); return; }
    if (!form.stunden) { setErr('Bitte Stunden angeben'); return; }
    setBusy(true);
    try {
      const ex = byDate[sel]; if (ex) await api.delete(`/arbeitsschichten/${ex.id}`);
      const fd = new FormData();
      fd.append('mandant_id', mandantId); fd.append('mitarbeiter_id', maId);
      fd.append('datum', sel); fd.append('stunden', form.stunden);
      if (form.von) fd.append('beginn', form.von); if (form.bis) fd.append('ende', form.bis);
      if (form.notiz) fd.append('notiz', form.notiz);
      await api.post('/arbeitsschichten', fd);
      setForm({ stunden: '', von: '', bis: '', notiz: '' }); setSel('');
      await loadShifts();
    } catch (ex2) { setErr(ex2.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  async function del(s) { try { await api.delete(`/arbeitsschichten/${s.id}`); await loadShifts(); } catch { /* */ } }
  const cellBtn = { border: 'none', background: C.bg, cursor: 'pointer', borderRadius: 6, fontFamily: 'inherit', width: 34, height: 30 };
  return (
    <div style={S.modalOverlay} onClick={() => { onClose(); if (onDone) onDone(); }}>
      <div style={{ ...S.modalCard, width: 640 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Arbeitsschichten erfassen</h2>
        <div style={S.field}><label style={S.label}>Mitarbeiter</label>
          <select style={S.select} value={maId} onChange={e => { setMaId(e.target.value); setSel(''); }}>
            <option value="">— wählen —</option>
            {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 6px' }}>
          <button type="button" onClick={() => shiftM(-1)} style={cellBtn}>‹</button>
          <div style={{ fontWeight: 600, color: C.primary }}>{MONATE_DE[ym.m]} {ym.y}</div>
          <button type="button" onClick={() => shiftM(1)} style={cellBtn}>›</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, textAlign: 'center' }}>
          {_WD_KURZ.map(w => <div key={w} style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{w}</div>)}
          {cells.map((d, i) => {
            if (d === null) return <div key={i} />;
            const i2 = iso(d); const ex = byDate[i2]; const isSel = sel === i2;
            const wd = new Date(ym.y, ym.m, d).getDay(); const we = (wd === 0 || wd === 6);
            return (
              <button key={i} type="button" onClick={() => pick(d)}
                style={{ border: `1px solid ${isSel ? C.primary : C.border}`, background: ex ? '#eaf1f8' : (isSel ? '#f0f6fc' : C.white), borderRadius: 6, padding: '3px 1px', cursor: 'pointer', fontFamily: 'inherit', minHeight: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: we ? C.muted : C.text }}>
                <span style={{ fontSize: 12 }}>{d}</span>
                {ex && <span style={{ fontSize: 10, fontWeight: 700, color: C.accent }}>{Number(ex.stunden).toFixed(1).replace('.', ',')}h</span>}
              </button>
            );
          })}
        </div>
        {sel && (
          <div style={{ marginTop: 12, background: C.bg, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{fmtDateDE(sel)}{byDate[sel] ? ' (vorhanden – wird überschrieben)' : ''}</div>
            <div style={S.row}>
              <div style={{ ...S.field, flex: '1 1 90px' }}><label style={S.label}>Stunden</label><input style={S.input} value={form.stunden} onChange={e => setForm({ ...form, stunden: e.target.value })} placeholder="z.B. 6" /></div>
              <div style={{ ...S.field, flex: '1 1 90px' }}><label style={S.label}>von (opt.)</label><input type="time" style={S.input} value={form.von} onChange={e => setForm({ ...form, von: e.target.value })} /></div>
              <div style={{ ...S.field, flex: '1 1 90px' }}><label style={S.label}>bis (opt.)</label><input type="time" style={S.input} value={form.bis} onChange={e => setForm({ ...form, bis: e.target.value })} /></div>
            </div>
            <label style={S.label}>Notiz (optional)</label>
            <input style={S.input} value={form.notiz} onChange={e => setForm({ ...form, notiz: e.target.value })} />
            <div style={S.pathInfo}>Uhrzeiten nur nötig, wenn Nachtzuschlag berechnet werden soll.</div>
            <button style={{ ...S.btnPrimary, marginTop: 4 }} disabled={busy} onClick={add}>{busy ? 'Speichert…' : 'Schicht speichern'}</button>
          </div>
        )}
        {err && <div style={S.err}>{err}</div>}
        {shifts.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.primary, marginBottom: 4 }}>Erfasste Schichten ({monatStr})</div>
            {shifts.map(s => (
              <div key={s.id} style={{ ...S.folderItem }}>
                <div style={{ flex: 1, fontSize: 13 }}>{fmtDateDE(s.datum)} · {_fmtH(s.stunden)}{s.beginn && s.ende ? ` · ${s.beginn}–${s.ende}` : ''}{s.notiz ? ` · ${s.notiz}` : ''}</div>
                <button style={S.fileRemove} title="Löschen" onClick={() => del(s)}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 14 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => { onClose(); if (onDone) onDone(); }}>Fertig</button></div>
      </div>
    </div>
  );
}

// Monats-Übersicht Arbeitszeiten + Zuschläge (Mandant via Erfassen, Büro als Tab)
function ArbeitszeitenView({ user, mandantId }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [monat, setMonat] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [maList, setMaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const effMandant = isStaff ? mandantId : (user.mandant_id ?? '');
  const ready = isStaff ? !!mandantId : true;
  const load = useCallback(async () => {
    if (!ready) { setData(null); return; }
    setLoading(true); setErr('');
    try {
      const params = { monat }; if (isStaff) params.mandant_id = mandantId;
      const [uRes, mRes] = await Promise.all([
        api.get('/arbeitszeiten-uebersicht', { params }),
        api.get('/mitarbeiter', { params: isStaff ? { mandant_id: mandantId } : {} }).catch(() => ({ data: [] })),
      ]);
      setData(uRes.data); setMaList(mRes.data.filter(m => m.aktiv));
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId, monat]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Arbeitszeiten & Zuschläge</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="month" style={{ ...S.input, width: 160 }} value={monat} onChange={e => setMonat(e.target.value)} />
            <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => setModalOpen(true)} disabled={!ready}>+ Schichten erfassen</button>
          </div>
        </div>
        {!ready ? <div style={S.hint}>Bitte oben links einen Mandanten wählen.</div>
          : loading ? <div style={S.empty}>Lade…</div>
            : err ? <div style={S.err}>{err}</div>
              : !data || data.mitarbeiter.length === 0 ? <div style={S.empty}>Für {monat} sind keine Arbeitszeiten erfasst. Über „+ Schichten erfassen" eintragen (oder individuelle Wochenarbeitszeit in der Mitarbeiter-Akte hinterlegen).</div>
                : (
                  <>
                    {!data.zuschlaege_aktiv && <div style={S.pathInfo}>Zuschläge sind für diesen Mandanten nicht aktiviert (unter „Einstellungen → Zuschläge"). Es werden nur die Stunden angezeigt.</div>}
                    {data.mitarbeiter.map(ma => {
                      const z = data.saetze; const zu = ma.zuschlag;
                      const hatZ = data.zuschlaege_aktiv && (zu.sonntag_std > 0 || zu.feiertag_std > 0 || zu.nacht_std > 0);
                      return (
                        <div key={ma.mitarbeiter_id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ fontWeight: 600, color: C.primary }}>{ma.name}</div>
                            <div style={{ fontSize: 14 }}>Summe Monat: <strong>{_fmtH(ma.summe_stunden)}</strong></div>
                          </div>
                          {ma.hat_projiziert && <div style={{ fontSize: 12, color: '#8a6d3b', background: '#fff7e6', border: '1px solid #ffe1a8', borderRadius: 6, padding: '4px 8px', marginTop: 6 }}>⚠ Enthält Tage, die automatisch aus dem Wochenplan projiziert wurden – bitte vor der Lexware-Übernahme prüfen.</div>}
                          {hatZ && (
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
                              {zu.sonntag_std > 0 && <span style={{ ...S.badge, background: '#eaf1f8' }}>Sonntag: {_fmtH(zu.sonntag_std)} × +{Number(z.sonntag)}%</span>}
                              {zu.feiertag_std > 0 && <span style={{ ...S.badge, background: '#fdecea', color: C.danger }}>Feiertag: {_fmtH(zu.feiertag_std)} × +{Number(z.feiertag)}%</span>}
                              {zu.nacht_std > 0 && <span style={{ ...S.badge, background: '#ede7f6', color: '#5e35b1' }}>Nacht ({z.nacht_start}–{z.nacht_ende}): {_fmtH(zu.nacht_std)} × +{Number(z.nacht)}%</span>}
                            </div>
                          )}
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, color: C.accent }}>Tage anzeigen ({ma.tage.length})</summary>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ ...S.table, marginTop: 6 }}>
                                <thead><tr><th style={S.th}>Tag</th><th style={S.th}>Std</th><th style={S.th}>Zeit</th><th style={S.th}>Merkmale</th><th style={S.th}>Quelle</th></tr></thead>
                                <tbody>{ma.tage.map(t => (
                                  <tr key={t.datum} style={{ background: t.quelle === 'projiziert' ? '#fffdf5' : 'transparent' }}>
                                    <td style={S.td}>{fmtDateDE(t.datum)} {_WD_KURZ[t.wochentag]}</td>
                                    <td style={S.td}>{_fmtH(t.stunden)}</td>
                                    <td style={S.td}>{t.beginn && t.ende ? `${t.beginn}–${t.ende}` : '—'}</td>
                                    <td style={S.td}>{[t.sonntag ? 'So' : null, t.feiertag ? 'Feiertag' : null, t.nacht_stunden > 0 ? `Nacht ${_fmtH(t.nacht_stunden)}` : null].filter(Boolean).join(' · ') || '—'}</td>
                                    <td style={S.td}>{t.quelle === 'projiziert' ? <span style={{ color: '#8a6d3b' }}>projiziert</span> : 'erfasst'}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            </div>
                          </details>
                        </div>
                      );
                    })}
                  </>
                )}
      </div>
      {modalOpen && <SchichtKalenderModal mandantId={effMandant} maList={maList} initialMonat={monat} onClose={() => setModalOpen(false)} onDone={load} />}
    </div>
  );
}

// Büro-Arbeitsliste: Fehlzeiten aller (zugeordneten) Mandanten abarbeiten
function FehlzeitenBuero({ user, mandanten, mandantId, onOpenMa }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('offen'); // offen | erledigt | alle
  const [mFilter, setMFilter] = useState(mandantId ? String(mandantId) : 'alle');
  const [typFilter, setTypFilter] = useState('alle');
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { const { data } = await api.get('/krankmeldungen'); setRows(data); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  async function toggle(k) {
    try { const fd = new FormData(); fd.append('erledigt', k.erledigt ? 'false' : 'true'); await api.patch(`/krankmeldungen/${k.id}`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }
  async function del(k) {
    if (!window.confirm('Fehlzeit wirklich löschen?')) return;
    try { await api.delete(`/krankmeldungen/${k.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }
  const mName = (id) => mandanten.find(m => m.id === id)?.name || `#${id}`;
  const usedMandanten = [...new Set(rows.map(k => k.mandant_id))];
  const filtered = rows
    .filter(k => status === 'alle' || (status === 'offen' ? !k.erledigt : k.erledigt))
    .filter(k => mFilter === 'alle' || String(k.mandant_id) === mFilter)
    .filter(k => typFilter === 'alle' || (k.typ || 'krankheit') === typFilter)
    .sort((a, b) => ((a.erledigt ? 1 : 0) - (b.erledigt ? 1 : 0)) || String(b.von || '').localeCompare(String(a.von || '')));
  const offenCount = rows.filter(k => !k.erledigt).length;
  return (
    <div>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Fehlzeiten bearbeiten <span style={{ fontSize: 13, color: C.muted, fontWeight: 400 }}>({offenCount} offen)</span></h2>
          <button style={S.btnSmall} onClick={load}>↻ Aktualisieren</button>
        </div>
        <div style={S.pathInfo}>Von Mandanten gemeldete Fehlzeiten. „Erledigt" = eAU abgerufen / in der Lohnabrechnung verarbeitet.</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
            {[['offen', 'Offen'], ['erledigt', 'Erledigt'], ['alle', 'Alle']].map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: '8px 12px', background: status === k ? C.primary : C.white, color: status === k ? C.white : C.text }}>{l}</button>
            ))}
          </div>
          <select style={{ ...S.select, width: 'auto', flex: '0 1 220px' }} value={mFilter} onChange={e => setMFilter(e.target.value)}>
            <option value="alle">Alle Mandanten</option>
            {mandanten.filter(m => !m.ist_eigenes_lohnbuero).map(m => <option key={m.id} value={String(m.id)}>{m.name}{m.ist_dummy ? ' (Test)' : ''}</option>)}
          </select>
          <select style={{ ...S.select, width: 'auto', flex: '0 1 200px' }} value={typFilter} onChange={e => setTypFilter(e.target.value)}>
            <option value="alle">Alle Arten</option>
            {FEHLZEIT_ARTEN.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </div>
        {err && <div style={S.err}>{err}</div>}
        {loading ? <div style={S.empty}>Lade…</div> : filtered.length === 0 ? <div style={S.empty}>{status === 'offen' ? 'Keine offenen Fehlzeiten – alles abgearbeitet 🎉' : 'Keine Einträge'}</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Mandant</th><th style={S.th}>Mitarbeiter</th><th style={S.th}>Art</th>
                <th style={S.th}>Zeitraum</th><th style={S.th}>Werktage</th><th style={S.th}>Entgeltf. (h)</th><th style={S.th}>Erfasst</th><th style={S.th}>Status</th><th style={S.th}>Aktion</th>
              </tr></thead>
              <tbody>
                {filtered.map(k => { const cfg = FEHLZEIT_MAP[k.typ || 'krankheit']; return (
                  <tr key={k.id} style={{ opacity: k.erledigt ? 0.6 : 1 }}>
                    <td style={S.td}>{mName(k.mandant_id)}</td>
                    <td style={S.td}>{k.mitarbeiter_id ? <span style={{ cursor: 'pointer', color: C.accent }} onClick={() => onOpenMa && onOpenMa(k.mitarbeiter_id)}>{k.ma_name || `MA #${k.mitarbeiter_id}`}</span> : '—'}</td>
                    <td style={S.td}><span style={{ ...S.badge, background: (cfg?.color || C.accent) + '22', color: cfg?.color || C.accent }}>{cfg ? `${cfg.icon} ${cfg.label}` : (k.typ_label || 'Krankheit')}</span>{(k.typ === 'krankheit' || !k.typ) && k.art === 'folge' ? <span style={{ ...S.badge, marginLeft: 4 }}>Folge</span> : null}{k.notiz ? <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{k.notiz}</div> : null}</td>
                    <td style={S.td}>{fmtDateDE(k.von)}{k.bis ? ` – ${fmtDateDE(k.bis)}` : ''}</td>
                    <td style={S.td}>{k.werktage != null ? k.werktage : '—'}</td>
                    <td style={S.td}>{k.entgelt_stunden != null ? `${String(k.entgelt_stunden).replace('.', ',')} h` : '—'}</td>
                    <td style={S.td}>{fmtDateDE(k.created_at)}</td>
                    <td style={S.td}>{k.erledigt ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>erledigt</span> : <span style={{ ...S.badge, background: '#fdeccd', color: '#8a6d3b' }}>offen</span>}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      <button style={{ ...S.btnSmall, background: k.erledigt ? C.muted : C.success }} onClick={() => toggle(k)}>{k.erledigt ? 'Wieder öffnen' : 'Erledigt'}</button>
                      <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => del(k)}>✕</button>
                    </td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard({ user, mandanten, mandantId, onOpenTicket, onGoMandant, onOpenMa, onMeinBueroChange }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [tickets, setTickets] = useState([]);
  const [fristen, setFristen] = useState([]);
  const [mandantDocs, setMandantDocs] = useState([]);
  const [offeneFreigaben, setOffeneFreigaben] = useState(0);
  const [pfbDocMaIds, setPfbDocMaIds] = useState(new Set());
  const [erfModal, setErfModal] = useState(null); // 'eau' | 'dok' | null
  const [maList, setMaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [files, setFiles] = useState([]);
  const [ticketTitel, setTicketTitel] = useState('');
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [krank, setKrank] = useState([]);
  const [km, setKm] = useState({ mitarbeiter_id: '', von: '', bis: '', art: 'erst', notiz: '' });

  const ready = isStaff ? !!mandantId : true;
  const effectiveMandant = isStaff ? mandantId : (user.mandant_id ?? '');

  const load = useCallback(async () => {
    if (!ready) { setTickets([]); setFristen([]); setMandantDocs([]); setMaList([]); setKrank([]); return; }
    setLoading(true); setErr('');
    try {
      const tparams = { status: 'offen' };
      const fparams = {}; const dparams = {}; const kparams = {};
      if (isStaff) { tparams.mandant_id = mandantId; fparams.mandant_id = mandantId; dparams.mandant_id = mandantId; kparams.mandant_id = mandantId; }
      const [tRes, fRes, dRes, maRes, kRes] = await Promise.all([
        api.get('/tickets', { params: tparams }),
        api.get('/fristen', { params: fparams }),
        api.get('/documents', { params: dparams }),
        api.get('/mitarbeiter', { params: isStaff ? { mandant_id: mandantId } : {} }).catch(() => ({ data: [] })),
        api.get('/krankmeldungen', { params: kparams }).catch(() => ({ data: [] })),
      ]);
      setTickets(tRes.data);
      setFristen(fRes.data);
      setMandantDocs(dRes.data.filter(d => d.label === 'Mandant'));
      setOffeneFreigaben(dRes.data.filter(d => istLohnDok(d) && !d.freigegeben).length);
      setPfbDocMaIds(new Set(dRes.data.filter(d => d.doc_type === 'personalfragebogen' && d.mitarbeiter_id).map(d => d.mitarbeiter_id)));
      setMaList(maRes.data);
      setKrank(kRes.data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId]);
  useEffect(() => { load(); }, [load]);

  async function addKrank() {
    if (!km.von) { setErr('Bitte Beginn der Arbeitsunfähigkeit angeben'); return; }
    try {
      const fd = new FormData();
      fd.append('mandant_id', effectiveMandant);
      if (km.mitarbeiter_id) fd.append('mitarbeiter_id', km.mitarbeiter_id);
      fd.append('von', km.von); if (km.bis) fd.append('bis', km.bis);
      fd.append('art', km.art); if (km.notiz) fd.append('notiz', km.notiz);
      await api.post('/krankmeldungen', fd);
      setKm({ mitarbeiter_id: '', von: '', bis: '', art: 'erst', notiz: '' });
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Krankmeldung fehlgeschlagen'); }
  }
  async function krankErledigt(k) {
    try { const fd = new FormData(); fd.append('erledigt', k.erledigt ? 'false' : 'true'); await api.patch(`/krankmeldungen/${k.id}`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }
  async function krankLoeschen(k) {
    if (!window.confirm('Krankmeldung löschen?')) return;
    try { await api.delete(`/krankmeldungen/${k.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }
  async function markPfbExtern(m) {
    if (!window.confirm(`Personalfragebogen von ${m.vorname} ${m.nachname} als bereits vorhanden markieren? (kein Upload nötig, z.B. Bestandsmitarbeiter)`)) return;
    try { const fd = new FormData(); fd.append('wert', 'true'); await api.post(`/mitarbeiter/${m.id}/pfb-extern`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  const maName = (id) => { const m = maList.find(x => x.id === id); return m ? `${m.vorname} ${m.nachname}` : (id ? `MA #${id}` : ''); };
  const myTickets = tickets.filter(t => t.empfaenger === (isStaff ? 'lohnbuero' : 'mandant'));
  const maTickets = tickets.filter(t => t.mitarbeiter_id);
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const upcomingExits = maList.filter(m => {
    if (!m.austritt) return false;
    const d = new Date(m.austritt); const diff = (d - today0) / 86400000;
    return diff >= 0 && diff <= 28;
  });
  const upcomingEntries = maList.filter(m => {
    if (!m.eintritt) return false;
    const d = new Date(m.eintritt); const diff = (d - today0) / 86400000;
    return diff >= 0 && diff <= 28;
  });
  // MA ohne Personalfragebogen (weder hochgeladen noch digital angefordert/eingereicht)
  const pendingPfb = maList.filter(m => m.aktiv && m.pfb_status == null && !pfbDocMaIds.has(m.id));

  async function download(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = d.original_name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('Download fehlgeschlagen'); }
  }

  async function uploadMandant() {
    if (!files.length) { setErr('Bitte mindestens eine Datei wählen'); return; }
    setBusy(true); setErr(''); setOkMsg('');
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      fd.append('mandant_id', effectiveMandant);
      fd.append('label', 'Mandant');
      fd.append('doc_type', 'mandant');
      fd.append('direction', isStaff ? 'to_client' : 'from_client');
      await api.post('/documents/upload', fd);
      if (ticketTitel.trim()) {
        const tf = new FormData();
        tf.append('titel', ticketTitel);
        tf.append('mandant_id', effectiveMandant);
        tf.append('beschreibung', 'Dokument(e): ' + files.map(f => f.name).join(', '));
        await api.post('/tickets', tf);
      }
      setFiles([]); setTicketTitel('');
      const inp = document.getElementById('mandant-doc-input'); if (inp) inp.value = '';
      setOkMsg('Hochgeladen' + (ticketTitel.trim() ? ' inkl. Ticket ans Lohnbüro.' : '.'));
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  async function fristErledigt(f) {
    if (f.art !== 'manuell' || !f.id) return;
    try { const fd = new FormData(); fd.append('erledigt', 'true'); await api.patch(`/fristen/${f.id}`, fd); await load(); }
    catch { setErr('Aktion fehlgeschlagen'); }
  }

  const TicketList = ({ items, empty }) => (
    items.length === 0 ? <div style={S.empty}>{empty}</div> : items.map(t => (
      <div key={t.id} style={S.msg}>
        <div style={S.msgMeta}>{fmtDateDE(t.created_at)} · von {t.erstellt_von_seite === 'mandant' ? 'Mandant' : 'Lohnbüro'}{t.quelle === 'ma_aenderung' ? ' · auto' : ''}</div>
        <div style={S.msgBody}>{t.titel}</div>
        <div style={{ marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ ...S.badge, cursor: 'pointer' }} onClick={() => onOpenTicket && onOpenTicket(t.id)}>🎫 Ticket öffnen</span>
          {t.mitarbeiter_id && <span style={{ ...S.badge, cursor: 'pointer', background: '#e8eef5' }} onClick={() => onOpenMa && onOpenMa(t.mitarbeiter_id)}>👤 {maName(t.mitarbeiter_id)} – Akte</span>}
        </div>
      </div>
    ))
  );

  return (
    <div>
      {!ready ? (
        isStaff
          ? <Cockpit onGoMandant={onGoMandant} isAdmin={user.role === 'admin'} onChanged={onMeinBueroChange} />
          : <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen, um das Dashboard anzuzeigen.</div></div>
      ) : (
        <>
          {err && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{err}</div>}
          {offeneFreigaben > 0 && (
            <div style={{ ...S.card, background: '#fff7e6', border: '1px solid #ffe1a8' }}>
              <div style={{ color: '#8a6d3b', fontSize: 14 }}>
                📄 Es {offeneFreigaben === 1 ? 'wartet' : 'warten'} <strong>{offeneFreigaben}</strong> Lohndokument{offeneFreigaben === 1 ? '' : 'e'} auf Ihre Freigabe für Mitarbeiter. {isStaff ? '' : 'Öffnen Sie den Mitarbeiter (Tab „Mitarbeiter" → „Lohnabr.") und klicken Sie auf „Freigeben".'}
              </div>
            </div>
          )}
          {pendingPfb.length > 0 && (
            <div style={{ ...S.card, background: '#fdecea', border: `1px solid ${C.danger}` }}>
              <div style={{ color: C.danger, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>⚠ Personalfragebogen ausstehend</div>
              <div style={{ fontSize: 13, marginBottom: 8 }}>Für folgende Mitarbeiter wurde noch kein Personalfragebogen hochgeladen oder angefordert. Bitte E-Mail erfassen und den Link senden:</div>
              {pendingPfb.map(m => (
                <div key={m.id} style={{ ...S.folderItem, borderLeft: `3px solid ${C.danger}`, flexWrap: 'wrap', gap: 6 }}>
                  <div style={{ flex: '1 1 160px', fontSize: 14 }}>{m.vorname} {m.nachname}{m.email ? '' : <span style={{ color: C.danger, fontSize: 12 }}> · keine E-Mail hinterlegt</span>}</div>
                  <button style={S.btnSmall} onClick={() => onOpenMa && onOpenMa(m.id)}>Jetzt erledigen →</button>
                  <button style={{ ...S.btnSmall, background: C.muted }} onClick={() => markPfbExtern(m)}>Liegt bereits vor</button>
                </div>
              ))}
            </div>
          )}

          <div style={S.card}>
            <h2 style={S.cardTitle}>Erfassen</h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => setErfModal('eau')}>🤒 eAU / Krankmeldung</button>
              <button style={{ ...S.btnPrimary, marginTop: 0, background: C.accent }} onClick={() => setErfModal('dok')}>📎 Dokument {isStaff ? 'für Mandant' : 'ans Lohnbüro'}</button>
            </div>
            <div style={S.pathInfo}>Schnellerfassung. Weitere Erfassungs-Funktionen folgen hier.</div>
          </div>

          {isStaff && <MandantBenachrichtigen mandantId={mandantId} />}

          <div style={S.card}>
            <h2 style={S.cardTitle}>{isStaff ? 'Offene Tickets (Lohnbüro)' : 'Offene Tickets vom Lohnbüro'}</h2>
            {loading ? <div style={S.empty}>Lade…</div> : <TicketList items={myTickets} empty="Keine offenen Tickets" />}
          </div>

          <div style={S.card}>
            <h2 style={S.cardTitle}>Fristen & Erinnerungen</h2>
            {fristen.length === 0 ? <div style={S.empty}>Keine offenen Fristen</div> : fristen.map((f, i) => (
              <div key={i} style={{ ...S.folderItem, borderLeft: `3px solid ${f.ueberfaellig ? C.danger : '#e0a800'}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>{f.titel}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>fällig: {fmtDateDE(f.faellig_am)} {f.ueberfaellig ? '· überfällig' : ''} · {({ immatrikulation: 'Immatrikulation', eintritt: 'Eintritt', austritt: 'Austritt' }[f.art]) || 'Erinnerung'}</div>
                </div>
                {f.mitarbeiter_id && <button style={{ ...S.btnSmall, marginRight: 6 }} onClick={() => onOpenMa && onOpenMa(f.mitarbeiter_id)}>👤 Akte</button>}
                {f.art === 'manuell' && <button style={S.btnSmall} onClick={() => fristErledigt(f)}>Erledigt</button>}
              </div>
            ))}
          </div>

          <div style={S.card}>
            <h2 style={S.cardTitle}>MA-bezogene Tickets</h2>
            <TicketList items={maTickets} empty="Keine MA-bezogenen offenen Tickets" />
          </div>

          <div style={S.card}>
            <h2 style={S.cardTitle}>Fehlzeiten</h2>
            {krank.length === 0 ? <div style={S.empty}>Keine Fehlzeiten erfasst.</div> : (
              <table style={{ ...S.table, marginTop: 12 }}>
                <thead><tr><th style={S.th}>Mitarbeiter</th><th style={S.th}>Zeitraum</th><th style={S.th}>Art</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
                <tbody>{krank.map(k => (
                  <tr key={k.id} style={{ opacity: k.erledigt ? 0.6 : 1 }}>
                    <td style={S.td}>{k.ma_name || (k.mitarbeiter_id ? maName(k.mitarbeiter_id) : '—')}</td>
                    <td style={S.td}>{fmtDateDE(k.von)}{k.bis ? ` – ${fmtDateDE(k.bis)}` : ''}</td>
                    <td style={S.td}><span style={S.badge}>{k.typ_label || 'Krankheit'}</span>{(k.typ === 'krankheit' || !k.typ) && k.art === 'folge' ? <span style={{ ...S.badge, marginLeft: 4, background: '#f0e6f5' }}>Folge</span> : null}</td>
                    <td style={S.td}>{k.erledigt
                      ? <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>erledigt</span>
                      : <span style={{ ...S.badge, background: '#fdeccd', color: '#8a6d3b' }}>offen</span>}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      {k.mitarbeiter_id && <button style={{ ...S.btnSmall, marginRight: 6 }} onClick={() => onOpenMa && onOpenMa(k.mitarbeiter_id)}>👤 Akte</button>}
                      <button style={{ ...S.btnSmall, background: k.erledigt ? C.muted : C.success }} onClick={() => krankErledigt(k)}>{k.erledigt ? 'Wieder öffnen' : 'Erledigt'}</button>
                      {isStaff && <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => krankLoeschen(k)}>✕</button>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ ...S.card, flex: '1 1 320px', minWidth: 280 }}>
              <h2 style={S.cardTitle}>Kommende Eintritte (nächste 4 Wochen)</h2>
              {upcomingEntries.length === 0 ? <div style={S.empty}>Keine</div> : upcomingEntries.map(m => (
                <div key={m.id} style={S.folderItem}>
                  <div style={{ flex: 1, fontSize: 14 }}><span style={{ cursor: 'pointer', color: C.accent }} onClick={() => onOpenMa && onOpenMa(m.id)}>{m.vorname} {m.nachname}</span> <span style={{ color: C.muted, fontSize: 12 }}>· Status {m.status}</span></div>
                  <div style={{ fontSize: 12, color: C.muted }}>Eintritt am {fmtDateDE(m.eintritt)}</div>
                </div>
              ))}
            </div>
            <div style={{ ...S.card, flex: '1 1 320px', minWidth: 280 }}>
              <h2 style={S.cardTitle}>Kommende Austritte (nächste 4 Wochen)</h2>
              {upcomingExits.length === 0 ? <div style={S.empty}>Keine</div> : upcomingExits.map(m => (
                <div key={m.id} style={S.folderItem}>
                  <div style={{ flex: 1, fontSize: 14 }}><span style={{ cursor: 'pointer', color: C.accent }} onClick={() => onOpenMa && onOpenMa(m.id)}>{m.vorname} {m.nachname}</span> <span style={{ color: C.muted, fontSize: 12 }}>· Status {m.status}</span></div>
                  <div style={{ fontSize: 12, color: C.muted }}>Austritt am {fmtDateDE(m.austritt)}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <h2 style={S.cardTitle}>{isStaff ? 'Mandant-Dokumente' : 'Post / Dokumente'}</h2>
            {mandantDocs.length === 0 ? <div style={S.empty}>Keine Mandant-Dokumente</div> : (
              <table style={S.table}>
                <thead><tr><th style={S.th}>Datei</th><th style={S.th}>Richtung</th><th style={S.th}>Datum</th><th style={S.th}></th></tr></thead>
                <tbody>{mandantDocs.map(d => (
                  <tr key={d.id}>
                    <td style={S.td}>{d.original_name}</td>
                    <td style={S.td}><span style={S.dirTag(d.direction)}>{d.direction === 'from_client' ? '← vom Mandant' : '→ an Mandant'}</span></td>
                    <td style={S.td}>{fmtDate(d.created_at)}</td>
                    <td style={S.td}><button style={S.btnSmall} onClick={() => download(d)}>Download</button></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>

          {erfModal === 'eau' && (
            <div style={S.modalOverlay} onClick={() => setErfModal(null)}>
              <form style={S.modalCard} onClick={e => e.stopPropagation()} onSubmit={async e => { e.preventDefault(); await addKrank(); if (!err) setErfModal(null); }}>
                <h2 style={S.cardTitle}>eAU / Krankmeldung erfassen</h2>
                <div style={S.row}>
                  <div style={S.field}><label style={S.label}>Mitarbeiter</label>
                    <select style={S.select} value={km.mitarbeiter_id} onChange={e => setKm({ ...km, mitarbeiter_id: e.target.value })}>
                      <option value="">— wählen —</option>
                      {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
                    </select>
                  </div>
                  <div style={S.field}><label style={S.label}>Art</label>
                    <select style={S.select} value={km.art} onChange={e => setKm({ ...km, art: e.target.value })}>
                      <option value="erst">Erstbescheinigung</option>
                      <option value="folge">Folgebescheinigung</option>
                    </select>
                  </div>
                </div>
                <div style={S.row}>
                  <div style={S.field}><label style={S.label}>AU von</label><input type="date" style={S.input} value={km.von} onChange={e => setKm({ ...km, von: e.target.value })} /></div>
                  <div style={S.field}><label style={S.label}>AU bis (optional)</label><input type="date" style={S.input} value={km.bis} onChange={e => setKm({ ...km, bis: e.target.value })} /></div>
                </div>
                <label style={S.label}>Notiz (optional)</label>
                <input style={S.input} value={km.notiz} onChange={e => setKm({ ...km, notiz: e.target.value })} />
                {err && <div style={S.err}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button type="submit" style={{ ...S.btnPrimary, marginTop: 0 }}>Erfassen</button>
                  <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setErfModal(null)}>Abbrechen</button>
                </div>
              </form>
            </div>
          )}

          {erfModal === 'dok' && (
            <div style={S.modalOverlay} onClick={() => setErfModal(null)}>
              <div style={S.modalCard} onClick={e => e.stopPropagation()}>
                <h2 style={S.cardTitle}>Dokument {isStaff ? 'an Mandant' : 'ans Lohnbüro'} hochladen</h2>
                <input id="mandant-doc-input" type="file" multiple style={S.input} onChange={e => setFiles(Array.from(e.target.files || []))} />
                <label style={S.label}>Ticket dazu (optional) — z.B. „Post von Krankenkasse"</label>
                <input style={S.input} value={ticketTitel} onChange={e => setTicketTitel(e.target.value)} placeholder="Ticket-Titel" />
                {okMsg && <div style={S.okMsg}>{okMsg}</div>}
                {err && <div style={S.err}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || !files.length} onClick={async () => { await uploadMandant(); if (!err) setErfModal(null); }}>{busy ? 'Lädt…' : 'Hochladen' + (files.length ? ` (${files.length})` : '')}</button>
                  <button type="button" style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setErfModal(null)}>Schließen</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const BUCKET_META = {
  nicht_faellig: { label: 'Nicht fällig', col: C.success },
  t1_30: { label: '1–30 Tage überf.', col: '#d9a400' },
  t31_60: { label: '31–60 Tage überf.', col: '#e07b00' },
  t60plus: { label: '> 60 Tage überf.', col: C.danger },
};

function Debitoren({ user, only }) {
  const [opos, setOpos] = useState(null);
  const [txns, setTxns] = useState([]);
  const tabB = useTabelle(txns);
  const [direktTxn, setDirektTxn] = useState(null);
  const [sammelTxn, setSammelTxn] = useState(null);
  const [konten, setKonten] = useState({ aufwand: [], gegen: [], erloes: [] });
  useEffect(() => { api.get('/konten').then(({ data }) => setKonten(data)).catch(() => {}); }, []);
  const [filter, setFilter] = useState('offen');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [mandanten, setMandanten] = useState([]);
  const [buchenTxn, setBuchenTxn] = useState(null);   // Bankbuchung manuell zuordnen
  const [kontoMid, setKontoMid] = useState(null);     // Debitorenkonto-Detail
  const [showImp, setShowImp] = useState(false);      // bestehende Ausgangsrechnung aus der Buchführung heraus hochladen
  const fileRef = useRef();

  const loadOpos = useCallback(async () => {
    try { const { data } = await api.get('/debitoren/opos'); setOpos(data); } catch { /* */ }
  }, []);
  const loadTxns = useCallback(async () => {
    try { const { data } = await api.get('/bank/transaktionen', { params: { status: filter || undefined } }); setTxns(data); } catch { /* */ }
  }, [filter]);
  useEffect(() => { loadOpos(); }, [loadOpos]);
  useEffect(() => { loadTxns(); }, [loadTxns]);
  useEffect(() => { api.get('/mandanten', { params: { alle: 1 } }).then(({ data }) => setMandanten(data.filter(m => !m.ist_eigenes_lohnbuero))).catch(() => {}); }, []);

  async function reloadAll() { await loadTxns(); await loadOpos(); }
  async function doImport(e) {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/bank/import', fd);
      setMsg(`${data.imported} Umsatz/Umsätze importiert · ${data.duplicates} waren schon vorhanden${data.vor_beginn ? ` · ${data.vor_beginn} vor dem Buchführungsbeginn (Ansicht „Altsystem“)` : ''} · Format ${String(data.quelle).toUpperCase()}.`);
      await reloadAll();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Import fehlgeschlagen.'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }
  async function zuordnenVorschlag(t, v) {
    const diff = Math.round(((v.offen_rest ?? v.brutto) - t.betrag) * 100) / 100;
    let hinweis = 'Die Rechnung wird als bezahlt markiert.';
    if (diff > 0.005) hinweis = `Teilzahlung – es bleiben ${fmtEur(diff)} offen.`;
    else if (diff < -0.005) hinweis = `Überzahlung – ${fmtEur(-diff)} werden dem Debitorenkonto gutgeschrieben.`;
    if (!window.confirm(`Zahlung über ${fmtEur(t.betrag)} der Rechnung ${v.rechnungsnummer} (${v.mandant_name || ''}) zuordnen?\n\n${hinweis}`)) return;
    setErr(''); setMsg('');
    try {
      const fd = new FormData(); fd.append('rechnung_id', v.rechnung_id);
      await api.post(`/bank/transaktionen/${t.id}/zuordnen`, fd);
      setMsg(`Zahlung ${v.rechnungsnummer} verbucht.`); await reloadAll();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Zuordnung fehlgeschlagen.'); }
  }
  async function zuordnenBeleg(t, v) {
    if (!window.confirm(`Zahlung ${fmtEur(t.betrag)} dem Kreditor-Beleg „${v.lieferant}" (${fmtEur(v.brutto)}) zuordnen? Der Beleg wird als bezahlt markiert.`)) return;
    setErr(''); setMsg('');
    try {
      const fd = new FormData(); fd.append('beleg_id', v.beleg_id);
      await api.post(`/bank/transaktionen/${t.id}/beleg`, fd);
      setMsg('Zahlung dem Kreditor-Beleg zugeordnet.'); await reloadAll();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Zuordnung fehlgeschlagen.'); }
  }
  async function zuordnenKombi(t) {
    const k = t.kombi;
    if (!window.confirm(`Sammelzahlung über ${fmtEur(t.betrag)} aufteilen auf:\n\n${k.text.split(' + ').join('\n')}\n\nAlle ${k.ids.length} ${k.art === 'belege' ? 'Belege' : 'Rechnungen'} werden als bezahlt markiert.`)) return;
    setErr(''); setMsg('');
    try {
      const fd = new FormData(); fd.append(k.art === 'belege' ? 'beleg_ids' : 'rechnung_ids', k.ids.join(','));
      await api.post(`/bank/transaktionen/${t.id}/${k.art}`, fd);
      setMsg(`Sammelzahlung auf ${k.ids.length} ${k.art === 'belege' ? 'Belege' : 'Rechnungen'} verteilt.`); await reloadAll();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Zuordnung fehlgeschlagen.'); }
  }
  async function zuruecksetzen(t) {
    try { await api.post(`/bank/transaktionen/${t.id}/zuruecksetzen`); await reloadAll(); } catch { /* */ }
  }
  async function dublette(t, x) {
    if (!window.confirm(`Umsatz über ${fmtEur(t.betrag)} als doppelt importiert kennzeichnen (gleich wie Umsatz #${x.id})?\n\nEr wird nicht gebucht, bleibt aber sichtbar und lässt sich wieder lösen.`)) return;
    try { const fd = new FormData(); fd.append('von', x.id); await api.post(`/bank/transaktionen/${t.id}/dublette`, fd); await reloadAll(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Kennzeichnen fehlgeschlagen.'); }
  }
  async function markBezahlt(p) {
    if (!window.confirm(`Rechnung ${p.rechnungsnummer} ohne Bankzuordnung manuell ausgleichen (${fmtEur(p.offen_rest)})?`)) return;
    try {
      const fd = new FormData(); fd.append('mandant_id', p.mandant_id); fd.append('rechnung_id', p.id);
      fd.append('betrag', p.offen_rest); fd.append('typ', 'ausgleich'); fd.append('notiz', 'Manueller Ausgleich');
      await api.post('/debitoren/buchung', fd); await loadOpos();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Buchung fehlgeschlagen.'); }
  }
  async function pdf(p) {
    try {
      const res = await api.get(`/rechnungen/${p.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = (p.rechnungsnummer || 'rechnung') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('PDF konnte nicht geladen werden.'); }
  }

  const bk = opos?.buckets || {};
  const ueberzahlt = opos?.ueberzahlt || [];
  return (
    <div>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>{only === 'bank' ? 'Alle Kontoumsätze erfassen und beidseitig zuordnen (Debitoren-Rechnungen, Kreditoren-Belege oder direkt auf ein Konto).' : 'Offene Posten Ihrer Ausgangsrechnungen (Debitoren).'}</div>
      {msg && <div style={{ ...S.pathInfo, background: '#e9f7ef', color: C.success, marginBottom: 12 }}>{msg}</div>}
      {err && <div style={{ ...S.pathInfo, background: '#fdecea', color: C.danger, marginBottom: 12 }}>{err}</div>}

      {only !== 'bank' && ueberzahlt.length > 0 && (
        <div style={{ ...S.card, borderLeft: `4px solid ${C.danger}` }}>
          <h2 style={S.cardTitle}>⚠️ Überzahlte Debitorenkonten</h2>
          <div style={S.pathInfo}>Auf diesen Konten besteht ein Guthaben (z.&nbsp;B. Doppelzahlung) – ggf. Erstattung erforderlich.</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Debitor</th><th style={{ ...S.th, textAlign: 'right' }}>Guthaben</th><th style={S.th}></th></tr></thead>
            <tbody>
              {ueberzahlt.map(k => (
                <tr key={k.mandant_id}>
                  <td style={S.td}>{k.mandant_name}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: C.danger }}>{fmtEur(Math.abs(k.saldo))}</td>
                  <td style={S.td}><button style={S.btnSmall} onClick={() => setKontoMid(k.mandant_id)}>Konto öffnen</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {only !== 'bank' && (
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Offene Posten (OPOS Debitoren)</h2>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} title="Außerhalb des Portals erstellte Ausgangsrechnung einem Mandanten zuordnen" onClick={() => { setErr(''); setMsg(''); setShowImp(true); }}>⬆ Bestehende Rechnung hochladen</button>
        </div>
        <div style={S.pathInfo}>Ausgangsrechnungen aus dem bisherigen Programm hier hochladen und dem Mandanten zuordnen – sie zählen dann bei den offenen Posten, im Bankabgleich und als Erlös der Kostenstelle. Eingangsrechnungen (Lieferanten) gehören in den Reiter „Eingangsbelege (Kreditoren)" und bekommen dort ihre Kostenstelle.</div>
        {!opos ? <div style={S.empty}>Lade…</div> : (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ flex: '1 1 160px', background: C.bg, borderRadius: 6, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: C.muted }}>Summe offen</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.primary }}>{fmtEur(opos.summe)}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{opos.anzahl} Posten</div>
              </div>
              {['nicht_faellig', 't1_30', 't31_60', 't60plus'].map(k => (
                <div key={k} style={{ flex: '1 1 140px', background: C.bg, borderRadius: 6, padding: '12px 14px', borderLeft: `4px solid ${BUCKET_META[k].col}` }}>
                  <div style={{ fontSize: 12, color: C.muted }}>{BUCKET_META[k].label}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{fmtEur(bk[k] || 0)}</div>
                </div>
              ))}
            </div>
            {opos.posten.length === 0 ? <div style={S.empty}>Keine offenen Rechnungen. 🎉</div> : (
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Rechnung</th><th style={S.th}>Mandant</th><th style={S.th}>Datum</th>
                  <th style={S.th}>Fällig</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: 'right' }}>Offen</th><th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {opos.posten.map(p => (
                    <tr key={p.id}>
                      <td style={S.td}>{p.rechnungsnummer}{p.ist_storno ? ' (Storno)' : ''}</td>
                      <td style={S.td}><button style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 13 }} onClick={() => setKontoMid(p.mandant_id)}>{p.mandant_name || '—'}</button></td>
                      <td style={S.td}>{p.rechnungsdatum ? new Date(p.rechnungsdatum).toLocaleDateString('de-DE') : '—'}</td>
                      <td style={S.td}>{p.zahlungsziel ? new Date(p.zahlungsziel).toLocaleDateString('de-DE') : '—'}</td>
                      <td style={S.td}>
                        <span style={{ ...S.badge, background: '#fff', color: BUCKET_META[p.bucket].col, border: `1px solid ${BUCKET_META[p.bucket].col}` }}>
                          {p.tage_ueberfaellig > 0 ? `${p.tage_ueberfaellig} T überf.` : 'offen'}
                        </span>
                        {p.teilbezahlt && <span style={{ ...S.badge, marginLeft: 4, background: '#fff7e6', color: '#b8860b' }}>teilbezahlt</span>}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>
                        {fmtEur(p.offen_rest)}
                        {p.teilbezahlt && <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>von {fmtEur(p.brutto)}</div>}
                      </td>
                      <td style={S.td}>
                        {p.pdf_filename && <button style={S.btnSmall} onClick={() => pdf(p)}>PDF</button>}
                        <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => markBezahlt(p)}>ausgleichen</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      )}

      {only !== 'opos' && (
      <div style={S.card}>
        <h2 style={S.cardTitle}>Bankabgleich</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <input ref={fileRef} type="file" accept=".xml,.csv,.txt,.sta,.mt940" onChange={doImport} disabled={busy} style={{ fontSize: 13 }} />
          {busy && <span style={{ color: C.muted, fontSize: 13 }}>Importiere…</span>}
          <span style={{ flex: 1 }} />
          <select style={{ ...S.select, width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="offen">Offene Umsätze</option>
            <option value="zugeordnet">Rechnung/Beleg zugeordnet</option>
            <option value="gebucht">direkt auf Konto gebucht</option>
            <option value="dublette">doppelt importiert</option>
            <option value="altsystem">vor Buchführungsbeginn (Altsystem)</option>
            <option value="">Alle</option>
          </select>
          {tabB.suche('Name, Zweck, Betrag, Datum…', 230)}
        </div>
        <div style={{ ...S.pathInfo, marginBottom: 12 }}>Exportieren Sie die Kontoumsätze aus Ihrem Online-Banking als <strong>CAMT.053 (XML)</strong>, <strong>MT940</strong> oder <strong>CSV</strong> und laden Sie die Datei hier hoch. Bereits importierte Umsätze werden automatisch übersprungen. Es wird nichts verworfen: Jede Bewegung wird einer Rechnung, einem Beleg oder einem Konto (auch Debitor/Kreditor) zugeordnet; versehentlich doppelt importierte Zeilen werden als Dublette gekennzeichnet.</div>
        {txns.length === 0 ? <div style={S.empty}>Keine Umsätze in dieser Ansicht.</div> : (
          <div style={{ overflowX: 'auto' }}><table style={S.table}>
            <thead><tr>
              {tabB.th('buchungsdatum', 'Datum')}{tabB.th('gegen_name', 'Kontoinhaber')}{tabB.th('verwendungszweck', 'Verwendungszweck')}
              {tabB.th('betrag', 'Betrag', { textAlign: 'right' })}{tabB.th('status', 'Zuordnung')}
            </tr></thead>
            <tbody>
              {tabB.rows.map(t => (
                <tr key={t.id}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{t.buchungsdatum ? new Date(t.buchungsdatum).toLocaleDateString('de-DE') : '—'}</td>
                  <td style={{ ...S.td, maxWidth: 180 }}>{t.gegen_name || <span style={{ color: C.muted }}>—</span>}</td>
                  <td style={S.td}>
                    <div style={{ maxWidth: 360, overflowWrap: 'anywhere' }}>{t.verwendungszweck || <span style={{ color: C.muted }}>—</span>}</div>
                    {t.buchungstext && <div style={{ fontSize: 11, color: C.muted }}>{t.buchungstext}</div>}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: t.betrag < 0 ? C.danger : C.text }}>{fmtEur(t.betrag)}</td>
                  <td style={S.td}>
                    {t.vor_beginn && t.status === 'offen' ? (<span style={{ color: C.muted }} title="Vor dem Buchführungsbeginn – wird im Altsystem gebucht">⏮ Altsystem (vor Buchführungsbeginn)</span>) : t.status === 'dublette' ? (
                      <span style={{ color: C.muted }}>⧉ doppelt importiert (= Umsatz #{t.dublette_von}) <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => zuruecksetzen(t)}>lösen</button></span>
                    ) : (t.status === 'zugeordnet' || t.status === 'gebucht') ? (
                      <span>✓ {t.zuordnung || t.rechnungsnummer || (t.buchung_konto ? `${t.buchung_konto}${t.buchung_op_nr ? ' · OP ' + t.buchung_op_nr : ''}` : null) || (t.beleg_id ? 'Kreditor-Beleg' : (t.mandant_name ? 'a-conto: ' + t.mandant_name : 'gebucht'))} <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => zuruecksetzen(t)}>lösen</button></span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {t.kombi && (
                          <button style={{ ...S.btnSmall, textAlign: 'left', color: C.text, background: t.kombi.art === 'belege' ? '#fbe9ec' : '#e3f4ea', border: `1px solid ${t.kombi.art === 'belege' ? C.danger : C.success}`, fontWeight: 600 }} onClick={() => zuordnenKombi(t)}
                            title="Die Summe dieser Posten ergibt genau den Zahlbetrag">
                            {t.kombi.art === 'belege' ? '🧾' : '💶'} ★ Sammelzahlung: {t.kombi.text}
                          </button>
                        )}
                        {(t.vorschlaege || []).map(v => (
                          <button key={v.rechnung_id} style={{ ...S.btnSmall, textAlign: 'left', color: C.text, background: v.exakt ? '#e3f4ea' : '#fff', border: `1px solid ${v.exakt ? C.success : C.border}`, fontWeight: v.exakt ? 600 : 400 }} onClick={() => zuordnenVorschlag(t, v)}>
                            💶 {v.exakt ? '★ ' : ''}{v.rechnungsnummer} vom {dDE(v.rechnungsdatum)} · {v.mandant_name || ''} · {v.teilbezahlt ? 'Rest ' : ''}{fmtEur(v.offen_rest)}{v.zahlungsziel ? ` · fällig ${dDE(v.zahlungsziel)}` : ''}{v.altposten ? ' · Altposten' : ''}
                          </button>
                        ))}
                        {(t.vorschlaege_kreditor || []).map(v => (
                          <button key={v.beleg_id} style={{ ...S.btnSmall, textAlign: 'left', color: C.text, background: v.exakt ? '#fbe9ec' : '#fff', border: `1px solid ${v.exakt ? C.danger : C.border}`, fontWeight: v.exakt ? 600 : 400 }} onClick={() => zuordnenBeleg(t, v)}>
                            🧾 {v.exakt ? '★ ' : ''}{v.lieferant}{v.rechnungsnummer ? ` · Nr. ${v.rechnungsnummer}` : ''} vom {dDE(v.beleg_datum)} · {fmtEur(v.brutto)}{v.konto ? ' · ' + v.konto.split(' ')[0] : ''}{v.altposten ? ' · Altposten' : ''}{v.doppelt ? ' · ⚠ doppelt hochgeladen?' : ''}
                          </button>
                        ))}
                        {(t.dublette_kandidaten || []).map(x => (
                          <button key={'d' + x.id} style={{ ...S.btnSmall, textAlign: 'left', background: '#eee', color: C.text }} onClick={() => dublette(t, x)}>⧉ doppelt importiert? gleich wie Umsatz #{x.id}{x.gegen_name ? ` (${x.gegen_name})` : ''}</button>
                        ))}
                        <div>
                          <button style={{ ...S.btnSmall, background: '#eef2f7', color: C.text, border: `1px solid ${C.border}`, marginRight: 6 }} onClick={() => setSammelTxn(t)}
                            title="Eine Zahlung für mehrere Posten (Sammelzahlung) – oder einen Posten frei aus der Liste wählen">{t.betrag > 0 ? 'Rechnungen wählen…' : 'Belege wählen…'}</button>
                          {t.betrag > 0 && <button style={{ ...S.btnSmall, background: C.accent }} onClick={() => setBuchenTxn(t)}>Debitor a-conto…</button>}
                          <button style={{ ...S.btnSmall, marginLeft: t.betrag > 0 ? 6 : 0 }} onClick={() => setDirektTxn(t)}>auf Konto buchen…</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        {txns.filter(t => t.status === 'offen' && !t.vor_beginn).length > 0 && <div style={{ ...S.hint, marginTop: 10 }}>⚠️ Es sind noch {txns.filter(t => t.status === 'offen' && !t.vor_beginn).length} Umsätze nicht gebucht – jede Bankbewegung sollte einer Rechnung, einem Kreditor-Beleg oder einem Konto zugeordnet werden.</div>}
      </div>
      )}

      {buchenTxn && <BuchenModal txn={buchenTxn} mandanten={mandanten} onClose={() => setBuchenTxn(null)} onDone={async () => { setBuchenTxn(null); await reloadAll(); }} />}
      {sammelTxn && <SammelzahlungModal txn={sammelTxn} onClose={() => setSammelTxn(null)} onDone={async (text) => { setSammelTxn(null); setMsg(text); await reloadAll(); }} />}
      {direktTxn && <BankDirektModal txn={direktTxn} konten={konten} mandanten={mandanten} onClose={() => setDirektTxn(null)} onDone={async () => { setDirektTxn(null); await reloadAll(); }} />}
      {kontoMid && <KontoModal mandantId={kontoMid} onClose={() => setKontoMid(null)} onChanged={reloadAll} />}
      {showImp && <RechnungImportDialog mandantId="" mandanten={mandanten} onClose={() => setShowImp(false)}
        onDone={async (data) => { setShowImp(false); setMsg(`Rechnung ${data.rechnungsnummer} übernommen und zugeordnet.`); await reloadAll(); }} />}
    </div>
  );
}

const kNr0 = (k) => (k || '').split(' ')[0];
// Sammelzahlung: ein Bankumsatz für mehrere Belege (Zahlungsausgang) bzw. Rechnungen (Zahlungseingang)
function SammelzahlungModal({ txn, onClose, onDone }) {
  const [daten, setDaten] = useState(null);
  const [sel, setSel] = useState((txn.kombi && txn.kombi.ids) || []);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/bank/transaktionen/${txn.id}/kandidaten`).then(r => setDaten(r.data)).catch(ex => setErr(ex.response?.data?.detail || 'Offene Posten konnten nicht geladen werden.'));
  }, [txn.id]);
  const liste = daten ? daten.liste : [];
  const tab = useTabelle(liste);
  const belege = (daten ? daten.art : (txn.betrag < 0 ? 'belege' : 'rechnungen')) === 'belege';
  const zahlung = Math.abs(txn.betrag || 0);
  const gewaehlt = sel.map(id => liste.find(x => x.id === id)).filter(Boolean);
  const summe = Math.round(gewaehlt.reduce((a, x) => a + (x.betrag || 0), 0) * 100) / 100;
  const diff = Math.round((zahlung - summe) * 100) / 100;
  const ohneLetzten = Math.round(gewaehlt.slice(0, -1).reduce((a, x) => a + (x.betrag || 0), 0) * 100) / 100;
  const reicht = gewaehlt.length === 0 || zahlung - ohneLetzten > 0.005;
  const letzter = gewaehlt[gewaehlt.length - 1];
  const toggle = (id) => setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  let hinweis = null;
  if (gewaehlt.length && !reicht) hinweis = { farbe: C.danger, text: `Die Zahlung reicht nicht für alle ausgewählten ${belege ? 'Belege' : 'Rechnungen'} – bitte die Auswahl verkleinern.` };
  else if (gewaehlt.length && Math.abs(diff) < 0.005) hinweis = { farbe: C.success, text: '✓ Die Summe passt genau zum Zahlbetrag.' };
  else if (gewaehlt.length && diff > 0) hinweis = { farbe: '#8a5a00', text: belege ? `Überzahlung ${fmtEur(diff)}: bleibt als Guthaben auf dem Kreditorenkonto (beim Beleg ${letzter?.nummer || ''}).` : `Überzahlung ${fmtEur(diff)}: wird dem Debitorenkonto gutgeschrieben (a-conto).` };
  else if (gewaehlt.length) hinweis = { farbe: '#8a5a00', text: `Es fehlen ${fmtEur(-diff)}: ${belege ? 'der Beleg' : 'die Rechnung'} ${letzter?.nummer || ''} bleibt mit diesem Betrag offen (zuletzt gewählt).` };
  async function speichern() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append(belege ? 'beleg_ids' : 'rechnung_ids', sel.join(','));
      await api.post(`/bank/transaktionen/${txn.id}/${belege ? 'belege' : 'rechnungen'}`, fd);
      onDone(sel.length > 1 ? `Sammelzahlung auf ${sel.length} ${belege ? 'Belege' : 'Rechnungen'} verteilt.` : 'Zahlung zugeordnet.');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Zuordnung fehlgeschlagen.'); setBusy(false); }
  }
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 'min(900px, 96vw)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>{belege ? 'Belege für diese Zahlung wählen' : 'Rechnungen für diese Zahlung wählen'}</h2>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          <b>{dDE(txn.buchungsdatum)}</b> · {txn.gegen_name || '—'} · <b style={{ color: txn.betrag < 0 ? C.danger : C.text }}>{fmtEur(txn.betrag)}</b>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 2, overflowWrap: 'anywhere' }}>{txn.verwendungszweck}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {tab.suche('Name, Nummer, Betrag, Datum…', 240)}
          <span style={{ fontSize: 12, color: C.muted }}>★ = Nummer steht im Verwendungszweck. Jeder Posten wird mit seinem offenen Betrag ausgeglichen, eine Differenz trägt der zuletzt gewählte.</span>
        </div>
        {!daten && !err && <div style={S.empty}>Lade offene Posten…</div>}
        {daten && liste.length === 0 && <div style={S.empty}>Keine offenen {belege ? 'Belege' : 'Rechnungen'} vorhanden.</div>}
        {liste.length > 0 && (
          <div style={{ overflowX: 'auto' }}><table style={S.table}>
            <thead><tr>
              <th style={S.th}></th>{tab.th('datum', 'Datum')}{tab.th('name', belege ? 'Lieferant' : 'Mandant')}{tab.th('nummer', belege ? 'Rechnungsnr.' : 'Rechnung')}
              {tab.th('betrag', 'Offen', { textAlign: 'right' })}<th style={S.th}></th>
            </tr></thead>
            <tbody>
              {tab.rows.map(x => {
                const pos = sel.indexOf(x.id);
                return (
                  <tr key={x.id} onClick={() => toggle(x.id)} style={{ cursor: 'pointer', background: pos >= 0 ? '#eef6ff' : undefined }}>
                    <td style={{ ...S.td, width: 44, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={pos >= 0} onChange={() => toggle(x.id)} onClick={e => e.stopPropagation()} aria-label={`${x.nummer || x.name} auswählen`} />
                      {pos >= 0 && <span style={{ fontSize: 11, color: C.muted, marginLeft: 4 }}>{pos + 1}.</span>}
                    </td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{dDE(x.datum)}</td>
                    <td style={{ ...S.td, maxWidth: 280 }}>{x.name}{x.text ? <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={x.text}>{x.text}</div> : null}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', fontWeight: x.im_zweck ? 600 : 400 }}>{x.im_zweck ? '★ ' : ''}{x.nummer || '—'}</td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtEur(x.betrag)}{x.teilbezahlt ? <div style={{ fontSize: 11, color: C.muted }}>von {fmtEur(x.brutto)}</div> : null}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      {x.altposten && <span style={{ ...S.badge, background: '#eceff3', color: C.muted, marginRight: 4 }}>Altposten</span>}
                      {x.doppelt && <span style={{ ...S.badge, background: '#fff7e6', color: '#8a5a00', marginRight: 4 }} title="Gleiche Rechnungsnummer beim gleichen Lieferanten mehrfach vorhanden – vermutlich doppelt hochgeladen">doppelt?</span>}
                      {x.faellig && <span style={{ fontSize: 11, color: C.muted }}>fällig {dDE(x.faellig)}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, margin: '14px 0 8px' }}>
          {[['Zahlung', fmtEur(zahlung)], [`Ausgewählt (${gewaehlt.length})`, fmtEur(summe)], ['Differenz', fmtEur(diff)]].map(([l, w]) => (
            <div key={l} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em' }}>{l}</div>
              <div style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{w}</div>
            </div>
          ))}
        </div>
        {hinweis && <div style={{ fontSize: 13, color: hinweis.farbe, marginBottom: 8 }}>{hinweis.text}</div>}
        {err && <div style={{ ...S.hint, color: C.danger }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnPrimary} disabled={busy || gewaehlt.length === 0 || !reicht} onClick={speichern}>
            {gewaehlt.length > 1 ? `Auf ${gewaehlt.length} ${belege ? 'Belege' : 'Rechnungen'} verteilen` : 'Zuordnen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BankDirektModal({ txn, konten, mandanten, onClose, onDone }) {
  const ausgabe = (txn.betrag || 0) < 0;
  const liste = kontoListe(konten).filter(k => k.nr !== '1200');
  const [konto, setKonto] = useState('');
  const [ust, setUst] = useState('19');
  const [op, setOp] = useState('');
  const [opSel, setOpSel] = useState([]);
  const [ops, setOps] = useState([]);
  const [anlagen, setAnlagen] = useState([]);
  const [anlBez, setAnlBez] = useState('');
  const [anlJahre, setAnlJahre] = useState('');
  const [anlGwg, setAnlGwg] = useState(null);
  const [anlId, setAnlId] = useState('');
  const [rcWahl, setRcWahl] = useState('');
  const [err, setErr] = useState('');
  const nr = kNr0(konto);
  const art = kArt(nr);
  const sach = ['E', 'W', 'A'].includes(art);
  const pers = ['D', 'K'].includes(art);
  const reduziert = (art === 'D' && (txn.betrag || 0) > 0) || (art === 'K' && (txn.betrag || 0) < 0);   // gleicht offene Posten aus
  const rc = ausgabe && ['W', 'A'].includes(art) ? rcWahl : '';
  const brutto = Math.abs(txn.betrag || 0);
  const satz = sach ? (parseFloat(ust) || 0) : 0;
  const ustB = (satz && !rc) ? Math.round((brutto - brutto / (1 + satz / 100)) * 100) / 100 : 0;
  const netto = Math.round((brutto - ustB) * 100) / 100;
  const zugang = art === 'A' && ausgabe;
  const verkauf = art === 'A' && !ausgabe;
  const gwgAuto = nr === '0480' || netto <= (konten?.gwg_grenze || 800);
  const gwg = anlGwg == null ? gwgAuto : anlGwg;
  useEffect(() => { if (sach) setUst(ustVorschlag(nr)); }, [nr, sach]);
  useEffect(() => {
    setOp(''); setOpSel([]);
    if (!pers) { setOps([]); return; }
    api.get(`/buchhaltung/konto/${nr}`).then(({ data }) => {
      const lst = (data.offene_posten || []).filter(o => o.op_nr);
      setOps(lst);
      const passend = lst.find(o => Math.abs(Math.abs(o.offen) - brutto) < 0.005);
      const txt = (txn.verwendungszweck || '').replace(/\s/g, '');
      const genannt = lst.filter(o => o.offen > 0.005 && txt.includes(o.op_nr)).sort((a, b) => txt.indexOf(a.op_nr) - txt.indexOf(b.op_nr)).map(o => o.op_nr);
      if (passend) setOp(passend.op_nr);
      if (genannt.length) setOpSel(genannt); else if (passend) setOpSel([passend.op_nr]);
    }).catch(() => setOps([]));
  }, [nr, pers, brutto, txn.verwendungszweck]);
  const opsOffen = ops.filter(o => o.offen > 0.005);
  const verteilungOk = !pers || !reduziert || opVerteilung(opsOffen, opSel, brutto).ok;
  useEffect(() => { if (art === 'A') api.get('/anlagen').then(({ data }) => setAnlagen((data.anlagen || []).filter(a => !a.abgang_datum))).catch(() => {}); }, [art]);
  useEffect(() => { if (zugang && konten?.anlage_nd) { const m = konten.anlage_nd[nr]; setAnlJahre(m != null ? String(Math.round(m / 12 * 10) / 10).replace('.', ',') : '3'); } }, [zugang, nr, konten]);
  async function buchen() {
    if (!nr) { setErr('Bitte ein Konto wählen.'); return; }
    if (verkauf && !anlId) { setErr('Bitte die verkaufte Anlage auswählen.'); return; }
    try {
      const fd = new FormData(); fd.append('konto', nr);
      if (sach) fd.append('ust_satz', rc ? (satz || 19) : satz);
      if (rc) fd.append('steuerschluessel', rc);
      if (pers && reduziert && opSel.length) fd.append('op_nrs', JSON.stringify(opSel));
      else if (pers && op) fd.append('op_nr', op);
      if (zugang) {
        fd.append('anlage_bezeichnung', anlBez || txn.verwendungszweck || '');
        const j = parseZahl(anlJahre);
        fd.append('anlage_nd', gwg ? 0 : Math.max(0, Math.round((j == null ? 3 : j) * 12)));
        fd.append('anlage_gwg', gwg ? '1' : '0');
      }
      if (verkauf) fd.append('anlage_id', anlId);
      await api.post(`/bank/transaktionen/${txn.id}/buchen`, fd); onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Buchen fehlgeschlagen.'); }
  }
  const gegen = verkauf ? '8820' : (nr || '—');
  const steuerK = (kArt(gegen) === 'E' ? ({ 19: '1776', 7: '1771' }[satz] || '1770') : ({ 19: '1576', 7: '1571' }[satz] || '1570'));
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: pers && reduziert ? 'min(860px, 96vw)' : 640, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Bankumsatz auf Konto buchen</h2>
        <div style={S.pathInfo}>{txn.buchungsdatum ? new Date(txn.buchungsdatum).toLocaleDateString('de-DE') : ''} · <strong>{fmtEur(txn.betrag)}</strong>{txn.gegen_name ? ' · ' + txn.gegen_name : ''}{txn.verwendungszweck ? ' · ' + txn.verwendungszweck : ''}</div>
        {err && <div style={{ ...S.pathInfo, color: C.danger, fontWeight: 600 }}>{err}</div>}
        <label style={S.label}>Gegenkonto – Erlös, Aufwand, Anlage, Debitor/Kreditor, Privat, Geldtransit, Finanzamt …</label>
        <input style={S.input} list="bank-konten" value={konto} onChange={e => setKonto(e.target.value)} placeholder="Nummer oder Name tippen…" />
        <datalist id="bank-konten">{liste.map(k => <option key={k.nr} value={`${k.nr} ${k.name || ''}`} />)}</datalist>
        {nr && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{KART_LABEL[art]}</div>}
        {pers && reduziert && (
          <>
            <label style={S.label}>Offene Posten ({art === 'D' ? 'Rechnungen' : 'Eingangsbelege'}) – mehrere anklicken, um die Zahlung aufzuteilen</label>
            <OpAuswahl ops={opsOffen} sel={opSel} setSel={setOpSel} betrag={brutto} />
            <div style={{ ...S.pathInfo, marginTop: 4 }}>Ohne Auswahl bleibt die Zahlung ohne Zuordnung auf dem Konto und lässt sich später im Kontoblatt über „zuordnen…“ ausziffern.</div>
          </>
        )}
        {pers && !reduziert && (
          <>
            <label style={S.label}>Offener Posten ({art === 'D' ? 'Rechnung' : 'Eingangsbeleg'})</label>
            <select style={S.select} value={op} onChange={e => setOp(e.target.value)}>
              <option value="">– ohne Zuordnung (a-conto) –</option>
              {ops.map(o => <option key={o.op_nr} value={o.op_nr}>{o.op_nr} · offen {fmtEur(o.offen)}</option>)}
            </select>
            {art === 'D' && <div style={{ ...S.pathInfo, marginTop: 4 }}>Tipp: Für Ausgangsrechnungen aus dem Portal ist „Debitor a-conto…“ bzw. der Vorschlag in der Liste gleichwertig.</div>}
          </>
        )}
        {art === 'F' && nr && <div style={{ ...S.pathInfo, color: C.muted, marginTop: 6 }}>Keine Gewinnauswirkung (Privat / Geldtransit / Umsatzsteuer ans Finanzamt) – ohne Umsatzsteuer.</div>}
        {zugang && (
          <div style={{ marginTop: 10, border: `1px solid ${C.accent}`, borderRadius: 8, padding: '8px 12px', background: '#f5f9fc' }}>
            <b style={{ fontSize: 13, color: C.primary }}>Zugang zum Anlagevermögen</b>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div><label style={S.label}>Bezeichnung</label><input style={S.input} value={anlBez} onChange={e => setAnlBez(e.target.value)} placeholder="z. B. Laptop" /></div>
              <div><label style={S.label}>Nutzungsdauer (Jahre)</label><input style={S.input} value={gwg ? '0' : anlJahre} disabled={gwg} onChange={e => setAnlJahre(e.target.value)} /></div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 6 }}><input type="checkbox" checked={gwg} onChange={e => setAnlGwg(e.target.checked)} /> Sofortabschreibung als GWG (bis {fmtEur(konten?.gwg_grenze || 800)} netto)</label>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>AfA bzw. Sofortabschreibung und der Eintrag im Anlagenverzeichnis werden automatisch gebucht.</div>
          </div>
        )}
        {verkauf && (
          <div style={{ marginTop: 10, border: `1px solid ${C.danger}`, borderRadius: 8, padding: '8px 12px', background: '#fdf7f6' }}>
            <b style={{ fontSize: 13, color: C.danger }}>Verkauf aus dem Anlagevermögen</b>
            <label style={S.label}>Verkaufte Anlage</label>
            <select style={S.select} value={anlId} onChange={e => setAnlId(e.target.value)}>
              <option value="">– wählen –</option>
              {anlagen.map(a => <option key={a.id} value={a.id}>{a.bezeichnung} · {a.konto_nr} · Buchwert {fmtEur(a.buchwert)}</option>)}
            </select>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Erlös auf 8820, AfA bis zum Abgangsmonat und Restbuchwert (2315) werden automatisch gebucht.</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          {sach && <div style={{ flex: 1 }}><label style={S.label}>USt %</label>
            <select style={S.select} value={ust} onChange={e => setUst(e.target.value)}><option value="19">19 %</option><option value="7">7 %</option><option value="0">0 % / steuerfrei</option></select></div>}
        </div>
        {ausgabe && ['W', 'A'].includes(art) && (
          <div>
            <label style={S.label}>Steuerschlüssel</label>
            <SteuerschluesselWahl value={rcWahl} onChange={v => { setRcWahl(v); if (v) setUst('19'); }} />
            {rc && <div style={{ ...S.pathInfo, marginTop: 4, color: C.text }}>{RC_HINWEIS}</div>}
          </div>
        )}
        {nr && (
          <div style={{ marginTop: 12, background: C.bg, borderRadius: 8, padding: '10px 12px', fontFamily: 'monospace', fontSize: 12.5 }}>
            {ausgabe && rc
              ? <>Soll {gegen} {fmtEur(brutto)} · Haben 1200 Bank {fmtEur(brutto)} · Soll 1577 Vorsteuer § 13b {fmtEur(Math.round(brutto * (satz || 19)) / 100)} · Haben 1787 USt § 13b {fmtEur(Math.round(brutto * (satz || 19)) / 100)}</>
              : ausgabe
              ? <>Soll {gegen} {fmtEur(sach ? netto : brutto)}{ustB ? ` · Soll ${steuerK} ${fmtEur(ustB)}` : ''} · Haben 1200 Bank {fmtEur(brutto)}</>
              : <>Soll 1200 Bank {fmtEur(brutto)} · Haben {gegen} {fmtEur(sach ? netto : brutto)}{ustB ? ` · Haben ${steuerK} ${fmtEur(ustB)}` : ''}</>}
            {pers && <div style={{ fontFamily: 'inherit', color: C.muted, marginTop: 4 }}>Sammelkonto {art === 'D' ? '1400' : '1600'} wird nicht zusätzlich bebucht.</div>}
          </div>
        )}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 8 }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnSmall} disabled={!verteilungOk} onClick={buchen}>Buchen</button>
        </div>
      </div>
    </div>
  );
}

function BuchenModal({ txn, mandanten, onClose, onDone }) {
  const [mid, setMid] = useState('');
  const [rid, setRid] = useState('');     // '' = a-conto
  const [offene, setOffene] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!mid) { setOffene([]); setRid(''); return; }
    api.get(`/debitoren/konto/${mid}`).then(({ data }) => setOffene(data.rechnungen.filter(r => r.offen_rest > 0.005))).catch(() => setOffene([]));
  }, [mid]);
  async function buchen() {
    if (!mid) { setErr('Bitte einen Debitor wählen.'); return; }
    try {
      const fd = new FormData();
      if (rid) fd.append('rechnung_id', rid); else fd.append('mandant_id', mid);
      await api.post(`/bank/transaktionen/${txn.id}/zuordnen`, fd);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Buchung fehlgeschlagen.'); }
  }
  const sel = offene.find(r => String(r.id) === String(rid));
  let hinweis = '';
  if (sel) {
    const diff = Math.round((sel.offen_rest - txn.betrag) * 100) / 100;
    if (diff > 0.005) hinweis = `Teilzahlung – es bleiben ${fmtEur(diff)} offen.`;
    else if (diff < -0.005) hinweis = `Überzahlung – ${fmtEur(-diff)} gehen als Guthaben aufs Debitorenkonto.`;
    else hinweis = 'Rechnung wird vollständig ausgeglichen.';
  } else if (mid) {
    hinweis = `${fmtEur(txn.betrag)} werden dem Debitorenkonto gutgeschrieben (a-conto).`;
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ ...S.modalCard }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Zahlung manuell buchen</h2>
        <div style={{ ...S.pathInfo }}>
          {txn.buchungsdatum ? new Date(txn.buchungsdatum).toLocaleDateString('de-DE') : ''} · <strong>{fmtEur(txn.betrag)}</strong>
          {txn.gegen_name ? ` · ${txn.gegen_name}` : ''}{txn.verwendungszweck ? ` · ${txn.verwendungszweck}` : ''}
        </div>
        {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
        <label style={S.label}>Debitor (Mandant)</label>
        <select style={S.select} value={mid} onChange={e => setMid(e.target.value)}>
          <option value="">– bitte wählen –</option>
          {mandanten.map(m => <option key={m.id} value={m.id}>{m.name}{m.ist_dummy ? ' (Test)' : ''}</option>)}
        </select>
        {mid && (
          <>
            <label style={{ ...S.label, marginTop: 12 }}>Auf Rechnung buchen</label>
            <select style={S.select} value={rid} onChange={e => setRid(e.target.value)}>
              <option value="">— aufs Debitorenkonto (a-conto / Guthaben) —</option>
              {offene.map(r => <option key={r.id} value={r.id}>{r.rechnungsnummer} · offen {fmtEur(r.offen_rest)}</option>)}
            </select>
          </>
        )}
        {hinweis && <div style={{ ...S.pathInfo, marginTop: 10, color: C.accent }}>{hinweis}</div>}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 8 }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnSmall} onClick={buchen}>Buchen</button>
        </div>
      </div>
    </div>
  );
}

function KontoModal({ mandantId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [bBetrag, setBBetrag] = useState('');
  const [bTyp, setBTyp] = useState('erstattung');
  const [bNotiz, setBNotiz] = useState('');
  const [bWeg, setBWeg] = useState('bank');
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/debitoren/konto/${mandantId}`); setData(data); } catch { /* */ }
  }, [mandantId]);
  useEffect(() => { load(); }, [load]);
  async function buchen() {
    const v = parseFloat(String(bBetrag).replace(',', '.'));
    if (!v) { setErr('Bitte einen Betrag angeben (negativ = Erstattung/Minderung).'); return; }
    try {
      const fd = new FormData(); fd.append('mandant_id', mandantId); fd.append('betrag', v);
      fd.append('typ', bTyp); fd.append('zahlungsweg', bWeg); if (bNotiz) fd.append('notiz', bNotiz);
      await api.post('/debitoren/buchung', fd);
      setBBetrag(''); setBNotiz(''); setErr(''); await load(); if (onChanged) await onChanged();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Buchung fehlgeschlagen.'); }
  }
  const k = data?.konto;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 720, maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Debitorenkonto {k?.mandant_name || ''}</h2>
        {!data ? <div style={S.empty}>Lade…</div> : (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <div style={{ flex: 1, background: C.bg, borderRadius: 6, padding: '10px 12px' }}><div style={{ fontSize: 12, color: C.muted }}>Offene Forderung</div><div style={{ fontWeight: 700 }}>{fmtEur(k.forderung)}</div></div>
              <div style={{ flex: 1, background: C.bg, borderRadius: 6, padding: '10px 12px' }}><div style={{ fontSize: 12, color: C.muted }}>Guthaben (a-conto)</div><div style={{ fontWeight: 700 }}>{fmtEur(k.guthaben)}</div></div>
              <div style={{ flex: 1, background: k.ueberzahlt ? '#fdecea' : C.bg, borderRadius: 6, padding: '10px 12px' }}><div style={{ fontSize: 12, color: C.muted }}>Saldo</div><div style={{ fontWeight: 700, color: k.ueberzahlt ? C.danger : C.text }}>{fmtEur(k.saldo)}{k.ueberzahlt ? ' (überzahlt)' : ''}</div></div>
            </div>

            <h3 style={{ fontSize: 14, color: C.primary, margin: '8px 0' }}>Rechnungen</h3>
            <table style={S.table}><thead><tr><th style={S.th}>Nr.</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: 'right' }}>Brutto</th><th style={{ ...S.th, textAlign: 'right' }}>Bezahlt</th><th style={{ ...S.th, textAlign: 'right' }}>Offen</th></tr></thead>
              <tbody>{data.rechnungen.map(r => (
                <tr key={r.id}><td style={S.td}>{r.rechnungsnummer}</td><td style={S.td}>{r.status}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(r.brutto)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(r.bezahlt)}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{fmtEur(r.offen_rest)}</td></tr>
              ))}</tbody>
            </table>

            <h3 style={{ fontSize: 14, color: C.primary, margin: '16px 0 8px' }}>Buchungen / Zahlungen</h3>
            {data.zahlungen.length === 0 ? <div style={S.empty}>Noch keine Buchungen.</div> : (
              <table style={S.table}><thead><tr><th style={S.th}>Datum</th><th style={S.th}>Typ</th><th style={S.th}>Bezug</th><th style={{ ...S.th, textAlign: 'right' }}>Betrag</th></tr></thead>
                <tbody>{data.zahlungen.map(z => (
                  <tr key={z.id}><td style={S.td}>{z.datum ? new Date(z.datum).toLocaleDateString('de-DE') : '—'}</td>
                    <td style={S.td}>{z.typ}{z.beleglos ? ' (beleglos)' : ''}</td>
                    <td style={S.td}>{z.rechnungsnummer || (z.aconto ? 'a-conto' : '—')}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: z.betrag < 0 ? C.danger : C.text }}>{fmtEur(z.betrag)}</td></tr>
                ))}</tbody>
              </table>
            )}

            <h3 style={{ fontSize: 14, color: C.primary, margin: '16px 0 8px' }}>Beleglose Buchung (Erstattung / Korrektur / Ausgleich)</h3>
            {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div><label style={S.label}>Betrag (− = Erstattung)</label><input style={{ ...S.input, width: 140 }} value={bBetrag} onChange={e => setBBetrag(e.target.value)} placeholder="-476,00" /></div>
              <div><label style={S.label}>Typ</label>
                <select style={{ ...S.select, width: 160 }} value={bTyp} onChange={e => setBTyp(e.target.value)}>
                  <option value="erstattung">Erstattung</option>
                  <option value="korrektur">Korrektur</option>
                  <option value="ausgleich">Ausgleich</option>
                </select>
              </div>
              <div><label style={S.label}>Geldfluss (EÜR)</label>
                <select style={{ ...S.select, width: 200 }} value={bWeg} onChange={e => setBWeg(e.target.value)}>
                  <option value="bank">Bank (ohne Bankabgleich)</option>
                  <option value="kasse">Kasse (bar)</option>
                  <option value="kein">kein Geldfluss (Verrechnung/Verzicht)</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 160 }}><label style={S.label}>Notiz</label><input style={S.input} value={bNotiz} onChange={e => setBNotiz(e.target.value)} placeholder="z.B. Rücküberweisung Doppelzahlung" /></div>
              <button style={S.btnSmall} onClick={buchen}>Buchen</button>
            </div>
            <div style={S.pathInfo}>Hinweis: Ein negativer Betrag bucht ein Guthaben aus (z.&nbsp;B. nach Rücküberweisung einer Doppelzahlung). Ist auch der Bankbeleg der Erstattung vorhanden, ordnen Sie ihn alternativ direkt im Bankabgleich diesem Debitor zu.</div>
          </>
        )}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

function Buchhaltung({ user, onKonto }) {
  const today = new Date();
  const iso = d => d.toISOString().slice(0, 10);
  const [von, setVon] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [bis, setBis] = useState(iso(today));
  const [tab, setTab] = useState('kostenstellen');
  const [aus, setAus] = useState(null);
  const [salden, setSalden] = useState(null);
  const tabK = useTabelle(salden?.konten);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        api.get('/buchhaltung/auswertung', { params: { von, bis } }),
        api.get('/buchhaltung/salden', { params: { von, bis } }),
      ]);
      setAus(a.data); setSalden(s.data);
    } catch { /* */ }
  }, [von, bis]);
  useEffect(() => { load(); }, [load]);

  function preset(p) {
    const y = today.getFullYear(), m = today.getMonth();
    if (p === 'monat') { setVon(iso(new Date(y, m, 1))); setBis(iso(new Date(y, m + 1, 0))); }
    else if (p === 'vmonat') { setVon(iso(new Date(y, m - 1, 1))); setBis(iso(new Date(y, m, 0))); }
    else if (p === 'quartal') { const q = Math.floor(m / 3); setVon(iso(new Date(y, q * 3, 1))); setBis(iso(new Date(y, q * 3 + 3, 0))); }
    else if (p === 'vquartal') { const q = Math.floor(m / 3) - 1; setVon(iso(new Date(y, q * 3, 1))); setBis(iso(new Date(y, q * 3 + 3, 0))); }
    else if (p === 'jahr') { setVon(iso(new Date(y, 0, 1))); setBis(iso(new Date(y, 11, 31))); }
  }

  const eur = v => fmtEur(v);

  return (
    <div>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 14 }}>Kostenstellen-Deckungsbeitrag je Bearbeiter und Mandant (nach Rechnungs-/Belegdatum, Leistungssicht) sowie Konten-Salden aus dem EÜR-Journal.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        {[['monat', 'Dieser Monat'], ['vmonat', 'Letzter Monat'], ['quartal', 'Dieses Quartal'], ['vquartal', 'Letztes Quartal'], ['jahr', 'Dieses Jahr']].map(([k, l]) =>
          <button key={k} style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => preset(k)}>{l}</button>)}
        <span style={{ flex: 1 }} />
        <input type="date" style={{ ...S.input, width: 'auto' }} value={von} onChange={e => setVon(e.target.value)} />
        <span style={{ color: C.muted }}>–</span>
        <input type="date" style={{ ...S.input, width: 'auto' }} value={bis} onChange={e => setBis(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['kostenstellen', 'Kostenstellen'], ['konten', 'Konten (Saldenliste)']].map(([k, l]) => {
          const a = tab === k;
          return <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${a ? C.primary : C.border}`, background: a ? C.primary : '#fff', color: a ? '#fff' : C.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: a ? 600 : 500 }}>{l}</button>;
        })}
      </div>

      {tab === 'kostenstellen' && aus && (() => {
        const conn = (h) => <div style={{ width: 2, height: h || 22, background: C.border, margin: '0 auto' }} />;
        const box = (label, value, col, big) => (
          <div style={{ display: 'inline-block', background: '#fff', border: `1px solid ${C.border}`, borderTop: `3px solid ${col}`, borderRadius: 10, padding: big ? '14px 26px' : '10px 18px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', minWidth: big ? 240 : 180 }}>
            <div style={{ fontSize: 12, color: C.muted }}>{label}</div>
            <div style={{ fontSize: big ? 26 : 18, fontWeight: 800, color: col }}>{eur(value)}</div>
          </div>
        );
        return (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Zeitraum {aus.von} – {aus.bis} · Bearbeiterkosten anteilig für {aus.monate} Monat(e). Werte fließen von unten (Mandanten) nach oben zum Ergebnis.</div>
            <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
              <div style={{ textAlign: 'center', minWidth: 'min-content' }}>
                {box('Betriebsergebnis', aus.betriebsergebnis, aus.betriebsergebnis >= 0 ? C.success : C.danger, true)}
                {conn()}
                {box('− Gemeinkosten (Büro)', -aus.gemeinkosten, '#b85c00')}
                {conn()}
                <div style={{ fontSize: 12, color: C.muted }}>Σ Deckungsbeitrag I = {eur(aus.summe_db1)}</div>
                {conn()}
                <div style={{ display: 'flex', gap: 22, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {aus.bearbeiter.map(b => (
                    <div key={b.user_id || 'ohne'} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderTop: `3px solid ${C.accent}`, borderRadius: 10, padding: '10px 16px', minWidth: 210, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontWeight: 700, color: C.primary }}>👤 {b.name}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: b.db1 >= 0 ? C.success : C.danger, margin: '2px 0' }}>{eur(b.db1)}<span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}> DB I</span></div>
                        <div style={{ fontSize: 11, color: C.muted }}>Umsatz {eur(b.umsatz)} · − Kosten {eur(b.kosten)}</div>
                      </div>
                      {b.mandate.length > 0 && conn(18)}
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 480 }}>
                        {b.mandate.map(m => (
                          <div key={m.mandant_id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', width: 145, textAlign: 'left' }}>
                            <div style={{ fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.name}>{m.name}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>{eur(m.umsatz)}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>direkt {m.direkt ? '−' + eur(m.direkt) : '—'}</div>
                            <div style={{ fontSize: 11, color: m.db >= 0 ? C.success : C.danger }}>DB {eur(m.db)}</div>
                          </div>
                        ))}
                        {b.mandate.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>keine Mandanten</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {tab === 'konten' && salden && (
        <div style={S.card}>
          <h3 style={{ marginTop: 0 }}>Saldenliste ({salden.von} – {salden.bis})</h3>
          <div style={{ marginBottom: 8 }}>{tabK.suche('Konto oder Name…', 220)}</div>
          <table style={S.table}>
            <thead><tr>{tabK.th('nr', 'Konto')}{tabK.th('soll', 'Soll', { textAlign: 'right' })}{tabK.th('haben', 'Haben', { textAlign: 'right' })}{tabK.th('saldo_natur', 'Saldo', { textAlign: 'right' })}</tr></thead>
            <tbody>
              {tabK.rows.map((k, i) => (
                <tr key={i} style={{ cursor: 'pointer' }} onClick={() => onKonto && onKonto(k.konto)} title="Kontenblatt im Journal anzeigen">
                  <td style={{ ...S.td, color: C.accent }}>{k.konto}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{k.soll ? eur(k.soll) : ''}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{k.haben ? eur(k.haben) : ''}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}><SaldoText wert={k.saldo_natur ?? k.saldo} natur={k.natur} /></td>
                </tr>
              ))}
              {salden.konten.length === 0 && <tr><td style={S.td} colSpan={4}><span style={{ color: C.muted }}>Keine Buchungen im Zeitraum.</span></td></tr>}
              <tr><td style={{ ...S.td, fontWeight: 700 }}>Summe</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{eur(salden.summe_soll)}</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{eur(salden.summe_haben)}</td><td style={S.td}></td></tr>
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

function Belege({ user }) {
  const [rows, setRows] = useState([]);
  const tabL = useTabelle(rows);
  const [filter, setFilter] = useState('offen');
  const [konten, setKonten] = useState({ aufwand: [], gegen: [] });
  const [sel, setSel] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [warn, setWarn] = useState([]);
  const fileRef = useRef();

  const [mandanten, setMandanten] = useState([]);
  const [kreditoren, setKreditoren] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get('/belege', { params: { status: filter || undefined } }); setRows(data); } catch { /* */ }
  }, [filter]);
  const loadKred = useCallback(async () => { try { const { data } = await api.get('/kreditoren'); setKreditoren(data); } catch { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/konten').then(({ data }) => setKonten(data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/mandanten', { params: { alle: 1 } }).then(({ data }) => setMandanten(data.filter(m => !m.ist_eigenes_lohnbuero))).catch(() => {}); }, []);
  useEffect(() => { loadKred(); }, [loadKred]);
  const [bezModal, setBezModal] = useState(null);
  async function neuerKreditor(prefill) {
    const name = window.prompt('Name des neuen Kreditors:', prefill || ''); if (!name || !name.trim()) return;
    try { const fd = new FormData(); fd.append('name', name.trim()); if (sel && sel.konto) fd.append('standard_konto', nr0(sel.konto)); const { data } = await api.post('/kreditoren', fd); await loadKred(); setSel(s => ({ ...s, kreditor_id: String(data.id), lieferant: data.name, gegenkonto: data.kreditor_nr })); setMsg(data.vorhanden ? `Kreditor „${data.name}“ gab es schon (${data.kreditor_nr}).` : `Kreditor ${data.kreditor_nr} „${data.name}“ angelegt.`); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
  }

  async function upload(e) {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true); setErr(''); setMsg(''); setWarn([]);
    try {
      const doppelt = [];
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        const { data } = await api.post('/belege', fd);
        if ((data.duplikate || []).length) doppelt.push(data);
      }
      setWarn(doppelt);
      setMsg(`${files.length} Beleg(e) hochgeladen und vorerkannt.`); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Upload fehlgeschlagen.'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }
  async function openBeleg(b) {
    setSel({ ...b, anlage_jahre: b.anlage_nd != null ? String(Math.round(b.anlage_nd / 12 * 10) / 10).replace('.', ',') : '' }); setPdfUrl(''); setErr('');
    try { const res = await api.get(b.ist_xml ? `/belege/${b.id}/ansicht` : `/belege/${b.id}/datei`, { responseType: 'blob' }); setPdfUrl(URL.createObjectURL(res.data)); } catch { /* */ }
  }
  async function ansichtOeffnen(b) {
    const w = window.open('', '_blank');
    try { const res = await api.get(`/belege/${b.id}/ansicht`, { responseType: 'blob' }); const u = URL.createObjectURL(res.data); if (w) w.location = u; } catch { if (w) w.close(); }
  }
  async function neuErkennen(b) {
    if (!window.confirm('Vorerkennung erneut ausführen? Die erfassten Felder werden überschrieben.')) return;
    try { const { data } = await api.post(`/belege/${b.id}/erkennen`); setSel(s => ({ ...data, anlage_jahre: s.anlage_jahre })); await load(); setMsg('Beleg neu erkannt.'); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Erkennung fehlgeschlagen.'); }
  }
  const belegFormData = (s) => {
    const fd = new FormData();
    ['lieferant', 'beleg_datum', 'rechnungsnummer', 'konto', 'gegenkonto', 'buchungstext', 'kreditor_id', 'kostenstelle', 'anlage_bezeichnung']
      .forEach(k => { if (s[k] !== null && s[k] !== undefined) fd.append(k, s[k]); });
    ['netto', 'ust_satz', 'ust_betrag', 'brutto'].forEach(k => { const v = parseZahl(s[k]); if (v != null) fd.append(k, v); });
    fd.append('steuerschluessel', s.steuerschluessel || '');
    if (kArt(s.konto) === 'A') {
      if (s.anlage_gwg !== null && s.anlage_gwg !== undefined) fd.append('anlage_gwg', s.anlage_gwg ? '1' : '0');
      const j = parseZahl(s.anlage_jahre);
      if (s.anlage_gwg) fd.append('anlage_nd', '0'); else if (j != null) fd.append('anlage_nd', String(Math.round(j * 12)));
    }
    return fd;
  };
  const field = (k, v) => setSel(s => ({ ...s, [k]: v }));
  async function save(close) {
    setBusy(true); setErr('');
    try {
      const { data } = await api.patch(`/belege/${sel.id}`, belegFormData(sel));
      await load(); if (close) setSel(null); else setSel(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  async function buchen() {
    setBusy(true); setErr('');
    try {
      await api.patch(`/belege/${sel.id}`, belegFormData(sel));
      let data;
      try { ({ data } = await api.post(`/belege/${sel.id}/buchen`)); }
      catch (ex) {
        if (ex.response?.status !== 409) throw ex;
        if (!window.confirm(`${ex.response.data.detail}\n\nTrotzdem buchen?`)) return;
        const fd = new FormData(); fd.append('trotz_duplikat', '1');
        ({ data } = await api.post(`/belege/${sel.id}/buchen`, fd));
      }
      setMsg(`Beleg gebucht: ${nr0(data.konto)} an ${data.gegenkonto}${data.kreditor_nr ? ' (' + data.lieferant + ')' : ''}.`); setSel(null); await load(); await loadKred();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Buchen fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  async function wiederOeffnen(b) { try { await api.post(`/belege/${b.id}/oeffnen`); await load(); if (sel) setSel(null); } catch { /* */ } }
  async function del(b) {
    if (!window.confirm(`Beleg ${b.lieferant || b.original_name || ''}${b.rechnungsnummer ? ' Nr. ' + b.rechnungsnummer : ''} löschen?`)) return;
    try { await api.delete(`/belege/${b.id}`); setSel(null); setWarn(w => w.filter(x => x.id !== b.id)); setMsg('Beleg gelöscht.'); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen.'); }
  }
  const dupZeile = (d) => `Beleg #${d.id} · ${d.lieferant || d.original_name || '—'}${d.rechnungsnummer ? ' · Nr. ' + d.rechnungsnummer : ''}${d.beleg_datum ? ' · ' + dDE(d.beleg_datum) : ''}${d.brutto != null ? ' · ' + fmtEur(d.brutto) : ''} · ${d.status === 'gebucht' ? (d.bezahlt ? 'gebucht, bezahlt' : 'gebucht') : 'offen'} (${d.gruende.join(', ')})`;

  const isImg = sel && /\.(png|jpe?g|webp|tif?f)$/i.test(sel.original_name || '');
  const lab = t => <label style={S.label}>{t}</label>;
  return (
    <div>
      {bezModal && <BelegBezahltModal beleg={bezModal} onClose={() => setBezModal(null)} onDone={async () => { setBezModal(null); await load(); }} />}
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Eingangsrechnungen hochladen, vorerkennen lassen und buchen: Aufwand an Kreditor (Verbindlichkeit), ausgeglichen durch die Zahlung im Bankabgleich. Offene Posten und Kreditorenkonten finden Sie im Reiter Kreditoren.</div>
      {msg && <div style={{ ...S.pathInfo, background: '#e9f7ef', color: C.success, marginBottom: 12 }}>{msg}</div>}
      {err && <div style={{ ...S.pathInfo, background: '#fdecea', color: C.danger, marginBottom: 12 }}>{err}</div>}
      {warn.length > 0 && (
        <div style={{ border: '1px solid #e0a800', background: '#fff8e6', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          <b>⚠ Möglicherweise doppelt erfasst</b>
          {warn.map(w => (
            <div key={w.id} style={{ marginTop: 6 }}>
              „{w.original_name}“ ({w.lieferant || '—'}{w.rechnungsnummer ? ', Nr. ' + w.rechnungsnummer : ''}) entspricht:
              {w.duplikate.map(d => <div key={d.id} style={{ marginLeft: 14, color: C.muted }}>{dupZeile(d)}</div>)}
              <div style={{ marginTop: 4 }}>
                <button style={{ ...S.btnSmall, background: C.danger }} onClick={() => del(w)}>Neuen Beleg wieder löschen</button>
                <button style={{ ...S.btnSmall, marginLeft: 6, background: '#eee', color: C.text }} onClick={() => setWarn(x => x.filter(y => y.id !== w.id))}>ist kein Duplikat</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={S.card}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.xml" onChange={upload} disabled={busy} style={{ fontSize: 13 }} />
          {busy && <span style={{ color: C.muted, fontSize: 13 }}>Verarbeite…</span>}
          <span style={{ flex: 1 }} />
          <select style={{ ...S.select, width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="offen">Offen</option><option value="gebucht">Gebucht</option><option value="">Alle</option>
          </select>
          {tabL.suche('Lieferant, Nr., Betrag…', 220)}
        </div>
        <div style={{ ...S.pathInfo, marginBottom: 12 }}>e-Rechnungen (ZUGFeRD/Factur-X im PDF oder XRechnung als XML) werden aus den strukturierten Daten gelesen, andere PDFs über den Textlayer, Fotos/Scans per OCR. Prüfen Sie die Vorschläge vor dem Buchen.</div>
        {rows.length === 0 ? <div style={S.empty}>Keine Belege in dieser Ansicht.</div> : (
          <table style={S.table}>
            <thead><tr>{tabL.th('beleg_datum', 'Datum')}{tabL.th('lieferant', 'Lieferant')}{tabL.th('rechnungsnummer', 'Rechnungsnr.')}{tabL.th('brutto', 'Brutto', { textAlign: 'right' })}{tabL.th('konto', 'Konto')}{tabL.th(b => (b.status !== 'gebucht' ? 'offen' : (b.op_offen === 0 || (b.op_offen == null && b.bezahlt_am)) ? 'bezahlt' : 'gebucht'), 'Status', null, 'status')}<th style={S.th}></th></tr></thead>
            <tbody>
              {tabL.rows.map(b => (
                <tr key={b.id}>
                  <td style={S.td}>{b.beleg_datum ? new Date(b.beleg_datum).toLocaleDateString('de-DE') : '—'}</td>
                  <td style={S.td}>{b.lieferant || '—'}{(b.duplikate || []).length > 0 && <span style={{ ...S.badge, marginLeft: 6, background: '#fff7e6', color: '#8a5a00' }} title={'Möglicherweise doppelt:\n' + b.duplikate.map(dupZeile).join('\n')}>doppelt?</span>}{b.erechnung && <span style={{ ...S.badge, marginLeft: 6, background: '#e9f7ef', color: C.success }} title={b.erechnung}>e-Rechnung</span>}{b.steuerschluessel && <span style={{ ...S.badge, marginLeft: 6 }} title="Reverse Charge – Steuer schuldet der Leistungsempfänger">§ 13b</span>}</td>
                  <td style={S.td}>{b.rechnungsnummer || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{b.brutto != null ? fmtEur(b.brutto) : '—'}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{b.konto ? <>{nr0(b.konto)} an {nr0(b.gegenkonto) || b.kreditor_nr || '—'}</> : <span style={{ color: C.muted }}>—</span>}</td>
                  <td style={S.td}>{b.status !== 'gebucht' ? <span style={{ ...S.badge, background: '#fff7e6', color: '#b8860b' }}>offen</span>
                    : (b.op_offen === 0 || (b.op_offen == null && b.bezahlt_am)) ? <span style={{ ...S.badge, background: '#e9f7ef', color: C.success }}>gebucht · bezahlt</span>
                      : <span style={{ ...S.badge }}>gebucht · offen {b.op_offen != null ? fmtEur(b.op_offen) : ''}</span>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}><button style={S.btnSmall} onClick={() => openBeleg(b)}>{b.status === 'gebucht' ? 'ansehen' : 'erfassen'}</button>
                    {b.status === 'gebucht' && b.op_offen > 0 && <button style={{ ...S.btnSmall, marginLeft: 6, background: '#eee', color: C.text }} title="Ohne Bankumsatz als bezahlt buchen (Kasse/privat/Bank)" onClick={() => setBezModal({ id: b.id })}>bezahlt…</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', zIndex: 1000 }} onClick={() => setSel(null)}>
          <div style={{ ...S.modalCard, width: 'min(1240px, 96vw)', height: '90vh', maxWidth: '98vw', maxHeight: '96vh', minWidth: 480, minHeight: 360, overflow: 'auto', resize: 'both' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ ...S.cardTitle, border: 'none', margin: 0 }}>Beleg erfassen</h2>
              <button onClick={() => setSel(null)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>
            {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
            {(sel.duplikate || []).length > 0 && (
              <div style={{ border: '1px solid #e0a800', background: '#fff8e6', borderRadius: 8, padding: '8px 12px', margin: '6px 0 10px', fontSize: 13 }}>
                <b>⚠ Dieser Beleg ist möglicherweise schon erfasst:</b>
                {sel.duplikate.map(d => <div key={d.id} style={{ marginLeft: 14 }}>{dupZeile(d)}</div>)}
                {sel.status !== 'gebucht' && <button style={{ ...S.btnSmall, background: C.danger, marginTop: 6 }} onClick={() => del(sel)}>Diesen Beleg löschen</button>}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 18, alignItems: 'stretch', minHeight: 'calc(90vh - 120px)' }}>
              <div style={{ background: C.bg, borderRadius: 8, overflow: 'auto', minHeight: 400 }}>
                {!pdfUrl ? <div style={S.empty}>Lade Vorschau…</div> : isImg
                  ? <img src={pdfUrl} alt={sel.original_name} style={{ width: '100%', display: 'block' }} />
                  : <iframe title="Beleg" src={pdfUrl} style={{ width: '100%', height: '100%', minHeight: 400, border: 'none' }} />}
              </div>
              <div style={{ overflowY: 'auto' }}>
                {sel.erechnung
                  ? <div style={{ ...S.pathInfo, background: '#e9f7ef', color: C.success, marginBottom: 10, padding: '6px 10px', borderRadius: 6 }}>✓ e-Rechnung ({sel.erechnung}) – Daten aus dem strukturierten XML übernommen.{!sel.ist_xml && <> <button type="button" style={{ ...S.btnSmall, padding: '2px 8px' }} onClick={() => ansichtOeffnen(sel)}>XML-Daten ansehen</button></>}</div>
                  : (sel.erkannt && <div style={{ ...S.pathInfo, background: '#eef5ff', color: C.accent, marginBottom: 10 }}>⚡ Automatisch vorerkannt (Text/OCR) – bitte prüfen.</div>)}
                {lab('Kreditor (= Lieferant) – steht im Haben')}
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={{ ...S.select, flex: 1 }} value={sel.kreditor_id || ''} onChange={e => { const id = e.target.value; const kr = kreditoren.find(k => String(k.id) === id); setSel(s => ({ ...s, kreditor_id: id, lieferant: kr ? kr.name : s.lieferant, gegenkonto: kr ? kr.kreditor_nr : (['1000', '1200', '1890', '1360'].includes(nr0(s.gegenkonto)) ? s.gegenkonto : ''), konto: (kr && kr.standard_konto && !s.konto) ? kr.standard_konto : s.konto })); }}>
                    <option value="">– Kreditor wählen –</option>
                    {kreditoren.map(k => <option key={k.id} value={String(k.id)}>{k.kreditor_nr ? `${k.kreditor_nr} · ` : ''}{k.name}</option>)}
                  </select>
                  <button type="button" style={{ ...S.btnSmall, whiteSpace: 'nowrap' }} onClick={() => neuerKreditor(sel.lieferant)}>+ neu</button>
                </div>
                {!sel.kreditor_id && sel.lieferant && (
                  <div style={{ ...S.pathInfo, marginTop: 4 }}>Erkannt: <strong>{sel.lieferant}</strong> – <button type="button" style={{ ...S.btnSmall, padding: '2px 8px' }} onClick={() => neuerKreditor(sel.lieferant)}>als Kreditor anlegen</button> · sonst wird er beim Buchen automatisch mit der nächsten freien Nummer angelegt.</div>
                )}
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>{lab('Belegdatum')}<input type="date" style={S.input} value={sel.beleg_datum || ''} onChange={e => field('beleg_datum', e.target.value)} /></div>
                  <div style={{ flex: 1 }}>{lab('Rechnungsnr. (= offener Posten)')}<input style={S.input} value={sel.rechnungsnummer || ''} onChange={e => field('rechnungsnummer', e.target.value)} /></div>
                </div>
                {['W', 'E', 'A'].includes(kArt(sel.konto)) && (<>
                  {lab('Kostenstelle')}
                  <select style={S.select} value={sel.kostenstelle || ''} onChange={e => field('kostenstelle', e.target.value)}>
                    <option value="">– wählen –</option>
                    <option value="gemein">Gemeinkosten (Büro)</option>
                    {mandanten.map(m => <option key={m.id} value={String(m.id)}>{m.name}{m.ist_dummy ? ' (Test)' : ''}</option>)}
                  </select>
                </>)}
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>{lab('Netto')}<input style={S.input} value={sel.netto ?? ''} onChange={e => field('netto', e.target.value)} /></div>
                  <div style={{ flex: 1 }}>{lab('USt %')}<input style={S.input} value={sel.ust_satz ?? ''} onChange={e => field('ust_satz', e.target.value)} /></div>
                  <div style={{ flex: 1 }}>{lab('USt-Betrag')}<input style={S.input} value={sel.ust_betrag ?? ''} onChange={e => field('ust_betrag', e.target.value)} /></div>
                  <div style={{ flex: 1 }}>{lab(sel.steuerschluessel ? 'Rechnungsbetrag' : 'Brutto')}<input style={S.input} value={sel.brutto ?? ''} onChange={e => field('brutto', e.target.value)} /></div>
                </div>
                {lab('Steuerschlüssel')}
                <SteuerschluesselWahl value={sel.steuerschluessel} onChange={v => setSel(s => ({ ...s, steuerschluessel: v, ...(v ? { ust_satz: '19', ust_betrag: '0', netto: s.brutto ?? s.netto } : {}) }))} />
                {sel.steuerschluessel && <div style={{ ...S.pathInfo, marginTop: 4, color: C.text }}>{RC_HINWEIS}{sel.erkannt && sel.erkannt.steuerschluessel ? ' (Hinweis „Reverse Charge“ auf der Rechnung erkannt)' : ''}</div>}
                {lab('Aufwands- oder Anlagekonto (Soll)')}
                <input style={S.input} list="konto-aufwand" value={sel.konto || ''} onChange={e => field('konto', e.target.value)} placeholder="tippen zum Suchen, z. B. zins, 4909, Laptop …" />
                <datalist id="konto-aufwand">{konten.aufwand.map(k => <option key={k.nr} value={`${k.nr} ${k.name}`} />)}</datalist>
                {kArt(sel.konto) === 'A' && (() => {
                  const num = v => parseZahl(v);
                  const netto = num(sel.netto) ?? (num(sel.brutto) != null ? num(sel.brutto) - (num(sel.ust_betrag) || 0) : null);
                  const gwgAuto = nr0(sel.konto) === '0480' || (netto != null && netto <= (konten.gwg_grenze || 800));
                  const gwg = sel.anlage_gwg == null ? gwgAuto : !!sel.anlage_gwg;
                  const ndStd = konten.anlage_nd ? konten.anlage_nd[nr0(sel.konto)] : null;
                  return (
                    <div style={{ marginTop: 10, border: `1px solid ${C.accent}`, borderRadius: 8, padding: '8px 12px', background: '#f5f9fc' }}>
                      <b style={{ fontSize: 13, color: C.primary }}>Zugang zum Anlagevermögen</b>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 2 }}>{lab('Bezeichnung')}<input style={S.input} value={sel.anlage_bezeichnung || ''} onChange={e => field('anlage_bezeichnung', e.target.value)} placeholder={sel.buchungstext || 'z. B. Laptop'} /></div>
                        <div style={{ flex: 1 }}>{lab('Nutzungsdauer (Jahre)')}<input style={S.input} value={gwg ? '0' : (sel.anlage_jahre || '')} disabled={gwg} onChange={e => field('anlage_jahre', e.target.value)} placeholder={ndStd != null ? String(Math.round(ndStd / 12 * 10) / 10).replace('.', ',') : '3'} /></div>
                      </div>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 6 }}><input type="checkbox" checked={gwg} onChange={e => field('anlage_gwg', e.target.checked)} /> Sofortabschreibung als GWG (bis {fmtEur(konten.gwg_grenze || 800)} netto){gwgAuto && sel.anlage_gwg == null ? ' – vorgeschlagen' : ''}</label>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Beim Buchen automatisch: Eintrag im Anlagenverzeichnis und {gwg ? `4855 Sofortabschreibung an ${nr0(sel.konto)}` : `jährliche AfA 4830 an ${nr0(sel.konto)}`}. Computer-Hardware/Software: 1 Jahr Nutzungsdauer zulässig.</div>
                    </div>
                  );
                })()}
                {lab('Haben: Kreditor – oder 1000 Kasse / 1200 Bank / 1890 privat, wenn sofort bezahlt')}
                <input style={S.input} list="konto-gegen" value={sel.gegenkonto || ''} onChange={e => field('gegenkonto', e.target.value)} placeholder="wird mit dem Kreditor gefüllt" />
                <datalist id="konto-gegen">
                  {kreditoren.filter(k => k.kreditor_nr).map(k => <option key={k.id} value={`${k.kreditor_nr} ${k.name}`} />)}
                  {(konten.finanz || []).map(k => <option key={k.nr} value={`${k.nr} ${k.name}`} />)}
                </datalist>
                {lab('Buchungstext')}<input style={S.input} value={sel.buchungstext || ''} onChange={e => field('buchungstext', e.target.value)} />
                {(() => {
                  const brutto = parseZahl(sel.brutto), satz = parseZahl(sel.ust_satz);
                  let ust = parseZahl(sel.ust_betrag), netto = parseZahl(sel.netto);
                  if (brutto != null && satz != null && ust == null) ust = Math.round((brutto - brutto / (1 + satz / 100)) * 100) / 100;
                  if (brutto != null && ust != null && netto == null) netto = Math.round((brutto - ust) * 100) / 100;
                  if (brutto == null) return null;
                  const rcSt = sel.steuerschluessel ? Math.round(brutto * (satz || 19)) / 100 : 0;
                  if (sel.steuerschluessel) { ust = 0; netto = brutto; }
                  const vstK = (konten.vst && satz != null) ? konten.vst[String(Math.round(satz))] : null;
                  const kNr = nr0(sel.konto) || '—';
                  const kr = kreditoren.find(k => String(k.id) === String(sel.kreditor_id || ''));
                  const gNr = nr0(sel.gegenkonto) || (kr ? kr.kreditor_nr : '');
                  const bar = ['1000', '1200', '1890', '1360'].includes(gNr);
                  return (
                    <div style={{ marginTop: 12, background: C.bg, borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                      <div style={{ fontWeight: 600, color: C.primary, marginBottom: 6 }}>Buchungsvorschau</div>
                      <table style={{ width: '100%', fontFamily: 'monospace', fontSize: 12.5 }}><tbody>
                        <tr><td>Soll {kNr} ({kArt(kNr) === 'A' ? 'Anlage' : 'Aufwand'})</td><td style={{ textAlign: 'right' }}>{fmtEur(netto != null ? netto : brutto)}</td></tr>
                        {ust ? <tr><td>Soll {vstK ? vstK.nr : '1576'} (Vorsteuer{satz ? ' ' + satz + ' %' : ''})</td><td style={{ textAlign: 'right' }}>{fmtEur(ust)}</td></tr> : null}
                        <tr style={{ borderTop: `1px solid ${C.border}` }}><td>Haben {gNr || '7xxxx (neuer Kreditor)'} {bar ? '(sofort bezahlt)' : `(Kreditor${kr ? ' ' + kr.name : sel.lieferant ? ' ' + sel.lieferant : ''})`}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtEur(brutto)}</td></tr>
                        {rcSt ? <tr style={{ borderTop: `1px solid ${C.border}` }}><td>Soll 1577 (Vorsteuer § 13b {satz || 19} %)</td><td style={{ textAlign: 'right' }}>{fmtEur(rcSt)}</td></tr> : null}
                        {rcSt ? <tr><td>Haben 1787 (Umsatzsteuer § 13b {satz || 19} %)</td><td style={{ textAlign: 'right' }}>{fmtEur(rcSt)}</td></tr> : null}
                      </tbody></table>
                      {!bar && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Verbindlichkeit auf dem Kreditorenkonto (Sammelkonto 1600 = Summe der Kreditoren, nicht zusätzlich gebucht); ausgeglichen durch die Zahlung (Kreditor an Bank) im Bankabgleich.</div>}
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                  {sel.status !== 'gebucht'
                    ? <>
                        <button disabled={busy} style={S.btnPrimary} onClick={buchen}>Buchen</button>
                        <button disabled={busy} style={{ ...S.btnSmall, background: '#eee', color: C.text, marginTop: 16 }} onClick={() => save(true)}>Speichern</button>
                        <button disabled={busy} style={{ ...S.btnSmall, background: '#eee', color: C.text, marginTop: 16 }} onClick={() => neuErkennen(sel)}>Neu erkennen</button>
                      </>
                    : <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginTop: 16 }} onClick={() => wiederOeffnen(sel)}>Buchung aufheben</button>}
                  <span style={{ flex: 1 }} />
                  <button style={{ ...S.btnSmall, background: C.danger }} onClick={() => del(sel)}>Löschen</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Extras({ user, onChanged, onGoMandant }) {
  const [alle, setAlle] = useState([]);
  const [eigeneIds, setEigeneIds] = useState([]);
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const [a, e] = await Promise.all([
        api.get('/mandanten', { params: { alle: 1 } }),
        api.get('/mandanten'),
      ]);
      setAlle(a.data.filter(m => !m.ist_eigenes_lohnbuero));
      setEigeneIds(e.data.map(m => m.id));
    } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function vertreten(m) {
    setMsg('');
    try {
      const fd = new FormData(); fd.append('mandant_id', m.id);
      await api.post('/extras/vertretung', fd);
      setMsg(`Vertretungszugriff auf „${m.name}" aktiviert – der Mandant erscheint jetzt in Ihrer Auswahl.`);
      await load(); if (onChanged) await onChanged();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }
  async function beenden(m) {
    setMsg('');
    try { await api.delete(`/extras/vertretung/${m.id}`); await load(); if (onChanged) await onChanged(); }
    catch (ex) { setMsg(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }
  function oeffnen(m) { if (onGoMandant) onGoMandant(m.id); }

  const gefiltert = alle.filter(m => !q || m.name.toLowerCase().includes(q.toLowerCase()));
  const status = (m) => m.vertretung ? 'vertretung' : (eigeneIds.includes(m.id) ? 'eigen' : 'fremd');

  return (
    <div>
      <h1 style={{ fontSize: 22, color: C.primary, margin: '0 0 4px' }}>Extras</h1>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Zusätzliche Werkzeuge für das Lohnbüro.</div>
      {msg && <div style={{ ...S.pathInfo, background: '#e9f7ef', color: C.success, marginBottom: 12 }}>{msg}</div>}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Mandat vertreten</h2>
        <div style={S.pathInfo}>Ihre Mandantenauswahl oben zeigt nur Ihre eigenen Mandate. Für eine <strong>Vertretung</strong> können Sie hier vorübergehend Zugriff auf ein fremdes Mandat freischalten – es erscheint dann zusätzlich in Ihrer Auswahl. Beenden Sie die Vertretung wieder, wenn sie nicht mehr nötig ist.</div>
        <input style={{ ...S.input, maxWidth: 320, marginBottom: 12 }} placeholder="Mandant suchen…" value={q} onChange={e => setQ(e.target.value)} />
        <table style={S.table}>
          <thead><tr><th style={S.th}>Mandant</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: 'right' }}>Aktion</th></tr></thead>
          <tbody>
            {gefiltert.map(m => {
              const st = status(m);
              return (
                <tr key={m.id}>
                  <td style={S.td}>{m.name}{m.ist_dummy ? ' (Test)' : ''}</td>
                  <td style={S.td}>
                    {st === 'eigen' && <span style={{ ...S.badge, background: '#e9f7ef', color: C.success }}>Ihr Mandat</span>}
                    {st === 'vertretung' && <span style={{ ...S.badge, background: '#fff7e6', color: '#b8860b' }}>Vertretung aktiv</span>}
                    {st === 'fremd' && <span style={{ ...S.badge, color: C.muted }}>fremdes Mandat</span>}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    {st === 'fremd' && <button style={S.btnSmall} onClick={() => vertreten(m)}>Vertretung öffnen</button>}
                    {st === 'vertretung' && <>
                      <button style={S.btnSmall} onClick={() => oeffnen(m)}>öffnen</button>
                      <button style={{ ...S.btnSmall, marginLeft: 6, background: '#eee', color: C.text }} onClick={() => beenden(m)}>Vertretung beenden</button>
                    </>}
                    {st === 'eigen' && <button style={S.btnSmall} onClick={() => oeffnen(m)}>öffnen</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MeineRechnungen({ user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { (async () => {
    try { const { data } = await api.get('/meine-rechnungen'); setRows(data); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  })(); }, []);

  async function download(r) {
    try {
      const res = await api.get(`/rechnungen/${r.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = r.rechnungsnummer + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('PDF konnte nicht geladen werden'); }
  }

  const offen = rows.filter(r => r.status === 'offen');
  const summeOffen = offen.reduce((s, r) => s + (r.brutto || 0), 0);
  const statusBadge = (r) => {
    const map = { bezahlt: ['#e8f4ea', C.success], storniert: ['#eceff3', C.muted], offen: ['#fdeccd', '#8a6d3b'] };
    const [bg, col] = map[r.status] || map.offen;
    return <span style={{ ...S.badge, background: bg, color: col }}>{r.status}</span>;
  };
  const zahlart = (r) => {
    if (r.status !== 'offen') return '—';
    if (r.lastschrift) return <span style={{ color: C.accent }}>wird per SEPA-Lastschrift eingezogen</span>;
    return <span style={{ color: '#8a6d3b' }}>bitte überweisen{r.zahlungsziel ? ` bis ${fmtDateDE(r.zahlungsziel)}` : ''}</span>;
  };

  return (
    <div>
      {err && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{err}</div>}
      {offen.length > 0 && (
        <div style={{ ...S.card, background: '#fff7e6', border: '1px solid #ffe1a8' }}>
          <div style={{ color: '#8a6d3b', fontSize: 14 }}>
            Sie haben <strong>{offen.length}</strong> offene Rechnung{offen.length === 1 ? '' : 'en'} über zusammen <strong>{fmtEur(summeOffen)}</strong>.
          </div>
        </div>
      )}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Meine Rechnungen</h2>
        {loading ? <div style={S.empty}>Lade…</div> : rows.length === 0 ? <div style={S.empty}>Noch keine Rechnungen vorhanden.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Nummer</th><th style={S.th}>Datum</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Betrag (brutto)</th>
                <th style={S.th}>Status</th><th style={S.th}>Zahlung</th><th style={S.th}></th>
              </tr></thead>
              <tbody>{rows.map(r => (
                <tr key={r.id} style={r.status === 'offen' ? { background: '#fffdf7' } : null}>
                  <td style={S.td}>{r.rechnungsnummer}{r.storno_von_id ? ' (Storno)' : ''}{r.erechnung ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8f4ea', color: C.success }} title="Enthält eingebettete e-Rechnung (ZUGFeRD/EN16931)">E-Rechnung</span> : null}{r.herkunft === 'import' ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8eef5', color: C.accent }} title="Außerhalb des Portals erstellt und hochgeladen">hochgeladen</span> : null}</td>
                  <td style={S.td}>{fmtDateDE(r.rechnungsdatum)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(r.brutto)}</td>
                  <td style={S.td}>{statusBadge(r)}</td>
                  <td style={S.td}>{zahlart(r)}</td>
                  <td style={S.td}>{r.pdf_filename ? <button style={S.btnSmall} onClick={() => download(r)}>PDF</button> : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div style={S.pathInfo}>Bei Lastschrift wird der Betrag automatisch von Ihrem Konto eingezogen – Sie müssen nichts veranlassen.</div>
      </div>
    </div>
  );
}

function ArbeitgeberStammblatt({ mandantId, onSaved, saveLabel }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [missing, setMissing] = useState([]);   // Pflichtfelder, die beim Speichern leer waren → rot markiert

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get('/arbeitgeberdaten', { params: mandantId ? { mandant_id: mandantId } : {} });
      setF(data || {});
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); setF({}); }
  })(); }, [mandantId]);

  const REQUIRED = ['firmenname', 'rechtsform', 'anschrift', 'steuernummer', 'ap_name', 'ap_email', 'lohnsteuer_zeitraum', 'fibu_kontenrahmen'];
  // Betriebs-/Unternehmensnummer: Nummer eintragen ODER angeben, dass sie (mit Unterstützung) beantragt wird
  const NR_OPTIONEN = {
    betriebsnummer: [['vorhanden', 'Betriebsnummer ist vorhanden'], ['beantrage_selbst', 'Ich beantrage die Betriebsnummer selbst'], ['unterstuetzung', 'Ich benötige Unterstützung bei der Beantragung der Betriebsnummer']],
    unternehmensnummer: [['vorhanden', 'Unternehmensnummer ist vorhanden'], ['beantrage_selbst', 'Ich beantrage die Unternehmensnummer selbst'], ['unterstuetzung', 'Ich benötige Unterstützung bei der Beantragung der Unternehmensnummer']],
  };
  const nrStatus = (k) => f[`${k}_status`] || (String(f[k] || '').trim() ? 'vorhanden' : '');
  const LABELS = { firmenname: 'Firmenbezeichnung', rechtsform: 'Rechtsform', anschrift: 'Anschrift', betriebsnummer: 'Betriebsnummer', unternehmensnummer: 'Unternehmensnummer', steuernummer: 'Steuernummer', ap_name: 'Ansprechpartner', ap_email: 'E-Mail', lohnsteuer_zeitraum: 'Anmeldezeitraum Lohnsteuer', fibu_kontenrahmen: 'FIBU-Kontenrahmen', fibu_kontenrahmen_sonstige: 'Kontenrahmen (Sonstige)' };
  const HINTS = {
    betriebsnummer: '8-stellige Nummer vom Betriebsnummern-Service der Bundesagentur für Arbeit – Grundlage aller Meldungen zur Sozialversicherung. Noch keine vorhanden? Dann wählen Sie, ob Sie sie selbst beantragen oder ob wir Sie dabei unterstützen.',
    unternehmensnummer: '15-stellig, seit 2024 für die gesetzliche Unfallversicherung (vergibt die Berufsgenossenschaft bzw. Unfallkasse). Noch keine vorhanden? Dann wählen Sie, ob Sie sie selbst beantragen oder ob wir Sie dabei unterstützen.',
    fibu_kontenrahmen: 'Kontenrahmen Ihrer Finanzbuchhaltung, an die wir die Lohnbuchungen übergeben.',
    steuernummer: 'Steuernummer des Unternehmens beim Finanzamt – für die Lohnsteueranmeldung.',
    uv_pin: 'PIN für die elektronische UV-Jahresmeldung (von der Berufsgenossenschaft).',
    abrechnung_ab: 'Ab welchem Monat sollen wir die Lohnabrechnung übernehmen?',
    lohnsteuer_zeitraum: 'Monatlich (Vorjahr > 5.000 €), vierteljährlich (> 1.080–5.000 €), jährlich (≤ 1.080 €).',
    ap_email: 'E-Mail des Hauptansprechpartners.',
    rechtsform: 'z. B. GmbH, GbR, e. K., Einzelunternehmen.',
  };
  const Hint = ({ k }) => HINTS[k] ? <span title={HINTS[k]} style={{ cursor: 'help', color: C.accent, marginLeft: 6, fontSize: 11, border: `1px solid ${C.accent}`, borderRadius: 999, padding: '0 6px', userSelect: 'none' }}>?</span> : null;
  const lab = (label, key) => <label style={S.label}>{label}{REQUIRED.includes(key) && <span style={{ color: C.danger }}> *</span>}<Hint k={key} /></label>;

  const set = (k, v) => { setF(p => ({ ...p, [k]: v })); if (missing.includes(k) && String(v || '').trim()) setMissing(ms => ms.filter(x => x !== k)); };
  const fieldStyle = (base, key) => missing.includes(key) ? { ...base, border: `1.5px solid ${C.danger}`, background: '#fdecea' } : base;
  const T = (label, key, ph) => <div style={S.field}>{lab(label, key)}<input id={`agf-${key}`} style={fieldStyle(S.input, key)} value={f[key] || ''} onChange={e => set(key, e.target.value)} placeholder={ph || ''} /></div>;
  const NrBlock = (key, label, laenge) => {
    const st = nrStatus(key);
    const rot = missing.includes(key);
    return (
      <div id={`agf-${key}`} style={{ flex: '1 1 320px', border: rot ? `1.5px solid ${C.danger}` : `1px solid ${C.border}`, background: rot ? '#fdecea' : C.white, borderRadius: 6, padding: '8px 12px', marginTop: 12 }}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{label} ({laenge}-stellig)<span style={{ color: C.danger }}> *</span><Hint k={key} /></div>
        {NR_OPTIONEN[key].map(([v, l]) => (
          <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', margin: '3px 0' }}>
            <input type="radio" name={`st-${key}`} checked={st === v} onChange={() => { setF(p => ({ ...p, [`${key}_status`]: v, ...(v !== 'vorhanden' ? { [key]: '' } : {}) })); if (v !== 'vorhanden') setMissing(ms => ms.filter(x => x !== key)); }} /> {l}
          </label>
        ))}
        {st === 'vorhanden' && <input style={{ ...S.input, marginTop: 6 }} value={f[key] || ''} onChange={e => set(key, e.target.value)} placeholder={`${laenge}-stellige Nummer`} />}
        {st === 'unterstuetzung' && <div style={{ fontSize: 12, color: C.accent, marginTop: 4 }}>Gern – wir melden uns dazu bei Ihnen.</div>}
      </div>
    );
  };
  const CB = (label, key) => <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginTop: 6 }}><input type="checkbox" checked={!!f[key]} onChange={e => set(key, e.target.checked)} /> {label}</label>;

  async function save() {
    setErr(''); setMsg('');
    const fehlt = REQUIRED.filter(k => !String(f[k] || '').trim());
    for (const k of ['betriebsnummer', 'unternehmensnummer']) {
      const st = nrStatus(k);
      if (!st || (st === 'vorhanden' && !String(f[k] || '').trim())) fehlt.push(k);
    }
    if (f.fibu_kontenrahmen === 'sonstige' && !String(f.fibu_kontenrahmen_sonstige || '').trim()) fehlt.push('fibu_kontenrahmen_sonstige');
    setMissing(fehlt);
    if (fehlt.length) {
      setErr('Bitte die rot markierten Pflichtfelder ausfüllen: ' + fehlt.map(k => LABELS[k] || k).join(', '));
      const el = document.getElementById(`agf-${fehlt[0]}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy(true);
    try {
      const body = { ...f, betriebsnummer_status: nrStatus('betriebsnummer'), unternehmensnummer_status: nrStatus('unternehmensnummer') };
      if (body.fibu_kontenrahmen !== 'sonstige') body.fibu_kontenrahmen_sonstige = '';
      if (mandantId) body.mandant_id = mandantId;
      await api.put('/arbeitgeberdaten', body);
      setMsg('Stammblatt gespeichert.');
      if (onSaved) onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  if (!f) return <div style={S.card}><div style={S.empty}>Lade…</div></div>;

  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 12, color: C.muted }}>Mit <span style={{ color: C.danger }}>*</span> markierte Felder sind Pflicht. Bei den <span style={{ color: C.accent, border: `1px solid ${C.accent}`, borderRadius: 999, padding: '0 5px', fontSize: 11 }}>?</span>-Symbolen finden Sie Erläuterungen (Maus darüber halten).</div>
        <h2 style={{ ...S.cardTitle, marginTop: 10 }}>Identifikation</h2>
        <div style={{ ...S.row, alignItems: 'stretch' }}>{NrBlock('betriebsnummer', 'Betriebsnummer', 8)}{NrBlock('unternehmensnummer', 'Unternehmensnummer', 15)}</div>
        <div style={S.row}>{T('Firmenbezeichnung', 'firmenname')}{T('Rechtsform', 'rechtsform')}</div>
        {lab('Anschrift', 'anschrift')}
        <textarea id="agf-anschrift" style={fieldStyle(S.textarea, 'anschrift')} value={f.anschrift || ''} onChange={e => set('anschrift', e.target.value)} placeholder={'Straße Nr.\nPLZ Ort'} />
        <div style={S.row}>{T('Gesetzl. Vertreter / Geschäftsführung', 'geschaeftsfuehrung')}{T('Website', 'website')}<div style={S.field}><label style={S.label}>Abrechnung ab</label><input type="month" style={S.input} value={f.abrechnung_ab || ''} onChange={e => set('abrechnung_ab', e.target.value)} /></div></div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Kontakt & Bank</h2>
        <div style={S.row}>{T('Telefon', 'ap_telefon')}{T('Handy', 'ap_handy')}{T('E-Mail', 'ap_email')}{T('Ansprechpartner', 'ap_name')}</div>
        {CB('Einverständnis zum elektronischen Datenaustausch wird erteilt', 'einwilligung_datenaustausch')}
        <div style={{ ...S.row, marginTop: 8 }}>{T('Bankbezeichnung', 'bank')}{T('Sitz der Bank', 'bank_sitz')}{T('IBAN', 'iban')}{T('BIC', 'bic')}</div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Finanzamt</h2>
        <div style={S.row}>{T('Steuernummer', 'steuernummer')}{T('Finanzamt-Nr.', 'finanzamt_nr')}{T('Finanzamt (Bezeichnung)', 'finanzamt_bez')}</div>
        <label style={S.label}>Anschrift Finanzamt</label>
        <textarea style={{ ...S.textarea, minHeight: 50 }} value={f.finanzamt_anschrift || ''} onChange={e => set('finanzamt_anschrift', e.target.value)} />
        <div style={S.field}>{lab('Anmeldezeitraum Lohnsteuer', 'lohnsteuer_zeitraum')}
          <select id="agf-lohnsteuer_zeitraum" style={fieldStyle(S.select, 'lohnsteuer_zeitraum')} value={f.lohnsteuer_zeitraum || ''} onChange={e => set('lohnsteuer_zeitraum', e.target.value)}>
            <option value="">—</option><option value="monatlich">monatlich</option><option value="vierteljaehrlich">vierteljährlich</option><option value="jaehrlich">jährlich</option>
          </select>
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Unfallversicherung</h2>
        <div style={S.row}>{T('Berufsgenossenschaft', 'bg_name')}{T('UV-/Mitgliedsnummer', 'uv_mitgliedsnummer')}{T('PIN UV-Jahresmeldung', 'uv_pin')}</div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Abrechnung</h2>
        <div style={S.row}>
          {T('Wöchentliche Arbeitszeit (Vollzeit)', 'wochenarbeitszeit')}
          <div style={S.field}><label style={S.label}>Tag der Lohnzahlung</label>
            <select style={S.select} value={f.tag_lohnzahlung || ''} onChange={e => set('tag_lohnzahlung', e.target.value)}>
              <option value="">—</option><option value="arbeitsmonat">Arbeitsmonat</option><option value="folgemonat">Folgemonat</option>
            </select>
          </div>
          {T('Tag des Monats', 'tag_lohnzahlung_tag')}
          <div style={S.field}><label style={S.label}>Festbetragsaufteilung</label>
            <select style={S.select} value={f.festbetrag_aufteilung || ''} onChange={e => set('festbetrag_aufteilung', e.target.value)}>
              <option value="">—</option><option value="30tage">30-Tage</option><option value="kalendertage">Kalendertage</option><option value="arbeitstage">Arbeitstage</option>
            </select>
          </div>
        </div>
        <div style={S.row}>{T('Tarif', 'tarif')}{T('Branche', 'branche')}{T('Kurzarbeitergeld-Nr.', 'kug_nr')}</div>
        {CB('Sofortmeldepflichtige Branche (§ 28a SGB IV)', 'sofortmeldepflicht')}
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Buchhaltung & Hinweise</h2>
        <div style={S.row}>
          <div style={S.field}>{lab('FIBU-Kontenrahmen', 'fibu_kontenrahmen')}
            <select id="agf-fibu_kontenrahmen" style={fieldStyle(S.select, 'fibu_kontenrahmen')} value={f.fibu_kontenrahmen || ''} onChange={e => set('fibu_kontenrahmen', e.target.value)}>
              <option value="">—</option><option value="SKR03">SKR 03</option><option value="SKR04">SKR 04</option><option value="keine">Keine Buchführungspflicht</option><option value="sonstige">Sonstige</option>
            </select>
          </div>
          {f.fibu_kontenrahmen === 'sonstige' && <div style={S.field}><label style={S.label}>Welcher Kontenrahmen?<span style={{ color: C.danger }}> *</span></label>
            <input id="agf-fibu_kontenrahmen_sonstige" style={fieldStyle(S.input, 'fibu_kontenrahmen_sonstige')} value={f.fibu_kontenrahmen_sonstige || ''} onChange={e => set('fibu_kontenrahmen_sonstige', e.target.value)} placeholder="z. B. eigener Kontenrahmen, SKR 49" /></div>}
        </div>
        {CB('Kostenstellen erforderlich', 'kostenstellen')}
        <label style={{ ...S.label, marginTop: 8 }}>Sonstige Hinweise</label>
        <textarea style={S.textarea} value={f.hinweise || ''} onChange={e => set('hinweise', e.target.value)} />
      </div>

      <div style={S.card}>
        <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={save}>{busy ? 'Speichert…' : (saveLabel || 'Stammblatt speichern')}</button>
        {msg && <div style={S.okMsg}>{msg}</div>}
        {err && <div style={S.err}>{err}</div>}
      </div>
    </div>
  );
}

function OnbSignPanel({ typ, label, onSigned, ausgefuellt }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasSig = useRef(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');

  function pos(e) { const c = canvasRef.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; }
  function start(e) { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }
  function move(e) { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = C.primary; ctx.stroke(); hasSig.current = true; }
  function end() { drawing.current = false; }
  function clearSig() { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); hasSig.current = false; }

  const fuellt = ausgefuellt ?? (typ === 'vertrag' || typ === 'vollmacht');
  async function ladeVorschau() {
    try {
      const res = await api.get(`/mein-onboarding/vorschau/${typ}`, { responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
    } catch (ex) {
      let msg = 'PDF konnte nicht geladen werden';
      try { const t = await ex.response?.data?.text?.(); const j = t ? JSON.parse(t) : null; if (j?.detail) msg = j.detail; } catch { /* Blob ohne JSON */ }
      setErr(msg);
    }
  }
  async function viewPdf() {
    setErr('');
    if (pdfUrl) { setPdfUrl(''); return; }  // Toggle: erneut klicken schließt die Vorschau
    await ladeVorschau();
  }
  // Ausgefüllte Dokumente (Vertrag, Vollmacht) gleich anzeigen – der Mandant sieht vor der Unterschrift, was er unterschreibt
  useEffect(() => { if (fuellt) ladeVorschau(); }, [typ]);
  async function sign() {
    setErr('');
    if (!consent) { setErr('Bitte bestätigen'); return; }
    if (!hasSig.current) { setErr('Bitte unterschreiben'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('typ', typ); fd.append('signatur', canvasRef.current.toDataURL('image/png')); fd.append('einwilligung', 'true');
      await api.post('/mein-onboarding/dokument-signieren', fd);
      if (onSigned) onSigned();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Signieren fehlgeschlagen'); setBusy(false); }
  }

  return (
    <div style={{ ...S.card }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: C.primary }}>{label}</h3>
        <button style={S.btnSmall} onClick={viewPdf}>📄 {pdfUrl ? 'Vorschau schließen' : 'Dokument ansehen'}</button>
      </div>
      {pdfUrl && <iframe title={label} src={pdfUrl} style={{ width: '100%', height: 480, border: `1px solid ${C.border}`, borderRadius: 6, marginTop: 10 }} />}
      {fuellt && <div style={{ ...S.pathInfo, marginTop: 8 }}>Das Dokument ist mit Ihren Angaben aus dem Stammblatt ausgefüllt. Stimmt etwas nicht, korrigieren Sie es bitte im Stammblatt (Knopf unten). Ihre Unterschrift wird direkt in das Unterschriftsfeld übernommen, Ort und Datum werden automatisch eingetragen.</div>}
      <label style={{ ...S.label, marginTop: 10 }}>Unterschrift (mit Maus oder Finger)</label>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, background: '#fff', display: 'inline-block', touchAction: 'none' }}>
        <canvas ref={canvasRef} width={520} height={150} style={{ display: 'block', maxWidth: '100%', cursor: 'crosshair' }}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      </div>
      <div><button type="button" style={{ ...S.btnSmall, background: C.muted, marginTop: 6 }} onClick={clearSig}>Unterschrift löschen</button></div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
        <span>Ich habe das Dokument gelesen, die Angaben sind richtig, und ich unterzeichne es rechtsverbindlich elektronisch.</span>
      </label>
      {err && <div style={S.err}>{err}</div>}
      <button style={{ ...S.btnPrimary, marginTop: 10 }} disabled={busy} onClick={sign}>{busy ? 'Wird signiert…' : 'Signieren'}</button>
    </div>
  );
}

function OnboardingWizard({ user, onDone }) {
  const [step, setStep] = useState(user.onboarding_status || 'stammblatt');
  const [docs, setDocs] = useState([]);
  const [wizErr, setWizErr] = useState('');
  const loadDocs = useCallback(async () => {
    try { const { data } = await api.get('/mein-onboarding/dokumente'); setDocs(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (step === 'dokumente' || step === 'vertrag') loadDocs(); }, [step, loadDocs]);

  async function setStatus(s) { const fd = new FormData(); fd.append('status', s); await api.post('/mein-onboarding/status', fd); }
  async function finish() {
    setWizErr('');
    try { await setStatus('fertig'); onDone(); }
    catch (ex) { setWizErr(ex.response?.data?.detail || 'Abschließen fehlgeschlagen'); setStep('vertrag'); }
  }
  async function zuUnterlagen() {
    setWizErr('');
    try { await loadDocs(); await setStatus('unterlagen'); setStep('unterlagen'); }
    catch (ex) { setWizErr(ex.response?.data?.detail || 'Weiter fehlgeschlagen'); await loadDocs(); setStep('vertrag'); }
  }
  function abmelden() {
    localStorage.removeItem('token'); localStorage.removeItem('user');
    window.location.reload();
  }

  const Stepper = () => {
    const steps = [['zugang', '1 Zugang ✓'], ['stammblatt', '2 Stammblatt'], ['dokumente', `3 ${dokTitel}`], ['vertrag', '4 Dienstvertrag'], ['unterlagen', '5 Unterlagen & Mitarbeiter']];
    return (
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {steps.map(([k, l]) => <span key={k} style={{ ...S.badge, background: step === k ? C.primary : (k === 'zugang' ? '#e8f4ea' : '#e8eef5'), color: step === k ? '#fff' : (k === 'zugang' ? C.success : C.accent), padding: '4px 10px' }}>{l}</span>)}
      </div>
    );
  };

  const dokTypen = (keys) => docs.filter(d => keys.includes(d.typ));
  const stepDoks = dokTypen(['vollmacht', 'datenschutz']).filter(d => d.vorhanden);   // ohne Vorlage → gar nicht anzeigen
  const offeneDoks = stepDoks.filter(d => !d.signiert);
  const dokTitel = stepDoks.some(d => d.typ === 'datenschutz') ? 'Vollmacht & Datenschutz' : 'Vollmacht';
  async function zumStammblatt() { await setStatus('stammblatt'); setStep('stammblatt'); }
  const ZurueckBtn = () => <button style={{ ...S.btnGhost, color: C.accent, borderColor: C.accent, marginTop: 0 }} onClick={zumStammblatt}>← Stammblatt korrigieren</button>;
  const vertragDoc = docs.find(d => d.typ === 'vertrag');

  return (
    <div style={{ ...S.app, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <header style={S.header}>
        <div style={S.brand}>Die Lohnkanzlei – Einrichtung</div>
        <div style={S.userBox}><span>{user.username}</span></div>
      </header>
      <main style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
        <Stepper />

        {step === 'stammblatt' && (
          <>
            <div style={{ ...S.card, background: '#eef4fb', border: `1px solid ${C.accent}` }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>Schritt 2: Arbeitgeber-Stammblatt</h2>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Bitte prüfen und ergänzen Sie Ihre Firmendaten. Was wir bereits von Ihnen wissen, ist vorausgefüllt. Aus diesen Angaben werden im nächsten Schritt Vollmacht und Dienstvertrag automatisch ausgefüllt.</div>
            </div>
            <ArbeitgeberStammblatt onSaved={async () => { await setStatus('dokumente'); setStep('dokumente'); }} saveLabel="Speichern & weiter" />
          </>
        )}

        {step === 'dokumente' && (
          <>
            <div style={{ ...S.card, background: '#eef4fb', border: `1px solid ${C.accent}` }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>Schritt 3: {dokTitel}</h2>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Mit der Vollmacht dürfen wir Sie gegenüber Krankenkassen, Rentenversicherung und Finanzamt vertreten. Sie ist bereits mit Ihren Firmendaten ausgefüllt – bitte prüfen und unterschreiben.</div>
            </div>
            {stepDoks.map(d => d.signiert
              ? <div key={d.typ} style={S.card}><span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>{d.label}: unterschrieben ✓</span></div>
              : <OnbSignPanel key={d.typ} typ={d.typ} label={d.label} ausgefuellt={d.ausgefuellt} onSigned={loadDocs} />)}
            <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <ZurueckBtn />
              <button style={{ ...S.btnPrimary, marginTop: 0, opacity: offeneDoks.length ? 0.6 : 1 }} disabled={offeneDoks.length > 0}
                      onClick={async () => { await setStatus('vertrag'); setStep('vertrag'); }}>Weiter zum Vertrag</button>
            </div>
          </>
        )}

        {step === 'vertrag' && (
          <>
            <div style={{ ...S.card, background: '#eef4fb', border: `1px solid ${C.accent}` }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>Schritt 4: Dienstvertrag</h2>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Der Vertrag ist mit Ihren Firmendaten ausgefüllt und enthält als Anlage die Vereinbarung zur Auftragsverarbeitung (Datenschutz). Bitte ansehen und unten unterschreiben – danach können Sie noch Ihre Unterlagen und Mitarbeiter hochladen.</div>
            </div>
            {vertragDoc && vertragDoc.signiert
              ? <div style={S.card}><span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>Vertrag signiert ✓</span></div>
              : <OnbSignPanel typ="vertrag" label="Dienstvertrag" ausgefuellt onSigned={zuUnterlagen} />}
            {wizErr && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{wizErr}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {!(vertragDoc && vertragDoc.signiert) && <ZurueckBtn />}
              <button style={{ ...S.btnPrimary, marginTop: 0, opacity: (vertragDoc && vertragDoc.signiert) ? 1 : 0.55 }}
                      disabled={!(vertragDoc && vertragDoc.signiert)} onClick={zuUnterlagen}>Weiter</button>
              {!(vertragDoc && vertragDoc.signiert) && <span style={{ fontSize: 12, color: C.muted }}>Erst nach Unterschrift des Vertrags möglich.</span>}
            </div>
          </>
        )}

        {step === 'unterlagen' && (
          <>
            <div style={{ ...S.card, background: '#eef4fb', border: `1px solid ${C.accent}` }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>Schritt 5: Unterlagen & Mitarbeiter</h2>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Geschafft – Ihr Vertrag ist unterschrieben. Damit wir direkt starten können, laden Sie hier gern Ihre bisherigen Lohnunterlagen und Ihre Mitarbeiterliste hoch. Beides können Sie auch später im Portal unter „Erfassen“ erledigen.</div>
            </div>
            <div style={S.card}><h3 style={{ margin: '0 0 8px', fontSize: 15, color: C.primary }}>Ihre bisherigen Unterlagen</h3><UnterlagenHochladen /></div>
            <div style={S.card}><h3 style={{ margin: '0 0 8px', fontSize: 15, color: C.primary }}>Ihre Mitarbeiter</h3><MaListeHochladen /></div>
            {wizErr && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{wizErr}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={finish}>Einrichtung abschließen & zum Portal</button>
              <span style={{ fontSize: 12, color: C.muted }}>Sie können auch ohne Uploads abschließen.</span>
            </div>
          </>
        )}

        <div style={{ marginTop: 12 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={abmelden}>Abmelden & später fortsetzen</button></div>
      </main>
    </div>
  );
}

const PREIS_ARTEN = [['monatspauschale', 'Monatsabrechnung je Mitarbeiter'], ['anlage', 'Anlage eines Mitarbeiters'], ['austritt', 'Austritt'],
  ['statuswechsel', 'Statuswechsel'], ['wiedereintritt', 'Wiedereintritt']];

function Settings({ user, mandanten, mandantId }) {
  const isStaff = user.role === 'admin' || user.role === 'staff';
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [lexKey, setLexKey] = useState('');
  const [onbEmail, setOnbEmail] = useState('');
  const [onbMsg, setOnbMsg] = useState('');
  const [preiseOffen, setPreiseOffen] = useState(true);

  const ready = isStaff ? !!mandantId : true;

  const load = useCallback(async () => {
    if (!ready) { setS(null); return; }
    setLoading(true); setErr('');
    try {
      const params = isStaff ? { mandant_id: mandantId } : {};
      const { data } = await api.get('/mandant-settings', { params });
      setS(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, isStaff, mandantId]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true); setErr(''); setOkMsg('');
    try {
      const fd = new FormData();
      if (isStaff) fd.append('mandant_id', mandantId);
      fd.append('notify_email', s.notify_email || '');
      fd.append('notify_new_document', s.notify_new_document ? 'true' : 'false');
      fd.append('notify_new_ticket', s.notify_new_ticket ? 'true' : 'false');
      fd.append('notify_frist_24h', s.notify_frist_24h ? 'true' : 'false');
      fd.append('rechnung_anschrift', s.rechnung_anschrift || '');
      fd.append('rechnung_ust_idnr', s.rechnung_ust_idnr || '');
      fd.append('rechnung_email', s.rechnung_email || '');
      fd.append('zuschlaege_aktiv', s.zuschlaege_aktiv ? 'true' : 'false');
      fd.append('zuschlag_sonntag', String(s.zuschlag_sonntag ?? ''));
      fd.append('zuschlag_nacht', String(s.zuschlag_nacht ?? ''));
      fd.append('zuschlag_feiertag', String(s.zuschlag_feiertag ?? ''));
      fd.append('nacht_start', s.nacht_start || '20:00');
      fd.append('nacht_ende', s.nacht_ende || '06:00');
      if (isStaff) {
        fd.append('bundesland', s.bundesland || '');
        fd.append('lohnsteuer_zeitraum', s.lohnsteuer_zeitraum || 'monatlich');
        fd.append('lastschrift', s.lastschrift ? 'true' : 'false');
        fd.append('kundennummer', s.kundennummer || '');
        fd.append('ist_eigenes_lohnbuero', s.ist_eigenes_lohnbuero ? 'true' : 'false');
        if (lexKey.trim()) fd.append('lexware_api_key', lexKey.trim());
        fd.append('lexware_contact_id', s.lexware_contact_id || '');
        fd.append('karte_aktiv', s.karte_aktiv ? 'true' : 'false');
        fd.append('preise', JSON.stringify(s.preise || {}));
        fd.append('preise_offen', preiseOffen ? '1' : '0');
      }
      const { data: gespeichert } = await api.put('/mandant-settings', fd);
      if (gespeichert && gespeichert.preise) setS(x => ({ ...x, preise: gespeichert.preise }));
      setLexKey('');
      setOkMsg('Einstellungen gespeichert.');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  const toggle = (key, label) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 14, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!s[key]} onChange={e => setS({ ...s, [key]: e.target.checked })} />
      {label}
    </label>
  );

  return (
    <div>
      {!ready ? (
        <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen.</div></div>
      ) : loading || !s ? (
        <div style={S.card}><div style={S.empty}>Lade…</div></div>
      ) : (
        <div style={S.card}>
          <h2 style={S.cardTitle}>E-Mail-Benachrichtigungen</h2>
          <label style={S.label}>Benachrichtigungs-E-Mail</label>
          <input style={S.input} value={s.notify_email || ''} onChange={e => setS({ ...s, notify_email: e.target.value })} placeholder="ihre@firma.de" />
          <div style={{ marginTop: 16 }}>
            {toggle('notify_new_document', 'Neue Dokumente vom Lohnbüro (Sammel-Mail, höchstens alle 2 Stunden)')}
            {toggle('notify_new_ticket', 'Neues Ticket vom Lohnbüro')}
            {toggle('notify_frist_24h', 'Frist, die in 24 Stunden abläuft')}
          </div>
          <div style={S.pathInfo}>Hinweis: Das Lohnbüro erhält automatisch eine E-Mail, sobald vom Mandanten ein Dokument oder Ticket eingeht.</div>

          <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Rechnungsdaten</h2>
          <div style={S.pathInfo}>Diese Anschrift erscheint als Empfänger auf Rechnungen des Lohnbüros. Leer lassen = Firmenname und Anschrift aus dem Arbeitgeber-Stammblatt werden verwendet.</div>
          <label style={S.label}>Rechnungsanschrift (mehrzeilig)</label>
          <textarea style={{ ...S.textarea, minHeight: 60 }} value={s.rechnung_anschrift || ''} onChange={e => setS({ ...s, rechnung_anschrift: e.target.value })} placeholder={'Firma\nStraße\nPLZ Ort'} />
          <div style={S.row}>
            <div style={S.field}><label style={S.label}>USt-IdNr. (optional)</label><input style={S.input} value={s.rechnung_ust_idnr || ''} onChange={e => setS({ ...s, rechnung_ust_idnr: e.target.value })} /></div>
            <div style={S.field}><label style={S.label}>Rechnungs-E-Mail (für Versand)</label><input style={S.input} value={s.rechnung_email || ''} onChange={e => setS({ ...s, rechnung_email: e.target.value })} /></div>
          </div>

          {isStaff && (
            <>
              <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Steuer & SV-Fälligkeiten</h2>
              <div style={S.pathInfo}>Bundesland bestimmt die Feiertagsverschiebung der Lohnsteuer-Frist (§108 AO). Der Anmeldungszeitraum richtet sich nach der Lohnsteuer des Vorjahres: monatlich (&gt; 5.000 €), vierteljährlich (&gt; 1.080–5.000 €), jährlich (≤ 1.080 €).</div>
              <div style={S.row}>
                <div style={S.field}><label style={S.label}>Bundesland</label>
                  <select style={S.select} value={s.bundesland || ''} onChange={e => setS({ ...s, bundesland: e.target.value })}>
                    <option value="">— wählen —</option>
                    {[['BW','Baden-Württemberg'],['BY','Bayern'],['BE','Berlin'],['BB','Brandenburg'],['HB','Bremen'],['HH','Hamburg'],['HE','Hessen'],['MV','Mecklenburg-Vorpommern'],['NI','Niedersachsen'],['NW','Nordrhein-Westfalen'],['RP','Rheinland-Pfalz'],['SL','Saarland'],['SN','Sachsen'],['ST','Sachsen-Anhalt'],['SH','Schleswig-Holstein'],['TH','Thüringen']].map(([k,n]) => <option key={k} value={k}>{n}</option>)}
                  </select>
                </div>
                <div style={S.field}><label style={S.label}>Lohnsteuer-Anmeldungszeitraum</label>
                  <select style={S.select} value={s.lohnsteuer_zeitraum || 'monatlich'} onChange={e => setS({ ...s, lohnsteuer_zeitraum: e.target.value })}>
                    <option value="monatlich">monatlich</option>
                    <option value="vierteljaehrlich">vierteljährlich</option>
                    <option value="jaehrlich">jährlich</option>
                  </select>
                </div>
              </div>

              <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Abrechnung</h2>
              <div style={{ ...S.field, maxWidth: 280, marginBottom: 10 }}>
                <label style={S.label}>Mandantennummer (erscheint auf den Rechnungen)</label>
                <input style={S.input} value={s.kundennummer || ''} onChange={e => setS({ ...s, kundennummer: e.target.value })} placeholder="z.B. 10042 – wie im bisherigen System" />
              </div>
              <h3 style={{ fontSize: 14, color: C.primary, margin: '14px 0 6px' }}>Preise für diesen Mandanten (netto)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {PREIS_ARTEN.map(([art, label]) => {
                  const wert = (s.preise || {})[art];
                  return (
                    <div key={art}>
                      <label style={S.label}>{label}</label>
                      <input style={S.input} inputMode="decimal" value={wert == null ? '' : String(wert).replace('.', ',')}
                        placeholder={`Standard ${fmtEur((s.preise_standard || {})[art])}`}
                        onChange={e => setS({ ...s, preise: { ...(s.preise || {}), [art]: e.target.value === '' ? null : e.target.value } })} />
                    </div>
                  );
                })}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 4px', fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={preiseOffen} onChange={e => setPreiseOffen(e.target.checked)} />
                Geänderte Preise auch für bereits erfasste, noch nicht abgerechnete Leistungen übernehmen
              </label>
              <div style={{ ...S.pathInfo, marginBottom: 10 }}>Leeres Feld = Standardpreis. Gilt für alle neu erfassten Leistungen dieses Mandanten; bereits abgerechnete Leistungen bleiben unverändert.</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!s.lastschrift} onChange={e => setS({ ...s, lastschrift: e.target.checked })} />
                SEPA-Lastschriftmandat liegt vor – Rechnungen werden eingezogen
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!s.ist_eigenes_lohnbuero} onChange={e => setS({ ...s, ist_eigenes_lohnbuero: e.target.checked })} />
                Eigenes Lohnbüro (eigene Mitarbeiter) – ohne Rechnungstellung &amp; Leistungserfassung
              </label>
              <div style={S.pathInfo}>„Eigenes Lohnbüro" blendet für diesen Mandanten die Leistungserfassung und Rechnungen aus; Mitarbeiter, SV-/Lohnsteuer-Fälligkeiten und Dokumente bleiben wie gewohnt.</div>

              <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Mitarbeiter-Karte</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!s.karte_aktiv} onChange={e => setS({ ...s, karte_aktiv: e.target.checked })} />
                Mitarbeiter-Karte für diesen Mandanten aktivieren
              </label>
              <div style={S.pathInfo}>Standardmäßig deaktiviert (Ausnahme). Aktiviert eine interaktive Karte mit den Wohnorten der Mitarbeiter. Adressen werden dabei einmalig per OpenStreetMap geocodiert – nur bei Bedarf einschalten.</div>

              <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Lexware Office (Beleg-Schnittstelle)</h2>
              <div style={S.pathInfo}>API-Key des Lexware-Office-Kontos dieses Mandanten. Damit können Belege aus „Dokumente" per Klick an Lexware übertragen werden. Status: {s.lexware_konfiguriert ? <span style={{ color: C.success }}>hinterlegt ✓</span> : <span style={{ color: C.muted }}>nicht hinterlegt</span>}</div>
              <input style={S.input} type="password" value={lexKey} onChange={e => setLexKey(e.target.value)} placeholder={s.lexware_konfiguriert ? 'Zum Ändern neuen Key eingeben…' : 'Lexware API-Key einfügen'} autoComplete="off" />
              <label style={{ ...S.label, marginTop: 8 }}>Lexware Kunden-/Kontakt-ID (für Ausgangsrechnungen ans Büro-Lexware)</label>
              <input style={S.input} value={s.lexware_contact_id || ''} onChange={e => setS({ ...s, lexware_contact_id: e.target.value })} placeholder="optional – Kontakt-ID dieses Mandanten in Lexware" />
              <div style={S.pathInfo}>Ist die Kontakt-ID gesetzt, wird die Ausgangsrechnung in Lexware diesem Kunden zugeordnet; sonst wird die Rechnungsanschrift übergeben.</div>

              <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Onboarding</h2>
              <div style={S.pathInfo}>Sendet dem Mandanten einen Einladungslink: er richtet selbst Benutzer, Arbeitgeber-Stammblatt und Unterlagen ein.</div>
              <div style={S.row}>
                <div style={{ ...S.field, flex: '2 1 280px' }}><label style={S.label}>E-Mail des Mandanten</label>
                  <input style={S.input} value={onbEmail} onChange={e => setOnbEmail(e.target.value)} placeholder="kontakt@firma.de" />
                </div>
                <div><button type="button" style={{ ...S.btnPrimary, marginTop: 0 }} onClick={async () => {
                  setOnbMsg(''); setErr('');
                  if (!onbEmail.trim()) { setErr('Bitte E-Mail angeben'); return; }
                  try { const fd = new FormData(); fd.append('email', onbEmail.trim()); await api.post(`/mandanten/${mandantId}/onboarding-start`, fd); setOnbMsg(`Einladung an ${onbEmail.trim()} gesendet.`); setOnbEmail(''); }
                  catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen'); }
                }}>Einladung senden</button></div>
              </div>
              {onbMsg && <div style={S.okMsg}>{onbMsg}</div>}
            </>
          )}

          <h2 style={{ ...S.cardTitle, marginTop: 18 }}>Zuschläge</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!s.zuschlaege_aktiv} onChange={e => setS({ ...s, zuschlaege_aktiv: e.target.checked })} />
            Zuschläge (Sonntag / Nacht / Feiertag) für diesen Mandanten aktivieren
          </label>
          {s.zuschlaege_aktiv && (
            <>
              <div style={S.row}>
                <div style={S.field}><label style={S.label}>Sonntag (%)</label><input style={S.input} value={s.zuschlag_sonntag ?? ''} onChange={e => setS({ ...s, zuschlag_sonntag: e.target.value })} placeholder="z.B. 25" /></div>
                <div style={S.field}><label style={S.label}>Nacht (%)</label><input style={S.input} value={s.zuschlag_nacht ?? ''} onChange={e => setS({ ...s, zuschlag_nacht: e.target.value })} placeholder="z.B. 25" /></div>
                <div style={S.field}><label style={S.label}>Feiertag (%)</label><input style={S.input} value={s.zuschlag_feiertag ?? ''} onChange={e => setS({ ...s, zuschlag_feiertag: e.target.value })} placeholder="z.B. 125" /></div>
              </div>
              <div style={S.row}>
                <div style={S.field}><label style={S.label}>Nachtfenster von</label><input type="time" style={S.input} value={s.nacht_start || '20:00'} onChange={e => setS({ ...s, nacht_start: e.target.value })} /></div>
                <div style={S.field}><label style={S.label}>Nachtfenster bis</label><input type="time" style={S.input} value={s.nacht_ende || '06:00'} onChange={e => setS({ ...s, nacht_ende: e.target.value })} /></div>
              </div>
              <div style={S.pathInfo}>Prozentsätze gelten für Arbeitsstunden, die auf Sonntag/Feiertag fallen bzw. im Nachtfenster liegen. Erfasst werden die Stunden über „Erfassen → Arbeitsschichten"; die Zuschläge erscheinen dort monatlich je Mitarbeiter.</div>
            </>
          )}

          <div style={{ marginTop: 12 }}>
            <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={save}>{busy ? 'Speichert…' : 'Speichern'}</button>
          </div>
          {okMsg && <div style={S.okMsg}>{okMsg}</div>}
          {err && <div style={S.err}>{err}</div>}
        </div>
      )}
    </div>
  );
}

const LEISTUNG_ART_LABEL = { anlage: 'Anlage', austritt: 'Austritt', statuswechsel: 'Statuswechsel', wiedereintritt: 'Wiedereintritt', monatspauschale: 'Monatspauschale', manuell: 'Manuell' };
function fmtEur(n) { return (n == null ? '—' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'); }

// Bestehende (außerhalb des Portals erstellte) Ausgangsrechnung hochladen und einem Mandanten zuordnen.
// Ohne mandantId (z.B. aus der Buchführung) wird der Mandant im Dialog gewählt.
function RechnungImportDialog({ mandantId, mandanten, onClose, onDone }) {
  const zahl = (v) => { const n = parseFloat(String(v ?? '').replace(/\s|€/g, '').replace(',', '.')); return isNaN(n) ? null : n; };
  const [mid, setMid] = useState(mandantId ? String(mandantId) : '');
  const [imp, setImp] = useState({ nummer: '', datum: new Date().toISOString().slice(0, 10), netto: '', ust: '19', brutto: '', bruttoManuell: false,
                                   von: '', bis: '', bezahlt: false, bezahltAm: '', beschreibung: '', file: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get('/kanzlei').then(({ data }) => setImp(p => ({ ...p, ust: data.kleinunternehmer ? '0' : String(data.ust_satz ?? 19).replace('.', ',') }))).catch(() => {});
  }, []);
  const brutto = (() => {
    if (imp.bruttoManuell) return zahl(imp.brutto);
    const n = zahl(imp.netto), u = zahl(imp.ust);
    return (n != null && u != null) ? Math.round(n * (1 + u / 100) * 100) / 100 : null;
  })();
  async function submit() {
    setErr('');
    if (!mid) { setErr('Bitte den Mandanten auswählen, dem die Rechnung gehört.'); return; }
    if (!imp.nummer.trim() || !imp.datum || zahl(imp.netto) == null) { setErr('Rechnungsnummer, Datum und Netto-Betrag sind erforderlich.'); return; }
    if (!imp.file) { setErr('Bitte die Rechnung als PDF auswählen.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('mandant_id', mid);
      fd.append('rechnungsnummer', imp.nummer.trim());
      fd.append('rechnungsdatum', imp.datum);
      fd.append('netto', String(zahl(imp.netto)));
      if (zahl(imp.ust) != null) fd.append('ust_satz', String(zahl(imp.ust)));
      if (imp.bruttoManuell && zahl(imp.brutto) != null) fd.append('brutto', String(zahl(imp.brutto)));
      if (imp.von) fd.append('zeitraum_von', imp.von);
      if (imp.bis) fd.append('zeitraum_bis', imp.bis);
      if (imp.beschreibung.trim()) fd.append('beschreibung', imp.beschreibung.trim());
      if (imp.bezahlt) { fd.append('bezahlt', 'true'); if (imp.bezahltAm) fd.append('bezahlt_am', imp.bezahltAm); }
      fd.append('file', imp.file);
      const { data } = await api.post('/rechnungen/import', fd);
      if (onDone) await onDone(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Hochladen fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  const auswahl = (mandanten || []).filter(m => !m.ist_eigenes_lohnbuero);
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 620, maxWidth: '96%' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Bestehende Rechnung hochladen</h2>
        <div style={S.pathInfo}>Für Ausgangsrechnungen, die außerhalb des Portals erstellt wurden (z.B. im bisherigen Programm). Die Rechnung wird dem Mandanten zugeordnet und erscheint in seiner Rechnungsliste, bei den offenen Posten/im Bankabgleich und in der Buchführung (Erlöse der Kostenstelle = Mandant).</div>
        {!mandantId && (
          <div style={S.field}><label style={S.label}>Mandant *</label>
            <select style={S.select} value={mid} onChange={e => setMid(e.target.value)}>
              <option value="">— Mandant wählen —</option>
              {auswahl.map(m => <option key={m.id} value={m.id}>{m.kundennummer ? `${m.kundennummer} · ` : ''}{m.name}</option>)}
            </select>
          </div>
        )}
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Rechnungsnummer *</label><input style={S.input} value={imp.nummer} onChange={e => setImp({ ...imp, nummer: e.target.value })} placeholder="wie auf der Rechnung" /></div>
          <div style={S.field}><label style={S.label}>Rechnungsdatum *</label><input type="date" style={S.input} value={imp.datum} onChange={e => setImp({ ...imp, datum: e.target.value })} /></div>
        </div>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Netto (€) *</label><input style={S.input} value={imp.netto} onChange={e => setImp({ ...imp, netto: e.target.value })} placeholder="z.B. 126,00" /></div>
          <div style={S.field}><label style={S.label}>USt-Satz (%)</label><input style={S.input} value={imp.ust} onChange={e => setImp({ ...imp, ust: e.target.value })} /></div>
          <div style={S.field}><label style={S.label}>Brutto (€)</label>
            <input style={{ ...S.input, background: imp.bruttoManuell ? C.white : C.bg }} value={imp.bruttoManuell ? imp.brutto : (brutto != null ? String(brutto.toFixed(2)).replace('.', ',') : '')}
              onChange={e => setImp({ ...imp, brutto: e.target.value, bruttoManuell: true })} placeholder="wird berechnet" />
          </div>
        </div>
        <div style={S.pathInfo}>Brutto wird aus Netto + USt berechnet. Weicht der Betrag auf der Rechnung um Cent ab, einfach das Brutto-Feld überschreiben.</div>
        <div style={S.row}>
          <div style={S.field}><label style={S.label}>Leistungszeitraum von (optional)</label><input type="date" style={S.input} value={imp.von} onChange={e => setImp({ ...imp, von: e.target.value })} /></div>
          <div style={S.field}><label style={S.label}>bis (optional)</label><input type="date" style={S.input} value={imp.bis} onChange={e => setImp({ ...imp, bis: e.target.value })} /></div>
        </div>
        <label style={S.label}>Beschreibung (optional)</label>
        <input style={S.input} value={imp.beschreibung} onChange={e => setImp({ ...imp, beschreibung: e.target.value })} placeholder="z.B. Lohnabrechnung Juni 2026" />
        <label style={{ ...S.label, marginTop: 8 }}>Rechnung als PDF *</label>
        <input type="file" accept="application/pdf,.pdf" style={S.input} onChange={e => setImp({ ...imp, file: (e.target.files && e.target.files[0]) || null })} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={imp.bezahlt} onChange={e => setImp({ ...imp, bezahlt: e.target.checked })} /> Rechnung ist bereits bezahlt
        </label>
        {imp.bezahlt && (
          <div style={{ ...S.field, maxWidth: 220 }}><label style={S.label}>Bezahlt am (optional)</label><input type="date" style={S.input} value={imp.bezahltAm} onChange={e => setImp({ ...imp, bezahltAm: e.target.value })} /></div>
        )}
        {err && <div style={S.err}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy} onClick={submit}>{busy ? 'Lädt hoch…' : 'Rechnung übernehmen'}</button>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

function Leistungen({ user, mandanten, mandantId }) {
  const [open, setOpen] = useState([]);
  const [archived, setArchived] = useState([]);
  const [rechnungen, setRechnungen] = useState([]);
  const [maList, setMaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [mp, setMp] = useState({ bezeichnung: '', anzahl: '1', einzelpreis: '', mitarbeiter_id: '' });
  const [showImp, setShowImp] = useState(false);   // Dialog „Bestehende Rechnung hochladen"
  const [showRechDlg, setShowRechDlg] = useState(false);
  const [rechNr, setRechNr] = useState('');
  const [rechBusy, setRechBusy] = useState(false);
  const [rechLs, setRechLs] = useState(false);
  const [rechLsDatum, setRechLsDatum] = useState('');

  const ready = !!mandantId;

  const load = useCallback(async () => {
    if (!ready) { setOpen([]); setArchived([]); setRechnungen([]); return; }
    setLoading(true); setErr('');
    try {
      const [oRes, aRes, rRes, maRes] = await Promise.all([
        api.get('/leistungen', { params: { mandant_id: mandantId, archiviert: 'false' } }),
        api.get('/leistungen', { params: { mandant_id: mandantId, archiviert: 'true' } }),
        api.get('/rechnungen', { params: { mandant_id: mandantId } }).catch(() => ({ data: [] })),
        api.get('/mitarbeiter', { params: { mandant_id: mandantId } }).catch(() => ({ data: [] })),
      ]);
      setOpen(oRes.data); setArchived(aRes.data); setRechnungen(rRes.data); setMaList(maRes.data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, [ready, mandantId]);
  useEffect(() => { load(); }, [load]);

  async function openRechnungDlg() {
    setErr(''); setOkMsg('');
    if (open.length === 0) return;
    let nr = '', ls = false, lsD = '';
    try { const { data } = await api.get('/naechste-rechnungsnummer', { params: { mandant_id: mandantId } }); nr = data.nummer || ''; ls = !!data.lastschrift; lsD = data.sepa_datum || ''; } catch { /* Vorschlag optional */ }
    setRechNr(nr); setRechLs(ls); setRechLsDatum(lsD); setShowRechDlg(true);
  }
  async function rechnungErstellen() {
    setErr(''); setRechBusy(true);
    try {
      const fd = new FormData(); fd.append('mandant_id', mandantId);
      if (rechNr.trim()) fd.append('rechnungsnummer', rechNr.trim());
      if (rechLs && rechLsDatum) fd.append('lastschrift_datum', rechLsDatum);
      const { data } = await api.post('/rechnungen', fd);
      setShowRechDlg(false);
      setOkMsg(`Rechnung ${data.rechnungsnummer} erstellt (${fmtEur(data.brutto)} brutto).`);
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Rechnung fehlgeschlagen'); }
    finally { setRechBusy(false); }
  }

  async function downloadRechnung(r) {
    try {
      const res = await api.get(`/rechnungen/${r.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = r.rechnungsnummer + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('PDF konnte nicht geladen werden'); }
  }
  async function sendRechnung(r) {
    if (!window.confirm(`Rechnung ${r.rechnungsnummer} per E-Mail an den Mandanten senden?`)) return;
    try { const { data } = await api.post(`/rechnungen/${r.id}/senden`); setOkMsg(`Versendet an ${data.to}.`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Versand fehlgeschlagen'); }
  }
  async function markBezahlt(r) {
    try { const fd = new FormData(); fd.append('status', r.status === 'bezahlt' ? 'offen' : 'bezahlt'); await api.patch(`/rechnungen/${r.id}`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }
  async function pdfNeu(r) {
    if (!window.confirm(`PDF der Rechnung ${r.rechnungsnummer} mit der aktuellen Empfängeranschrift und Mandantennummer neu erzeugen?\n\nDie Rechnungsnummer und alle Beträge bleiben gleich, das bisherige PDF wird aufbewahrt. Nur als Ausnahme nutzen (z.B. fehlende Anschrift) – bereits versendete Rechnungen bitte dem Mandanten erneut zusenden.`)) return;
    try { await api.post(`/rechnungen/${r.id}/pdf-neu`); setOkMsg(`PDF ${r.rechnungsnummer} neu erzeugt.`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Neu erzeugen fehlgeschlagen'); }
  }
  async function stornoRechnung(r) {
    if (!window.confirm(`Rechnung ${r.rechnungsnummer} stornieren? Es wird eine Stornorechnung erzeugt (unveränderbar).`)) return;
    try { const { data } = await api.post(`/rechnungen/${r.id}/storno`); setOkMsg(`Storno ${data.rechnungsnummer} erstellt.`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Storno fehlgeschlagen'); }
  }
  async function lexwareRechnung(r) {
    if (!window.confirm(`Rechnung ${r.rechnungsnummer} als Ausgangsrechnung an Lexware Office übertragen?`)) return;
    setErr(''); setOkMsg('');
    try { await api.post(`/rechnungen/${r.id}/lexware`); setOkMsg(`Rechnung ${r.rechnungsnummer} an Lexware übertragen.`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Lexware-Übertragung fehlgeschlagen'); }
  }

  const maName = (id) => { const m = maList.find(x => x.id === id); return m ? `${m.vorname} ${m.nachname}` : (id ? `MA #${id}` : '—'); };
  const summeOpen = open.reduce((s, l) => s + (l.betrag_netto || 0), 0);
  const summeArch = archived.reduce((s, l) => s + (l.betrag_netto || 0), 0);

  async function reaktivieren(l) {
    if (!window.confirm(`Position „${l.bezeichnung}" wieder abrechenbar machen (Archivierung rückgängig)?`)) return;
    try { const fd = new FormData(); fd.append('archiviert', 'false'); await api.patch(`/leistungen/${l.id}/archivieren`, fd); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  }

  async function delOpen(l) {
    if (!window.confirm(`Position „${l.bezeichnung}" löschen?`)) return;
    try { await api.delete(`/leistungen/${l.id}`); await load(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  }

  const zahl = (v) => { const n = parseFloat(String(v ?? '').replace(/\s|€/g, '').replace(',', '.')); return isNaN(n) ? null : n; };
  const mpSumme = (() => { const a = zahl(mp.anzahl), e = zahl(mp.einzelpreis); return (a != null && e != null) ? Math.round(a * e * 100) / 100 : null; })();

  async function addManual(e) {
    e.preventDefault(); setErr('');
    const a = zahl(mp.anzahl), ep = zahl(mp.einzelpreis);
    if (!mp.bezeichnung.trim() || ep == null) { setErr('Bezeichnung und Einzelpreis erforderlich'); return; }
    if (a == null || a <= 0) { setErr('Die Anzahl muss größer als 0 sein'); return; }
    try {
      const fd = new FormData();
      fd.append('mandant_id', mandantId);
      fd.append('bezeichnung', mp.bezeichnung);
      fd.append('menge', String(a));
      fd.append('einzelpreis', String(ep));
      if (mp.mitarbeiter_id) fd.append('mitarbeiter_id', mp.mitarbeiter_id);
      await api.post('/leistungen', fd);
      setMp({ bezeichnung: '', anzahl: '1', einzelpreis: '', mitarbeiter_id: '' });
      await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
  }

  if (!ready) return <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen, um die Leistungserfassung anzuzeigen.</div></div>;

  const row = (l, archiv) => (
    <tr key={l.id}>
      <td style={S.td}>{fmtDateDE(l.leistungsdatum)}</td>
      <td style={S.td}><span style={S.badge}>{LEISTUNG_ART_LABEL[l.art] || l.art}</span></td>
      <td style={S.td}>{l.bezeichnung}{(l.menge != null && l.einzelpreis != null && Number(l.menge) !== 1) ? <span style={{ color: C.muted, fontSize: 12 }}> · {String(l.menge).replace('.', ',')} × {fmtEur(l.einzelpreis)}</span> : null}</td>
      <td style={S.td}>{l.mitarbeiter_id ? maName(l.mitarbeiter_id) : '—'}</td>
      <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtEur(l.betrag_netto)}</td>
      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
        {archiv
          ? <button style={S.btnSmall} onClick={() => reaktivieren(l)}>Rückgängig</button>
          : <button style={S.btnDanger} onClick={() => delOpen(l)}>✕</button>}
      </td>
    </tr>
  );

  return (
    <div>
      {showRechDlg && (
        <div style={S.modalOverlay} onClick={() => setShowRechDlg(false)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <h2 style={S.cardTitle}>Rechnung erstellen</h2>
            <div style={S.pathInfo}><strong>{open.length}</strong> Position(en) · <strong>{fmtEur(summeOpen)}</strong> netto werden abgerechnet und archiviert.</div>
            <label style={S.label}>Rechnungsnummer</label>
            <input style={S.input} value={rechNr} onChange={e => setRechNr(e.target.value)} placeholder="z.B. RE-2026-0042" />
            <div style={S.pathInfo}>Vorbefüllt mit dem nächsten automatischen Vorschlag. Für noch nicht migrierte Mandanten kannst du hier deine eigene, fortlaufende Nummer eintragen. Die Nummer muss eindeutig sein.</div>
            {rechLs && (
              <div style={{ background: '#eef4fb', border: `1px solid ${C.accent}`, borderRadius: 6, padding: 10, marginTop: 4 }}>
                <label style={{ ...S.label, marginTop: 0 }}>SEPA-Lastschrift – Einzugsdatum</label>
                <input type="date" style={S.input} value={rechLsDatum} onChange={e => setRechLsDatum(e.target.value)} />
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Lastschrift ist aktiv. Standard: heute + 14 Tage (nächster Bankarbeitstag) – gesetzeskonforme Vorabankündigungsfrist. Datum erscheint auf der Rechnung.</div>
              </div>
            )}
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Die Rechnung wird automatisch als <strong>E-Rechnung (ZUGFeRD)</strong> erzeugt: sichtbares PDF mit eingebettetem gesetzeskonformem Rechnungs-XML.</div>
            {err && <div style={S.err}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={rechBusy || !rechNr.trim()} onClick={rechnungErstellen}>{rechBusy ? 'Erstellt…' : 'Rechnung endgültig erstellen'}</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setShowRechDlg(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
      {err && !showRechDlg && <div style={{ ...S.card, color: C.danger, fontSize: 13 }}>{err}</div>}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Offene Leistungen</h2>
          <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={open.length === 0} onClick={openRechnungDlg}>Rechnung erstellen</button>
        </div>
        {okMsg && <div style={S.okMsg}>{okMsg}</div>}
        {loading ? <div style={S.empty}>Lade…</div> : open.length === 0 ? <div style={S.empty}>Keine offenen Leistungen</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Datum</th><th style={S.th}>Art</th><th style={S.th}>Bezeichnung</th>
              <th style={S.th}>Mitarbeiter</th><th style={{ ...S.th, textAlign: 'right' }}>Netto</th><th style={S.th}></th>
            </tr></thead>
            <tbody>
              {open.map(l => row(l, false))}
              <tr><td style={{ ...S.td, fontWeight: 700 }} colSpan={4}>Summe offen</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtEur(summeOpen)}</td><td style={S.td}></td></tr>
            </tbody>
          </table>
        )}
      </div>

      {showImp && (
        <RechnungImportDialog mandantId={mandantId} mandanten={mandanten} onClose={() => setShowImp(false)}
          onDone={async (data) => { setShowImp(false); setOkMsg(`Rechnung ${data.rechnungsnummer} übernommen (${fmtEur(data.brutto)} brutto).`); await load(); }} />
      )}

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Rechnungen</h2>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={() => { setErr(''); setOkMsg(''); setShowImp(true); }}>⬆ Bestehende Rechnung hochladen</button>
        </div>
        {rechnungen.length === 0 ? <div style={S.empty}>Noch keine Rechnungen</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Nummer</th><th style={S.th}>Datum</th><th style={{ ...S.th, textAlign: 'right' }}>Netto</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Brutto</th><th style={S.th}>Status</th><th style={S.th}>Aktionen</th>
              </tr></thead>
              <tbody>{rechnungen.map(r => (
                <tr key={r.id}>
                  <td style={S.td}>{r.rechnungsnummer}{r.storno_von_id ? ' (Storno)' : ''}{r.erechnung ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8f4ea', color: C.success }} title="Enthält eingebettete e-Rechnung (ZUGFeRD/EN16931)">E-Rechnung</span> : null}{r.herkunft === 'import' ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8eef5', color: C.accent }} title="Außerhalb des Portals erstellt und hochgeladen">hochgeladen</span> : null}</td>
                  <td style={S.td}>{fmtDateDE(r.rechnungsdatum)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(r.netto)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(r.brutto)}</td>
                  <td style={S.td}><span style={{ ...S.badge, background: r.status === 'bezahlt' ? '#e8f4ea' : r.status === 'storniert' ? '#eceff3' : '#fdeccd', color: r.status === 'bezahlt' ? C.success : r.status === 'storniert' ? C.muted : '#8a6d3b' }}>{r.status}</span></td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button style={S.btnSmall} onClick={() => downloadRechnung(r)}>PDF</button>
                    <button style={{ ...S.btnSmall, marginLeft: 6 }} onClick={() => sendRechnung(r)}>Senden{r.versendet_am ? ' ✓' : ''}</button>
                    {r.status !== 'storniert' && <button style={{ ...S.btnSmall, marginLeft: 6, background: r.status === 'bezahlt' ? C.muted : C.success }} onClick={() => markBezahlt(r)}>{r.status === 'bezahlt' ? 'offen' : 'bezahlt'}</button>}
                    {r.lexware_uebertragen_am
                      ? <span style={{ ...S.badge, marginLeft: 6, background: '#e8f4ea', color: C.success }} title={`an Lexware übertragen am ${fmtDate(r.lexware_uebertragen_am)}`}>Lexware ✓</span>
                      : <button style={{ ...S.btnSmall, marginLeft: 6, background: C.accent }} onClick={() => lexwareRechnung(r)}>→ Lexware</button>}
                    {r.status !== 'storniert' && !r.storno_von_id && <button style={{ ...S.btnDanger, marginLeft: 6 }} onClick={() => stornoRechnung(r)}>Storno</button>}
                    {r.status !== 'storniert' && r.herkunft !== 'import' && <button style={{ ...S.btnSmall, marginLeft: 6, background: '#eee', color: C.text }} onClick={() => pdfNeu(r)} title="PDF mit aktueller Anschrift und Mandantennummer neu erzeugen (Nummer bleibt)">PDF neu</button>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Manuelle Position hinzufügen</h2>
        <form onSubmit={addManual}>
          <div style={S.row}>
            <div style={{ ...S.field, flex: '2 1 240px' }}><label style={S.label}>Bezeichnung</label><input style={S.input} value={mp.bezeichnung} onChange={e => setMp({ ...mp, bezeichnung: e.target.value })} placeholder="z.B. Bescheinigung" /></div>
            <div style={{ ...S.field, flex: '0 1 90px' }}><label style={S.label}>Anzahl</label><input style={{ ...S.input, textAlign: 'center' }} value={mp.anzahl} onChange={e => setMp({ ...mp, anzahl: e.target.value })} placeholder="1" /></div>
            <div style={{ ...S.field, flex: '0 1 140px' }}><label style={S.label}>Einzelpreis netto (€)</label><input style={S.input} value={mp.einzelpreis} onChange={e => setMp({ ...mp, einzelpreis: e.target.value })} placeholder="z.B. 12,00" /></div>
            <div style={{ ...S.field, flex: '0 1 120px' }}><label style={S.label}>Summe netto</label><div style={{ ...S.input, background: C.bg, fontWeight: 700 }}>{mpSumme != null ? fmtEur(mpSumme) : '—'}</div></div>
            <div style={S.field}><label style={S.label}>Mitarbeiter (optional)</label>
              <select style={S.select} value={mp.mitarbeiter_id} onChange={e => setMp({ ...mp, mitarbeiter_id: e.target.value })}>
                <option value="">— kein MA —</option>
                {maList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
              </select>
            </div>
            <div><button style={{ ...S.btnPrimary, marginTop: 0 }}>Hinzufügen</button></div>
          </div>
        </form>
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>Archivierte Leistungen ({archived.length}) · {fmtEur(summeArch)}</h2>
          <button style={S.btnSmall} onClick={() => setShowArchived(s => !s)}>{showArchived ? 'Ausblenden' : 'Anzeigen'}</button>
        </div>
        {showArchived && (archived.length === 0 ? <div style={S.empty}>Keine archivierten Leistungen</div> : (
          <table style={{ ...S.table, marginTop: 12 }}>
            <thead><tr>
              <th style={S.th}>Datum</th><th style={S.th}>Art</th><th style={S.th}>Bezeichnung</th>
              <th style={S.th}>Mitarbeiter</th><th style={{ ...S.th, textAlign: 'right' }}>Netto</th><th style={S.th}></th>
            </tr></thead>
            <tbody>{archived.map(l => row(l, true))}</tbody>
          </table>
        ))}
      </div>
    </div>
  );
}

const LEAD_PHASEN = [
  ['neu', 'Neu', '#fdeccd', '#8a6d3b'],
  ['kontaktiert', 'Kontaktiert', '#e8eef5', C.accent],
  ['qualifiziert', 'Qualifiziert', '#e3e8ff', '#3d4eb0'],
  ['angebot', 'Angebot', '#fff3cd', '#8a6d3b'],
  ['gewonnen', 'Gewonnen', '#e8f4ea', C.success],
  ['verloren', 'Verloren', '#fdecea', C.danger],
];
const ACT_ICON = { notiz: '✏️', anruf: '☎️', email: '✉️', termin: '📅', phase: '🔀', system: '⚙️' };

const ANGEBOT_EINHEITEN_FE = [
  ['pro_ma_monat', 'je MA / Monat'], ['pauschal_monat', 'pauschal / Monat'], ['einmalig', 'einmalig'],
  ['pro_ma', 'je MA, einmalig'], ['je_vorgang', 'je Vorgang'], ['pro_stunde', 'je Stunde'],
];
const AE_LABEL = Object.fromEntries(ANGEBOT_EINHEITEN_FE);
const aePreis = (v) => (parseFloat(String(v ?? '').replace(',', '.')) || 0);
const aeEur = (n) => Number(n || 0).toFixed(2).replace('.', ',') + ' €';

function AngebotBaukasten({ lead, onClose, onCreated }) {
  const [bausteine, setBausteine] = useState([]);
  const [pos, setPos] = useState([]);
  const [x, setX] = useState(() => { const m = /\d+/.exec(lead.anzahl_mitarbeiter || ''); return m ? Math.max(1, Number(m[0])) : 1; });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [erstellt, setErstellt] = useState(null);
  const [eigenOpen, setEigenOpen] = useState(false);
  const [eigen, setEigen] = useState({ titel: '', preis: '', einheit: 'einmalig', beschreibung: '', vorlage: false });

  const standardAnschreiben = useCallback((anz) => (
    `Guten Tag${lead.ansprechpartner ? ' ' + lead.ansprechpartner : ''},\n\n` +
    `vielen Dank für Ihr Interesse an der Lohnkanzlei. Gerne bieten wir Ihnen die laufende ` +
    `Lohn- und Gehaltsabrechnung für Ihr Unternehmen mit ${anz} Mitarbeitern wie folgt an:`
  ), [lead.ansprechpartner]);
  const [anschreiben, setAnschreiben] = useState('');
  const [anschreibenTouched, setAnschreibenTouched] = useState(false);
  useEffect(() => {
    if (!anschreibenTouched) setAnschreiben(standardAnschreiben(x));
  }, [x, anschreibenTouched, standardAnschreiben]);

  function mengeFor(einheit, anz) { return (einheit === 'pro_ma_monat' || einheit === 'pro_ma') ? anz : 1; }
  function toPos(b, anz) { return { titel: b.titel, beschreibung: b.beschreibung || '', preis: String(b.preis).replace('.', ','), einheit: b.einheit, menge: mengeFor(b.einheit, anz) }; }

  useEffect(() => {
    api.get('/angebot-bausteine').then(({ data }) => {
      const aktive = data.filter(b => b.aktiv);
      setBausteine(aktive);
      const m = /\d+/.exec(lead.anzahl_mitarbeiter || '');
      const anz = m ? Math.max(1, Number(m[0])) : 1;
      setPos(aktive.filter(b => b.standard).map(b => toPos(b, anz)));
    }).catch(() => setErr('Bausteine konnten nicht geladen werden'));
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setPos(ps => ps.map(p => (p.einheit === 'pro_ma_monat' || p.einheit === 'pro_ma') ? { ...p, menge: x } : p));
  }, [x]);

  const monatlich = pos.reduce((s, p) => s + ((p.einheit === 'pro_ma_monat' || p.einheit === 'pauschal_monat') ? aePreis(p.preis) * (Number(p.menge) || 1) : 0), 0);
  const einmalig = pos.reduce((s, p) => s + ((p.einheit === 'einmalig' || p.einheit === 'pro_ma') ? aePreis(p.preis) * (Number(p.menge) || 1) : 0), 0);

  function addBaustein(b) { setPos(ps => [...ps, toPos(b, x)]); }
  function move(i, d) { setPos(ps => { const n = [...ps]; const j = i + d; if (j < 0 || j >= n.length) return ps; [n[i], n[j]] = [n[j], n[i]]; return n; }); }
  const updPos = (i, k, v) => setPos(ps => ps.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  async function eigenHinzu() {
    if (!eigen.titel.trim()) { setErr('Titel des eigenen Bausteins fehlt.'); return; }
    setErr('');
    const p = { titel: eigen.titel.trim(), beschreibung: eigen.beschreibung.trim(), preis: eigen.preis || '0', einheit: eigen.einheit, menge: mengeFor(eigen.einheit, x) };
    setPos(ps => [...ps, p]);
    if (eigen.vorlage) {
      try {
        const fd = new FormData();
        fd.append('titel', p.titel); fd.append('beschreibung', p.beschreibung);
        fd.append('preis', String(aePreis(p.preis))); fd.append('einheit', p.einheit);
        await api.post('/angebot-bausteine', fd);
        const { data } = await api.get('/angebot-bausteine');
        setBausteine(data.filter(b => b.aktiv));
      } catch { /* Vorlage optional */ }
    }
    setEigen({ titel: '', preis: '', einheit: 'einmalig', beschreibung: '', vorlage: false });
    setEigenOpen(false);
  }

  async function erstellen() {
    if (pos.length === 0) { setErr('Bitte mindestens einen Baustein hinzufügen.'); return; }
    setBusy(true); setErr('');
    try {
      const clean = pos.map(p => ({ titel: p.titel, beschreibung: p.beschreibung || '', einheit: p.einheit, preis: aePreis(p.preis), menge: Number(p.menge) || 1 }));
      const fd = new FormData();
      fd.append('positionen', JSON.stringify(clean)); fd.append('anzahl_ma', String(x));
      fd.append('anschreiben', anschreiben);
      const { data } = await api.post(`/leads/${lead.id}/angebot`, fd);
      setErstellt(data); if (onCreated) onCreated();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Erstellen fehlgeschlagen'); } finally { setBusy(false); }
  }
  async function download() {
    try {
      const res = await api.get(`/angebote/${erstellt.id}/pdf`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = u; a.download = `${erstellt.nummer}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch { setErr('Download fehlgeschlagen'); }
  }
  const [sendenOffen, setSendenOffen] = useState(false);
  function sendNow() { setErr(''); setSendenOffen(true); }

  const einheitBadge = (e) => <span style={{ ...S.badge, background: '#e8eef5', color: C.accent }}>{AE_LABEL[e] || e}</span>;
  return (
    <div style={{ ...S.modalOverlay, zIndex: 1100 }} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 940, maxWidth: '98%' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
          <h2 style={{ ...S.cardTitle, margin: 0 }}>Angebot erstellen – {lead.firmenname}</h2>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: C.muted }}>Anzahl Mitarbeiter (X):</label>
            <input style={{ ...S.input, width: 70, textAlign: 'center' }} value={x} onChange={e => setX(Math.max(1, parseInt(e.target.value, 10) || 1))} />
          </div>
        </div>
        <div style={S.pathInfo}>Bausteine links per Klick auf „+" oder per Ziehen ins Angebot übernehmen. Menge und Preis sind je Position anpassbar.</div>
        {!erstellt && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={S.label}>Anschreiben (erscheint über den Positionen im PDF)</label>
              {anschreibenTouched && (
                <button style={{ border: 'none', background: 'transparent', color: C.accent, fontSize: 12, cursor: 'pointer', padding: 0 }}
                  onClick={() => { setAnschreibenTouched(false); setAnschreiben(standardAnschreiben(x)); }}>↺ Standardtext wiederherstellen</button>
              )}
            </div>
            <textarea style={{ ...S.textarea, minHeight: 90 }} value={anschreiben}
              onChange={e => { setAnschreiben(e.target.value); setAnschreibenTouched(true); }} />
            <div style={S.pathInfo}>Hier kannst du individuell auf Themen aus dem Gespräch eingehen. Solange der Text unverändert ist, passt sich die Mitarbeiterzahl automatisch an.</div>
          </div>
        )}
        {erstellt ? (
          <div style={{ background: '#e8f4ea', border: '1px solid #cfe6d6', borderRadius: 8, padding: 18, marginTop: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.success, marginBottom: 6 }}>✓ Angebot {erstellt.nummer} erstellt</div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>Monatlich: <b>{aeEur(erstellt.monatlich_netto)}</b> netto · Einmalig: <b>{aeEur(erstellt.einmalig_netto)}</b> netto</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={download}>⬇ PDF herunterladen</button>
              <button disabled={busy || !lead.email} title={lead.email ? '' : 'Keine E-Mail-Adresse hinterlegt'} style={{ ...S.btnPrimary, marginTop: 0, background: lead.email ? C.success : C.muted }} onClick={sendNow}>✉ Per E-Mail an Lead senden</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button>
            </div>
            {err && <div style={S.err}>{err}</div>}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 300px', minWidth: 280 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 6 }}>Bausteine</div>
                <div style={{ maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
                  {bausteine.map(b => (
                    <div key={b.id} draggable
                      onDragStart={e => { e.dataTransfer.setData('text/baustein', String(b.id)); e.dataTransfer.effectAllowed = 'copy'; }}
                      style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.accent}`, borderRadius: 6, padding: '8px 10px', marginBottom: 8, background: '#fff', cursor: 'grab' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ fontSize: 13, flex: 1 }}>{b.titel}</strong>
                        <button title="Hinzufügen" style={{ ...S.btnSmall, background: C.success }} onClick={() => addBaustein(b)}>+</button>
                      </div>
                      <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{aeEur(b.preis)}</span>{einheitBadge(b.einheit)}
                      </div>
                      {b.beschreibung && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{b.beschreibung}</div>}
                    </div>
                  ))}
                </div>
                {!eigenOpen ? (
                  <button style={{ ...S.btnGhost, color: C.accent, borderColor: C.accent, marginTop: 4 }} onClick={() => setEigenOpen(true)}>+ Eigener Baustein</button>
                ) : (
                  <div style={{ border: `1px dashed ${C.accent}`, borderRadius: 6, padding: 10, marginTop: 4 }}>
                    <label style={S.label}>Titel</label>
                    <input style={S.input} value={eigen.titel} onChange={e => setEigen({ ...eigen, titel: e.target.value })} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}><label style={S.label}>Preis (€ netto)</label><input style={S.input} value={eigen.preis} onChange={e => setEigen({ ...eigen, preis: e.target.value })} /></div>
                      <div style={{ flex: 1 }}><label style={S.label}>Einheit</label>
                        <select style={S.select} value={eigen.einheit} onChange={e => setEigen({ ...eigen, einheit: e.target.value })}>
                          {ANGEBOT_EINHEITEN_FE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select></div>
                    </div>
                    <label style={S.label}>Beschreibung (optional)</label>
                    <input style={S.input} value={eigen.beschreibung} onChange={e => setEigen({ ...eigen, beschreibung: e.target.value })} />
                    <label style={{ fontSize: 12, color: C.muted, display: 'flex', gap: 6, alignItems: 'center', margin: '6px 0' }}>
                      <input type="checkbox" checked={eigen.vorlage} onChange={e => setEigen({ ...eigen, vorlage: e.target.checked })} /> als Vorlage für künftige Angebote speichern
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={eigenHinzu}>Hinzufügen</button>
                      <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setEigenOpen(false)}>Abbrechen</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ flex: '1.4 1 400px', minWidth: 320 }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={e => { e.preventDefault(); const id = Number(e.dataTransfer.getData('text/baustein')); const b = bausteine.find(z => z.id === id); if (b) addBaustein(b); }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 6 }}>Angebot ({pos.length} Positionen)</div>
                <div style={{ minHeight: 200, border: `1px dashed ${pos.length ? C.border : C.accent}`, borderRadius: 8, padding: 10, background: pos.length ? '#fff' : '#f7faf8' }}>
                  {pos.length === 0 && <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 30 }}>Bausteine hierher ziehen oder links „+" klicken</div>}
                  {pos.map((p, i) => (
                    <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 13, flex: '1 1 160px' }}>{p.titel}</strong>
                        {einheitBadge(p.einheit)}
                        <button title="nach oben" style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => move(i, -1)}>↑</button>
                        <button title="nach unten" style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => move(i, 1)}>↓</button>
                        <button title="entfernen" style={{ ...S.btnSmall, background: C.danger }} onClick={() => setPos(ps => ps.filter((_, idx) => idx !== i))}>✕</button>
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {(p.einheit === 'pro_ma_monat' || p.einheit === 'pro_ma') ? (
                          <span style={{ fontSize: 12, color: C.muted }}>Menge: <b>{p.menge} MA</b></span>
                        ) : (p.einheit === 'je_vorgang' || p.einheit === 'pro_stunde') ? (
                          <span style={{ fontSize: 12, color: C.muted }}>nach Anfall</span>
                        ) : (
                          <span style={{ fontSize: 12, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Menge:
                            <input style={{ ...S.input, width: 56, padding: '4px 6px', textAlign: 'center' }} value={p.menge} onChange={e => updPos(i, 'menge', Math.max(1, parseInt(e.target.value, 10) || 1))} />
                          </span>
                        )}
                        <span style={{ fontSize: 12, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Preis €:
                          <input style={{ ...S.input, width: 80, padding: '4px 6px', textAlign: 'right' }} value={p.preis} onChange={e => updPos(i, 'preis', e.target.value)} />
                        </span>
                        {(p.einheit === 'pro_ma_monat' || p.einheit === 'pauschal_monat' || p.einheit === 'einmalig' || p.einheit === 'pro_ma') &&
                          <span style={{ fontSize: 12, marginLeft: 'auto', fontWeight: 700, color: C.primary }}>{aeEur(aePreis(p.preis) * (Number(p.menge) || 1))}{(p.einheit === 'pro_ma_monat' || p.einheit === 'pauschal_monat') ? ' / Monat' : ''}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 10, background: '#e7f1ec', borderRadius: 8, padding: '10px 14px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13 }}>Monatlich: <b style={{ color: '#155941' }}>{aeEur(monatlich)}</b> netto</div>
                  <div style={{ fontSize: 13 }}>Einmalig: <b style={{ color: '#155941' }}>{aeEur(einmalig)}</b> netto</div>
                  <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0, marginLeft: 'auto' }} onClick={erstellen}>{busy ? 'Erstellt…' : '📄 Angebot erstellen (PDF)'}</button>
                </div>
              </div>
            </div>
            {err && <div style={S.err}>{err}</div>}
            <div style={{ marginTop: 12 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Abbrechen</button></div>
          </>
        )}
      </div>
      {sendenOffen && erstellt && <AngebotSendenDialog angebot={erstellt} lead={lead} onClose={() => setSendenOffen(false)} onSent={() => { setSendenOffen(false); if (onCreated) onCreated(); onClose(); }} />}
    </div>
  );
}

// Angebot versenden: Mail frei bearbeiten (z. B. Fragen der Anfrage beantworten) + optional Button „Angebot annehmen und registrieren“
function AngebotSendenDialog({ angebot, lead, onClose, onSent }) {
  const [d, setD] = useState(null);
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [onb, setOnb] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/angebote/${angebot.id}/email-entwurf`); setD(data); setBetreff(data.betreff); setText(data.text); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Entwurf konnte nicht geladen werden.'); }
  })(); }, [angebot.id]);
  async function senden() {
    if (!betreff.trim() || !text.trim()) { setErr('Betreff und Text dürfen nicht leer sein.'); return; }
    if (!window.confirm(`Angebot ${angebot.nummer} jetzt an ${d.to} senden?${onb ? '\n\nMit Button „Angebot annehmen und registrieren“ – ein Klick legt den Mandanten an und startet die Selbst-Einrichtung.' : ''}`)) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('betreff', betreff); fd.append('text', text); fd.append('onboarding', onb ? 'true' : 'false');
      const { data } = await api.post(`/angebote/${angebot.id}/senden`, fd);
      window.alert(`Angebot an ${data.to} gesendet${data.annahme_link ? ' – mit Annahme-Button' : ''}.`);
      onSent(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Senden fehlgeschlagen.'); setBusy(false); }
  }
  const fertig = d && !d.annahme_moeglich;
  return (
    <div style={{ ...S.modalOverlay, zIndex: 1200 }} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 720, maxWidth: '98%' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Angebot {angebot.nummer} versenden</h2>
        {!d ? <div style={S.empty}>{err || 'Lade…'}</div> : (
          <>
            <div style={{ fontSize: 13, color: C.muted }}>An: <b style={{ color: C.text }}>{d.to || '— keine E-Mail hinterlegt —'}</b> · Anhang: {angebot.nummer}.pdf</div>
            <label style={S.label}>Betreff</label>
            <input style={S.input} value={betreff} onChange={e => setBetreff(e.target.value)} />
            <label style={S.label}>Text der E-Mail</label>
            <textarea style={{ ...S.textarea, minHeight: 230, lineHeight: 1.5 }} value={text} onChange={e => setText(e.target.value)} />
            <div style={S.pathInfo}>Hier können Sie direkt auf Fragen aus der Anfrage eingehen, die nicht ins Angebot gehören. Die Mail erscheint im Design der Lohnkanzlei; Antworten gehen an Ihre Adresse.</div>
            <div style={{ border: `1px solid ${onb ? C.primary : C.border}`, background: onb ? '#eef4fb' : C.white, borderRadius: 8, padding: '10px 12px', marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, cursor: fertig ? 'not-allowed' : 'pointer' }}>
                <input type="checkbox" checked={onb} disabled={fertig} onChange={e => setOnb(e.target.checked)} />
                <span><b>Self-Onboarding direkt zulassen</b> – die Mail bekommt den Button „{d.button}“.
                  <span style={{ display: 'block', color: C.muted, fontSize: 12.5, marginTop: 3 }}>Ein Klick nimmt das Angebot an, legt den Mandanten an und startet die Selbst-Einrichtung (Zugang, Stammblatt, Vollmacht, Dienstvertrag) – ohne dass Sie erst freischalten müssen. Sie bekommen eine Mail, sobald das Angebot angenommen wurde.{fertig ? ` Nicht möglich: ${d.mandant_name} ist bereits Mandant und hat sich im Portal registriert.` : ''}</span>
                </span>
              </label>
              {onb && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: '#fff', border: `1px dashed ${C.border}`, borderRadius: 6 }}>
                  <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 6 }}>So erscheint der Button unter Ihrem Text:</div>
                  <span style={{ display: 'inline-block', background: '#1f6f54', color: '#fff', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 14 }}>{d.button}</span>
                </div>
              )}
            </div>
            {err && <div style={S.err}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} disabled={busy || !d.to} onClick={senden}>{busy ? 'Sendet…' : '✉ Angebot senden'}</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={() => { setBetreff(d.betreff); setText(d.text); }}>Text zurücksetzen</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0 }} onClick={onClose}>Abbrechen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Öffentliche Seite hinter dem Button „Angebot annehmen und jetzt im Mandantenportal registrieren“
function AngebotAnnahmePage({ token }) {
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState('');
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { (async () => {
    try { const { data } = await axios.get(`/api/angebot-annahme/${token}`); setInfo(data); }
    catch (ex) { setFatal(ex.response?.data?.detail || 'Dieser Link ist ungültig.'); }
  })(); }, [token]);
  const eur = (v) => Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  async function annehmen() {
    if (info.stand === 'offen' && !ok) { setErr('Bitte bestätigen Sie die Annahme des Angebots.'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`/api/angebot-annahme/${token}`);
      window.location.assign(`${window.location.pathname}?onboarding=${encodeURIComponent(data.onboarding_token)}`);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Annahme fehlgeschlagen.'); setBusy(false); }
  }
  async function pdf() {
    try {
      const res = await axios.get(`/api/angebot-annahme/${token}/pdf`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data); window.open(u, '_blank');
    } catch { setErr('PDF konnte nicht geladen werden.'); }
  }
  return (
    <div style={S.loginPage}>
      <div style={{ ...S.loginCard, width: 560, maxWidth: '92vw' }}>
        <h1 style={S.loginTitle}>{info ? `Ihr Angebot ${info.nummer}` : 'Ihr Angebot'}</h1>
        {fatal ? <div style={S.err}>{fatal}</div> : !info ? <div style={S.empty}>Lade…</div> : (
          <>
            <p style={S.loginSub}>{info.firmenname}{info.datum ? ` · vom ${new Date(info.datum).toLocaleDateString('de-DE')}` : ''}</p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, background: '#e7f1ec', borderRadius: 8, padding: '10px 12px' }}><div style={{ fontSize: 12, color: C.muted }}>Monatlich</div><div style={{ fontSize: 20, fontWeight: 800, color: '#155941' }}>{eur(info.monatlich_netto)}</div><div style={{ fontSize: 11, color: C.muted }}>netto</div></div>
              <div style={{ flex: 1, background: '#e7f1ec', borderRadius: 8, padding: '10px 12px' }}><div style={{ fontSize: 12, color: C.muted }}>Einmalig</div><div style={{ fontSize: 20, fontWeight: 800, color: '#155941' }}>{eur(info.einmalig_netto)}</div><div style={{ fontSize: 11, color: C.muted }}>netto</div></div>
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              {info.positionen.map((p, i) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: `1px solid ${C.border}` }}><span>{p.titel}</span><span style={{ whiteSpace: 'nowrap', color: C.muted }}>{p.preis ? `${eur(p.preis)} ${p.einheit}` : 'kostenfrei'}</span></div>)}
            </div>
            <button type="button" style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={pdf}>📄 Angebot als PDF ansehen</button>
            {info.stand === 'offen' && (
              <>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer', marginTop: 14 }}>
                  <input type="checkbox" checked={ok} onChange={e => setOk(e.target.checked)} />
                  <span>Ich nehme das Angebot an. Im nächsten Schritt richte ich meinen Zugang zum Mandantenportal ein und unterschreibe dort den Dienstvertrag.</span>
                </label>
                <button style={{ ...S.btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={annehmen}>{busy ? '…' : 'Angebot annehmen und registrieren'}</button>
                {info.gueltig_bis && <div style={{ ...S.pathInfo, marginTop: 8 }}>Das Angebot kann bis {new Date(info.gueltig_bis).toLocaleDateString('de-DE')} angenommen werden.</div>}
              </>
            )}
            {info.stand === 'weiter' && (
              <>
                <div style={{ ...S.okMsg, fontSize: 14 }}>Sie haben das Angebot bereits angenommen – vielen Dank!</div>
                <button style={{ ...S.btnPrimary, width: '100%' }} disabled={busy} onClick={annehmen}>Registrierung fortsetzen</button>
              </>
            )}
            {info.stand === 'registriert' && <div style={{ ...S.okMsg, fontSize: 14 }}>Das Angebot ist angenommen und Ihr Zugang eingerichtet. <a href="/" style={{ color: C.accent }}>Zur Anmeldung</a></div>}
            {info.stand === 'abgelaufen' && <div style={S.err}>Dieses Angebot ist abgelaufen. Bitte wenden Sie sich an die Lohnkanzlei – wir senden Ihnen gern ein aktuelles Angebot.</div>}
            <div style={S.err}>{err}</div>
          </>
        )}
      </div>
    </div>
  );
}

function AngebotBausteineVerwalten({ onClose }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [neu, setNeu] = useState(null);

  async function load() {
    try { const { data } = await api.get('/angebot-bausteine'); setRows(data); }
    catch { setErr('Laden fehlgeschlagen'); }
  }
  useEffect(() => { load(); }, []);
  const upd = (i, k, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  async function save(r) {
    try {
      const fd = new FormData();
      fd.append('titel', r.titel || ''); fd.append('beschreibung', r.beschreibung || '');
      fd.append('preis', String(r.preis)); fd.append('einheit', r.einheit);
      fd.append('standard', r.standard ? 'true' : 'false'); fd.append('aktiv', r.aktiv ? 'true' : 'false');
      await api.patch(`/angebot-bausteine/${r.id}`, fd);
      setErr(''); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  }
  async function del(r) {
    if (!window.confirm(`Baustein „${r.titel}" löschen?`)) return;
    await api.delete(`/angebot-bausteine/${r.id}`); await load();
  }
  async function moveRow(i, d) {
    const j = i + d; if (j < 0 || j >= rows.length) return;
    const a = rows[i], b = rows[j];
    const fa = new FormData(); fa.append('sortierung', String(b.sortierung));
    const fb = new FormData(); fb.append('sortierung', String(a.sortierung));
    await api.patch(`/angebot-bausteine/${a.id}`, fa);
    await api.patch(`/angebot-bausteine/${b.id}`, fb);
    await load();
  }
  async function addNeu() {
    if (!neu.titel.trim()) { setErr('Titel fehlt'); return; }
    try {
      const fd = new FormData();
      fd.append('titel', neu.titel.trim()); fd.append('beschreibung', neu.beschreibung || '');
      fd.append('preis', String(aePreis(neu.preis))); fd.append('einheit', neu.einheit);
      await api.post('/angebot-bausteine', fd);
      setNeu(null); setErr(''); await load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
  }

  return (
    <div style={{ ...S.modalOverlay, zIndex: 1100 }} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 860, maxWidth: '98%' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Angebots-Bausteine verwalten</h2>
        <div style={S.pathInfo}>Diese Bausteine stehen im Angebots-Baukasten zur Auswahl. „Standard" = beim Öffnen automatisch im Angebot. Inaktive Bausteine bleiben erhalten, werden aber nicht angeboten.</div>
        {err && <div style={S.err}>{err}</div>}
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {rows.map((r, i) => (
            <div key={r.id} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, marginBottom: 8, opacity: r.aktiv ? 1 : 0.55 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input style={{ ...S.input, flex: '2 1 220px' }} value={r.titel || ''} onChange={e => upd(i, 'titel', e.target.value)} />
                <input style={{ ...S.input, width: 90, textAlign: 'right' }} value={String(r.preis).replace('.', ',')} onChange={e => upd(i, 'preis', e.target.value.replace(',', '.'))} />
                <select style={{ ...S.select, width: 150 }} value={r.einheit} onChange={e => upd(i, 'einheit', e.target.value)}>
                  {ANGEBOT_EINHEITEN_FE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <label style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={!!r.standard} onChange={e => upd(i, 'standard', e.target.checked)} />Standard</label>
                <label style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={!!r.aktiv} onChange={e => upd(i, 'aktiv', e.target.checked)} />aktiv</label>
              </div>
              <input style={{ ...S.input, marginTop: 6 }} placeholder="Beschreibung (erscheint klein im Angebot)" value={r.beschreibung || ''} onChange={e => upd(i, 'beschreibung', e.target.value)} />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button style={{ ...S.btnSmall, background: C.success }} onClick={() => save(r)}>Speichern</button>
                <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => moveRow(i, -1)}>↑</button>
                <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => moveRow(i, 1)}>↓</button>
                <button style={{ ...S.btnSmall, background: C.danger, marginLeft: 'auto' }} onClick={() => del(r)}>Löschen</button>
              </div>
            </div>
          ))}
        </div>
        {neu ? (
          <div style={{ border: `1px dashed ${C.accent}`, borderRadius: 6, padding: 10, marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input style={{ ...S.input, flex: '2 1 220px' }} placeholder="Titel" value={neu.titel} onChange={e => setNeu({ ...neu, titel: e.target.value })} />
              <input style={{ ...S.input, width: 90, textAlign: 'right' }} placeholder="Preis" value={neu.preis} onChange={e => setNeu({ ...neu, preis: e.target.value })} />
              <select style={{ ...S.select, width: 150 }} value={neu.einheit} onChange={e => setNeu({ ...neu, einheit: e.target.value })}>
                {ANGEBOT_EINHEITEN_FE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <input style={{ ...S.input, marginTop: 6 }} placeholder="Beschreibung (optional)" value={neu.beschreibung} onChange={e => setNeu({ ...neu, beschreibung: e.target.value })} />
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={addNeu}>Anlegen</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setNeu(null)}>Abbrechen</button>
            </div>
          </div>
        ) : (
          <button style={{ ...S.btnGhost, color: C.accent, borderColor: C.accent, marginTop: 8 }} onClick={() => setNeu({ titel: '', preis: '', einheit: 'einmalig', beschreibung: '' })}>+ Neuer Baustein</button>
        )}
        <div style={{ marginTop: 12 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button></div>
      </div>
    </div>
  );
}

function Leads({ user, reloadMandanten }) {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [acts, setActs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({});
  const [actText, setActText] = useState('');
  const [actTyp, setActTyp] = useState('notiz');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [mailOpen, setMailOpen] = useState(false);
  const [mail, setMail] = useState({ betreff: '', text: '' });
  const [mailFiles, setMailFiles] = useState([]);
  const [angebotOpen, setAngebotOpen] = useState(false);
  const [bausteineOpen, setBausteineOpen] = useState(false);
  const [angebote, setAngebote] = useState([]);

  async function load() { try { const { data } = await api.get('/leads'); setLeads(data); } catch (ex) { setMsg('Konnte Leads nicht laden'); } }
  useEffect(() => { load(); api.get('/users').then(({ data }) => setUsers(data.filter(u => u.role === 'admin' || u.role === 'staff'))).catch(() => {}); }, []);

  const eur = n => n == null ? '–' : (Number(n).toFixed(2).replace('.', ',') + ' €');
  const userName = id => { const u = users.find(x => x.id === id); return u ? (u.name || u.username) : null; };
  const phaseInfo = p => LEAD_PHASEN.find(x => x[0] === p) || ['neu', 'Neu', '#eee', C.muted];

  async function openLead(id) {
    setMsg('');
    try {
      const fresh = (await api.get('/leads')).data; setLeads(fresh);
      const l = fresh.find(x => x.id === id); setSel(l || null);
      if (l) { const { data } = await api.get(`/leads/${id}/aktivitaeten`); setActs(data); }
      if (l) { try { const a = await api.get(`/leads/${id}/angebote`); setAngebote(a.data); } catch { setAngebote([]); } }
    } catch (ex) { setMsg('Lead konnte nicht geladen werden'); }
  }
  const field = (k, v) => setSel(s => ({ ...s, [k]: v }));

  async function save() {
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      ['firmenname', 'ansprechpartner', 'email', 'telefon', 'anzahl_mitarbeiter', 'aktuelle_software', 'nachricht', 'interne_notiz', 'phase', 'prioritaet', 'wert', 'naechster_kontakt'].forEach(k => fd.append(k, sel[k] == null ? '' : sel[k]));
      fd.append('owner_id', sel.owner_id == null ? '' : sel.owner_id);
      const { data } = await api.patch(`/leads/${sel.id}`, fd);
      setSel(data); setMsg('Gespeichert.'); await load();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Speichern fehlgeschlagen'); } finally { setBusy(false); }
  }
  async function addAct() {
    if (!actText.trim()) return;
    const fd = new FormData(); fd.append('typ', actTyp); fd.append('text', actText.trim());
    await api.post(`/leads/${sel.id}/aktivitaet`, fd); setActText('');
    const { data } = await api.get(`/leads/${sel.id}/aktivitaeten`); setActs(data); await load();
  }
  async function konvertieren() {
    if (!window.confirm(`„${sel.firmenname}" jetzt in einen Mandanten umwandeln?`)) return;
    const senden = window.confirm('Self-Onboarding-Einladung per E-Mail senden?\n\nOK = ja, an ' + (sel.email || '(keine E-Mail)') + '\nAbbrechen = nur Mandant anlegen, ohne E-Mail');
    setBusy(true); setMsg('');
    try {
      const fd = new FormData(); fd.append('onboarding_senden', senden ? 'true' : 'false');
      const { data } = await api.post(`/leads/${sel.id}/annehmen`, fd);
      setMsg(`In Mandant umgewandelt${(senden && data.email) ? ` – Einladung an ${data.email}` : ''}.`);
      if (reloadMandanten) reloadMandanten(); setSel(null); await load();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Umwandeln fehlgeschlagen'); } finally { setBusy(false); }
  }
  async function verloren() {
    const grund = window.prompt('Grund (optional):', ''); if (grund === null) return;
    const absage = sel.email ? window.confirm('Höfliche Absage-E-Mail an ' + sel.email + ' senden?') : false;
    setBusy(true);
    try {
      const fd = new FormData(); if (grund) fd.append('grund', grund); fd.append('absage_senden', absage ? 'true' : 'false');
      await api.post(`/leads/${sel.id}/ablehnen`, fd); setSel(null); await load();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Fehlgeschlagen'); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm('Lead endgültig löschen?')) return;
    await api.delete(`/leads/${sel.id}`); setSel(null); await load();
  }
  function openMail() {
    setMail({
      betreff: 'Ihre Anfrage bei der Lohnkanzlei',
      text: `Guten Tag ${sel.ansprechpartner || ''},\n\nvielen Dank für Ihre Anfrage.\n\n\n\nMit freundlichen Grüßen\nIhr Team der Lohnkanzlei`,
    });
    setMailFiles([]); setMailOpen(true);
  }
  async function moveLead(id, phase) {
    const l = leads.find(x => x.id === id);
    if (!l || (l.phase || 'neu') === phase) return;
    try {
      const fd = new FormData(); fd.append('phase', phase);
      await api.patch(`/leads/${id}`, fd);
      await load();
      if (sel && sel.id === id) setSel(s => ({ ...s, phase }));
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Verschieben fehlgeschlagen'); }
  }
  async function sendMail() {
    if (!mail.betreff.trim() || !mail.text.trim()) { setMsg('Betreff und Text sind erforderlich.'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('betreff', mail.betreff.trim()); fd.append('text', mail.text);
      mailFiles.forEach(f => fd.append('files', f));
      await api.post(`/leads/${sel.id}/email`, fd);
      setMailOpen(false); setMailFiles([]); setMsg(`E-Mail an ${sel.email} gesendet.`);
      const { data } = await api.get(`/leads/${sel.id}/aktivitaeten`); setActs(data); await load();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'E-Mail konnte nicht gesendet werden'); }
    finally { setBusy(false); }
  }
  async function refreshAngebote() {
    if (!sel) return;
    try { const a = await api.get(`/leads/${sel.id}/angebote`); setAngebote(a.data); } catch { /* */ }
    try { const r = await api.get(`/leads/${sel.id}/aktivitaeten`); setActs(r.data); } catch { /* */ }
    await load();
  }
  async function downloadAngebot(a) {
    try {
      const res = await api.get(`/angebote/${a.id}/pdf`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      const el = document.createElement('a'); el.href = u; el.download = `${a.nummer}.pdf`;
      document.body.appendChild(el); el.click(); el.remove(); URL.revokeObjectURL(u);
    } catch { setMsg('Download fehlgeschlagen'); }
  }
  const [sendenAngebot, setSendenAngebot] = useState(null);
  function sendAngebot(a) { setSendenAngebot(a); }
  async function createLead() {
    if (!(nf.firmenname || '').trim()) { window.alert('Firmenname erforderlich'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      ['firmenname', 'ansprechpartner', 'email', 'telefon', 'anzahl_mitarbeiter', 'nachricht', 'wert', 'prioritaet'].forEach(k => { if (nf[k]) fd.append(k, nf[k]); });
      fd.append('quelle', nf.quelle || 'telefon');
      const { data } = await api.post('/leads/intern', fd);
      setShowNew(false); setNf({}); await load(); openLead(data.id);
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Anlegen fehlgeschlagen'); } finally { setBusy(false); }
  }

  const aktiv = leads.filter(l => !['gewonnen', 'verloren'].includes(l.phase || 'neu'));
  const pipelineWert = aktiv.reduce((s, l) => s + (l.wert || 0), 0);
  const lab = (t) => <label style={S.label}>{t}</label>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, color: C.primary, margin: 0 }}>Leads &amp; CRM</h1>
        <span style={{ color: C.muted, fontSize: 13 }}>{aktiv.length} aktive Leads · Pipeline {eur(pipelineWert)}</span>
        <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, marginTop: 0, marginLeft: 'auto' }} onClick={() => setBausteineOpen(true)}>⚙ Angebots-Bausteine</button>
        <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => { setNf({}); setShowNew(true); }}>+ Lead anlegen</button>
      </div>
      <p style={{ color: C.muted, marginTop: 0, fontSize: 13 }}>Anfragen von der Website und telefonische Leads. Nur für Büro-Admins. <b>Tipp:</b> Karten per Drag &amp; Drop zwischen den Phasen verschieben.</p>
      {msg && <div style={{ background: '#e8f4ea', color: C.success, padding: 10, borderRadius: 4, marginBottom: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
        {LEAD_PHASEN.map(([key, label, bg, col]) => {
          const items = leads.filter(l => (l.phase || 'neu') === key);
          const sum = items.reduce((s, l) => s + (l.wert || 0), 0);
          return (
            <div key={key} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const id = Number(e.dataTransfer.getData('text/plain')); if (id) moveLead(id, key); }} style={{ minWidth: 230, flex: '1 0 230px', background: C.bg, borderRadius: 8, padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ ...S.badge, background: bg, color: col }}>{label} · {items.length}</span>
                {sum > 0 && <span style={{ fontSize: 11, color: C.muted }}>{eur(sum)}</span>}
              </div>
              {items.map(l => (
                <div key={l.id} draggable onDragStart={e => { e.dataTransfer.setData('text/plain', String(l.id)); e.dataTransfer.effectAllowed = 'move'; }} onClick={() => openLead(l.id)} style={{ background: '#fff', border: `1px solid ${C.border}`, borderLeft: `3px solid ${col}`, borderRadius: 6, padding: 10, marginBottom: 8, cursor: 'grab' }}>
                  <strong style={{ fontSize: 13 }}>{l.firmenname}</strong>
                  <div style={{ fontSize: 12, color: C.muted }}>{l.ansprechpartner || '—'}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: C.muted }}>
                    <span>{l.wert ? eur(l.wert) : ''}</span><span>{l.quelle || ''}</span>
                  </div>
                  {l.naechster_kontakt && <div style={{ fontSize: 11, color: C.accent, marginTop: 2 }}>📅 {fmtDateDE(l.naechster_kontakt)}</div>}
                </div>
              ))}
              {items.length === 0 && <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: 8 }}>–</div>}
            </div>
          );
        })}
      </div>

      {sel && (
        <div style={S.modalOverlay} onClick={() => setSel(null)}>
          <div style={{ ...S.modalCard, width: 720, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ margin: 0, color: C.primary }}>{sel.firmenname || 'Lead'}</h2>
              <button onClick={() => setSel(null)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>
            {sel.mandant_id && <div style={{ ...S.badge, background: '#e8f4ea', color: C.success, marginBottom: 8 }}>✓ in Mandant #{sel.mandant_id} umgewandelt</div>}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 300px' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>{lab('Phase')}<select style={S.select} value={sel.phase || 'neu'} onChange={e => field('phase', e.target.value)}>{LEAD_PHASEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
                  <div style={{ flex: 1 }}>{lab('Priorität')}<select style={S.select} value={sel.prioritaet || ''} onChange={e => field('prioritaet', e.target.value)}><option value="">–</option><option value="hoch">Hoch</option><option value="mittel">Mittel</option><option value="niedrig">Niedrig</option></select></div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>{lab('Wert (€/Monat)')}<input style={S.input} value={sel.wert ?? ''} onChange={e => field('wert', e.target.value)} /></div>
                  <div style={{ flex: 1 }}>{lab('Wiedervorlage')}<input type="date" style={S.input} value={(sel.naechster_kontakt || '').slice(0, 10)} onChange={e => field('naechster_kontakt', e.target.value)} /></div>
                </div>
                {lab('Zuständig')}<select style={S.select} value={sel.owner_id || ''} onChange={e => field('owner_id', e.target.value)}><option value="">–</option>{users.map(u => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}</select>
                {lab('Firma')}<input style={S.input} value={sel.firmenname || ''} onChange={e => field('firmenname', e.target.value)} />
                {lab('Ansprechpartner')}<input style={S.input} value={sel.ansprechpartner || ''} onChange={e => field('ansprechpartner', e.target.value)} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>{lab('E-Mail')}<input style={S.input} value={sel.email || ''} onChange={e => field('email', e.target.value)} /></div>
                  <div style={{ flex: 1 }}>{lab('Telefon')}<input style={S.input} value={sel.telefon || ''} onChange={e => field('telefon', e.target.value)} /></div>
                </div>
                {lab('Mitarbeiterzahl')}<input style={S.input} value={sel.anzahl_mitarbeiter || ''} onChange={e => field('anzahl_mitarbeiter', e.target.value)} />
                {lab('Interne Notiz')}<textarea style={S.textarea} value={sel.interne_notiz || ''} onChange={e => field('interne_notiz', e.target.value)} />
                {sel.nachricht && <div style={{ fontSize: 12, color: C.muted, marginTop: 8, whiteSpace: 'pre-wrap', background: C.bg, padding: 8, borderRadius: 4 }}><b>Nachricht:</b> {sel.nachricht}</div>}
                <button disabled={busy} style={{ ...S.btnPrimary }} onClick={save}>Speichern</button>
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                {angebote.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <h3 style={{ fontSize: 14, color: C.primary, marginTop: 0 }}>Angebote</h3>
                    {angebote.map(a => (
                      <div key={a.id} style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.accent}`, borderRadius: 6, padding: '6px 8px', marginBottom: 6, fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <strong>{a.nummer}</strong>
                          {a.versendet_am && <span style={{ ...S.badge, background: '#e8f4ea', color: C.success }}>✓ versendet</span>}
                          {a.angenommen_am && <span style={{ ...S.badge, background: '#1f6f54', color: '#fff' }} title={`online angenommen am ${fmtDateDE(a.angenommen_am)}`}>angenommen</span>}
                          {!a.angenommen_am && a.annahme_link_bis && <span style={{ ...S.badge, background: '#fff7e6', color: '#b8860b' }} title="Mail enthält den Button „Angebot annehmen und registrieren“">Annahme-Link bis {fmtDateDE(a.annahme_link_bis)}</span>}
                          <span style={{ marginLeft: 'auto', color: C.muted }}>{fmtDateDE(a.created_at)}</span>
                        </div>
                        <div style={{ color: C.muted, marginTop: 2 }}>{aeEur(a.monatlich_netto)}/Monat · {aeEur(a.einmalig_netto)} einmalig · {a.anzahl_ma} MA</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <button style={S.btnSmall} onClick={() => downloadAngebot(a)}>⬇ PDF</button>
                          <button disabled={busy || !sel.email} style={{ ...S.btnSmall, background: sel.email ? C.success : C.muted }} onClick={() => sendAngebot(a)}>✉ Senden</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <h3 style={{ fontSize: 14, color: C.primary }}>Aktivitäten</h3>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <select style={{ ...S.select, width: 110 }} value={actTyp} onChange={e => setActTyp(e.target.value)}><option value="notiz">Notiz</option><option value="anruf">Anruf</option><option value="email">E-Mail</option><option value="termin">Termin</option></select>
                  <input style={{ ...S.input, flex: 1 }} value={actText} placeholder="Eintrag …" onChange={e => setActText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addAct(); }} />
                  <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={addAct}>+</button>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {acts.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Noch keine Aktivitäten.</div>}
                  {acts.map(a => (
                    <div key={a.id} style={{ borderBottom: `1px solid ${C.bg}`, padding: '6px 0', fontSize: 12 }}>
                      <div>{ACT_ICON[a.typ] || '•'} {a.text}</div>
                      <div style={{ color: C.muted, fontSize: 11 }}>{a.user || 'System'} · {fmtDate(a.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => setAngebotOpen(true)}>📄 Angebot erstellen</button>
              <button disabled={busy || !sel.email} title={sel.email ? '' : 'Keine E-Mail-Adresse hinterlegt'} style={{ ...S.btnPrimary, marginTop: 0, background: sel.email ? C.accent : C.muted }} onClick={openMail}>✉ E-Mail schreiben</button>
              {!sel.mandant_id && <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0, background: C.success }} onClick={konvertieren}>➜ In Mandant umwandeln</button>}
              {!sel.mandant_id && <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0, background: C.danger }} onClick={verloren}>Verloren / Ablehnen</button>}
              <button disabled={busy} style={{ ...S.btnGhost, marginTop: 0, marginLeft: 'auto', color: C.danger, borderColor: C.danger }} onClick={del}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {angebotOpen && sel && (
        <AngebotBaukasten lead={sel} onClose={() => setAngebotOpen(false)} onCreated={refreshAngebote} />
      )}
      {bausteineOpen && <AngebotBausteineVerwalten onClose={() => setBausteineOpen(false)} />}

      {mailOpen && sel && (
        <div style={{ ...S.modalOverlay, zIndex: 1100 }} onClick={() => setMailOpen(false)}>
          <div style={{ ...S.modalCard, width: 640, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <h2 style={S.cardTitle}>E-Mail an {sel.firmenname || 'Lead'}</h2>
            <div style={S.pathInfo}>Wird im gebrandeten Lohnkanzlei-Design versendet – wie die automatischen E-Mails. Antworten des Empfängers gehen an Ihre E-Mail-Adresse.</div>
            <label style={S.label}>An</label>
            <input style={{ ...S.input, background: C.bg }} value={sel.email || ''} readOnly />
            <label style={{ ...S.label, marginTop: 8 }}>Betreff</label>
            <input style={S.input} value={mail.betreff} onChange={e => setMail({ ...mail, betreff: e.target.value })} />
            <label style={{ ...S.label, marginTop: 8 }}>Nachricht</label>
            <textarea style={{ ...S.textarea, minHeight: 200 }} value={mail.text} onChange={e => setMail({ ...mail, text: e.target.value })} />
            <label style={{ ...S.label, marginTop: 8 }}>Anhänge (optional)</label>
            <input type="file" multiple style={S.input} onChange={e => setMailFiles(Array.from(e.target.files || []))} />
            {mailFiles.length > 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{mailFiles.length} Datei(en): {mailFiles.map(f => f.name).join(', ')}</div>}
            <div style={S.pathInfo}>Tipp: Links (https://…) werden automatisch klickbar formatiert. Anhänge max. 15 MB gesamt.</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0 }} onClick={sendMail}>{busy ? 'Sendet…' : '✉ Senden'}</button>
              <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={() => setMailOpen(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div style={S.modalOverlay} onClick={() => setShowNew(false)}>
          <div style={{ ...S.modalCard, width: 480, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, color: C.primary }}>Neuer Lead</h2>
            {lab('Firma *')}<input style={S.input} value={nf.firmenname || ''} onChange={e => setNf({ ...nf, firmenname: e.target.value })} />
            {lab('Ansprechpartner')}<input style={S.input} value={nf.ansprechpartner || ''} onChange={e => setNf({ ...nf, ansprechpartner: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>{lab('E-Mail')}<input style={S.input} value={nf.email || ''} onChange={e => setNf({ ...nf, email: e.target.value })} /></div>
              <div style={{ flex: 1 }}>{lab('Telefon')}<input style={S.input} value={nf.telefon || ''} onChange={e => setNf({ ...nf, telefon: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>{lab('Mitarbeiter')}<input style={S.input} value={nf.anzahl_mitarbeiter || ''} onChange={e => setNf({ ...nf, anzahl_mitarbeiter: e.target.value })} /></div>
              <div style={{ flex: 1 }}>{lab('Wert (€/Monat)')}<input style={S.input} value={nf.wert || ''} onChange={e => setNf({ ...nf, wert: e.target.value })} /></div>
            </div>
            {lab('Quelle')}<select style={S.select} value={nf.quelle || 'telefon'} onChange={e => setNf({ ...nf, quelle: e.target.value })}><option value="telefon">Telefon</option><option value="empfehlung">Empfehlung</option><option value="email">E-Mail</option><option value="messe">Messe/Event</option><option value="sonstige">Sonstige</option></select>
            {lab('Notiz')}<textarea style={S.textarea} value={nf.nachricht || ''} onChange={e => setNf({ ...nf, nachricht: e.target.value })} />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0 }} onClick={createLead}>Anlegen</button>
              <button style={{ ...S.btnGhost, marginTop: 0 }} onClick={() => setShowNew(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
      {sendenAngebot && sel && <AngebotSendenDialog angebot={sendenAngebot} lead={sel} onClose={() => setSendenAngebot(null)} onSent={async (data) => { setSendenAngebot(null); setMsg(`Angebot ${sendenAngebot.nummer} an ${data.to} gesendet${data.annahme_link ? ' (mit Annahme-Button)' : ''}.`); await refreshAngebote(); }} />}
    </div>
  );
}

function Verwaltung({ user, mandanten, mandantId, reloadMandanten }) {
  const isAdmin = user.role === 'admin';
  const [sub, setSub] = useState(isAdmin ? 'buero' : 'mandant');
  const subTabs = [
    ...(isAdmin ? [['buero', 'Einstellungen Büro']] : []),
    ['mandant', 'Einstellungen Mandant'],
    ['stammblatt', 'Arbeitgeber-Stammblatt'],
  ];
  const subBtn = (active) => ({
    background: active ? C.primary : C.white, color: active ? C.white : C.text,
    border: `1px solid ${active ? C.primary : C.border}`, borderRadius: 6,
    padding: '8px 16px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {subTabs.map(([k, label]) => (
          <button key={k} style={subBtn(sub === k)} onClick={() => setSub(k)}>{label}</button>
        ))}
      </div>
      {sub === 'buero' && isAdmin && <Admin user={user} mandanten={mandanten} reloadMandanten={reloadMandanten} />}
      {sub === 'mandant' && <Settings user={user} mandanten={mandanten} mandantId={mandantId} />}
      {sub === 'stammblatt' && (mandantId
        ? <ArbeitgeberStammblatt mandantId={mandantId} />
        : <div style={S.card}><div style={S.hint}>Bitte oben links einen Mandanten auswählen (oder „Mein Büro" einrichten), um das Arbeitgeber-Stammblatt zu bearbeiten.</div></div>)}
    </div>
  );
}

function SecurityModal({ onClose, onLoggedOut }) {
  const [enabled, setEnabled] = useState(null);
  const [role, setRole] = useState('');
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => { (async () => {
    try { const { data } = await api.get('/auth/me'); setEnabled(!!data.totp_enabled); setRole(data.role); }
    catch { /* ignore */ }
  })(); }, []);

  async function start() {
    setErr(''); setMsg('');
    try { const { data } = await api.post('/2fa/enable-start'); setUri(data.otpauth_uri); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Fehler'); }
  }
  async function confirm() {
    setBusy(true); setErr('');
    try { const fd = new FormData(); fd.append('code', code.trim()); await api.post('/2fa/enable-confirm', fd); setEnabled(true); setUri(''); setCode(''); setMsg('2FA aktiviert.'); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Code ungültig'); }
    finally { setBusy(false); }
  }
  async function disable() {
    setBusy(true); setErr('');
    try { const fd = new FormData(); fd.append('code', code.trim()); await api.post('/2fa/disable', fd); setEnabled(false); setCode(''); setMsg('2FA deaktiviert.'); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Code ungültig'); }
    finally { setBusy(false); }
  }
  async function logoutAll() {
    if (!window.confirm('Auf allen Geräten abmelden? Sie werden überall neu angemeldet werden müssen.')) return;
    try { await api.post('/auth/logout-all'); onLoggedOut(); } catch { /* ignore */ }
  }

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 460 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Konto-Sicherheit</h2>
        <div style={{ fontSize: 14, marginBottom: 10 }}>Zwei-Faktor-Authentifizierung: {enabled === null ? '…' : enabled ? <span style={{ color: C.success, fontWeight: 600 }}>aktiv ✓</span> : <span style={{ color: C.muted }}>inaktiv</span>}</div>
        {!enabled && !uri && (
          <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={start}>2FA aktivieren</button>
        )}
        {!enabled && uri && (
          <>
            <p style={S.loginSub}>Scannen Sie den QR-Code mit einer Authenticator-App und geben Sie den 6-stelligen Code ein.</p>
            <div style={{ background: '#fff', padding: 12, borderRadius: 6, display: 'flex', justifyContent: 'center' }}><QRCodeSVG value={uri} size={180} /></div>
            <label style={S.label}>Code</label>
            <input style={S.input} value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" placeholder="123456" />
            <button style={{ ...S.btnPrimary, width: '100%' }} disabled={busy} onClick={confirm}>{busy ? 'Prüfe…' : 'Bestätigen'}</button>
          </>
        )}
        {enabled && role !== 'mitarbeiter' && (
          <>
            <div style={S.pathInfo}>Zum Deaktivieren bitte einen aktuellen Code aus Ihrer App eingeben.</div>
            <input style={S.input} value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" placeholder="123456" />
            <button style={{ ...S.btnDanger, marginLeft: 0, marginTop: 8 }} disabled={busy} onClick={disable}>2FA deaktivieren</button>
          </>
        )}
        {enabled && role === 'mitarbeiter' && <div style={S.pathInfo}>Für Mitarbeiter ist 2FA verpflichtend und kann nicht deaktiviert werden.</div>}

        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 16, paddingTop: 12 }}>
          <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={logoutAll}>Auf allen Geräten abmelden</button>
        </div>
        {msg && <div style={S.okMsg}>{msg}</div>}
        {err && <div style={S.err}>{err}</div>}
        <div style={{ marginTop: 12 }}><button style={{ ...S.btnGhost, color: C.text, borderColor: C.border }} onClick={onClose}>Schließen</button></div>
      </div>
    </div>
  );
}

const QA_THEMEN = [
  {
    f: 'Was ist bei Neueinstellung eines Mitarbeiters zu beachten?',
    a: [
      'Personalfragebogen vollständig ausfüllen (lassen) – im Portal digital möglich (Akte → „Personalfragebogen anfordern").',
      'Erforderliche Angaben: Steuer-Identifikationsnummer (für ELStAM-Abruf), Sozialversicherungsnummer, Krankenkasse, Geburtsdatum, Anschrift, IBAN, Beschäftigungsbeginn, vereinbartes Entgelt/Stunden.',
      'Anmeldung zur Sozialversicherung mit der ersten Abrechnung (Grund 10); in bestimmten Branchen (z. B. Bau, Gastronomie) zusätzlich Sofortmeldung am ersten Arbeitstag (§ 28a SGB IV).',
      'Arbeitsvertrag schließen; Nachweis der wesentlichen Arbeitsbedingungen (Nachweisgesetz, Textform genügt seit 2025).',
      'Mindestlohn beachten (2026: 13,90 €/Std.); bei ausländischen Beschäftigten Aufenthalts-/Arbeitserlaubnis prüfen.',
      'Unterlagen rechtzeitig vor dem ersten Abrechnungslauf ans Lohnbüro übermitteln.',
    ],
  },
  {
    f: 'Werkstudenten',
    a: [
      'Werkstudentenprivileg: in KV, PV und AV beitragsfrei – nur Rentenversicherung ist pflichtig.',
      'Voraussetzung: ordentlich eingeschriebene/r Student/in; in der Vorlesungszeit i. d. R. max. 20 Std./Woche.',
      'Immatrikulationsbescheinigung zu Beginn und bei Verlängerung vorlegen (im Portal als Frist hinterlegt).',
      'Bei Überschreiten der 20-Std.-Grenze oder am Semesterende ohne Vorlesung kann volle SV-Pflicht entstehen.',
    ],
  },
  {
    f: 'Praktika',
    a: [
      'Vorgeschriebenes (Pflicht-)Praktikum während des Studiums: meist SV-frei, kein Mindestlohnanspruch.',
      'Freiwilliges Praktikum: SV- und ggf. mindestlohnpflichtig (Ausnahmen z. B. Orientierungspraktikum bis 3 Monate).',
      'Vor-/Nachpraktikum außerhalb der Studienordnung: regelmäßig sozialversicherungspflichtig.',
      'Immer prüfen: Pflicht- oder freiwilliges Praktikum, Dauer, Entgelthöhe.',
    ],
  },
  {
    f: 'Gesellschafter-Geschäftsführer (GGF)',
    a: [
      'Sozialversicherungsstatus hängt vom beherrschenden Einfluss ab: Mehrheitsbeteiligung oder Sperrminorität → i. d. R. sozialversicherungsfrei.',
      'Minderheitsgesellschafter ohne maßgeblichen Einfluss → meist sozialversicherungspflichtig.',
      'Im Zweifel Statusfeststellungsverfahren (Clearingstelle der DRV Bund) durchführen.',
      'Auswirkung auf KV/PV/RV/AV – bitte vor der ersten Abrechnung klären.',
    ],
  },
  {
    f: 'Minijobber (geringfügige Beschäftigung)',
    a: [
      'Minijob-Grenze 2026: 603 €/Monat (dynamisch an den Mindestlohn gekoppelt).',
      'Pauschalabgaben trägt der Arbeitgeber; der Minijobber ist rentenversicherungspflichtig, kann sich aber auf Antrag befreien lassen.',
      'Befreiung von der RV-Pflicht: im Portal per Knopf anfordern – der Minijobber unterschreibt online, der Antrag bleibt in den Entgeltunterlagen.',
      'Mehrere Minijobs werden zusammengerechnet; Überschreiten der Grenze kann SV-Pflicht auslösen.',
    ],
  },
  {
    f: 'Kurzfristige Beschäftigung',
    a: [
      'SV-frei, wenn auf max. 3 Monate oder 70 Arbeitstage im Kalenderjahr befristet und nicht berufsmäßig ausgeübt.',
      'Keine Verdienstgrenze, aber Mindestlohn gilt.',
      'Berufsmäßigkeit prüfen (z. B. bei Arbeitssuchenden) – sonst SV-Pflicht.',
    ],
  },
  {
    f: 'Midijob / Übergangsbereich',
    a: [
      'Verdienst zwischen 556,01 € und 2.000 €/Monat: reduzierte Arbeitnehmer-Beiträge im Übergangsbereich.',
      'Voller Rentenversicherungsschutz trotz reduzierter Beiträge.',
    ],
  },
  {
    f: 'Beschäftigung von Rentnern',
    a: [
      'Altersvollrentner nach Regelaltersgrenze: in der RV beitragsfrei (Arbeitgeberanteil fällt an), KV/PV nach allgemeinen Regeln.',
      'Vorgezogene Altersrente: frühere Hinzuverdienstgrenzen sind entfallen – dennoch individuell prüfen.',
    ],
  },
  {
    f: 'Krankmeldung & Entgeltfortzahlung (eAU)',
    a: [
      'Arbeitsunfähigkeit im Portal unter „Erfassen → Fehlzeit → Krankheit" mit Zeitraum im Kalender melden – das Lohnbüro ruft die eAU bei der Krankenkasse ab.',
      'Entgeltfortzahlung i. d. R. bis zu 6 Wochen; danach Krankengeld der Krankenkasse.',
      'Erstattung nach U1 (kleine Betriebe) wird vom Lohnbüro beantragt.',
    ],
  },
];

function QandA() {
  const [open, setOpen] = useState(0);
  return (
    <div>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: C.primary }}>Hilfe & häufige Fragen zum Lohn</h2>
        </div>
        <div style={S.pathInfo}>Allgemeine Hinweise ohne Gewähr – im Zweifel bitte Rücksprache mit dem Lohnbüro.</div>
      </div>
      {QA_THEMEN.map((t, i) => (
        <div key={i} style={S.card}>
          <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setOpen(open === i ? -1 : i)}>
            <h3 style={{ margin: 0, fontSize: 15, color: C.primary }}>{t.f}</h3>
            <span style={{ color: C.muted }}>{open === i ? '▲' : '▼'}</span>
          </div>
          {open === i && (
            <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
              {t.a.map((p, j) => <li key={j} style={{ marginBottom: 4 }}>{p}</li>)}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function fmtDur(s) {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h > 0 ? h + ':' : '') + String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
}
function fmtStd(s) { return ((s || 0) / 3600).toFixed(2).replace('.', ',') + ' h'; }

function TimerBar({ mandantId, mandantName, onStopped }) {
  const [z, setZ] = useState(null);
  const [el, setEl] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setZ(null); setEl(0);
    api.get('/zeiterfassung/aktiv', { params: { mandant_id: mandantId } })
      .then(({ data }) => { if (live && data) { setZ(data); setEl(data.elapsed || 0); } })
      .catch(() => {});
    return () => { live = false; };
  }, [mandantId]);

  useEffect(() => {
    if (!z || !z.running) return;
    const t = setInterval(() => setEl(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [z && z.running]);

  async function act(path, method = 'post') {
    setBusy(true);
    try {
      const { data } = await api[method](path);
      setZ(data); setEl(data.elapsed || 0);
      return data;
    } catch (ex) { alert(ex.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  }
  async function start() {
    setBusy(true);
    try { const fd = new FormData(); fd.append('mandant_id', mandantId);
      const { data } = await api.post('/zeiterfassung/start', fd); setZ(data); setEl(data.elapsed || 0);
    } catch (ex) { alert(ex.response?.data?.detail || 'Start fehlgeschlagen'); } finally { setBusy(false); }
  }
  async function stop() {
    const data = await act(`/zeiterfassung/${z.id}/stop`);
    if (data) { const n = (data.items || []).length; setZ(null); setEl(0);
      if (onStopped) onStopped();
      alert(`Zeitabschnitt gespeichert (${fmtDur(data.elapsed)}, ${n} Aufgabe(n)). Du findest ihn unter „Leistungen".`); }
  }

  const running = z && z.running;
  const paused = z && z.status === 'pausiert';
  const dot = running ? '#27ae60' : (paused ? '#e0a800' : '#9aa6a0');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', marginBottom: 16,
      background: '#fff', border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.accent}`, borderRadius: 6 }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: dot, flex: 'none' }} />
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 16, minWidth: 70 }}>{fmtDur(el)}</span>
      <span style={{ color: C.muted, fontSize: 13 }}>Zeiterfassung · {mandantName || 'Mandant'}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        {!z && <button disabled={busy} style={tbBtn(C.success)} onClick={start}>▶ Start</button>}
        {running && <button disabled={busy} style={tbBtn(C.muted)} onClick={() => act(`/zeiterfassung/${z.id}/pause`)}>⏸ Pause</button>}
        {paused && <button disabled={busy} style={tbBtn(C.success)} onClick={() => act(`/zeiterfassung/${z.id}/resume`)}>▶ Weiter</button>}
        {z && <button disabled={busy} style={tbBtn(C.danger)} onClick={stop}>⏹ Stop</button>}
      </div>
    </div>
  );
}
const tbBtn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' });

const ZE_TYP_LABEL = { ticket: '🎫', abrechnung: '📄', beitragsnachweis: '🏥', lohnsteuer: '🧾', custom: '✏️' };

function Zeitabschnitte({ mandantId }) {
  const [list, setList] = useState([]);
  const [showArchiv, setShowArchiv] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [edit, setEdit] = useState(null);   // { id, ende: "YYYY-MM-DDTHH:MM" (lokal) }

  function toLocalInput(iso) {
    const d = new Date(_asUtc(iso));
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }
  const editDauerSek = (z) => {
    if (!edit || !edit.ende) return null;
    const s = (new Date(edit.ende).getTime() - new Date(_asUtc(z.start_at)).getTime()) / 1000;
    return isNaN(s) ? null : Math.round(s);
  };
  async function saveZeit(z) {
    const sek = editDauerSek(z);
    if (sek == null || sek <= 0) { window.alert('Das Ende muss nach dem Start liegen.'); return; }
    try {
      const fd = new FormData(); fd.append('ende', new Date(edit.ende).toISOString());
      await api.patch(`/zeiterfassung/${z.id}/zeit`, fd);
      setEdit(null); await load();
    } catch (ex) { window.alert(ex.response?.data?.detail || 'Korrektur fehlgeschlagen'); }
  }

  async function load() {
    if (!mandantId) { setList([]); return; }
    const { data } = await api.get('/zeiterfassung', { params: { mandant_id: mandantId } });
    setList(data.filter(z => z.status === 'gestoppt' || (showArchiv && z.status === 'archiviert')));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mandantId, showArchiv]);

  async function saveItems(z, items) {
    const fd = new FormData(); fd.append('items', JSON.stringify(items));
    await api.patch(`/zeiterfassung/${z.id}/items`, fd); load();
  }
  function delItem(z, idx) { saveItems(z, z.items.filter((_, i) => i !== idx)); }
  function addItem(z) {
    const v = newItem[z.id]; if (!v || !v.trim()) return;
    saveItems(z, [...(z.items || []), { typ: 'custom', label: v.trim(), count: 1 }]);
    setNewItem(p => ({ ...p, [z.id]: '' }));
  }
  async function archivieren(z) {
    if (!window.confirm('Diesen Zeitabschnitt archivieren?')) return;
    await api.post(`/zeiterfassung/${z.id}/archivieren`); load();
  }
  async function del(z) {
    if (!window.confirm('Zeitabschnitt löschen?')) return;
    await api.delete(`/zeiterfassung/${z.id}`); load();
  }

  if (!mandantId) return null;
  return (
    <div style={S.card}>
      <h2 style={S.cardTitle}>Zeitabschnitte (Zeiterfassung)</h2>
      <label style={{ fontSize: 12, color: C.muted, display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <input type="checkbox" checked={showArchiv} onChange={e => setShowArchiv(e.target.checked)} /> auch archivierte anzeigen
      </label>
      {list.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>Noch keine gestoppten Zeitabschnitte. Starte den Timer oben im Banner.</div>}
      {list.map(z => (
        <div key={z.id} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 14, marginBottom: 12, opacity: z.status === 'archiviert' ? 0.65 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>⏱ {fmtDur(z.dauer_sekunden)}</strong>
            <span style={{ color: C.muted, fontSize: 12 }}>{fmtDate(z.start_at)}{z.end_at ? ` – ${fmtDate(z.end_at)}` : ''}</span>
            {z.status === 'archiviert' && <span style={{ ...S.badge }}>archiviert</span>}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {z.status === 'gestoppt' && <button title="Bearbeitungszeit nachträglich korrigieren" style={{ ...S.btnPrimary, marginTop: 0, padding: '5px 10px', fontSize: 12, background: C.accent }} onClick={() => setEdit(edit && edit.id === z.id ? null : { id: z.id, ende: toLocalInput(z.end_at || z.start_at) })}>✎ Zeit</button>}
              {z.status === 'gestoppt' && <button style={{ ...S.btnPrimary, marginTop: 0, padding: '5px 10px', fontSize: 12 }} onClick={() => archivieren(z)}>Archivieren</button>}
              <button style={{ ...S.btnPrimary, marginTop: 0, padding: '5px 10px', fontSize: 12, background: C.danger }} onClick={() => del(z)}>Löschen</button>
            </div>
          </div>
          {edit && edit.id === z.id && (
            <div style={{ background: C.bg, border: `1px dashed ${C.accent}`, borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Start: <b>{fmtDate(z.start_at)}</b> · Neues Ende wählen – die Dauer wird daraus berechnet.</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="datetime-local" style={{ ...S.input, width: 210 }} value={edit.ende} onChange={e => setEdit({ ...edit, ende: e.target.value })} />
                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  {(() => { const s = editDauerSek(z); return s == null || s <= 0 ? <span style={{ color: C.danger }}>Ende vor Start!</span> : <>neue Dauer: <b>{fmtDur(s)}</b></>; })()}
                </span>
                <button style={{ ...S.btnPrimary, marginTop: 0, padding: '5px 12px', fontSize: 12 }} onClick={() => saveZeit(z)}>Speichern</button>
                <button style={{ ...S.btnGhost, color: C.text, borderColor: C.border, padding: '5px 12px', fontSize: 12 }} onClick={() => setEdit(null)}>Abbrechen</button>
              </div>
            </div>
          )}
          {(z.items || []).length === 0 && <div style={{ color: C.muted, fontSize: 12, marginBottom: 8 }}>Keine Aufgaben erfasst.</div>}
          {(z.items || []).map((it, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${C.bg}` }}>
              <span>{ZE_TYP_LABEL[it.typ] || '•'}</span>
              <span style={{ fontSize: 13 }}>{it.label}{it.count > 1 ? ` (×${it.count})` : ''}</span>
              {z.status === 'gestoppt' && <button title="entfernen" style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: C.danger, cursor: 'pointer', fontSize: 16 }} onClick={() => delItem(z, idx)}>×</button>}
            </div>
          ))}
          {z.status === 'gestoppt' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="Eigene Aufgabe hinzufügen …" value={newItem[z.id] || ''}
                onChange={e => setNewItem(p => ({ ...p, [z.id]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') addItem(z); }} />
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={() => addItem(z)}>+ Aufgabe</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Insights({ mandantId, mandantName }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!mandantId) { setD(null); return; }
    setD(null); setErr('');
    api.get(`/mandanten/${mandantId}/insights`).then(({ data }) => setD(data)).catch(() => setErr('Konnte Insights nicht laden'));
  }, [mandantId]);

  if (!mandantId) return <div style={S.card}>Bitte oben einen Mandanten wählen.</div>;
  if (err) return <div style={S.card}>{err}</div>;
  if (!d) return <div style={S.card}>Lädt …</div>;
  const eur = n => (n == null ? '–' : (n.toFixed(2).replace('.', ',') + ' €'));
  const kpi = (label, value, color) => (
    <div style={{ flex: '1 1 150px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || C.primary }}>{value}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{label}</div>
    </div>
  );
  const deckPos = (d.deckungsbeitrag || 0) >= 0;
  return (
    <div>
      <h1 style={{ fontSize: 22, color: C.primary, marginBottom: 4 }}>Insights · {mandantName || 'Mandant'}</h1>
      <p style={{ color: C.muted, marginTop: 0 }}>Wirtschaftlichkeit: getrackte Zeit gegenübergestellt zum Umsatz.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        {kpi('Getrackte Zeit', fmtStd(d.getrackte_sekunden))}
        {kpi('Umsatz gesamt (netto)', eur(d.umsatz_gesamt), C.success)}
        {kpi(`Zeitwert (interne Kosten, Ø ${eur(d.stundensatz)}/h)`, eur(d.zeitwert_kosten))}
        {kpi('Deckungsbeitrag', eur(d.deckungsbeitrag), deckPos ? C.success : C.danger)}
        {kpi('Ertrag / Stunde', d.ertrag_pro_stunde == null ? '–' : eur(d.ertrag_pro_stunde), (d.ertrag_pro_stunde || 0) >= d.stundensatz ? C.success : C.danger)}
      </div>
      <div style={S.card}>
        <h2 style={S.cardTitle}>Zusammensetzung</h2>
        <div style={{ fontSize: 13, lineHeight: 1.9 }}>
          <div>Berechnete Rechnungen (netto): <b>{eur(d.umsatz_berechnet)}</b></div>
          <div>Offene, noch nicht abgerechnete Leistungen: <b>{eur(d.offene_leistungen_netto)}</b></div>
          <div>Zeitabschnitte: <b>{d.anzahl_sessions}</b> · erfasste Aufgaben: <b>{d.aufgaben_gesamt}</b></div>
          <div style={{ marginTop: 6 }}>
            {Object.entries(d.typ_count || {}).map(([t, c]) => (
              <span key={t} style={{ ...S.badge, marginRight: 6 }}>{(ZE_TYP_LABEL[t] || '•')} {t}: {c}</span>
            ))}
          </div>
        </div>
      </div>
      <div style={S.card}>
        <h2 style={S.cardTitle}>Zeitabschnitte</h2>
        {(d.sessions || []).length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>Noch keine erfasste Zeit.</div>}
        {(d.sessions || []).length > 0 && (
          <table style={S.table}><thead><tr><th style={S.th}>Datum</th><th style={S.th}>Dauer</th><th style={S.th}>Aufgaben</th><th style={S.th}>Status</th></tr></thead>
            <tbody>{d.sessions.map(s => (
              <tr key={s.id}><td style={S.td}>{fmtDate(s.datum)}</td><td style={S.td}>{fmtDur(s.dauer_sekunden)}</td><td style={S.td}>{s.aufgaben}</td><td style={S.td}><span style={S.badge}>{s.status}</span></td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MitarbeiterKarte({ mandantId, onOpenMa }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  async function load() {
    if (!mandantId) { setList([]); return; }
    try { const { data } = await api.get('/mitarbeiter-karte', { params: { mandant_id: mandantId } }); setList(data); }
    catch (ex) { setMsg('Konnte Mitarbeiter nicht laden'); }
  }
  useEffect(() => { setSel(null); load(); /* eslint-disable-next-line */ }, [mandantId]);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: true }).setView([51.16, 10.45], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, lg = layerRef.current;
    if (!map || !lg) return;
    lg.clearLayers();
    const pts = [];
    list.filter(m => m.lat != null && m.lng != null).forEach(m => {
      const col = m.aktiv ? '#27ae60' : '#9aa6a0';
      const mk = L.circleMarker([m.lat, m.lng], { radius: 9, color: '#fff', weight: 2, fillColor: col, fillOpacity: 0.95 });
      mk.bindTooltip(m.name);
      mk.on('click', () => setSel(m));
      mk.addTo(lg); pts.push([m.lat, m.lng]);
    });
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 13 });
  }, [list]);

  async function geocode() {
    setBusy(true); setMsg('');
    try {
      const fd = new FormData(); fd.append('mandant_id', mandantId);
      const { data } = await api.post('/mitarbeiter-karte/geocode', fd);
      setMsg(`${data.geocodiert} Adresse(n) verortet.${data.ohne_adresse ? ` ${data.ohne_adresse} ohne hinterlegte Adresse.` : ''}${data.ohne_koordinaten ? ` ${data.ohne_koordinaten} nicht gefunden.` : ''}`);
      await load();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Verorten fehlgeschlagen'); }
    finally { setBusy(false); }
  }

  if (!mandantId) return <div style={S.card}>Bitte oben einen Mandanten wählen.</div>;
  const verortet = list.filter(m => m.lat != null).length;
  const mitAdresse = list.filter(m => m.hat_adresse).length;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, color: C.primary, margin: 0 }}>Mitarbeiter-Karte</h1>
        <span style={{ color: C.muted, fontSize: 13 }}>{verortet} von {mitAdresse} mit Adresse verortet</span>
        <button disabled={busy || mitAdresse === 0} style={{ ...S.btnPrimary, marginTop: 0, marginLeft: 'auto' }} onClick={geocode}>{busy ? 'Verorte …' : '📍 Adressen verorten'}</button>
      </div>
      {msg && <div style={{ background: '#e8f4ea', color: C.success, padding: 8, borderRadius: 4, marginBottom: 10, fontSize: 13 }}>{msg}</div>}
      <div style={{ position: 'relative' }}>
        <div ref={mapEl} style={{ height: 540, borderRadius: 8, border: `1px solid ${C.border}`, zIndex: 1 }} />
        {sel && (
          <div style={{ position: 'absolute', top: 12, right: 12, width: 280, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.15)', padding: 14, zIndex: 1100 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <strong style={{ fontSize: 15 }}>{sel.name}</strong>
              <button onClick={() => setSel(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: C.muted }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.8, marginTop: 6 }}>
              <div><span style={S.badge}>{sel.status}</span>{sel.aktiv ? '' : ' · inaktiv'}</div>
              {sel.pers_nr && <div>Pers-Nr.: {sel.pers_nr}</div>}
              {sel.eintritt && <div>Eintritt: {fmtDateDE(sel.eintritt)}</div>}
              {sel.wochenstunden && <div>Wochenstd.: {sel.wochenstunden}</div>}
              {sel.krankenkasse && <div>KK: {sel.krankenkasse}</div>}
              {sel.telefon && <div>☎ {sel.telefon}</div>}
              {sel.email && <div>✉ {sel.email}</div>}
              {sel.anschrift && <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{sel.anschrift}</div>}
            </div>
            <button style={{ ...S.btnPrimary, marginTop: 12, width: '100%' }} onClick={() => onOpenMa && onOpenMa(sel.id)}>Vollständige Akte öffnen →</button>
          </div>
        )}
      </div>
      {verortet === 0 && <div style={{ color: C.muted, fontSize: 13, marginTop: 10 }}>Noch keine Mitarbeiter verortet. Klicke auf „Adressen verorten", um die hinterlegten Wohnadressen einmalig auf der Karte zu platzieren.</div>}
    </div>
  );
}

const IDEE_STATUS = [
  ['neu', 'Neu', '#fdeccd', '#8a6d3b'],
  ['geplant', 'Geplant', '#e3e8ff', '#3d4eb0'],
  ['in_arbeit', 'In Arbeit', '#e8eef5', C.accent],
  ['erledigt', 'Erledigt', '#e8f4ea', C.success],
  ['abgelehnt', 'Abgelehnt', '#fdecea', C.danger],
];
const IDEE_KATEGORIEN = ['Mandantenportal', 'Lohn & Abrechnung', 'Dokumente', 'CRM & Leads', 'Website & SEO',
  'E-Mail & Benachrichtigungen', 'Sicherheit & DSGVO', 'Schnittstellen (Lexware/DATEV)', 'Onboarding',
  'Mitarbeiter-Karte', 'Zeiterfassung & Insights', 'UI/UX', 'Bug', 'Sonstiges'];
const PRIO = { kritisch: ['Kritisch', C.danger], hoch: ['Hoch', '#e67e22'], mittel: ['Mittel', C.accent], niedrig: ['Niedrig', C.muted] };

function IdeenBoard({ user }) {
  const [ideen, setIdeen] = useState([]);
  const [sel, setSel] = useState(null);
  const [komm, setKomm] = useState([]);
  const [kommText, setKommText] = useState('');
  const [anh, setAnh] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ prioritaet: 'mittel' });
  const [katFilter, setKatFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [nfFiles, setNfFiles] = useState([]);

  async function moveIdee(id, statusKey) {
    setDragId(null); setDragOver(null);
    const it = ideen.find(x => x.id === id);
    if (!it || (it.status || 'neu') === statusKey) return;
    setIdeen(list => list.map(x => x.id === id ? { ...x, status: statusKey } : x)); // optimistisch
    try { const fd = new FormData(); fd.append('status', statusKey); await api.patch(`/ideen/${id}`, fd); }
    catch { await load(); }
  }

  async function load() { try { const { data } = await api.get('/ideen'); setIdeen(data); } catch { setMsg('Konnte Ideen nicht laden'); } }
  useEffect(() => { load(); }, []);

  async function openIdee(id) {
    const fresh = (await api.get('/ideen')).data; setIdeen(fresh);
    const i = fresh.find(x => x.id === id); setSel(i || null);
    if (i) {
      const { data } = await api.get(`/ideen/${id}/kommentare`); setKomm(data);
      const a = await api.get(`/ideen/${id}/anhaenge`); setAnh(a.data);
    }
  }
  async function uploadAnh(fileList) {
    const files = Array.from(fileList || []); if (!files.length) return;
    setBusy(true);
    try { const fd = new FormData(); files.forEach(f => fd.append('files', f));
      await api.post(`/ideen/${sel.id}/anhaenge`, fd);
      const a = await api.get(`/ideen/${sel.id}/anhaenge`); setAnh(a.data);
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Upload fehlgeschlagen'); } finally { setBusy(false); }
  }
  async function downloadAnh(d) {
    try { const res = await api.get(`/ideen/${sel.id}/anhaenge/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = d.original_name; a.click(); URL.revokeObjectURL(url);
    } catch { setMsg('Download fehlgeschlagen'); }
  }
  const field = (k, v) => setSel(s => ({ ...s, [k]: v }));

  async function save() {
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      ['titel', 'beschreibung', 'kategorie', 'prioritaet', 'status'].forEach(k => fd.append(k, sel[k] == null ? '' : sel[k]));
      const { data } = await api.patch(`/ideen/${sel.id}`, fd); setSel(data); setMsg('Gespeichert.'); await load();
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Fehler'); } finally { setBusy(false); }
  }
  async function vote(id, richtung, e) {
    if (e) e.stopPropagation();
    const fd = new FormData(); fd.append('richtung', richtung);
    await api.post(`/ideen/${id}/vote`, fd); await load();
    if (sel && sel.id === id) { const fresh = (await api.get('/ideen')).data; setSel(fresh.find(x => x.id === id)); }
  }
  async function addKomm() {
    if (!kommText.trim()) return;
    const fd = new FormData(); fd.append('text', kommText.trim());
    await api.post(`/ideen/${sel.id}/kommentar`, fd); setKommText('');
    const { data } = await api.get(`/ideen/${sel.id}/kommentare`); setKomm(data);
  }
  async function del() {
    if (!window.confirm('Idee löschen?')) return;
    await api.delete(`/ideen/${sel.id}`); setSel(null); await load();
  }
  async function createIdee() {
    if (!(nf.titel || '').trim()) { window.alert('Titel erforderlich'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      ['titel', 'beschreibung', 'kategorie', 'prioritaet'].forEach(k => { if (nf[k]) fd.append(k, nf[k]); });
      const { data } = await api.post('/ideen', fd);
      if (nfFiles.length) {
        const afd = new FormData(); nfFiles.forEach(f => afd.append('files', f));
        try { await api.post(`/ideen/${data.id}/anhaenge`, afd); } catch { /* */ }
      }
      setShowNew(false); setNf({ prioritaet: 'mittel' }); setNfFiles([]); await load(); openIdee(data.id);
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Fehler'); } finally { setBusy(false); }
  }

  const sichtbar = katFilter ? ideen.filter(i => i.kategorie === katFilter) : ideen;
  const lab = t => <label style={S.label}>{t}</label>;
  const prioBadge = p => { const [t, c] = PRIO[p] || PRIO.mittel; return <span style={{ ...S.badge, background: '#fff', border: `1px solid ${c}`, color: c }}>{t}</span>; };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, color: C.primary, margin: 0 }}>Ideen &amp; Roadmap</h1>
        <span style={{ color: C.muted, fontSize: 13 }}>{ideen.length} Einträge</span>
        <select style={{ ...S.select, width: 200, marginLeft: 8 }} value={katFilter} onChange={e => setKatFilter(e.target.value)}>
          <option value="">Alle Kategorien</option>{IDEE_KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <button style={{ ...S.btnPrimary, marginTop: 0, marginLeft: 'auto' }} onClick={() => { setNf({ prioritaet: 'mittel' }); setShowNew(true); }}>+ Idee / Ticket</button>
      </div>
      <p style={{ color: C.muted, marginTop: 0, fontSize: 13 }}>Sammle frei Ideen, Tickets und Bugs für die Weiterentwicklung. Nur für Büro-Admins.</p>
      {msg && <div style={{ background: '#e8f4ea', color: C.success, padding: 10, borderRadius: 4, marginBottom: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
        {IDEE_STATUS.map(([key, label, bg, col]) => {
          const items = sichtbar.filter(i => (i.status || 'neu') === key);
          return (
            <div key={key}
              onDragOver={e => { e.preventDefault(); if (dragOver !== key) setDragOver(key); }}
              onDragLeave={() => setDragOver(o => o === key ? null : o)}
              onDrop={() => moveIdee(dragId, key)}
              style={{ minWidth: 230, flex: '1 0 230px', background: dragOver === key ? '#e8f0fb' : C.bg, outline: dragOver === key ? `2px dashed ${C.accent}` : 'none', borderRadius: 8, padding: 10, transition: 'background .15s' }}>
              <div style={{ marginBottom: 8 }}><span style={{ ...S.badge, background: bg, color: col }}>{label} · {items.length}</span></div>
              {items.map(i => (
                <div key={i.id} draggable
                  onDragStart={e => { setDragId(i.id); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragId(null); setDragOver(null); }}
                  onClick={() => openIdee(i.id)}
                  style={{ background: '#fff', border: `1px solid ${C.border}`, borderLeft: `3px solid ${col}`, borderRadius: 6, padding: 10, marginBottom: 8, cursor: 'grab', opacity: dragId === i.id ? 0.5 : 1 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ textAlign: 'center', minWidth: 30 }}>
                      <button onClick={e => vote(i.id, 'up', e)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.accent, fontSize: 14, lineHeight: 1 }}>▲</button>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{i.votes}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 13 }}>{i.titel}</strong>
                      <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {prioBadge(i.prioritaet)}
                        {i.kategorie && <span style={{ ...S.badge }}>{i.kategorie}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: 8 }}>–</div>}
            </div>
          );
        })}
      </div>

      {sel && (
        <div style={S.modalOverlay} onClick={() => setSel(null)}>
          <div style={{ ...S.modalCard, width: 680, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ margin: 0, color: C.primary }}>Idee #{sel.id}</h2>
              <button onClick={() => setSel(null)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <button onClick={() => vote(sel.id, 'up')} style={{ ...S.btnGhost, marginTop: 0, padding: '4px 10px' }}>▲ {sel.votes} Stimmen</button>
            </div>
            {lab('Titel')}<input style={S.input} value={sel.titel || ''} onChange={e => field('titel', e.target.value)} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>{lab('Status')}<select style={S.select} value={sel.status || 'neu'} onChange={e => field('status', e.target.value)}>{IDEE_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <div style={{ flex: 1 }}>{lab('Priorität')}<select style={S.select} value={sel.prioritaet || 'mittel'} onChange={e => field('prioritaet', e.target.value)}>{Object.entries(PRIO).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}</select></div>
              <div style={{ flex: 1 }}>{lab('Kategorie')}<select style={S.select} value={sel.kategorie || ''} onChange={e => field('kategorie', e.target.value)}><option value="">–</option>{IDEE_KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}</select></div>
            </div>
            {lab('Beschreibung')}<textarea style={{ ...S.textarea, minHeight: 120 }} value={sel.beschreibung || ''} onChange={e => field('beschreibung', e.target.value)} placeholder="Beschreib die Idee frei – Problem, Wunsch, Lösungsansatz …" />
            <button disabled={busy} style={{ ...S.btnPrimary }} onClick={save}>Speichern</button>

            <h3 style={{ fontSize: 14, color: C.primary, marginTop: 18 }}>Anhänge</h3>
            {anh.length === 0 ? <div style={{ fontSize: 12, color: C.muted }}>Keine Anhänge.</div> : anh.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, borderBottom: `1px solid ${C.bg}` }}>
                <span>📎</span>
                <button onClick={() => downloadAnh(d)} style={{ border: 'none', background: 'transparent', color: C.accent, cursor: 'pointer', padding: 0, textAlign: 'left', flex: 1 }}>{d.original_name}</button>
                <span style={{ color: C.muted, fontSize: 11 }}>{d.uploaded_by || ''} · {fmtDate(d.created_at)}</span>
              </div>
            ))}
            <label style={{ ...S.btnGhost, display: 'inline-block', cursor: 'pointer', marginTop: 8, color: C.accent, borderColor: C.accent }}>
              📎 Datei anhängen
              <input type="file" multiple style={{ display: 'none' }} onChange={e => { uploadAnh(e.target.files); e.target.value = ''; }} />
            </label>

            <h3 style={{ fontSize: 14, color: C.primary, marginTop: 18 }}>Kommentare</h3>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input style={{ ...S.input, flex: 1 }} value={kommText} placeholder="Kommentar …" onChange={e => setKommText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addKomm(); }} />
              <button style={{ ...S.btnPrimary, marginTop: 0 }} onClick={addKomm}>+</button>
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {komm.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Noch keine Kommentare.</div>}
              {komm.map(k => (
                <div key={k.id} style={{ borderBottom: `1px solid ${C.bg}`, padding: '6px 0', fontSize: 13 }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{k.text}</div>
                  <div style={{ color: C.muted, fontSize: 11 }}>{k.user || '—'} · {fmtDate(k.created_at)}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12, textAlign: 'right' }}>
              <button disabled={busy} style={{ ...S.btnGhost, marginTop: 0, color: C.danger, borderColor: C.danger }} onClick={del}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div style={S.modalOverlay} onClick={() => setShowNew(false)}>
          <div style={{ ...S.modalCard, width: 520, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, color: C.primary }}>Neue Idee / Ticket</h2>
            {lab('Titel *')}<input style={S.input} value={nf.titel || ''} onChange={e => setNf({ ...nf, titel: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>{lab('Priorität')}<select style={S.select} value={nf.prioritaet} onChange={e => setNf({ ...nf, prioritaet: e.target.value })}>{Object.entries(PRIO).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}</select></div>
              <div style={{ flex: 1 }}>{lab('Kategorie')}<select style={S.select} value={nf.kategorie || ''} onChange={e => setNf({ ...nf, kategorie: e.target.value })}><option value="">–</option>{IDEE_KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}</select></div>
            </div>
            {lab('Beschreibung')}<textarea style={{ ...S.textarea, minHeight: 120 }} value={nf.beschreibung || ''} onChange={e => setNf({ ...nf, beschreibung: e.target.value })} placeholder="Frei beschreiben – was, warum, wie?" />
            {lab('Anhänge (optional)')}
            <input type="file" multiple style={{ fontSize: 13 }} onChange={e => setNfFiles(Array.from(e.target.files || []))} />
            {nfFiles.length > 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{nfFiles.length} Datei(en) ausgewählt: {nfFiles.map(f => f.name).join(', ')}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button disabled={busy} style={{ ...S.btnPrimary, marginTop: 0 }} onClick={createIdee}>Anlegen</button>
              <button style={{ ...S.btnGhost, marginTop: 0 }} onClick={() => setShowNew(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Buchführung (EÜR, § 4 Abs. 3 EStG): Übersicht, Buchungen, Anlagen, Steuern, Einstellungen ----------
const ART_FARBE = {
  einnahme: ['#e9f7ef', C.success], ausgabe: ['#fdeccd', '#8a6d3b'], anlage: ['#e8eef5', C.accent], afa: ['#eef0f3', C.muted],
  privateinlage: ['#f3e8f7', '#7d3c98'], privatentnahme: ['#f3e8f7', '#7d3c98'], geldtransit: ['#eceff3', C.muted],
  ust_zahlung: ['#fdecea', C.danger], ust_erstattung: ['#e9f7ef', C.success],
  forderung: ['#e9f7ef', C.success], zahlungseingang: ['#e3f1fb', C.accent], verbindlichkeit: ['#fdeccd', '#8a6d3b'],
  zahlungsausgang: ['#e3f1fb', C.accent], ausgleich: ['#eef0f3', C.muted], umbuchung: ['#eef0f3', C.muted], anlagenabgang: ['#fdecea', C.danger],
};
const ArtBadge = ({ art, label }) => { const [bg, col] = ART_FARBE[art] || ['#eceff3', C.muted]; return <span style={{ ...S.badge, background: bg, color: col, whiteSpace: 'nowrap' }}>{label || art}</span>; };
const dDE = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');
const isoTag = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const kpiBox = (label, value, col, sub, onClick) => (
  <div onClick={onClick} style={{ flex: '1 1 170px', background: '#fff', border: `1px solid ${C.border}`, borderTop: `3px solid ${col || C.accent}`, borderRadius: 10, padding: '12px 16px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', cursor: onClick ? 'pointer' : 'default', minWidth: 150 }}>
    <div style={{ fontSize: 12, color: C.muted }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: col || C.primary, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);
async function dlDatei(url, params) {
  const { data, headers } = await api.get(url, { params, responseType: 'blob' });
  const cd = headers['content-disposition'] || ''; const m = /filename="?([^";]+)"?/.exec(cd);
  const a = document.createElement('a'); a.href = URL.createObjectURL(data); a.download = m ? m[1] : 'export.csv'; document.body.appendChild(a); a.click(); a.remove();
}

function ZeitraumWahl({ von, bis, setVon, setBis }) {
  const today = new Date();
  function preset(p) {
    const y = today.getFullYear(), m = today.getMonth();
    if (p === 'monat') { setVon(isoTag(new Date(y, m, 1))); setBis(isoTag(new Date(y, m + 1, 0))); }
    else if (p === 'vmonat') { setVon(isoTag(new Date(y, m - 1, 1))); setBis(isoTag(new Date(y, m, 0))); }
    else if (p === 'quartal') { const q = Math.floor(m / 3); setVon(isoTag(new Date(y, q * 3, 1))); setBis(isoTag(new Date(y, q * 3 + 3, 0))); }
    else if (p === 'vquartal') { const q = Math.floor(m / 3) - 1; setVon(isoTag(new Date(y, q * 3, 1))); setBis(isoTag(new Date(y, q * 3 + 3, 0))); }
    else if (p === 'jahr') { setVon(isoTag(new Date(y, 0, 1))); setBis(isoTag(new Date(y, 11, 31))); }
    else if (p === 'vjahr') { setVon(isoTag(new Date(y - 1, 0, 1))); setBis(isoTag(new Date(y - 1, 11, 31))); }
  }
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {[['monat', 'Monat'], ['vmonat', 'Vormonat'], ['quartal', 'Quartal'], ['vquartal', 'Vorquartal'], ['jahr', 'Jahr'], ['vjahr', 'Vorjahr']].map(([k, l]) =>
        <button key={k} style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => preset(k)}>{l}</button>)}
      <input type="date" style={{ ...S.input, width: 'auto', padding: '6px 8px' }} value={von} onChange={e => setVon(e.target.value)} />
      <span style={{ color: C.muted }}>–</span>
      <input type="date" style={{ ...S.input, width: 'auto', padding: '6px 8px' }} value={bis} onChange={e => setBis(e.target.value)} />
    </div>
  );
}

function MonatsChart({ monate, hoehe }) {
  const H = hoehe || 120;
  const max = Math.max(1, ...monate.map(m => Math.max(m.einnahmen || 0, m.ausgaben || 0)));
  const namen = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: H + 20, padding: '0 4px' }}>
        {monate.map(m => (
          <div key={m.monat} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }} title={`${namen[m.monat - 1]}: Einnahmen ${fmtEur(m.einnahmen)} · Ausgaben ${fmtEur(m.ausgaben)}`}>
            <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: H, width: '100%', justifyContent: 'center' }}>
              <div style={{ width: '40%', maxWidth: 22, height: Math.max(2, Math.round((m.einnahmen || 0) / max * H)), background: C.success, borderRadius: '3px 3px 0 0' }} />
              <div style={{ width: '40%', maxWidth: 22, height: Math.max(2, Math.round((m.ausgaben || 0) / max * H)), background: '#b85c00', borderRadius: '3px 3px 0 0' }} />
            </div>
            <div style={{ fontSize: 10, color: C.muted }}>{namen[m.monat - 1]}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: C.success, borderRadius: 2, marginRight: 4 }} />Einnahmen (netto) <span style={{ display: 'inline-block', width: 10, height: 10, background: '#b85c00', borderRadius: 2, margin: '0 4px 0 12px' }} />Ausgaben (netto)</div>
    </div>
  );
}

function FinanzUebersicht({ onGo }) {
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { const { data } = await api.get('/buchhaltung/uebersicht', { params: { jahr } }); setD(data); setErr(''); } catch (ex) { setErr(ex.response?.data?.detail || 'Übersicht konnte nicht geladen werden.'); } }, [jahr]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div style={{ ...S.hint, color: C.danger }}>{err}</div>;
  if (!d) return <div style={S.empty}>Lade…</div>;
  const u = d.ustva_naechste;
  const jahre = [jahr - 1, jahr, jahr + 1].filter(j => j <= new Date().getFullYear());
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ color: C.muted, fontSize: 13 }}>Einnahmen-Überschuss-Rechnung (§ 4 Abs. 3 EStG) · Zu-/Abflussprinzip · {d.kleinunternehmer ? 'Kleinunternehmer (§ 19 UStG)' : 'umsatzsteuerpflichtig'}</div>
        <span style={{ flex: 1 }} />
        <label style={{ fontSize: 13, color: C.muted }}>Jahr</label>
        <select style={{ ...S.select, width: 'auto' }} value={jahr} onChange={e => setJahr(parseInt(e.target.value, 10))}>{jahre.map(j => <option key={j} value={j}>{j}</option>)}</select>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {kpiBox(`Betriebseinnahmen ${jahr}`, fmtEur(d.einnahmen), C.success, `davon netto ${fmtEur(d.einnahmen_netto)}`, () => onGo('steuern', 'euer'))}
        {kpiBox(`Betriebsausgaben ${jahr}`, fmtEur(d.ausgaben), '#b85c00', `davon netto ${fmtEur(d.ausgaben_netto)}`, () => onGo('steuern', 'euer'))}
        {kpiBox(`Gewinn ${jahr} (vorläufig)`, fmtEur(d.gewinn), d.gewinn >= 0 ? C.success : C.danger, 'Einnahmen − Ausgaben laut EÜR', () => onGo('steuern', 'euer'))}
        {!d.kleinunternehmer && kpiBox(`USt-Saldo ${jahr}`, fmtEur(d.ust_saldo), d.ust_saldo > 0 ? C.danger : C.success, 'vereinnahmte USt − gezahlte Vorsteuer', () => onGo('steuern', 'ustva'))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        {kpiBox('Offene Forderungen', fmtEur(d.forderungen), d.forderungen_ueberfaellig > 0 ? C.danger : C.primary, `${d.forderungen_anzahl} offene Posten${d.forderungen_ueberfaellig > 0 ? ` · ${fmtEur(d.forderungen_ueberfaellig)} überfällig` : ''}${d.guthaben_debitoren > 0 ? ` · Guthaben ${fmtEur(d.guthaben_debitoren)}` : ''}`, () => onGo('debitoren'))}
        {kpiBox('Offene Verbindlichkeiten', fmtEur(d.verbindlichkeiten), C.primary, `${d.verbindlichkeiten_anzahl} offene Posten bei Kreditoren`, () => onGo('kreditoren'))}
        {kpiBox('Bank', `${d.bank_offen}`, d.bank_offen > 0 ? '#b85c00' : C.success, 'offene Umsätze im Abgleich', () => onGo('bank'))}
        {kpiBox('Belege', `${d.belege_offen}`, d.belege_offen > 0 ? '#b85c00' : C.success, 'noch nicht gebucht', () => onGo('belege'))}
        {kpiBox('Kasse', fmtEur(d.kasse), C.primary, 'Bestand laut Kassenbuchungen', () => onGo('buchungen', { gegenkonto: '1000' }))}
        {kpiBox('Anlagevermögen', fmtEur(d.anlagen_buchwert), C.primary, `${d.anlagen_anzahl} Wirtschaftsgut/-güter · Buchwert`, () => onGo('anlagen'))}
        {!d.kleinunternehmer && kpiBox('Nächste UStVA', u ? u.name : '—', u && u.ueberfaellig ? C.danger : C.primary, u ? `fällig ${dDE(u.faellig)} · Kz 83 ${fmtEur(u.kz83)}${u.ueberfaellig ? ' · überfällig' : ''}` : 'alle Zeiträume gemeldet', () => onGo('steuern', 'ustva'))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 16 }}>
        <div style={S.card}>
          <h3 style={{ ...S.cardTitle, fontSize: 14 }}>Monatsverlauf {jahr}</h3>
          <MonatsChart monate={d.monate} hoehe={130} />
        </div>
        <div style={S.card}>
          <h3 style={{ ...S.cardTitle, fontSize: 14 }}>Zuletzt gebucht</h3>
          {d.letzte_buchungen.length === 0 ? <div style={S.empty}>Noch keine Buchungen.</div> : (
            <table style={S.table}><tbody>
              {d.letzte_buchungen.map(b => (
                <tr key={b.id}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{dDE(b.datum)}</td>
                  <td style={{ ...S.td, fontSize: 12 }}><ArtBadge art={b.art} label={b.art_label} /></td>
                  <td style={{ ...S.td, fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.text}>{b.text}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, fontSize: 12 }}>{fmtEur(b.brutto)}</td>
                </tr>
              ))}
            </tbody></table>
          )}
          <div style={{ textAlign: 'right', marginTop: 8 }}><button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => onGo('buchungen')}>Alle Buchungen →</button></div>
        </div>
      </div>
      <div style={{ ...S.pathInfo, marginTop: 6 }}>
        {d.festgeschrieben_bis ? `Buchungen bis ${dDE(d.festgeschrieben_bis)} sind festgeschrieben.` : 'Noch kein Zeitraum festgeschrieben.'} {d.anzahl_buchungen} Buchung(en) im Jahr {jahr}. Test-/Dummy-Mandanten sind nicht enthalten.
      </div>
    </div>
  );
}

// Kontenarten wie im Backend (_kontoart): D Debitor, K Kreditor, A Anlage, E Ertrag, W Aufwand, T Steuer, S Sammelkonto, F Finanz/neutral
const nr0 = (k) => String(k || '').trim().split(' ')[0];
function kArt(k) {
  const nr = nr0(k);
  if (!/^\d+$/.test(nr)) return 'F';
  const n = parseInt(nr, 10);
  if (nr.length === 5) return (n >= 10000 && n <= 69999) ? 'D' : (n >= 70000 ? 'K' : 'F');
  if (nr === '1400' || nr === '1600') return 'S';
  if (['1576', '1571', '1570', '1776', '1771', '1770'].includes(nr)) return 'T';
  if (nr.length === 4) {
    if (nr[0] === '0' && n < 800) return 'A';
    if (nr[0] === '8' || (n >= 2500 && n <= 2799)) return 'E';
    if (nr[0] === '3' || nr[0] === '4' || (n >= 2000 && n <= 2499)) return 'W';
  }
  return 'F';
}
const KART_LABEL = { D: 'Debitor', K: 'Kreditor', A: 'Anlagevermögen', E: 'Ertrag', W: 'Aufwand', T: 'Steuer', S: 'Sammelkonto', F: 'Finanz-/neutrales Konto' };
// Steuerschlüssel Reverse Charge (§ 13b UStG): Rechnung ohne deutsche USt, die Steuer schuldet der Empfänger (zugleich Vorsteuer)
const STEUERSCHLUESSEL_OPT = [
  ['', 'Normal – Steuer laut Rechnung'],
  ['13b_eu', 'Reverse Charge § 13b – Leistung eines EU-Unternehmers (z. B. Indeed Irland) · UStVA Kz 46/47 + 67'],
  ['13b', 'Reverse Charge § 13b – andere Fälle (Drittland, § 13b Abs. 2) · UStVA Kz 84/85 + 67'],
];
const RC_HINWEIS = 'Die Rechnung enthält keine deutsche Umsatzsteuer: Betrag = netto. Die Umsatzsteuer schuldest du selbst (1787) und ziehst sie gleichzeitig als Vorsteuer ab (1577) – Zahllast ±0; in der EÜR zählt nur der Nettobetrag.';
function SteuerschluesselWahl({ value, onChange }) {
  return (
    <select style={S.select} value={value || ''} onChange={e => onChange(e.target.value)}>
      {STEUERSCHLUESSEL_OPT.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
}
const parseBetrag = (v) => { if (v === '' || v == null) return null; const n = parseFloat(String(v).replace(/\./g, '').replace(',', '.')); return isNaN(n) ? null : n; };
const parseZahl = (v) => { if (v === '' || v == null) return null; const n = parseFloat(String(v).replace(',', '.')); return isNaN(n) ? null : n; };
function kontoListe(k) {
  if (!k) return [];
  const out = []; const da = new Set();
  const add = (lst, art) => (lst || []).forEach(x => { if (x && x.nr && !da.has(x.nr)) { da.add(x.nr); out.push({ nr: x.nr, name: x.name, art: art || kArt(x.nr) }); } });
  add(k.debitoren, 'D'); add(k.kreditoren, 'K'); add(k.finanz); add(k.erloes); add(k.aufwand); add(k.neutral); add(k.sonder);
  return out.filter(x => x.art !== 'S' && x.art !== 'T');
}
function ustVorschlag(nr, klein) {
  if (klein) return '0';
  const n = nr0(nr);
  if (n === '8300' || n === '3300') return '7';
  if (/^(41|436|438|439|451|497|2|8100|8195)/.test(n)) return '0';
  return '19';
}
// Belege einer Buchung: eigene Datei, Ausgangsrechnung (PDF) oder Eingangsbeleg (XML-e-Rechnung als lesbare Ansicht)
const belegUrl = (b) => (b.typ === 'rechnung' ? `/rechnungen/${b.id}/pdf`
  : b.typ === 'eingangsbeleg' ? (b.xml ? `/belege/${b.id}/ansicht` : `/belege/${b.id}/datei`)
    : `/buchhaltung/buchung/${b.id}/datei`);
function BelegVorschau({ liste, index, setIndex, onClose, hoehe }) {
  const b = liste[index];
  const schluessel = b ? `${b.typ}-${b.id}` : '';
  const [url, setUrl] = useState('');
  const [art, setArt] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!b) return undefined;
    let aktiv = true; let u = '';
    setUrl(''); setErr('');
    api.get(belegUrl(b), { responseType: 'blob' })
      .then(res => { if (!aktiv) return; u = URL.createObjectURL(res.data); setArt(res.data.type || ''); setUrl(u); })
      .catch(() => { if (aktiv) setErr('Beleg konnte nicht geladen werden.'); });
    return () => { aktiv = false; if (u) URL.revokeObjectURL(u); };
  }, [schluessel]);
  useEffect(() => {
    const taste = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target && e.target.tagName) || '')) return;
      if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
      if (e.key === 'ArrowRight' && index < liste.length - 1) setIndex(index + 1);
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [index, liste.length, setIndex]);
  if (!b) return null;
  const pfeil = (an) => ({ ...S.btnSmall, background: an ? C.primary : '#e3e7ec', color: an ? '#fff' : '#8a94a0', padding: '4px 11px', fontSize: 15, cursor: an ? 'pointer' : 'default' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: hoehe || '78vh', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
        <button style={pfeil(index > 0)} disabled={index <= 0} onClick={() => setIndex(index - 1)} title="vorheriger Beleg (←)">‹</button>
        <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{index + 1} / {liste.length}</span>
        <button style={pfeil(index < liste.length - 1)} disabled={index >= liste.length - 1} onClick={() => setIndex(index + 1)} title="nächster Beleg (→)">›</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.name}>{b.name}</div>
          <div style={{ fontSize: 11.5, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.rolle}{b.kontext ? ` · ${b.kontext}` : ''}</div>
        </div>
        {url && <a href={url} download={b.name} title="Herunterladen" style={{ ...S.btnSmall, textDecoration: 'none', padding: '5px 10px' }}>⬇</a>}
        {onClose && <button onClick={onClose} title="Vorschau schließen" style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.muted, lineHeight: 1 }}>×</button>}
      </div>
      <div style={{ flex: 1, minHeight: 0, background: '#e9edf2', overflow: 'auto' }}>
        {err ? <div style={{ ...S.empty, color: C.danger }}>{err}</div>
          : !url ? <div style={S.empty}>Lade Beleg…</div>
            : art.startsWith('image/') ? <img src={url} alt={b.name} style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }} />
              : <iframe title={b.name} src={art === 'application/pdf' ? `${url}#view=FitH&navpanes=0` : url} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />}
      </div>
    </div>
  );
}
const KLink = ({ nr, name, onKonto, stil }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); if (onKonto) onKonto(nr0(nr)); }} title="Kontoblatt öffnen"
    style={{ background: 'none', border: 'none', padding: 0, color: C.accent, cursor: onKonto ? 'pointer' : 'default', fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'left', ...(stil || {}) }}>
    {name || nr}
  </button>
);
function DateiFeld({ datei, onDatei, hinweis }) {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  return (
    <div>
      <div role="button" tabIndex={0} onClick={() => ref.current && ref.current.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (ref.current) ref.current.click(); } }}
        onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); if (e.dataTransfer.files && e.dataTransfer.files[0]) onDatei(e.dataTransfer.files[0]); }}
        style={{ border: `1.5px dashed ${over ? C.primary : C.border}`, background: over ? '#eaf1f8' : C.bg, borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: over ? C.primary : C.muted, cursor: 'pointer', textAlign: 'center' }}>
        {datei ? <>📎 <b style={{ color: C.text }}>{datei.name}</b> – zum Ersetzen klicken oder neue Datei hierher ziehen</> : <>📎 Beleg hierher ziehen oder klicken (PDF, Foto, e-Rechnung XML){hinweis ? ` – ${hinweis}` : ''}</>}
        <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.xml" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onDatei(e.target.files[0]); e.target.value = ''; }} />
      </div>
      {datei && <button type="button" style={{ ...S.btnSmall, background: '#eee', color: C.danger, marginTop: 4 }} onClick={() => onDatei(null)}>Beleg entfernen</button>}
    </div>
  );
}
function KostenstelleWahl({ value, onChange, mandanten }) {
  return (
    <select style={S.select} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">– keine –</option><option value="gemein">Gemeinkosten (Büro)</option>
      {(mandanten || []).filter(m => !m.ist_dummy && !m.ist_eigenes_lohnbuero).map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
    </select>
  );
}
// Buchungsvorschau wie das Backend (_zeilen): Sachkonto netto, Steuer auf eigenem Konto, Gegenkonto brutto
function vorschauZeilen(konto, gegen, sh, brutto, ust, satz, rc) {
  const gs = sh === 'S' ? 'H' : 'S';
  const steuerKonto = (sach) => (kArt(sach) === 'E' ? ({ 19: '1776', 7: '1771' }[satz] || '1770') : ({ 19: '1576', 7: '1571' }[satz] || '1570'));
  const sk = ['E', 'W', 'A'].includes(kArt(konto)) ? 'konto' : ['E', 'W', 'A'].includes(kArt(gegen)) ? 'gegen' : null;
  if (rc && sk) {   // Reverse Charge: Betrag netto, dazu 1577 Vorsteuer an 1787 Umsatzsteuer § 13b
    const sachSeite = sk === 'konto' ? sh : gs;
    const st = Math.round(brutto * (satz || 19)) / 100;
    return [[konto, sh, brutto], [gegen, gs, brutto], ['1577', sachSeite, st], ['1787', sachSeite === 'S' ? 'H' : 'S', st]];
  }
  const netto = Math.round((brutto - (ust || 0)) * 100) / 100;
  if (ust && sk === 'konto') return [[konto, sh, netto], [steuerKonto(konto), sh, ust], [gegen, gs, brutto]];
  if (ust && sk === 'gegen') return [[konto, sh, brutto], [gegen, gs, netto], [steuerKonto(gegen), gs, ust]];
  return [[konto, sh, brutto], [gegen, gs, brutto]];
}

// Tabellen der Buchführung: Suche über alle Spalten und Sortierung per Klick auf die Spaltenüberschrift
const sortWert = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  const s = String(v);
  return /^-?\d+([.,]\d+)?$/.test(s) ? parseFloat(s.replace(',', '.')) : s.toLowerCase();
};
function useTabelle(zeilen, start) {
  const [sort, setSort] = useState(start || { key: null, id: null, dir: 1 });
  const [q, setQ] = useState('');
  const qq = q.trim().toLowerCase();
  let rows = zeilen || [];
  if (qq) {
    rows = rows.filter(r => Object.values(r).some(v => {
      if (v == null || typeof v === 'object') return false;
      const s = String(v).toLowerCase();
      if (s.includes(qq)) return true;
      if (typeof v === 'number') return s.replace('.', ',').includes(qq) || fmtEur(v).toLowerCase().includes(qq);
      return /^\d{4}-\d{2}-\d{2}/.test(s) && dDE(s).includes(qq);
    }));
  }
  if (sort.key) {
    const wert = (r) => sortWert(typeof sort.key === 'function' ? sort.key(r) : r[sort.key]);
    rows = [...rows].sort((a, b) => {
      const x = wert(a), y = wert(b);
      if (x === y) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * sort.dir;
      return String(x).localeCompare(String(y), 'de', { numeric: true }) * sort.dir;
    });
  }
  const th = (key, label, stil, id) => {
    const sid = id || (typeof key === 'string' ? key : label);
    const aktiv = sort.id === sid;
    return (
      <th key={sid} style={{ ...S.th, ...(stil || {}), cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: aktiv ? C.primary : S.th.color }}
        title="Klick sortiert – zweiter Klick kehrt die Reihenfolge um"
        onClick={() => setSort(s => (s.id === sid ? { ...s, dir: -s.dir } : { key, id: sid, dir: 1 }))}>
        {label}{aktiv ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
      </th>
    );
  };
  const suche = (ph, breite) => <input style={{ ...S.input, width: breite || 240, padding: '6px 10px' }} placeholder={ph || 'In der Tabelle suchen…'} value={q} onChange={e => setQ(e.target.value)} />;
  return { rows, th, suche, q, sort };
}
// Saldo mit Vorzeichen nach Kontoart: auf der natürlichen Seite positiv (Erlöse im Haben = +, Aufwand im Soll = +)
const fmtSaldo = (x) => fmtEur(Math.abs(x || 0) < 0.005 ? 0 : x);
const SaldoText = ({ wert, natur }) => (
  <span style={{ color: (wert || 0) < -0.004 ? C.danger : C.text }} title={natur ? `positiv = Saldo im ${natur === 'S' ? 'Soll' : 'Haben'} (übliche Seite des Kontos)` : undefined}>
    {fmtSaldo(wert)}{natur ? <span style={{ fontSize: 10.5, color: C.muted, marginLeft: 3 }}>{natur}</span> : null}
  </span>
);

function KostenstelleAendern({ buchung, onClose, onDone }) {
  const [ks, setKs] = useState(buchung.kostenstelle || '');
  const [text, setText] = useState(buchung.text || '');
  const [mandanten, setMandanten] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => { api.get('/mandanten', { params: { alle: 1 } }).then(({ data }) => setMandanten(data || [])).catch(() => {}); }, []);
  const quelleText = { beleg: 'Eingangsbeleg', belegzahlung: 'Zahlung zum Eingangsbeleg', bank: 'Bank-Direktbuchung', afa: 'Abschreibung (Anlage)' }[buchung.quelle] || buchung.quelle;
  async function speichern() {
    try {
      const fd = new FormData(); fd.append('kostenstelle', ks || '');
      if (buchung.quelle === 'beleg' && text !== (buchung.text || '')) fd.append('text', text || '');
      await api.patch(`/buchhaltung/buchung/${buchung.id}`, fd); onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen.'); }
  }
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 520 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Buchung bearbeiten</h2>
        <div style={S.pathInfo}>{dDE(buchung.datum)} · {buchung.soll} an {buchung.haben} · {fmtEur(buchung.brutto)}<br />Diese Buchung entsteht aus: {quelleText}. Geändert wird an der Quelle – die Buchung folgt automatisch.</div>
        {err && <div style={{ ...S.pathInfo, color: C.danger, fontWeight: 600 }}>{err}</div>}
        <label style={S.label}>Kostenstelle</label>
        <KostenstelleWahl value={ks} onChange={setKs} mandanten={mandanten} />
        {buchung.quelle === 'beleg' && <><label style={S.label}>Buchungstext</label><input style={S.input} value={text} onChange={e => setText(e.target.value)} /></>}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 8 }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnSmall} onClick={speichern}>Speichern</button>
        </div>
      </div>
    </div>
  );
}
function BuchungBearbeiten({ id, onClose, onDone }) {
  const [b, setB] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/buchhaltung/buchung/${id}`).then(({ data }) => setB(data)).catch(ex => setErr(ex.response?.data?.detail || 'Buchung konnte nicht geladen werden.'));
  }, [id]);
  if (err) return <div style={S.modalOverlay} onClick={onClose}><div style={{ ...S.modalCard, color: C.danger }}>{err}</div></div>;
  if (!b) return null;
  if (b.quelle === 'manuell' || b.quelle === 'lohn') return <BuchungMaske bearbeiten={b} onClose={onClose} onDone={onDone} />;
  if (['rechnung', 'zahlung'].includes(b.quelle)) {
    return (
      <div style={S.modalOverlay} onClick={onClose}><div style={{ ...S.modalCard, width: 480 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Buchung bearbeiten</h2>
        <div style={S.pathInfo}>Rechnungen und Zahlungen von Mandanten tragen automatisch die Kostenstelle des Mandanten. Änderungen bitte an der Rechnung bzw. über „Auszifferung lösen“ und neu zuordnen.</div>
        <div style={{ textAlign: 'right' }}><button style={S.btnSmall} onClick={onClose}>OK</button></div>
      </div></div>
    );
  }
  if (!b.kst_relevant) {
    return (
      <div style={S.modalOverlay} onClick={onClose}><div style={{ ...S.modalCard, width: 480 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Buchung bearbeiten</h2>
        <div style={S.pathInfo}>Diese Buchung betrifft kein Aufwands- oder Ertragskonto und braucht keine Kostenstelle. Die Zuordnung zu offenen Posten änderst du im Kontoblatt über „zuordnen…“ bzw. „lösen“.</div>
        <div style={{ textAlign: 'right' }}><button style={S.btnSmall} onClick={onClose}>OK</button></div>
      </div></div>
    );
  }
  return <KostenstelleAendern buchung={b} onClose={onClose} onDone={onDone} />;
}

function LohnImportModal({ onClose, onDone }) {
  const [datei, setDatei] = useState(null);
  const [erg, setErg] = useState(null);
  const [ks, setKs] = useState('gemein');
  const [vk, setVk] = useState('');
  const [ok, setOk] = useState(false);
  const [mandanten, setMandanten] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { api.get('/mandanten', { params: { alle: 1 } }).then(({ data }) => setMandanten(data || [])).catch(() => {}); }, []);
  async function pruefen(fl) {
    setDatei(fl); setErg(null); setErr(''); setOk(false);
    if (!fl) return;
    try {
      const fd = new FormData(); fd.append('file', fl);
      const { data } = await api.post('/buchhaltung/lohnimport/pruefen', fd);
      setErg(data); setVk(data.verrechnungskonto || '1755');
    } catch (ex) { setErr(ex.response?.data?.detail || 'Die Datei konnte nicht gelesen werden.'); }
  }
  async function importieren() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', datei); fd.append('kostenstelle', ks || ''); fd.append('verrechnungskonto', vk || '');
      fd.append('bestaetigt', ok ? '1' : '0');
      const { data } = await api.post('/buchhaltung/lohnimport', fd); onDone(data);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Import fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  const tab = useTabelle(erg?.zeilen);
  const kannImport = erg && !erg.sperre.length && (!erg.bestaetigen.length || ok);
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 'min(1000px, 96vw)' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Lohnbuchungen importieren</h2>
        <div style={S.pathInfo}>Buchungsliste aus dem Lohnprogramm (z. B. Lexware Lohn+Gehalt) als PDF oder CSV. Jede Zeile wird gegen das Verrechnungskonto gebucht (Lohnaufwand an 1755, 1755 an Verbindlichkeiten 1740/1741/1742); Korrekturen mit Minus als Generalumkehr. Die Liste hängt als Beleg an jeder Buchung. Die Auszahlungen von Lohn, SV-Beiträgen und Lohnsteuer buchst du später im Bankabgleich auf 1740, 1742 und 1741.</div>
        <div role="button" tabIndex={0} onClick={() => ref.current && ref.current.click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (ref.current) ref.current.click(); } }}
          onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) pruefen(e.dataTransfer.files[0]); }}
          style={{ border: `1.5px dashed ${C.border}`, background: C.bg, borderRadius: 8, padding: '12px', fontSize: 13, color: C.muted, cursor: 'pointer', textAlign: 'center' }}>
          {datei ? <>📄 <b style={{ color: C.text }}>{datei.name}</b> – andere Datei hierher ziehen oder klicken</> : '📄 Buchungsliste (PDF oder CSV) hierher ziehen oder klicken'}
          <input ref={ref} type="file" accept=".pdf,.csv,.txt" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) pruefen(e.target.files[0]); e.target.value = ''; }} />
        </div>
        {err && <div style={{ ...S.pathInfo, color: C.danger, fontWeight: 600, marginTop: 8 }}>{err}</div>}
        {erg && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '12px 0' }}>
              {kpiBox('Firma laut Liste', erg.firma || '—', C.primary)}
              {kpiBox('Monat', (erg.monate || []).join(', ') || '—', C.primary, `${erg.zeilen.length} Zeilen · ${erg.format}`)}
              {kpiBox('Summe Soll', fmtEur(erg.summe_soll), C.primary)}
              {kpiBox('Summe Haben', fmtEur(erg.summe_haben), Math.abs(erg.summe_soll - erg.summe_haben) < 0.005 ? C.success : C.danger)}
            </div>
            {erg.sperre.map((t, i) => <div key={'s' + i} style={{ ...S.hint, background: '#fdecea', borderColor: '#f5c2bd', color: C.danger, marginBottom: 6 }}>{t}</div>)}
            {erg.bestaetigen.map((t, i) => <label key={'b' + i} style={{ ...S.hint, display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, cursor: 'pointer' }}><input type="checkbox" checked={ok} onChange={e => setOk(e.target.checked)} /> <span>{t}</span></label>)}
            {erg.hinweise.map((t, i) => <div key={'h' + i} style={{ ...S.pathInfo }}>{t}</div>)}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
              <div><label style={S.label}>Verrechnungskonto</label><input style={S.input} value={vk} onChange={e => setVk(e.target.value)} /></div>
              <div><label style={S.label}>Kostenstelle</label><KostenstelleWahl value={ks} onChange={setKs} mandanten={mandanten} /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 6px' }}><b style={{ fontSize: 13, color: C.primary }}>Vorschau</b><span style={{ flex: 1 }} />{tab.suche('Zeilen durchsuchen…', 200)}</div>
            <div style={{ maxHeight: '40vh', overflow: 'auto' }}>
              <table style={S.table}>
                <thead><tr>{tab.th('datum', 'Datum')}{tab.th('nr', 'Buchungsnr.')}{tab.th('text', 'Text')}{tab.th('konto', 'Konto')}{tab.th(z => (z.seite === 'S' ? z.betrag : null), 'Soll', { textAlign: 'right' }, 'soll')}{tab.th(z => (z.seite === 'H' ? z.betrag : null), 'Haben', { textAlign: 'right' }, 'haben')}</tr></thead>
                <tbody>{tab.rows.map((z, i) => (
                  <tr key={i}>
                    <td style={S.td}>{dDE(z.datum)}</td><td style={{ ...S.td, fontSize: 12 }}>{z.nr}</td><td style={S.td}>{z.text}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }} title={z.konto_name}>{z.konto_name}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: z.betrag < 0 ? C.danger : C.text }}>{z.seite === 'S' ? fmtEur(z.betrag) : ''}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: z.betrag < 0 ? C.danger : C.text }}>{z.seite === 'H' ? fmtEur(z.betrag) : ''}</td>
                  </tr>))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={onClose}>Abbrechen</button>
          <button style={{ ...S.btnSmall, padding: '8px 18px', opacity: kannImport ? 1 : 0.5 }} disabled={!kannImport || busy} onClick={importieren}>{busy ? 'Importiere…' : `${erg ? erg.zeilen.length : 0} Buchungen importieren`}</button>
        </div>
      </div>
    </div>
  );
}

function BuchungMaske({ prefill, onClose, onDone, bearbeiten }) {
  const p = prefill || (bearbeiten ? {
    datum: bearbeiten.datum, belegdatum: bearbeiten.belegdatum || '', konto: bearbeiten.konto || '', gegenkonto: bearbeiten.gegenkonto || '',
    sh: bearbeiten.sh, betrag: Math.abs(bearbeiten.brutto || 0), ust_satz: bearbeiten.ust_satz, gu: (bearbeiten.brutto || 0) < 0,
    ust_betrag: (bearbeiten.ust && !bearbeiten.steuerschluessel) ? String(Math.abs(bearbeiten.ust)).replace('.', ',') : '',
    text: bearbeiten.text || '', beleg: bearbeiten.beleg || '', op_nr: bearbeiten.op_nr || '', kostenstelle: bearbeiten.kostenstelle || '',
    steuerschluessel: bearbeiten.steuerschluessel || '',
  } : {});
  const [konten, setKonten] = useState(null);
  const [mandanten, setMandanten] = useState([]);
  const [kanzlei, setKanzlei] = useState(null);
  const [anlagen, setAnlagen] = useState([]);
  const [f, setF] = useState({
    datum: p.datum || isoTag(new Date()), belegdatum: p.belegdatum || '', konto: p.konto || '', gegenkonto: p.gegenkonto || '',
    sh: p.sh || 'S', betrag: p.betrag != null ? String(p.betrag).replace('.', ',') : '', ust_satz: p.ust_satz != null ? String(p.ust_satz) : '',
    ust_betrag: p.ust_betrag || '', gu: !!p.gu, text: p.text || '', beleg: p.beleg || '', op_nr: p.op_nr || '', kostenstelle: p.kostenstelle || '', notiz: '',
    anlage_bezeichnung: '', anlage_jahre: '', anlage_gwg: null, anlage_id: '', steuerschluessel: p.steuerschluessel || '',
  });
  const [datei, setDatei] = useState(null);
  const [erk, setErk] = useState(null);
  const [ops, setOps] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/konten').then(({ data }) => setKonten(data)).catch(() => {});
    api.get('/mandanten', { params: { alle: 1 } }).then(({ data }) => setMandanten(data || [])).catch(() => {});
    api.get('/kanzlei').then(({ data }) => setKanzlei(data)).catch(() => {});
    api.get('/anlagen').then(({ data }) => setAnlagen(data.anlagen || [])).catch(() => {});
  }, []);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const kNr = nr0(f.konto), gNr = nr0(f.gegenkonto);
  const ka = kArt(kNr), ga = kArt(gNr);
  const soll = f.sh === 'S' ? kNr : gNr, haben = f.sh === 'S' ? gNr : kNr;
  const sach = ['E', 'W', 'A'].includes(ka) ? kNr : (['E', 'W', 'A'].includes(ga) ? gNr : '');
  const pk = ['D', 'K'].includes(ka) ? kNr : (['D', 'K'].includes(ga) ? gNr : '');
  const klein = !!kanzlei?.kleinunternehmer;
  const zugang = kArt(soll) === 'A' && !f.gu;
  const abgang = kArt(haben) === 'A' && !f.gu;
  useEffect(() => { if (sach && f.ust_satz === '') set('ust_satz', ustVorschlag(sach, klein)); }, [sach, klein, f.ust_satz]);
  useEffect(() => {
    if (!pk) { setOps([]); return; }
    api.get(`/buchhaltung/konto/${pk}`).then(({ data }) => setOps(data.offene_posten || [])).catch(() => setOps([]));
  }, [pk]);
  useEffect(() => {
    if (zugang && f.anlage_jahre === '' && konten?.anlage_nd) {
      const m = konten.anlage_nd[soll]; set('anlage_jahre', m != null ? String(Math.round(m / 12 * 10) / 10).replace('.', ',') : '3');
    }
  }, [zugang, soll, konten, f.anlage_jahre]);
  const liste = kontoListe(konten);
  const betrag = parseBetrag(f.betrag) || 0;
  const satz = sach && !klein ? (parseZahl(f.ust_satz) || 0) : 0;
  const rcMoeglich = !!sach && ['W', 'A'].includes(kArt(sach));
  const rc = rcMoeglich && f.steuerschluessel ? f.steuerschluessel : '';
  const ustFix = parseBetrag(f.ust_betrag);
  const ust = (satz && !rc) ? (ustFix != null ? Math.abs(ustFix) : Math.round((betrag - betrag / (1 + satz / 100)) * 100) / 100) : 0;
  const netto = Math.round((betrag - ust) * 100) / 100;
  const gwgAuto = soll === '0480' || (netto > 0 && netto <= (konten?.gwg_grenze || 800));
  const gwg = f.anlage_gwg == null ? gwgAuto : f.anlage_gwg;
  const vz = f.gu ? -1 : 1;
  const zeilen = (kNr && gNr && betrag) ? vorschauZeilen(abgang ? (haben === kNr ? '8820' : kNr) : kNr, abgang ? (haben === gNr ? '8820' : gNr) : gNr, f.sh, vz * betrag, vz * ust, rc ? (satz || 19) : satz, rc) : [];
  const offeneAnlagen = anlagen.filter(a => !a.abgang_datum && (!haben || a.konto_nr === haben || kArt(haben) === 'A'));
  const name = (nr) => { const x = liste.find(k => k.nr === nr); return x ? x.name : (nr === '8820' ? 'Erlöse aus Verkauf Anlagevermögen' : ({ 1776: 'USt 19 %', 1771: 'USt 7 %', 1576: 'Vorsteuer 19 %', 1571: 'Vorsteuer 7 %', 1577: 'Vorsteuer § 13b 19 %', 1787: 'Umsatzsteuer § 13b 19 %' }[nr] || '')); };

  async function dateiGewaehlt(fl) {
    setDatei(fl); setErk(null);
    if (!fl) return;
    try {
      const fd = new FormData(); fd.append('file', fl);
      const { data } = await api.post('/buchhaltung/beleg-erkennen', fd);
      setErk(data);
      setF(s => {
        const n = { ...s };
        if (data.brutto != null && !s.betrag) n.betrag = String(Math.abs(data.brutto)).replace('.', ',');
        if (data.ust_satz != null && s.ust_satz === '') n.ust_satz = String(data.ust_satz);
        if (data.ust_betrag != null) n.ust_betrag = String(Math.abs(data.ust_betrag)).replace('.', ',');
        if (data.beleg_datum && !s.belegdatum) n.belegdatum = data.beleg_datum;
        if (data.rechnungsnummer && !s.beleg) n.beleg = data.rechnungsnummer;
        if (data.buchungstext && !s.text) n.text = data.buchungstext;
        if (data.steuerschluessel) { n.steuerschluessel = data.steuerschluessel; n.ust_satz = '19'; n.ust_betrag = ''; }
        if (data.kreditor_nr && !s.gegenkonto && !s.konto) {
          n.gegenkonto = `${data.kreditor_nr} ${data.kreditor_name}`; n.sh = 'S';
          if (data.standard_konto) n.konto = data.standard_konto;
          if (data.rechnungsnummer) n.op_nr = data.rechnungsnummer;
        }
        return n;
      });
    } catch (ex) { setErr(ex.response?.data?.detail || 'Vorerkennung fehlgeschlagen – Beleg wird trotzdem angehängt.'); }
  }
  async function buchen() {
    setErr('');
    if (!kNr || !gNr) { setErr('Bitte Konto und Gegenkonto wählen.'); return; }
    if (!betrag && !abgang) { setErr('Bitte einen Betrag angeben.'); return; }
    if (abgang && !f.anlage_id) { setErr('Bitte die abgehende Anlage auswählen.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('datum', f.datum); fd.append('konto', kNr); fd.append('gegenkonto', gNr); fd.append('betrag', betrag || 0); fd.append('sh', f.sh);
      if (sach) fd.append('ust_satz', rc ? (satz || 19) : satz);
      if (sach && ustFix != null && !rc) fd.append('ust_betrag', Math.abs(ustFix));
      if (rc) fd.append('steuerschluessel', rc);
      fd.append('gu', f.gu ? '1' : '0');
      ['belegdatum', 'text', 'beleg', 'op_nr', 'kostenstelle', 'notiz'].forEach(k => { if (f[k] && (k !== 'kostenstelle' || sach)) fd.append(k, f[k]); });
      if (zugang) {
        fd.append('anlage_bezeichnung', f.anlage_bezeichnung || f.text || '');
        const j = parseZahl(f.anlage_jahre);
        fd.append('anlage_nd', gwg ? 0 : Math.max(0, Math.round((j == null ? 3 : j) * 12)));
        fd.append('anlage_gwg', gwg ? '1' : '0');
      }
      if (abgang) fd.append('anlage_id', f.anlage_id);
      if (datei) fd.append('datei', datei);
      if (bearbeiten) await api.patch(`/buchhaltung/buchung/${bearbeiten.id}`, fd); else await api.post('/buchhaltung/buchung', fd);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Buchung fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  const lab = (t) => <label style={S.label}>{t}</label>;
  const artHinweis = (nr) => (nr ? <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{KART_LABEL[kArt(nr)]}{name(nr) ? '' : ' · unbekanntes Konto'}</div> : null);
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 820 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>{bearbeiten ? 'Buchung bearbeiten' : 'Buchung erfassen'}</h2>
        <div style={S.pathInfo}>Buchungssatz „Soll an Haben“. Personenkonten: Debitoren 10000–69999 (Mandanten), Kreditoren ab 70000. Erlöse/Aufwände gegen ein Personenkonto wirken in der EÜR erst mit der Zahlung. Liegt ein Bankumsatz vor, buchen Sie ihn besser im Reiter Bank.</div>
        {err && <div style={{ ...S.pathInfo, color: C.danger, fontWeight: 600 }}>{err}</div>}
        {!bearbeiten && <DateiFeld datei={datei} onDatei={dateiGewaehlt} hinweis="Betrag, Steuer, Datum und Nummer werden vorerkannt" />}
        {erk && <div style={{ ...S.pathInfo, background: '#eef5ff', color: C.accent, marginTop: 6, padding: '6px 10px', borderRadius: 6 }}>⚡ {erk.quelle === 'erechnung' ? `e-Rechnung (${erk.format}) gelesen` : 'Text erkannt'}: {erk.lieferant || '—'}{erk.rechnungsnummer ? ` · Nr. ${erk.rechnungsnummer}` : ''}{erk.brutto != null ? ` · ${fmtEur(erk.brutto)}` : ''}{erk.kreditor_nr ? ` · Kreditor ${erk.kreditor_nr}` : ''} – bitte prüfen.</div>}
        <datalist id="bm-konten">{liste.map(k => <option key={k.nr} value={`${k.nr} ${k.name || ''}`} />)}</datalist>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 72px 1fr', gap: 10, alignItems: 'end', marginTop: 4 }}>
          <div>{lab(f.sh === 'S' ? 'Konto (Soll)' : 'Konto (Haben)')}<input style={S.input} list="bm-konten" value={f.konto} onChange={e => set('konto', e.target.value)} placeholder="Nummer oder Name…" />{artHinweis(kNr)}</div>
          <div style={{ textAlign: 'center', paddingBottom: kNr || gNr ? 18 : 0 }}>
            <button type="button" title="Soll und Haben tauschen" style={{ ...S.btnSmall, width: '100%', padding: '9px 0', background: C.primary }} onClick={() => set('sh', f.sh === 'S' ? 'H' : 'S')}>{f.sh === 'S' ? 'S ⇄ H' : 'H ⇄ S'}</button>
          </div>
          <div>{lab(f.sh === 'S' ? 'Gegenkonto (Haben)' : 'Gegenkonto (Soll)')}<input style={S.input} list="bm-konten" value={f.gegenkonto} onChange={e => set('gegenkonto', e.target.value)} placeholder="Nummer oder Name…" />{artHinweis(gNr)}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <div>{lab(rc ? 'Rechnungsbetrag (netto, €)' : 'Betrag brutto (€)')}<input style={S.input} value={f.betrag} onChange={e => set('betrag', e.target.value)} placeholder="0,00" /></div>
          <div>{lab('USt-Satz')}<select style={S.select} value={sach ? f.ust_satz : ''} disabled={!sach || klein} onChange={e => { set('ust_satz', e.target.value); set('ust_betrag', ''); }}>
            {!sach && <option value="">– (kein Sachkonto)</option>}<option value="19">19 %</option><option value="7">7 %</option><option value="0">0 % / steuerfrei</option></select></div>
          <div>{lab('USt-Betrag (optional)')}<input style={S.input} value={rc ? '' : f.ust_betrag} disabled={!sach || !satz || !!rc} onChange={e => set('ust_betrag', e.target.value)} placeholder={rc ? 'entfällt (§ 13b)' : (ust ? String(ust).replace('.', ',') : '')} /></div>
          {rcMoeglich && !klein && <div style={{ gridColumn: 'span 4' }}>{lab('Steuerschlüssel')}<SteuerschluesselWahl value={f.steuerschluessel} onChange={v => { set('steuerschluessel', v); if (v) { set('ust_satz', '19'); set('ust_betrag', ''); } }} />
            {rc && <div style={{ ...S.pathInfo, marginTop: 4, color: C.text }}>{RC_HINWEIS}</div>}</div>}
          <div>{lab('Buchungsdatum')}<input type="date" style={S.input} value={f.datum} onChange={e => set('datum', e.target.value)} /></div>
          <div>{lab('Belegdatum')}<input type="date" style={S.input} value={f.belegdatum} onChange={e => set('belegdatum', e.target.value)} /></div>
          <div>{lab('Beleg-Nr.')}<input style={S.input} value={f.beleg} onChange={e => set('beleg', e.target.value)} /></div>
          {sach ? <div style={{ gridColumn: 'span 2' }}>{lab('Kostenstelle')}<KostenstelleWahl value={f.kostenstelle} onChange={v => set('kostenstelle', v)} mandanten={mandanten} /></div> : null}
          {pk && <div style={{ gridColumn: 'span 2' }}>{lab(`Offener Posten (${KART_LABEL[kArt(pk)]} ${pk})`)}
            <input style={S.input} list="bm-ops" value={f.op_nr} onChange={e => set('op_nr', e.target.value)} placeholder="Rechnungs-/Belegnummer – leer = ohne Zuordnung" />
            <datalist id="bm-ops">{ops.filter(o => o.op_nr).map(o => <option key={o.op_nr} value={o.op_nr}>{`offen ${fmtEur(o.offen)}`}</option>)}</datalist></div>}
          <div style={{ gridColumn: pk ? 'span 2' : 'span 4' }}>{lab('Buchungstext')}<input style={S.input} value={f.text} onChange={e => set('text', e.target.value)} /></div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.gu} onChange={e => set('gu', e.target.checked)} />
          <span><b>Generalumkehr (GU)</b> – Korrektur: bucht den Betrag negativ auf dieselben Seiten (Verkehrszahlen bleiben sauber). Für eine Gegenbuchung mit vertauschten Seiten stattdessen S ⇄ H nutzen.</span>
        </label>
        {sach === '4650' && <div style={{ ...S.pathInfo, marginTop: 6 }}>Bewirtung: wird automatisch 70 % abziehbar / 30 % nicht abziehbar (4654) aufgeteilt, Vorsteuer voll.</div>}
        {zugang && (
          <div style={{ marginTop: 12, border: `1px solid ${C.accent}`, borderRadius: 8, padding: '10px 14px', background: '#f5f9fc' }}>
            <b style={{ color: C.primary, fontSize: 13 }}>Zugang zum Anlagevermögen ({soll} {name(soll)})</b>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div>{lab('Bezeichnung des Wirtschaftsguts')}<input style={S.input} value={f.anlage_bezeichnung} onChange={e => set('anlage_bezeichnung', e.target.value)} placeholder={f.text || 'z. B. Laptop Lenovo ThinkPad'} /></div>
              <div>{lab('Nutzungsdauer (Jahre)')}<input style={S.input} value={gwg ? '0' : f.anlage_jahre} disabled={gwg} onChange={e => set('anlage_jahre', e.target.value)} /></div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={gwg} onChange={e => set('anlage_gwg', e.target.checked)} />
              Sofortabschreibung als GWG (bis {fmtEur(konten?.gwg_grenze || 800)} netto){gwgAuto && f.anlage_gwg == null ? ' – automatisch vorgeschlagen' : ''}
            </label>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
              Wird automatisch mitgebucht: {gwg ? `4855 Sofortabschreibung an ${soll} über ${fmtEur(netto)} zum Belegdatum` : `jährliche AfA (linear, monatsgenau) 4830 an ${soll}`} · Eintrag im Anlagenverzeichnis. Computer-Hardware und Software dürfen mit 1 Jahr Nutzungsdauer abgeschrieben werden (BMF-Schreiben vom 22.02.2022).
            </div>
          </div>
        )}
        {abgang && (
          <div style={{ marginTop: 12, border: `1px solid ${C.danger}`, borderRadius: 8, padding: '10px 14px', background: '#fdf7f6' }}>
            <b style={{ color: C.danger, fontSize: 13 }}>Abgang aus dem Anlagevermögen</b>
            {lab('Abgehende Anlage')}
            <select style={S.select} value={f.anlage_id} onChange={e => set('anlage_id', e.target.value)}>
              <option value="">– wählen –</option>
              {offeneAnlagen.map(a => <option key={a.id} value={a.id}>{a.bezeichnung} · {a.konto_nr} · Buchwert {fmtEur(a.buchwert)}</option>)}
            </select>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Der Verkaufserlös wird auf 8820 (Erlöse aus Anlagenverkauf, mit USt) gebucht; AfA bis zum Abgangsmonat und der Restbuchwert (2315 an Anlagekonto) werden automatisch gebucht. Betrag 0 = Verschrottung ohne Erlös.</div>
          </div>
        )}
        {zeilen.length > 0 && (
          <div style={{ marginTop: 12, background: C.bg, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontWeight: 600, color: C.primary, fontSize: 13, marginBottom: 6 }}>Buchungsvorschau{f.gu ? ' (Generalumkehr)' : ''}</div>
            <table style={{ width: '100%', fontFamily: 'monospace', fontSize: 12.5 }}><tbody>
              {zeilen.map(([k, s, b], i) => (
                <tr key={i}><td style={{ width: 60 }}>{s === 'S' ? 'Soll' : 'Haben'}</td><td>{k} {name(k)}</td><td style={{ textAlign: 'right' }}>{fmtEur(b)}</td></tr>
              ))}
            </tbody></table>
            {pk && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Das Sammelkonto {kArt(pk) === 'D' ? '1400 Forderungen' : '1600 Verbindlichkeiten'} L+L wird nicht zusätzlich bebucht – es ist die Summe der Personenkonten.</div>}
          </div>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={onClose}>Abbrechen</button>
          <button style={{ ...S.btnSmall, padding: '8px 18px' }} onClick={buchen} disabled={busy}>{busy ? 'Buche…' : 'Buchen'}</button>
        </div>
      </div>
    </div>
  );
}

// Verteilung einer Zahlung auf offene Posten – wie im Backend: der Reihe nach je offener Rest, ein Rest bleibt ohne Zuordnung
function opVerteilung(ops, sel, betrag) {
  let rest = Math.round(Math.abs(betrag || 0) * 100) / 100;
  const anteile = {};
  let ok = true;
  sel.forEach(nr => {
    const o = ops.find(x => x.op_nr === nr);
    const a = Math.round(Math.max(0, Math.min(o ? o.offen : 0, rest)) * 100) / 100;
    if (a <= 0.005) ok = false;
    anteile[nr] = a;
    rest = Math.round((rest - a) * 100) / 100;
  });
  return { anteile, rest, ok };
}

function OpAuswahl({ ops, sel, setSel, betrag }) {
  const tab = useTabelle(ops);
  const { anteile, rest, ok } = opVerteilung(ops, sel, betrag);
  const gesamt = Math.abs(betrag || 0);
  const toggle = (nr) => setSel(s => (s.includes(nr) ? s.filter(x => x !== nr) : [...s, nr]));
  return (
    <div>
      {ops.length === 0 ? <div style={S.empty}>Keine offenen Posten auf diesem Konto.</div> : (
        <div style={{ overflow: 'auto', maxHeight: 320, border: `1px solid ${C.border}`, borderRadius: 6 }}>
          <table style={S.table}>
            <thead><tr><th style={S.th}></th>{tab.th('op_nr', 'Offener Posten')}{tab.th('datum', 'Datum')}{tab.th('text', 'Text')}{tab.th('offen', 'offen', { textAlign: 'right' })}<th style={{ ...S.th, textAlign: 'right' }}>zugeordnet</th></tr></thead>
            <tbody>
              {tab.rows.map(o => {
                const pos = sel.indexOf(o.op_nr);
                return (
                  <tr key={o.op_nr} onClick={() => toggle(o.op_nr)} style={{ cursor: 'pointer', background: pos >= 0 ? '#eef6ff' : undefined }}>
                    <td style={{ ...S.td, width: 44, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={pos >= 0} onChange={() => toggle(o.op_nr)} onClick={e => e.stopPropagation()} aria-label={`${o.op_nr} auswählen`} />
                      {pos >= 0 && <span style={{ fontSize: 11, color: C.muted, marginLeft: 4 }}>{pos + 1}.</span>}
                    </td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {o.op_nr}
                      {o.altposten && <span style={{ ...S.badge, marginLeft: 6 }}>Altposten</span>}
                      {o.mehrfach && <span style={{ ...S.badge, marginLeft: 6, background: '#fff7e6', color: '#8a5a00' }} title="Mehrere Belege mit derselben Nummer – vermutlich doppelt hochgeladen">doppelt?</span>}
                    </td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{dDE(o.datum)}</td>
                    <td style={{ ...S.td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.text || ''}>{o.text || ''}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtEur(o.offen)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pos >= 0 ? fmtEur(anteile[o.op_nr]) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, marginTop: 8 }}>
        <span>Zahlung <b>{fmtEur(gesamt)}</b></span>
        <span>zugeordnet <b>{fmtEur(gesamt - rest)}</b></span>
        <span style={{ color: sel.length && rest > 0.005 ? '#8a5a00' : C.muted }}>ohne Zuordnung <b>{fmtEur(rest)}</b></span>
      </div>
      {!ok && <div style={{ fontSize: 13, color: C.danger, marginTop: 4 }}>Die Zahlung reicht nicht für alle ausgewählten Posten – bitte weniger auswählen.</div>}
    </div>
  );
}

function AusziffernModal({ konto, zeile, ops, onClose, onDone }) {
  const betrag = zeile.soll ?? zeile.haben ?? 0;
  const offen = (ops || []).filter(o => o.op_nr && o.offen > 0.005);
  const [sel, setSel] = useState(() => {   // im Text genannte Nummern vorauswählen, sonst den betragsgleichen Posten
    const txt = (zeile.text || '').replace(/\s/g, '');
    const genannt = offen.filter(o => txt.includes(o.op_nr)).sort((a, b) => txt.indexOf(a.op_nr) - txt.indexOf(b.op_nr)).map(o => o.op_nr);
    if (genannt.length) return genannt;
    const p = offen.find(o => Math.abs(o.offen - betrag) < 0.005); return p ? [p.op_nr] : [];
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const { ok } = opVerteilung(offen, sel, betrag);
  async function speichern() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('op_nrs', JSON.stringify(sel));
      await api.post(`/buchhaltung/buchung/${zeile.buchung_id}/ausziffern`, fd); onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Zuordnen fehlgeschlagen.'); setBusy(false); }
  }
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 'min(860px, 96vw)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Zahlung offenen Posten zuordnen</h2>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          <b>{dDE(zeile.datum)}</b> · {konto} · <b>{fmtEur(betrag)}</b>
          <div style={{ color: C.muted, fontSize: 12, overflowWrap: 'anywhere' }}>{zeile.text}</div>
        </div>
        <div style={{ ...S.pathInfo, marginBottom: 8 }}>Posten in der Reihenfolge anklicken, in der die Zahlung sie ausgleichen soll. Jeder Posten erhält höchstens seinen offenen Betrag; was übrig bleibt, steht weiter ohne Zuordnung auf dem Konto.</div>
        <OpAuswahl ops={offen} sel={sel} setSel={setSel} betrag={betrag} />
        {err && <div style={{ ...S.hint, color: C.danger }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnPrimary} disabled={busy || !sel.length || !ok} onClick={speichern}>{sel.length > 1 ? `Auf ${sel.length} Posten aufteilen` : 'Zuordnen'}</button>
        </div>
      </div>
    </div>
  );
}

const OP_FARBEN = ['#2e86de', '#e67e22', '#27ae60', '#8e44ad', '#c0392b', '#16a085', '#d4a017', '#5d6d7e'];

function KontoblattModal({ nr, onClose, onBuchen }) {
  const heute = new Date();
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [stapel, setStapel] = useState([nr]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [ver, setVer] = useState(0);
  const [vorschau, setVorschau] = useState(null);
  const [bearbeiten, setBearbeiten] = useState(null);
  const [zuordnen, setZuordnen] = useState(null);
  const [gruppiert, setGruppiertS] = useState(() => { try { return localStorage.getItem('kontoblatt-ausziffern') !== '0'; } catch { return true; } });
  const setGruppiert = (v) => { setGruppiertS(v); try { localStorage.setItem('kontoblatt-ausziffern', v ? '1' : '0'); } catch { /* */ } };
  const [zu, setZu] = useState({});
  const aktuell = stapel[stapel.length - 1];
  useEffect(() => {
    setD(null); setErr('');
    api.get(`/buchhaltung/konto/${aktuell}`, { params: { von: `${jahr}-01-01`, bis: `${jahr}-12-31` } })
      .then(({ data }) => setD(data)).catch(ex => setErr(ex.response?.data?.detail || 'Kontoblatt konnte nicht geladen werden.'));
  }, [aktuell, jahr, ver]);
  const gehe = (n) => { if (n && n !== aktuell) { setVorschau(null); setStapel(s => [...s, n]); } };
  async function anhaengen(z, fl) {
    if (!fl) return;
    try { const fd = new FormData(); fd.append('datei', fl); await api.post(`/buchhaltung/buchung/${z.buchung_id}/datei`, fd); setVorschau(null); setVer(v => v + 1); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Anhängen fehlgeschlagen.'); }
  }
  async function loesen(z) {
    if (!window.confirm(`Auszifferung lösen?\n\nDie Zahlung über ${fmtEur(z.soll ?? z.haben)} ist danach nicht mehr dem offenen Posten ${z.op_nr} zugeordnet${['zahlung', 'belegzahlung'].includes(z.quelle) ? ' und steht im Bankabgleich wieder zur Zuordnung bereit' : z.quelle === 'bank' ? ' – sie bleibt ohne Zuordnung auf dem Konto und lässt sich über „zuordnen…“ neu zuordnen' : ''}.`)) return;
    try { await api.post(`/buchhaltung/buchung/${z.buchung_id}/ausziffern-loesen`); setVorschau(null); setVer(v => v + 1); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Lösen fehlgeschlagen.'); }
  }
  const pk = d && (d.art === 'D' || d.art === 'K' || d.sammelkonto);
  const zeilenI = (d?.zeilen || []).map((z, i) => ({ ...z, _i: i }));
  const tabZ = useTabelle(zeilenI);
  const tabO = useTabelle(d?.offene_posten);
  // alle Belege des Kontos in Buchungsreihenfolge (Ursprungsbelege stehen bei Zahlungen bewusst ein zweites Mal)
  const belegListe = [];
  zeilenI.forEach((z) => (z.belege || []).forEach(b => belegListe.push({ ...b, zeile: z._i, kontext: `${dDE(z.datum)} · ${z.text || ''}` })));
  const fehlend = pk ? zeilenI.filter(z => z.beleg_fehlt && z.quelle !== 'afa').length : 0;
  const aktZeile = vorschau != null && belegListe[vorschau] ? belegListe[vorschau].zeile : null;
  // Ansicht nach Auszifferung: Beleg/Rechnung und ihre Zahlungen als Vorgang; Reihenfolge nach Belegdatum (nicht Zahldatum)
  // bzw. nach der gewählten Spalte. Ohne Häkchen: alle Buchungen als Liste (Buchungsreihenfolge bzw. streng nach Spalte).
  const gruppenAn = pk && !d?.sammelkonto && gruppiert;
  const gruppen = [];
  const belegTag = z => z.belegdatum || z.quelle_datum || z.datum || '';
  if (gruppenAn) {
    const idx = {};
    tabZ.rows.forEach((z, pos) => {
      const key = z.op_nr || '';
      if (!(key in idx)) { idx[key] = gruppen.length; gruppen.push({ key, op: z.op_nr, zeilen: [], pos: [] }); }
      gruppen[idx[key]].zeilen.push(z); gruppen[idx[key]].pos.push(pos);
    });
    gruppen.forEach(g => {
      const entst = g.zeilen.filter(z => !z.ist_zahlung);
      const posE = g.pos.filter((p, i) => !g.zeilen[i].ist_zahlung);
      g.erste = Math.min(...(posE.length ? posE : g.pos));
      g.belegdatum = (entst.length ? entst : g.zeilen).map(belegTag).sort()[0] || '';
      g.betrag = Math.round(entst.reduce((a, z) => a + (z.soll || 0) + (z.haben || 0), 0) * 100) / 100;
      g.offen = g.zeilen[0]?.op_offen;
      g.ausgeziffert = !!g.op && g.offen != null && Math.abs(g.offen) < 0.005;
      g.zeilen.sort((a, b) => (a.ist_zahlung ? 1 : 0) - (b.ist_zahlung ? 1 : 0) || belegTag(a).localeCompare(belegTag(b)) || (a.datum || '').localeCompare(b.datum || '') || a._i - b._i);
    });
    const so = tabZ.sort || {};
    const nachDatum = !so.key || so.id === 'datum';
    const dir = so.key ? so.dir : 1;
    gruppen.sort((a, b) => (a.op ? 1 : 0) - (b.op ? 1 : 0) || (nachDatum ? a.belegdatum.localeCompare(b.belegdatum) * dir : a.erste - b.erste));
    let n = 0;
    gruppen.forEach(g => { g.farbe = g.op ? OP_FARBEN[n++ % OP_FARBEN.length] : '#aab3bd'; });
  }
  const eingeklappt = g => (zu[g.key] ?? g.ausgeziffert);
  const ausgleichVorlage = (o) => (d.art === 'D'
    ? { konto: '', gegenkonto: d.name, sh: 'S', betrag: o.offen, op_nr: o.op_nr || '', text: `Ausgleich ${o.op_nr || ''}`.trim() }
    : { konto: d.name, gegenkonto: '', sh: 'S', betrag: o.offen, op_nr: o.op_nr || '', text: `Ausgleich ${o.op_nr || ''}`.trim() });
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: vorschau != null ? 'min(1760px, 98vw)' : 'min(1240px, 96vw)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}`, paddingBottom: 8, marginBottom: 10 }}>
          {stapel.length > 1 && <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => setStapel(s => s.slice(0, -1))}>← zurück</button>}
          <h2 style={{ margin: 0, fontSize: 17, color: C.primary }}>Kontoblatt {d ? d.name : aktuell}</h2>
          {d && <span style={{ ...S.badge }}>{KART_LABEL[d.art]}</span>}
          <span style={{ flex: 1 }} />
          {fehlend > 0 && <span style={{ ...S.badge, background: '#fdecea', color: C.danger }}>{fehlend} Buchung(en) ohne Beleg</span>}
          {belegListe.length > 0 && vorschau == null && <button style={{ ...S.btnSmall, background: '#eef3f8', color: C.text, border: `1px solid ${C.border}` }} onClick={() => setVorschau(0)}>📄 Belege ansehen ({belegListe.length})</button>}
          <select style={{ ...S.select, width: 'auto' }} value={jahr} onChange={e => { setVorschau(null); setJahr(parseInt(e.target.value, 10)); }}>
            {[heute.getFullYear() + 1, heute.getFullYear(), heute.getFullYear() - 1, heute.getFullYear() - 2].map(j => <option key={j} value={j}>{j}</option>)}
          </select>
          {pk && d && !d.sammelkonto && <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }} title="Beleg bzw. Rechnung und die zugeordneten Zahlungen zusammen als Vorgang anzeigen – sortiert nach Belegdatum">
            <input type="checkbox" checked={gruppiert} onChange={e => setGruppiert(e.target.checked)} /> nach Auszifferung</label>}
          {onBuchen && d && !d.sammelkonto && <button style={S.btnSmall} onClick={() => onBuchen({ konto: d.name })}>+ Buchung auf diesem Konto</button>}
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
        </div>
        {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
        {!d ? <div style={S.empty}>Lade…</div> : (
          <div style={vorschau != null ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(420px, 1fr)', gap: 14, alignItems: 'start' } : undefined}>
          <div style={{ minWidth: 0 }}>
            {d.sammelkonto && <div style={{ ...S.pathInfo, background: C.bg, padding: '8px 10px', borderRadius: 6 }}>Sammelkonto: zeigt alle Buchungen der {d.nr === '1400' ? 'Debitorenkonten' : 'Kreditorenkonten'}. Es wird nicht selbst bebucht und zählt in der Saldenliste nicht doppelt.</div>}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              {kpiBox('Saldovortrag', fmtSaldo(d.vortrag_natur), C.muted)}
              {kpiBox('Soll', fmtEur(d.summe_soll), C.primary)}
              {kpiBox('Haben', fmtEur(d.summe_haben), C.primary)}
              {kpiBox(`Saldo (+ = ${d.natur === 'S' ? 'Soll' : 'Haben'})`, fmtSaldo(d.saldo_natur), d.saldo_natur < -0.004 ? C.danger : C.primary,
                d.art === 'D' ? (d.saldo_natur >= 0 ? 'offene Forderung' : 'Guthaben des Mandanten') : d.art === 'K' ? (d.saldo_natur >= 0 ? 'offene Verbindlichkeit' : 'Guthaben beim Lieferanten') : '')}
            </div>
            {pk && (d.offene_posten || []).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <h3 style={{ fontSize: 14, color: C.primary, margin: '4px 0 6px' }}>Offene Posten</h3>
                <table style={S.table}><thead><tr>{tabO.th('op_nr', 'OP-Nr.')}{tabO.th('datum', 'Datum')}{tabO.th('text', 'Text')}{tabO.th('betrag', 'Betrag', { textAlign: 'right' })}{tabO.th('ausgeglichen', 'ausgeglichen', { textAlign: 'right' })}{tabO.th('offen', 'offen', { textAlign: 'right' })}<th style={S.th}></th></tr></thead>
                  <tbody>{tabO.rows.map(o => (
                    <tr key={o.op_nr || '-'}>
                      <td style={S.td}>{o.op_nr || <span style={{ color: C.muted }}>ohne Zuordnung</span>}{o.altposten && <span style={{ ...S.badge, marginLeft: 6 }} title="Offener Posten aus dem Altsystem, übernommen zum Buchführungsbeginn">Altposten</span>}</td>
                      <td style={S.td}>{dDE(o.datum)}</td>
                      <td style={{ ...S.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.text || ''}>{o.text || ''}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(o.betrag)}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(o.ausgeglichen)}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: o.offen < 0 ? C.danger : C.text }}>{fmtEur(o.offen)}</td>
                      <td style={S.td}>{onBuchen && o.offen > 0 && <button style={S.btnSmall} title="Ausgleich ohne Bankumsatz (z. B. Kasse, Verrechnung). Bankzahlungen bitte im Reiter Bank zuordnen." onClick={() => onBuchen(ausgleichVorlage(o))}>Ausgleich buchen…</button>}</td>
                    </tr>))}</tbody></table>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <b style={{ fontSize: 13, color: C.primary }}>Buchungen</b>
              {gruppenAn && <>
                <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => setZu(Object.fromEntries(gruppen.map(g => [g.key, false])))}>alle aufklappen</button>
                <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => setZu(Object.fromEntries(gruppen.map(g => [g.key, true])))}>alle einklappen</button>
              </>}
              <span style={{ flex: 1 }} />{tabZ.suche('Text, Beleg, OP, Betrag…', 220)}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead><tr>{tabZ.th('datum', 'Datum')}<th style={S.th}>Belege</th>{tabZ.th(z => z.beleg || z.op_nr, 'Beleg-Nr. / OP', null, 'beleg')}{tabZ.th('text', 'Text')}{tabZ.th('gegenkonto', 'Gegenkonto')}
                  {tabZ.th('soll', 'Soll', { textAlign: 'right' })}{tabZ.th('haben', 'Haben', { textAlign: 'right' })}{pk && <th style={S.th}>Ausgleich</th>}<th style={S.th}></th></tr></thead>
                <tbody>
                  {(gruppenAn ? gruppen.flatMap(g => [
                    <tr key={`g-${g.key}`} onClick={() => setZu(x => ({ ...x, [g.key]: !eingeklappt(g) }))} style={{ cursor: 'pointer' }} title={eingeklappt(g) ? 'Aufklappen' : 'Einklappen'}>
                      <td colSpan={9} style={{ ...S.td, background: g.op ? `${g.farbe}14` : '#f4f6f8', boxShadow: `inset 4px 0 0 ${g.farbe}`, fontSize: 12.5, padding: '6px 10px' }}>
                        <span style={{ display: 'inline-block', width: 16, color: C.muted }}>{eingeklappt(g) ? '▸' : '▾'}</span>
                        {g.op ? (
                          <span><b>Vorgang {g.op}</b>
                            <span style={{ color: C.muted, marginLeft: 8 }}>Beleg vom {dDE(g.belegdatum)}{g.betrag ? ` · ${fmtEur(g.betrag)}` : ''}</span>
                            {g.offen != null && (g.ausgeziffert
                              ? <span style={{ ...S.badge, marginLeft: 8, background: '#e9f7ef', color: C.success }}>⇄ ausgeziffert</span>
                              : <span style={{ ...S.badge, marginLeft: 8, background: '#fff7e6', color: '#8a5a00' }}>noch offen {fmtEur(g.offen)}</span>)}
                            <span style={{ color: C.muted, marginLeft: 8 }}>{g.zeilen.length} Buchung{g.zeilen.length === 1 ? '' : 'en'}</span></span>
                        ) : (
                          <span><b>Nicht zugeordnet</b><span style={{ color: C.muted, marginLeft: 8 }}>Zahlungen ohne Beleg bzw. Rechnung – über „zuordnen…“ ausziffern</span></span>
                        )}
                      </td>
                    </tr>,
                    ...(eingeklappt(g) ? [] : g.zeilen.map(z => [z, z._i, g.farbe])),
                  ]) : tabZ.rows.map(z => [z, z._i, null])).map(x => (!Array.isArray(x) ? x : (([z, i, farbe]) => (
                    <tr key={`${z.buchung_id}-${z.konto}-${i}`} title={vorschau != null && (z.belege || []).length ? 'Klick zeigt den Beleg dieser Buchung' : undefined}
                      onClick={() => { if (vorschau != null && (z.belege || []).length) setVorschau(belegListe.findIndex(b => b.zeile === i)); }}
                      style={{ opacity: z.storniert ? 0.55 : 1, cursor: vorschau != null && (z.belege || []).length ? 'pointer' : 'default', background: aktZeile === i ? '#e8f0fa' : (z.quelle === 'storno' ? '#fff8f7' : 'transparent'), boxShadow: farbe ? `inset 4px 0 0 ${farbe}` : undefined }}>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }} title={z.notiz || ''}>{dDE(z.datum)}{z.notiz && z.notiz.startsWith('Nachbuchung') && <span style={{ color: '#b85c00', marginLeft: 4 }} title={z.notiz}>↩</span>}{z.festgeschrieben ? ' 🔒' : ''}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {(z.belege || []).length > 0 && <button title={z.belege.map(b => `${b.rolle}: ${b.name}`).join('\n')}
                          style={{ ...S.btnSmall, marginRight: 4, background: aktZeile === i ? C.primary : '#eef3f8', color: aktZeile === i ? '#fff' : C.text, border: `1px solid ${aktZeile === i ? C.primary : C.border}` }}
                          onClick={() => setVorschau(belegListe.findIndex(b => b.zeile === i))}>📄{z.belege.length > 1 ? ` ${z.belege.length}` : ''}</button>}
                        {pk && z.beleg_fehlt && z.quelle !== 'afa' && <span style={{ ...S.badge, background: '#fdecea', color: C.danger, marginRight: 4 }}>fehlt</span>}
                        {!z.datei && !z.festgeschrieben && <label title={(z.belege || []).length ? 'Weiteren Beleg anhängen' : 'Beleg anhängen'} style={{ ...S.btnSmall, background: '#eee', color: C.text, display: 'inline-block' }}>📎+<input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xml" style={{ display: 'none' }} onChange={e => anhaengen(z, e.target.files[0])} /></label>}
                      </td>
                      <td style={{ ...S.td, fontSize: 12 }}>{z.beleg || '—'}{z.op_nr && z.op_nr !== z.beleg ? <div style={{ color: C.muted }}>OP {z.op_nr}</div> : null}{z.altposten && <div><span style={{ ...S.badge }}>Altposten</span></div>}</td>
                      <td style={{ ...S.td, maxWidth: vorschau != null ? 230 : 320, ...(vorschau != null ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}) }} title={z.text || ''}>{z.gu ? <span style={{ ...S.badge, marginRight: 6, background: '#fdecea', color: C.danger }}>GU</span> : null}{z.teil_von ? <span style={{ ...S.badge, marginRight: 6 }} title="Eine Bankzahlung, aufgeteilt auf mehrere Vorgänge">Teil von {fmtEur(z.teil_von)}</span> : null}{z.text}{d.sammelkonto ? <span style={{ color: C.muted }}> · {z.konto_name}</span> : null}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }} title={z.gegenkonto_name}><KLink nr={z.gegenkonto} name={vorschau != null ? z.gegenkonto : z.gegenkonto_name} onKonto={gehe} /></td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{z.soll != null ? fmtEur(z.soll) : ''}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{z.haben != null ? fmtEur(z.haben) : ''}</td>
                      {pk && <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }} onClick={e => e.stopPropagation()}>
                        {z.op_nr && z.op_offen != null && (Math.abs(z.op_offen) < 0.005
                          ? <span style={{ ...S.badge, background: '#e9f7ef', color: C.success }} title={`Offener Posten ${z.op_nr} ist ausgeglichen`}>⇄ ausgeglichen</span>
                          : <span style={{ ...S.badge }} title={`Offener Posten ${z.op_nr}`}>offen {fmtEur(z.op_offen)}</span>)}
                        {z.loesbar && <button title="Auszifferung lösen – die Zahlung wird wieder frei und kann neu zugeordnet werden" style={{ ...S.btnSmall, background: '#eee', color: C.text, marginLeft: 4 }} onClick={() => loesen(z)}>lösen</button>}
                        {z.zuordenbar && <button title="Diese Zahlung einem oder mehreren offenen Posten zuordnen (ausziffern)" style={{ ...S.btnSmall, marginLeft: 4 }} onClick={() => setZuordnen(z)}>zuordnen…</button>}
                      </td>}
                      <td style={S.td} onClick={e => e.stopPropagation()}>{z.bearbeitbar && !['rechnung', 'zahlung'].includes(z.quelle) && <button title="Buchung bearbeiten" style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => setBearbeiten(z.buchung_id)}>✎</button>}</td>
                    </tr>
                  ))(x)))}
                  {zeilenI.length === 0 && <tr><td style={S.td} colSpan={10}><span style={{ color: C.muted }}>Keine Buchungen im Jahr {jahr}.</span></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          {vorschau != null && belegListe[vorschau] && (
            <div style={{ position: 'sticky', top: 0 }}>
              <BelegVorschau liste={belegListe} index={vorschau} setIndex={setVorschau} onClose={() => setVorschau(null)} hoehe="calc(100vh - 120px)" />
            </div>
          )}
          </div>
        )}
      </div>
      {bearbeiten && <div onClick={e => e.stopPropagation()}><BuchungBearbeiten id={bearbeiten} onClose={() => setBearbeiten(null)} onDone={() => { setBearbeiten(null); setVer(v => v + 1); }} /></div>}
      {zuordnen && d && <div onClick={e => e.stopPropagation()}><AusziffernModal konto={d.name} zeile={zuordnen} ops={d.offene_posten || []} onClose={() => setZuordnen(null)} onDone={() => { setZuordnen(null); setVer(v => v + 1); }} /></div>}
    </div>
  );
}

const OP_BUCKETS = {
  D: [['nicht_faellig', 'Nicht fällig', C.success], ['t1_30', '1–30 Tage überf.', '#d9a400'], ['t31_60', '31–60 Tage überf.', '#b85c00'], ['t60plus', '> 60 Tage überf.', C.danger], ['guthaben', 'Guthaben (Haben)', C.accent]],
  K: [['t0_30', 'bis 30 Tage alt', C.success], ['t31_60', '31–60 Tage alt', '#b85c00'], ['t60plus', '> 60 Tage alt', C.danger], ['guthaben', 'Guthaben (Soll)', C.accent]],
};
function OposListe({ seite, onKonto, onBuchen, extra }) {
  const art = seite === 'kreditoren' ? 'K' : 'D';
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const tab = useTabelle(d?.posten);
  useEffect(() => {
    api.get('/buchhaltung/opos', { params: { seite } }).then(({ data }) => setD(data)).catch(ex => setErr(ex.response?.data?.detail || 'Offene Posten konnten nicht geladen werden.'));
  }, [seite]);
  async function pdf(p) {
    try {
      const res = await api.get(`/rechnungen/${p.rechnung_id}/pdf`, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = (p.op_nr || 'rechnung') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove();
    } catch { setErr('PDF konnte nicht geladen werden.'); }
  }
  if (err) return <div style={{ ...S.hint, color: C.danger }}>{err}</div>;
  if (!d) return <div style={S.empty}>Lade…</div>;
  const posten = tab.rows;
  const ausgleich = (p) => (art === 'D'
    ? { konto: '', gegenkonto: `${p.konto} ${p.name}`, sh: 'S', betrag: p.offen, op_nr: p.op_nr || '', text: `Ausgleich ${p.op_nr || ''} ${p.name}`.trim() }
    : { konto: `${p.konto} ${p.name}`, gegenkonto: '', sh: 'S', betrag: p.offen, op_nr: p.op_nr || '', text: `Ausgleich ${p.op_nr || ''} ${p.name}`.trim() });
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>OPOS-Liste {art === 'D' ? 'Debitoren (Forderungen)' : 'Kreditoren (Verbindlichkeiten)'}</h2>
        <span style={{ flex: 1 }} />
        {tab.suche('Name, Konto, OP-Nr., Betrag…', 220)}
        {extra}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {kpiBox('Offen gesamt', fmtEur(d.summe_offen), C.primary, `${d.anzahl} Posten · Saldo ${fmtEur(d.summe)}`)}
        {OP_BUCKETS[art].map(([k, l, col]) => (d.buckets[k] ? <div key={k} style={{ flex: '1 1 130px', background: C.bg, borderRadius: 6, padding: '10px 12px', borderLeft: `4px solid ${col}` }}>
          <div style={{ fontSize: 12, color: C.muted }}>{l}</div><div style={{ fontSize: 16, fontWeight: 600 }}>{fmtEur(d.buckets[k])}</div></div> : null))}
      </div>
      {posten.length === 0 ? <div style={S.empty}>Keine offenen Posten.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr>{tab.th('konto', 'Konto')}{tab.th('name', art === 'D' ? 'Debitor' : 'Kreditor')}{tab.th('op_nr', 'OP-Nr.')}{tab.th('datum', 'Datum')}
              {art === 'D' ? tab.th('faellig', 'Fällig') : tab.th('tage', 'Alter')}{tab.th('betrag', 'Betrag', { textAlign: 'right' })}{tab.th('ausgeglichen', 'ausgeglichen', { textAlign: 'right' })}{tab.th('offen', 'offen', { textAlign: 'right' })}<th style={S.th}></th></tr></thead>
            <tbody>
              {posten.map(p => (
                <tr key={`${p.konto}-${p.op_nr || ''}`} style={{ cursor: 'pointer' }} onClick={() => onKonto(p.konto)} title="Kontoblatt öffnen">
                  <td style={{ ...S.td, fontWeight: 600 }}><KLink nr={p.konto} onKonto={onKonto} /></td>
                  <td style={S.td}><KLink nr={p.konto} name={p.name} onKonto={onKonto} /></td>
                  <td style={S.td}>{p.op_nr || <span style={{ color: C.muted }}>ohne Zuordnung</span>}{p.altposten && <span style={{ ...S.badge, marginLeft: 6 }} title="Offener Posten aus dem Altsystem, übernommen zum Buchführungsbeginn">Altposten</span>}</td>
                  <td style={S.td}>{dDE(p.datum)}</td>
                  <td style={S.td}>{art === 'D'
                    ? <>{dDE(p.faellig)}{p.tage > 0 && p.offen > 0 && <span style={{ ...S.badge, marginLeft: 6, background: '#fff', color: C.danger, border: `1px solid ${C.danger}` }}>{p.tage} T überf.</span>}</>
                    : <span>{p.tage} Tage</span>}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(p.betrag)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(p.ausgeglichen)}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: p.offen < 0 ? C.accent : C.text }}>{fmtEur(p.offen)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {art === 'D' && p.rechnung_id && p.pdf_filename && <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 6 }} onClick={() => pdf(p)}>PDF</button>}
                    {onBuchen && p.offen > 0 && <button style={S.btnSmall} onClick={() => onBuchen(ausgleich(p))}>Ausgleich buchen…</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ ...S.pathInfo, marginTop: 8 }}>Aus dem Journal: {art === 'D' ? 'Ausgangsrechnungen (Debitor an Erlöse) abzüglich Zahlungseingängen (Bank an Debitor). Altersstruktur nach Zahlungsziel.' : 'Eingangsrechnungen (Aufwand an Kreditor) abzüglich Zahlungen (Kreditor an Bank). Alter ab Belegdatum.'} Klick auf eine Zeile öffnet das Personenkonto, Klick auf eine Spaltenüberschrift sortiert. Altposten = Rechnung aus dem Altsystem, die zum Buchführungsbeginn noch offen war. Zahlungen mit Bankumsatz bitte im Reiter Bank zuordnen.</div>
    </div>
  );
}

function KreditorModal({ kreditor, konten, onClose, onDone }) {
  const [f, setF] = useState({ name: kreditor?.name || '', kreditor_nr: kreditor?.nr || '', iban: kreditor?.iban || '', standard_konto: kreditor?.standard_konto || '', notiz: kreditor?.notiz || '' });
  const [err, setErr] = useState('');
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  async function speichern() {
    if (!f.name.trim()) { setErr('Bitte einen Namen angeben.'); return; }
    try {
      const fd = new FormData();
      fd.append('name', f.name.trim()); if (f.iban) fd.append('iban', f.iban); fd.append('standard_konto', nr0(f.standard_konto)); fd.append('notiz', f.notiz || '');
      if (kreditor) { if (f.kreditor_nr) fd.append('kreditor_nr', f.kreditor_nr); await api.patch(`/kreditoren/${kreditor.kreditor_id}`, fd); onDone(null); }
      else { const { data } = await api.post('/kreditoren', fd); onDone(data); }
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen.'); }
  }
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 520 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>{kreditor ? `Kreditor ${kreditor.nr} bearbeiten` : 'Kreditor anlegen'}</h2>
        {!kreditor && <div style={S.pathInfo}>Die Kontonummer wird automatisch vergeben (nächste freie ab 70001). Der Kreditor steht danach beim Erfassen eines Eingangsbelegs automatisch im Haben.</div>}
        {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
        <label style={S.label}>Name (Lieferant)</label><input style={S.input} value={f.name} onChange={e => set('name', e.target.value)} />
        {kreditor && <><label style={S.label}>Kontonummer</label><input style={S.input} value={f.kreditor_nr} onChange={e => set('kreditor_nr', e.target.value)} /></>}
        <label style={S.label}>IBAN (für die Zuordnung von Bankumsätzen und e-Rechnungen)</label><input style={S.input} value={f.iban} onChange={e => set('iban', e.target.value)} />
        <label style={S.label}>Standard-Aufwandskonto</label>
        <input style={S.input} list="kred-aufwand" value={f.standard_konto} onChange={e => set('standard_konto', e.target.value)} placeholder="z. B. 4955 Buchführungskosten" />
        <datalist id="kred-aufwand">{(konten?.aufwand || []).map(k => <option key={k.nr} value={`${k.nr} ${k.name}`} />)}</datalist>
        <label style={S.label}>Notiz</label><input style={S.input} value={f.notiz} onChange={e => set('notiz', e.target.value)} />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 8 }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnSmall} onClick={speichern}>{kreditor ? 'Speichern' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}

function PersonenkontenListe({ seite, onKonto }) {
  const art = seite === 'kreditoren' ? 'K' : 'D';
  const [d, setD] = useState(null);
  const [konten, setKonten] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const [msg, setMsg] = useState('');
  const tab = useTabelle(d?.konten);
  const load = useCallback(async () => { try { const { data } = await api.get('/buchhaltung/personenkonten', { params: { seite } }); setD(data); } catch { /* */ } }, [seite]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (art === 'K') api.get('/konten').then(({ data }) => setKonten(data)).catch(() => {}); }, [art]);
  if (!d) return <div style={S.empty}>Lade…</div>;
  const rows = tab.rows;
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: C.primary }}>{art === 'D' ? 'Debitorenkonten (Mandanten)' : 'Kreditorenkonten (Lieferanten)'}</h2>
        <span style={{ flex: 1 }} />
        {tab.suche('Nummer oder Name…', 200)}
        {art === 'K' && <button style={S.btnSmall} onClick={() => setEdit(null)}>+ Kreditor anlegen</button>}
      </div>
      {msg && <div style={{ ...S.pathInfo, color: C.success }}>{msg}</div>}
      <div style={S.pathInfo}>{art === 'D' ? 'Kontonummer = Kundennummer des Mandanten (10000–69999); Mandanten ohne Kundennummer haben automatisch die nächste freie Nummer bekommen.' : 'Kreditoren bekommen beim Anlegen automatisch eine Kontonummer ab 70001.'} Summe aller Konten = Sammelkonto {d.sammelkonto}: <b>{fmtEur(d.summe)}</b>.</div>
      <table style={S.table}>
        <thead><tr>{tab.th('nr', 'Konto')}{tab.th('name', 'Name')}{art === 'K' && tab.th('standard_konto', 'Standardkonto')}{tab.th('offene_posten', 'offene Posten', { textAlign: 'right' })}{tab.th('saldo', 'Saldo', { textAlign: 'right' })}<th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map(k => (
            <tr key={k.nr} style={{ cursor: 'pointer', opacity: k.bebucht ? 1 : 0.7 }} onClick={() => onKonto(k.nr)}>
              <td style={{ ...S.td, fontWeight: 600 }}><KLink nr={k.nr} onKonto={onKonto} /></td>
              <td style={S.td}>{k.name}</td>
              {art === 'K' && <td style={{ ...S.td, fontSize: 12 }}>{k.standard_konto || '—'}</td>}
              <td style={{ ...S.td, textAlign: 'right' }}>{k.offene_posten || ''}</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: k.saldo < 0 ? C.accent : C.text }}>{k.bebucht ? fmtEur(k.saldo) : <span style={{ color: C.muted }}>noch nicht bebucht</span>}</td>
              <td style={S.td} onClick={e => e.stopPropagation()}>{art === 'K' && <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => setEdit(k)}>bearbeiten</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit !== undefined && <KreditorModal kreditor={edit} konten={konten} onClose={() => setEdit(undefined)}
        onDone={async (neu) => { setEdit(undefined); if (neu) setMsg(neu.vorhanden ? `Kreditor „${neu.name}“ gibt es bereits (${neu.kreditor_nr}).` : `Kreditor ${neu.kreditor_nr} „${neu.name}“ angelegt.`); await load(); }} />}
    </div>
  );
}

function UnterReiter({ wert, setWert, optionen }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
      {optionen.map(([k, l]) => { const a = wert === k; return <button key={k} onClick={() => setWert(k)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${a ? C.primary : C.border}`, background: a ? C.primary : '#fff', color: a ? '#fff' : C.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: a ? 600 : 500 }}>{l}</button>; })}
    </div>
  );
}
function DebitorenTab({ onKonto, onBuchen }) {
  const [ansicht, setAnsicht] = useState('opos');
  const [showImp, setShowImp] = useState(false);
  const [mandanten, setMandanten] = useState([]);
  const [msg, setMsg] = useState('');
  const [ver, setVer] = useState(0);
  useEffect(() => { api.get('/mandanten', { params: { alle: 1 } }).then(({ data }) => setMandanten((data || []).filter(m => !m.ist_eigenes_lohnbuero))).catch(() => {}); }, []);
  return (
    <div>
      <UnterReiter wert={ansicht} setWert={setAnsicht} optionen={[['opos', 'Offene Posten'], ['konten', 'Debitorenkonten']]} />
      {msg && <div style={{ ...S.pathInfo, background: '#e9f7ef', color: C.success, padding: '8px 10px', borderRadius: 6 }}>{msg}</div>}
      {ansicht === 'opos' && <OposListe key={ver} seite="debitoren" onKonto={onKonto} onBuchen={onBuchen}
        extra={<button style={{ ...S.btnSmall, background: '#eee', color: C.text }} title="Außerhalb des Portals erstellte Ausgangsrechnung einem Mandanten zuordnen" onClick={() => setShowImp(true)}>⬆ Bestehende Rechnung hochladen</button>} />}
      {ansicht === 'konten' && <PersonenkontenListe seite="debitoren" onKonto={onKonto} />}
      {showImp && <RechnungImportDialog mandantId="" mandanten={mandanten} onClose={() => setShowImp(false)}
        onDone={(data) => { setShowImp(false); setMsg(`Rechnung ${data.rechnungsnummer} übernommen – sie ist jetzt als offener Posten auf dem Debitorenkonto gebucht.`); setVer(v => v + 1); }} />}
    </div>
  );
}
function KreditorenTab({ onKonto, onBuchen }) {
  const [ansicht, setAnsicht] = useState('opos');
  return (
    <div>
      <UnterReiter wert={ansicht} setWert={setAnsicht} optionen={[['opos', 'Offene Posten'], ['konten', 'Kreditorenkonten']]} />
      {ansicht === 'opos' && <OposListe seite="kreditoren" onKonto={onKonto} onBuchen={onBuchen} />}
      {ansicht === 'konten' && <PersonenkontenListe seite="kreditoren" onKonto={onKonto} />}
    </div>
  );
}

function Buchungen({ filter, onFilterUsed, onKonto, onBuchen }) {
  const today = new Date();
  const [von, setVon] = useState(isoTag(new Date(today.getFullYear(), 0, 1)));
  const [bis, setBis] = useState(isoTag(new Date(today.getFullYear(), 11, 31)));
  const [konto, setKonto] = useState('');
  const [gegenkonto, setGegenkonto] = useState('');
  const [art, setArt] = useState('');
  const [quelle, setQuelle] = useState('');
  const [jrn, setJrn] = useState(null);
  const [salden, setSalden] = useState(null);
  const [view, setView] = useState('journal');
  const [festBis, setFestBis] = useState('');
  const [vorschauListe, setVorschauListe] = useState(null);
  const [bearbeiten, setBearbeiten] = useState(null);
  const [lohnImport, setLohnImport] = useState(false);
  const [msg, setMsg] = useState('');
  const tabJ = useTabelle(jrn?.zeilen);
  const tabS = useTabelle(salden?.konten);
  useEffect(() => { if (filter) { if (filter.konto) setKonto(filter.konto); if (filter.gegenkonto) setGegenkonto(filter.gegenkonto); if (onFilterUsed) onFilterUsed(); } }, [filter, onFilterUsed]);
  const load = useCallback(async () => {
    try {
      const [j, s] = await Promise.all([
        api.get('/buchhaltung/journal', { params: { von, bis, konto: konto || undefined, gegenkonto: gegenkonto || undefined, art: art || undefined, quelle: quelle || undefined } }),
        api.get('/buchhaltung/salden', { params: { von, bis } }),
      ]);
      setJrn(j.data); setSalden(s.data);
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Journal konnte nicht geladen werden.'); }
  }, [von, bis, konto, gegenkonto, art, quelle]);
  useEffect(() => { load(); }, [load]);
  async function loeschen(z, ganz) {
    const frage = ganz ? 'Die ganze Lohnbuchungsliste (alle Zeilen dieses Imports) löschen?'
      : z.festgeschrieben ? 'Diese Buchung ist festgeschrieben und wird per Generalumkehr storniert (bleibt sichtbar). Fortfahren?' : 'Buchung löschen?';
    if (!window.confirm(frage)) return;
    try { await api.delete(`/buchhaltung/buchung/${z.id}`, { params: ganz ? { ganzer_import: 1 } : {} }); await load(); } catch (ex) { setMsg(ex.response?.data?.detail || 'Löschen fehlgeschlagen.'); }
  }
  async function anhaengen(z, fl) {
    if (!fl) return;
    try { const fd = new FormData(); fd.append('datei', fl); await api.post(`/buchhaltung/buchung/${z.id}/datei`, fd); await load(); }
    catch (ex) { setMsg(ex.response?.data?.detail || 'Anhängen fehlgeschlagen.'); }
  }
  async function festschreiben() {
    if (!festBis) { setMsg('Bitte ein Datum wählen, bis zu dem festgeschrieben werden soll.'); return; }
    if (!window.confirm(`Alle Buchungen bis einschließlich ${dDE(festBis)} festschreiben?\n\nFestgeschriebene Buchungen können danach nicht mehr geändert oder gelöscht werden (GoBD) – nur noch per Storno/Nachbuchung korrigiert. Das lässt sich nicht rückgängig machen.`)) return;
    try { const fd = new FormData(); fd.append('bis', festBis); const { data } = await api.post('/buchhaltung/festschreiben', fd); setMsg(`${data.anzahl} Buchung(en) festgeschrieben bis ${dDE(data.festgeschrieben_bis)}.`); setFestBis(''); await load(); }
    catch (ex) { setMsg(ex.response?.data?.detail || 'Festschreiben fehlgeschlagen.'); }
  }
  const quelleLabel = { rechnung: 'Ausgangsrechnung', zahlung: 'Zahlung Debitor', beleg: 'Eingangsbeleg', belegzahlung: 'Zahlung Kreditor', bank: 'Bank direkt', afa: 'Anlage/AfA', manuell: 'manuell', lohn: 'Lohnbuchung', storno: 'Storno' };
  const artLabel = { forderung: 'Ausgangsrechnungen', zahlungseingang: 'Zahlungen Debitoren', verbindlichkeit: 'Eingangsrechnungen', zahlungsausgang: 'Zahlungen Kreditoren', einnahme: 'Einnahmen (direkt)', ausgabe: 'Ausgaben (direkt)', anlage: 'Anlagenzugänge', afa: 'Abschreibungen', anlagenabgang: 'Anlagenabgänge', ausgleich: 'Ausgleich ohne Geldfluss', privateinlage: 'Privateinlagen', privatentnahme: 'Privatentnahmen', geldtransit: 'Geldtransit', ust_zahlung: 'USt-Zahlungen', ust_erstattung: 'USt-Erstattungen', umbuchung: 'Umbuchungen' };
  const ab = jrn?.buchfuehrung_ab;
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <ZeitraumWahl von={von} bis={bis} setVon={setVon} setBis={setBis} />
        <span style={{ flex: 1 }} />
        <button style={S.btnSmall} onClick={() => onBuchen({})}>+ Buchung erfassen</button>
        <button style={{ ...S.btnSmall, background: '#eef3f8', color: C.text, border: `1px solid ${C.border}` }} onClick={() => setLohnImport(true)}>⬆ Lohnbuchungen importieren</button>
        <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => dlDatei('/buchhaltung/export', { von, bis, format: 'csv' })}>⬇ CSV</button>
        <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => dlDatei('/buchhaltung/export', { von, bis, format: 'datev' })} title="DATEV-Buchungsstapel (EXTF) für den Steuerberater">⬇ DATEV</button>
      </div>
      {ab && von < ab && <div style={{ ...S.pathInfo, marginBottom: 8 }}>Buchführung im Portal ab {dDE(ab)} – Vorgänge davor stehen im Altsystem und werden hier nicht gezeigt.</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {[['journal', 'Journal'], ['salden', 'Summen- und Saldenliste']].map(([k, l]) => { const a = view === k; return <button key={k} onClick={() => setView(k)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${a ? C.primary : C.border}`, background: a ? C.primary : '#fff', color: a ? '#fff' : C.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: a ? 600 : 500 }}>{l}</button>; })}
        {view === 'journal' && (
          <>
            <select style={{ ...S.select, width: 'auto' }} value={konto} onChange={e => setKonto(e.target.value)}>
              <option value="">Alle Konten</option>
              {(jrn?.konten || []).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <select style={{ ...S.select, width: 'auto' }} value={art} onChange={e => setArt(e.target.value)}>
              <option value="">Alle Arten</option>
              {Object.entries(artLabel).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select style={{ ...S.select, width: 'auto' }} value={quelle} onChange={e => setQuelle(e.target.value)}>
              <option value="">Alle Quellen</option>
              {Object.entries(quelleLabel).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select style={{ ...S.select, width: 'auto' }} value={gegenkonto} onChange={e => setGegenkonto(e.target.value)}>
              <option value="">alle Geldkonten</option><option value="1200">nur Bank</option><option value="1000">nur Kasse (Kassenbuch)</option><option value="1890">nur privat bezahlt</option>
            </select>
            {tabJ.suche('Text, Konto, Beleg, Betrag, Datum…', 230)}
            {(konto || art || quelle || gegenkonto) && <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => { setKonto(''); setArt(''); setQuelle(''); setGegenkonto(''); }}>Filter zurücksetzen</button>}
          </>
        )}
        {view === 'salden' && tabS.suche('Konto oder Name…', 220)}
      </div>
      {msg && <div style={{ ...S.hint, marginBottom: 10 }}>{msg} <button style={{ ...S.btnSmall, background: 'transparent', color: C.text, marginLeft: 8 }} onClick={() => setMsg('')}>✕</button></div>}

      {view === 'journal' && jrn && (
        <div style={S.card}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: C.muted, marginBottom: 10 }}>
            <span>{tabJ.rows.length}{tabJ.q ? ` von ${jrn.anzahl}` : ''} Buchungssätze · Umsatz {fmtEur(jrn.summe_umsatz)}</span>
            <span>EÜR im Zeitraum: Einnahmen netto <b style={{ color: C.success }}>{fmtEur(jrn.summe_einnahmen)}</b></span>
            <span>Ausgaben netto <b style={{ color: '#b85c00' }}>{fmtEur(jrn.summe_ausgaben)}</b></span>
            <span>USt {fmtEur(jrn.summe_ust)} · Vorsteuer {fmtEur(jrn.summe_vst)}</span>
            <span style={{ flex: 1 }} />
            <span>{jrn.festgeschrieben_bis ? `🔒 festgeschrieben bis ${dDE(jrn.festgeschrieben_bis)}` : 'noch nichts festgeschrieben'}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>{tabJ.th('datum', 'Datum')}{tabJ.th('art_label', 'Art')}{tabJ.th(z => z.beleg || z.op_nr, 'Beleg / OP', null, 'beleg')}{tabJ.th('text', 'Text')}{tabJ.th('soll', 'Soll')}{tabJ.th('haben', 'Haben')}{tabJ.th('brutto', 'Betrag', { textAlign: 'right' })}{tabJ.th('ust', 'USt', { textAlign: 'right' })}{tabJ.th(z => (z.kostenstelle === 'gemein' ? 'Gemein' : (z.mandant_name || z.kostenstelle || '')), 'KSt', null, 'kst')}{tabJ.th(z => quelleLabel[z.quelle] || z.quelle, 'Quelle', null, 'quelle')}<th style={S.th}></th></tr></thead>
              <tbody>
                {tabJ.rows.map(z => (
                  <tr key={z.id} style={{ opacity: z.storniert ? 0.55 : 1, textDecoration: z.storniert ? 'line-through' : 'none', background: z.quelle === 'storno' ? '#fff8f7' : 'transparent' }}>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }} title={z.notiz || ''}>{dDE(z.datum)}{z.notiz && z.notiz.startsWith('Nachbuchung') && <span style={{ color: '#b85c00', marginLeft: 4 }} title={z.notiz}>↩</span>}</td>
                    <td style={S.td}><ArtBadge art={z.art} label={z.art_label} />{z.brutto < 0 && <span style={{ ...S.badge, marginLeft: 4, background: '#fdecea', color: C.danger }}>GU</span>}{z.altposten && <span style={{ ...S.badge, marginLeft: 4 }} title="Offener Posten aus dem Altsystem, übernommen zum Buchführungsbeginn">Altposten</span>}</td>
                    <td style={{ ...S.td, fontSize: 12 }}>{z.beleg || '—'}{z.op_nr && z.op_nr !== z.beleg ? <div style={{ color: C.muted }}>OP {z.op_nr}</div> : null}</td>
                    <td style={{ ...S.td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={z.text}>{z.text}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}><KLink nr={z.soll_nr} name={z.soll} onKonto={onKonto} /></td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}><KLink nr={z.haben_nr} name={z.haben} onKonto={onKonto} /></td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{fmtEur(z.brutto)}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: C.muted }}>{z.ust ? `${fmtEur(z.ust)} (${Math.round(z.ust_satz)} %)` : ''}</td>
                    <td style={{ ...S.td, fontSize: 12, color: z.kostenstelle ? C.muted : '#b85c00' }}>{!z.kst_relevant ? '' : z.kostenstelle === 'gemein' ? 'Gemein' : (z.mandant_name || z.kostenstelle || '–')}</td>
                    <td style={{ ...S.td, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{quelleLabel[z.quelle] || z.quelle}{z.festgeschrieben ? ' 🔒' : ''}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      {(z.belege || []).length > 0 && <button title={z.belege.map(b => `${b.rolle}: ${b.name}`).join('\n')} style={{ ...S.btnSmall, background: '#eef3f8', color: C.text, border: `1px solid ${C.border}`, marginRight: 4 }}
                        onClick={() => setVorschauListe({ liste: z.belege.map(b => ({ ...b, kontext: `${dDE(z.datum)} · ${z.text || ''}` })), index: 0 })}>📄{z.belege.length > 1 ? ` ${z.belege.length}` : ''}</button>}
                      {!z.datei && !z.festgeschrieben && !z.storniert && <label title={(z.belege || []).length ? 'Weiteren Beleg anhängen' : 'Digitalen Beleg anhängen'} style={{ ...S.btnSmall, background: '#eee', color: C.text, display: 'inline-block', marginRight: 4 }}>📎+<input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xml" style={{ display: 'none' }} onChange={e => anhaengen(z, e.target.files[0])} /></label>}
                      {z.bearbeitbar && !['rechnung', 'zahlung'].includes(z.quelle) && <button title="Buchung bearbeiten (solange nicht festgeschrieben)" style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 4 }} onClick={() => setBearbeiten(z.id)}>✎</button>}
                      {(z.quelle === 'manuell' || z.quelle === 'lohn') && !z.storniert && <button style={{ ...S.btnSmall, background: '#eee', color: C.danger }} onClick={() => loeschen(z)} title={z.festgeschrieben ? 'Stornieren' : 'Löschen'}>{z.festgeschrieben ? 'Storno' : '✕'}</button>}
                      {z.quelle === 'lohn' && !z.storniert && !z.festgeschrieben && <button style={{ ...S.btnSmall, background: '#eee', color: C.danger, marginLeft: 4 }} onClick={() => loeschen(z, true)} title="Ganze Lohnbuchungsliste löschen">Liste ✕</button>}
                    </td>
                  </tr>
                ))}
                {tabJ.rows.length === 0 && <tr><td style={S.td} colSpan={11}><span style={{ color: C.muted }}>Keine Buchungen im Zeitraum.</span></td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ ...S.pathInfo, marginTop: 8 }}>Ausgangsrechnungen, Zahlungen, Eingangsbelege, Bank-Direktbuchungen und Anlagen/AfA werden automatisch gebucht und folgen Änderungen an der Quelle. ✎ ändert eine Buchung, solange sie nicht festgeschrieben ist (bei automatischen Buchungen die Kostenstelle). Klick auf eine Spaltenüberschrift sortiert. ↩ = Nachbuchung, GU = Generalumkehr/Storno.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, padding: '10px 12px', background: C.bg, borderRadius: 8, flexWrap: 'wrap' }}>
            <b style={{ fontSize: 13 }}>Festschreiben</b>
            <span style={{ fontSize: 12, color: C.muted }}>bis einschließlich</span>
            <input type="date" style={{ ...S.input, width: 'auto', padding: '6px 8px' }} value={festBis} max={isoTag(new Date())} onChange={e => setFestBis(e.target.value)} />
            <button style={S.btnSmall} onClick={festschreiben}>Festschreiben</button>
            <span style={{ fontSize: 12, color: C.muted }}>Nach der Übermittlung einer UStVA wird der Zeitraum automatisch festgeschrieben.</span>
          </div>
        </div>
      )}

      {view === 'salden' && salden && (
        <div style={S.card}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Summen- und Saldenliste {dDE(salden.von)} – {dDE(salden.bis)}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>{tabS.th('nr', 'Konto')}{tabS.th(k => KART_LABEL[k.art] || '', 'Art', null, 'art')}{tabS.th(k => (k.natur === 'S' ? k.vortrag : -k.vortrag), 'Vortrag', { textAlign: 'right' }, 'vortrag')}{tabS.th('soll', 'Soll', { textAlign: 'right' })}{tabS.th('haben', 'Haben', { textAlign: 'right' })}{tabS.th('saldo_natur', 'Saldo', { textAlign: 'right' })}</tr></thead>
              <tbody>
                {tabS.rows.map(k => (
                  <tr key={k.nr} style={{ cursor: 'pointer', background: k.sammelkonto ? '#f5f7fa' : 'transparent', fontStyle: k.sammelkonto ? 'italic' : 'normal' }} onClick={() => onKonto(k.nr)} title="Kontoblatt öffnen">
                    <td style={{ ...S.td, color: C.accent }}>{k.konto}{k.sammelkonto ? <span style={{ color: C.muted }}> – Summe von {k.anzahl} Konten, nicht addiert</span> : null}</td>
                    <td style={{ ...S.td, fontSize: 12, color: C.muted }}>{KART_LABEL[k.art] || ''}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: C.muted }}>{k.vortrag ? fmtSaldo(k.natur === 'S' ? k.vortrag : -k.vortrag) : ''}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{k.soll ? fmtEur(k.soll) : ''}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{k.haben ? fmtEur(k.haben) : ''}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}><SaldoText wert={k.saldo_natur} natur={k.natur} /></td>
                  </tr>
                ))}
                {tabS.rows.length === 0 && <tr><td style={S.td} colSpan={6}><span style={{ color: C.muted }}>Keine Buchungen im Zeitraum.</span></td></tr>}
                <tr><td style={{ ...S.td, fontWeight: 700 }} colSpan={3}>Summe (ohne Sammelkonten)</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtEur(salden.summe_soll)}</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtEur(salden.summe_haben)}</td><td style={S.td}></td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ ...S.pathInfo, marginTop: 8 }}>Saldo mit Vorzeichen je Kontoart: positiv ist der Saldo auf der üblichen Seite des Kontos (S = Soll, H = Haben) – Erlöse, Verbindlichkeiten und Umsatzsteuer im Haben, Aufwände, Bank und Forderungen im Soll. {salden.hinweis}</div>
        </div>
      )}
      {vorschauListe && (
        <div style={S.modalOverlay} onClick={() => setVorschauListe(null)}>
          <div style={{ ...S.modalCard, width: 'min(1000px, 96vw)', padding: 12 }} onClick={e => e.stopPropagation()}>
            <BelegVorschau liste={vorschauListe.liste} index={vorschauListe.index} setIndex={(i) => setVorschauListe(v => ({ ...v, index: i }))} onClose={() => setVorschauListe(null)} hoehe="84vh" />
          </div>
        </div>
      )}
      {bearbeiten && <BuchungBearbeiten id={bearbeiten} onClose={() => setBearbeiten(null)} onDone={async () => { setBearbeiten(null); await load(); }} />}
      {lohnImport && <LohnImportModal onClose={() => setLohnImport(false)} onDone={async (r) => { setLohnImport(false); setMsg(`${r.anzahl} Lohnbuchungen importiert (${(r.monate || []).join(', ')}, Summe ${fmtEur(r.summe)}).`); await load(); }} />}
    </div>
  );
}

function AnlageModal({ anlage, konten, onClose, onDone }) {
  const [f, setF] = useState(anlage ? { bezeichnung: anlage.bezeichnung || '', konto: anlage.konto || '', anschaffung_datum: anlage.anschaffung_datum || '', anschaffungskosten: String(anlage.anschaffungskosten ?? '').replace('.', ','), nutzungsdauer_monate: String(anlage.nutzungsdauer_monate ?? 36), gwg: !!anlage.gwg, abgang_datum: anlage.abgang_datum || '', abgang_erloes: anlage.abgang_erloes != null ? String(anlage.abgang_erloes).replace('.', ',') : '', notiz: anlage.notiz || '' }
    : { bezeichnung: '', konto: '0420 Büroeinrichtung', anschaffung_datum: isoTag(new Date()), anschaffungskosten: '', nutzungsdauer_monate: '36', gwg: false, abgang_datum: '', abgang_erloes: '', notiz: '' });
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const num = (s) => parseFloat(String(s).replace(',', '.'));
  async function speichern() {
    if (!f.bezeichnung.trim()) { setErr('Bitte eine Bezeichnung angeben.'); return; }
    if (!anlage && !(num(f.anschaffungskosten) > 0)) { setErr('Bitte Anschaffungskosten (netto) angeben.'); return; }
    try {
      const fd = new FormData();
      fd.append('bezeichnung', f.bezeichnung.trim()); fd.append('konto', f.konto); fd.append('anschaffung_datum', f.anschaffung_datum);
      if (f.anschaffungskosten !== '') fd.append('anschaffungskosten', num(f.anschaffungskosten));
      fd.append('nutzungsdauer_monate', parseInt(f.nutzungsdauer_monate, 10) || 0); fd.append('gwg', f.gwg ? '1' : '0');
      if (anlage) { fd.append('abgang_datum', f.abgang_datum || ''); if (f.abgang_erloes !== '') fd.append('abgang_erloes', num(f.abgang_erloes)); }
      fd.append('notiz', f.notiz || '');
      if (anlage) await api.patch(`/anlagen/${anlage.id}`, fd); else await api.post('/anlagen', fd);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Speichern fehlgeschlagen.'); }
  }
  const ak = num(f.anschaffungskosten) || 0;
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 600 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>{anlage ? 'Anlage bearbeiten' : 'Anlage erfassen (ohne Zahlungsbuchung)'}</h2>
        {!anlage && <div style={S.pathInfo}>Für Wirtschaftsgüter, deren Kauf nicht über Bank/Beleg gebucht wird – z.B. Altbestand oder Einlage aus dem Privatvermögen. Käufe über die Bank oder einen Beleg einfach auf ein Anlagekonto (0xxx) buchen, dann erscheint die Anlage hier automatisch.</div>}
        {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}><label style={S.label}>Bezeichnung</label><input style={S.input} value={f.bezeichnung} onChange={e => set('bezeichnung', e.target.value)} /></div>
          <div><label style={S.label}>Anlagekonto</label>
            <select style={S.select} value={f.konto} onChange={e => set('konto', e.target.value)}>
              {(konten || []).map(k => <option key={k.nr} value={`${k.nr} ${k.name}`}>{k.nr} {k.name}</option>)}
              {f.konto && !(konten || []).some(k => `${k.nr} ${k.name}` === f.konto) && <option value={f.konto}>{f.konto}</option>}
            </select></div>
          <div><label style={S.label}>Anschaffungsdatum</label><input type="date" style={S.input} value={f.anschaffung_datum} onChange={e => set('anschaffung_datum', e.target.value)} /></div>
          <div><label style={S.label}>Anschaffungskosten netto (€)</label><input style={S.input} value={f.anschaffungskosten} onChange={e => set('anschaffungskosten', e.target.value)} /></div>
          <div><label style={S.label}>Nutzungsdauer (Monate)</label><input style={S.input} value={f.nutzungsdauer_monate} onChange={e => set('nutzungsdauer_monate', e.target.value)} disabled={f.gwg} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={{ ...S.label, marginTop: 4 }}><input type="checkbox" checked={f.gwg} onChange={e => set('gwg', e.target.checked)} /> Geringwertiges Wirtschaftsgut (bis 800 € netto) – Sofortabschreibung im Jahr der Anschaffung</label>
            {ak > 800 && f.gwg && <div style={{ ...S.pathInfo, color: C.danger }}>Über 800 € netto ist eine Sofortabschreibung als GWG nicht zulässig.</div>}</div>
          {anlage && (<>
            <div><label style={S.label}>Abgang am (Verkauf/Entnahme)</label><input type="date" style={S.input} value={f.abgang_datum} onChange={e => set('abgang_datum', e.target.value)} /></div>
            <div><label style={S.label}>Veräußerungserlös netto (€, optional)</label><input style={S.input} value={f.abgang_erloes} onChange={e => set('abgang_erloes', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / -1', ...S.pathInfo }}>Beim Abgang wird die AfA zeitanteilig bis zum Abgangsmonat gebucht und der Restbuchwert als Betriebsausgabe (Konto 2315) erfasst. Einen Verkaufserlös bitte zusätzlich als Einnahme auf „8820 Erlöse aus Verkauf Anlagevermögen“ buchen (Bank oder manuell).</div>
          </>)}
          <div style={{ gridColumn: '1 / -1' }}><label style={S.label}>Notiz</label><input style={S.input} value={f.notiz} onChange={e => set('notiz', e.target.value)} placeholder="z.B. Seriennummer, Standort" /></div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 8 }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnSmall} onClick={speichern}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

function Anlagen() {
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null);   // null | 'neu' | anlage
  const [open, setOpen] = useState({});
  const [msg, setMsg] = useState('');
  const tabA = useTabelle(d?.anlagen);
  const load = useCallback(async () => { try { const { data } = await api.get('/anlagen'); setD(data); } catch (ex) { setMsg(ex.response?.data?.detail || 'Anlagen konnten nicht geladen werden.'); } }, []);
  useEffect(() => { load(); }, [load]);
  async function loeschen(a) {
    if (!window.confirm(`Anlage „${a.bezeichnung}“ samt Abschreibungen löschen?`)) return;
    try { await api.delete(`/anlagen/${a.id}`); await load(); } catch (ex) { setMsg(ex.response?.data?.detail || 'Löschen fehlgeschlagen.'); }
  }
  if (!d) return <div style={S.empty}>Lade…</div>;
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {kpiBox('Anschaffungskosten (Bestand)', fmtEur(d.summe_ak), C.primary)}
        {kpiBox('Buchwert (Ende ' + d.jahr + ')', fmtEur(d.summe_buchwert), C.primary)}
        {kpiBox('Abschreibung ' + d.jahr, fmtEur(d.summe_afa_jahr), '#b85c00', 'wird zum 31.12. als Betriebsausgabe gebucht')}
        <span style={{ flex: 1 }} />
        <button style={S.btnSmall} onClick={() => setModal('neu')}>+ Anlage erfassen</button>
      </div>
      {msg && <div style={{ ...S.hint, marginBottom: 10 }}>{msg} <button style={{ ...S.btnSmall, background: 'transparent', color: C.text, marginLeft: 8 }} onClick={() => setMsg('')}>✕</button></div>}
      <div style={S.card}>
        <h3 style={{ ...S.cardTitle, fontSize: 15 }}>Anlagenverzeichnis (Anlage AVEÜR)</h3>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: 8 }}>{tabA.suche('Bezeichnung, Konto, Betrag…', 240)}</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}></th>{tabA.th('bezeichnung', 'Bezeichnung')}{tabA.th('konto', 'Konto')}{tabA.th('anschaffung_datum', 'Anschaffung')}{tabA.th('anschaffungskosten', 'AK netto', { textAlign: 'right' })}{tabA.th('nutzungsdauer_monate', 'Nutzungsdauer')}{tabA.th('afa_jahr', `AfA ${d.jahr}`, { textAlign: 'right' })}{tabA.th('afa_kumuliert', 'AfA kumuliert', { textAlign: 'right' })}{tabA.th('buchwert', 'Buchwert', { textAlign: 'right' })}{tabA.th(a => (a.abgang_datum ? 'abgegangen' : 'aktiv'), 'Status', null, 'status')}<th style={S.th}></th></tr></thead>
            <tbody>
              {tabA.rows.map(a => (
                <React.Fragment key={a.id}>
                  <tr style={{ opacity: a.abgang_datum ? 0.6 : 1 }}>
                    <td style={S.td}><button style={{ ...S.btnSmall, background: '#eee', color: C.text, padding: '2px 8px' }} onClick={() => setOpen(o => ({ ...o, [a.id]: !o[a.id] }))}>{open[a.id] ? '▾' : '▸'}</button></td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{a.bezeichnung}{a.notiz && <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{a.notiz}</div>}</td>
                    <td style={{ ...S.td, fontSize: 12 }}>{a.konto}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{dDE(a.anschaffung_datum)}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(a.anschaffungskosten)}</td>
                    <td style={S.td}>{a.gwg ? <span style={{ ...S.badge }}>GWG · sofort</span> : `${a.nutzungsdauer_monate} Monate`}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(a.afa_jahr)}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: C.muted }}>{fmtEur(a.afa_kumuliert)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{fmtEur(a.buchwert)}</td>
                    <td style={S.td}>{a.abgang_datum ? <span style={{ ...S.badge, background: '#eceff3', color: C.muted }}>Abgang {dDE(a.abgang_datum)}</span> : <span style={{ ...S.badge, background: '#e9f7ef', color: C.success }}>im Bestand</span>}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => setModal(a)}>Bearbeiten</button>
                      {a.quelle === 'manuell' && <button style={{ ...S.btnSmall, background: '#eee', color: C.danger, marginLeft: 4 }} onClick={() => loeschen(a)}>✕</button>}
                    </td>
                  </tr>
                  {open[a.id] && (
                    <tr><td style={S.td}></td><td style={{ ...S.td, background: C.bg }} colSpan={10}>
                      <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>AfA-Plan (linear, monatsgenau ab Anschaffungsmonat; {a.quelle === 'manuell' ? 'manuell erfasst' : `Zugang aus ${a.quelle === 'beleg' ? 'Beleg' : a.quelle === 'bank' ? 'Bankbuchung' : a.quelle}`})</div>
                      <table style={{ ...S.table, width: 'auto' }}><thead><tr><th style={S.th}>Jahr</th><th style={S.th}>Monate</th><th style={{ ...S.th, textAlign: 'right' }}>AfA</th><th style={{ ...S.th, textAlign: 'right' }}>Buchwert Ende</th><th style={S.th}></th></tr></thead>
                        <tbody>{a.plan.map(p => <tr key={p.jahr}><td style={S.td}>{p.jahr}</td><td style={S.td}>{p.gwg ? 'sofort' : `${p.monate}/12`}</td><td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(p.afa)}</td><td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(p.buchwert)}</td><td style={{ ...S.td, fontSize: 12, color: C.muted }}>{p.abgang ? `Abgang ${dDE(p.datum)} · Restbuchwert ${fmtEur(p.restbuchwert)} als Ausgabe` : ''}</td></tr>)}</tbody>
                      </table>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {d.anlagen.length === 0 && <tr><td style={S.td} colSpan={11}><span style={{ color: C.muted }}>Noch keine Anlagen. Käufe auf ein Anlagekonto (0420 Büroeinrichtung, 0490 Software, 0480 GWG) buchen – per Bank, Beleg oder manuell – dann erscheinen sie hier automatisch.</span></td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ ...S.pathInfo, marginTop: 8 }}>Lineare AfA nach § 7 Abs. 1 EStG (monatsgenau), GWG bis {fmtEur(d.gwg_grenze)} netto sofort (§ 6 Abs. 2 EStG). Nutzungsdauer laut AfA-Tabelle: Computer/Software 3 Jahre, Büromöbel 13 Jahre, Pkw 6 Jahre, Smartphone 5 Jahre.</div>
      </div>
      {modal && <AnlageModal anlage={modal === 'neu' ? null : modal} konten={d.konten} onClose={() => setModal(null)} onDone={async () => { setModal(null); await load(); }} />}
    </div>
  );
}

function Steuern({ start, onGo }) {
  const [sub, setSub] = useState(start || 'ustva');
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [u, setU] = useState(null);
  const [e, setE] = useState(null);
  const [open, setOpen] = useState({});
  const [msg, setMsg] = useState('');
  useEffect(() => { if (start) setSub(start); }, [start]);
  const load = useCallback(async () => {
    try { const [a, b] = await Promise.all([api.get('/buchhaltung/ustva', { params: { jahr } }), api.get('/buchhaltung/euer', { params: { jahr } })]); setU(a.data); setE(b.data); }
    catch (ex) { setMsg(ex.response?.data?.detail || 'Auswertung konnte nicht geladen werden.'); }
  }, [jahr]);
  useEffect(() => { load(); }, [load]);
  async function uebermittelt(p) {
    if (!window.confirm(`UStVA ${p.name} als übermittelt markieren?\n\nKz 83 (Vorauszahlung/Überschuss): ${fmtEur(p.werte.kz83)}\n\nDie gemeldeten Werte werden gespeichert und alle Buchungen bis ${dDE(p.bis)} festgeschrieben.`)) return;
    try { await api.post(`/buchhaltung/ustva/${p.label}/uebermittelt`, new FormData()); await load(); } catch (ex) { setMsg(ex.response?.data?.detail || 'Fehlgeschlagen.'); }
  }
  async function offen(p) { try { await api.post(`/buchhaltung/ustva/${p.label}/offen`); await load(); } catch (ex) { setMsg(ex.response?.data?.detail || 'Fehlgeschlagen.'); } }
  const jahre = [jahr - 2, jahr - 1, jahr, jahr + 1].filter(j => j >= 2024 && j <= new Date().getFullYear());
  const kzRow = (label, kz, val, bold) => <tr><td style={{ ...S.td, fontWeight: bold ? 700 : 400 }}>{label}</td><td style={{ ...S.td, color: C.muted, fontSize: 12 }}>{kz}</td><td style={{ ...S.td, textAlign: 'right', fontWeight: bold ? 700 : 400 }}>{val}</td></tr>;
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        {[['ustva', 'Umsatzsteuer-Voranmeldung'], ['euer', 'EÜR (Anlage EÜR)']].map(([k, l]) => { const a = sub === k; return <button key={k} onClick={() => setSub(k)} style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${a ? C.primary : C.border}`, background: a ? C.primary : '#fff', color: a ? '#fff' : C.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: a ? 600 : 500 }}>{l}</button>; })}
        <span style={{ flex: 1 }} />
        <label style={{ fontSize: 13, color: C.muted }}>Jahr</label>
        <select style={{ ...S.select, width: 'auto' }} value={jahr} onChange={ev => setJahr(parseInt(ev.target.value, 10))}>{jahre.map(j => <option key={j} value={j}>{j}</option>)}</select>
      </div>
      {msg && <div style={{ ...S.hint, marginBottom: 10 }}>{msg} <button style={{ ...S.btnSmall, background: 'transparent', color: C.text, marginLeft: 8 }} onClick={() => setMsg('')}>✕</button></div>}

      {sub === 'ustva' && u && (
        <div style={S.card}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, color: C.muted, marginBottom: 12, alignItems: 'center' }}>
            <span>{u.ist_versteuerung ? 'Ist-Versteuerung (nach vereinnahmten Entgelten, § 20 UStG)' : 'Soll-Versteuerung (nach vereinbarten Entgelten)'}</span>
            <span>· {u.zeitraum === 'monat' ? 'monatlich' : u.zeitraum === 'quartal' ? 'vierteljährlich' : 'jährlich'}{u.dauerfrist ? ' · Dauerfristverlängerung' : ''}</span>
            {u.steuernummer && <span>· St.-Nr. {u.steuernummer}</span>}{u.finanzamt && <span>· {u.finanzamt}</span>}
            <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => onGo('einstellungen')}>Einstellungen</button>
          </div>
          {u.kleinunternehmer ? <div style={S.hint}>Als Kleinunternehmer (§ 19 UStG) geben Sie keine Umsatzsteuer-Voranmeldungen ab.</div> : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead><tr><th style={S.th}></th><th style={S.th}>Zeitraum</th><th style={S.th}>Abgabe bis</th><th style={{ ...S.th, textAlign: 'right' }}>Kz 81 Umsätze 19 %</th><th style={{ ...S.th, textAlign: 'right' }}>USt</th><th style={{ ...S.th, textAlign: 'right' }}>Kz 66 Vorsteuer</th><th style={{ ...S.th, textAlign: 'right' }}>Kz 83 Zahllast</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
                  <tbody>
                    {u.perioden.map(p => (
                      <React.Fragment key={p.label}>
                        <tr style={{ opacity: p.relevant === false ? 0.5 : 1 }}>
                          <td style={S.td}><button style={{ ...S.btnSmall, background: '#eee', color: C.text, padding: '2px 8px' }} onClick={() => setOpen(o => ({ ...o, [p.label]: !o[p.label] }))}>{open[p.label] ? '▾' : '▸'}</button></td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{p.name}</td>
                          <td style={{ ...S.td, color: p.ueberfaellig ? C.danger : C.text, whiteSpace: 'nowrap' }}>{dDE(p.faellig)}{p.ueberfaellig ? ' · überfällig' : ''}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{p.werte.kz81.toLocaleString('de-DE')} €</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(p.werte.ust_summe)}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{fmtEur((p.werte.kz66 || 0) + (p.werte.kz67 || 0))}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: p.werte.kz83 > 0 ? C.danger : C.success }}>{fmtEur(p.werte.kz83)}</td>
                          <td style={S.td}>{p.vor_beginn ? <span style={{ ...S.badge, background: '#eceff3', color: C.muted }} title="vor dem Buchführungsbeginn – im Altsystem gemeldet">Altsystem</span> : p.status === 'uebermittelt' ? <span style={{ ...S.badge, background: '#e9f7ef', color: C.success }}>übermittelt {dDE(p.gemeldet?.uebermittelt_am)}</span> : p.abgelaufen ? <span style={{ ...S.badge, background: '#fff7e6', color: '#b8860b' }}>offen</span> : <span style={{ ...S.badge }}>läuft</span>}
                            {p.vor_beginn && (p.werte.kz46 || p.werte.kz84) ? <span style={{ ...S.badge, background: '#fff7e6', color: '#8a5a00', marginLeft: 4 }} title="Reverse Charge aus diesem Zeitraum – Meldung im Altsystem prüfen">§ 13b prüfen</span> : null}
                            {p.berichtigung && <span style={{ ...S.badge, background: '#fdecea', color: C.danger, marginLeft: 4 }} title={`gemeldet: ${fmtEur(p.gemeldet?.zahllast)} · aktuell: ${fmtEur(p.werte.kz83)}`}>Berichtigung nötig</span>}</td>
                          <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                            {p.status !== 'uebermittelt' && p.abgelaufen && !p.vor_beginn && <button style={S.btnSmall} onClick={() => uebermittelt(p)}>Als übermittelt markieren</button>}
                            {p.status === 'uebermittelt' && !p.vor_beginn && (p.berichtigung ? <button style={S.btnSmall} onClick={() => uebermittelt(p)} title="berichtigte Werte speichern">Berichtigung gemeldet</button> : <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => offen(p)}>wieder öffnen</button>)}
                          </td>
                        </tr>
                        {open[p.label] && (
                          <tr><td style={S.td}></td><td style={{ ...S.td, background: C.bg }} colSpan={8}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                              <table style={{ ...S.table, width: 'auto' }}><tbody>
                                <tr><td style={S.td} colSpan={3}><b>Aktuelle Werte (Buchungen)</b></td></tr>
                                {kzRow('Steuerpflichtige Umsätze 19 %', 'Kz 81', `${p.werte.kz81.toLocaleString('de-DE')} € · USt ${fmtEur(p.werte.kz81_steuer)}`)}
                                {kzRow('Steuerpflichtige Umsätze 7 %', 'Kz 86', `${p.werte.kz86.toLocaleString('de-DE')} € · USt ${fmtEur(p.werte.kz86_steuer)}`)}
                                {kzRow('Umsätze andere Steuersätze', 'Kz 35/36', `${p.werte.kz35.toLocaleString('de-DE')} € · ${fmtEur(p.werte.kz36)}`)}
                                {kzRow('Steuerfreie Umsätze ohne Vorsteuerabzug', 'Kz 48', `${p.werte.kz48.toLocaleString('de-DE')} €`)}
                                {(p.werte.kz46 || p.werte.kz47) ? kzRow('Sonstige Leistungen eines EU-Unternehmers (§ 13b Abs. 1, Reverse Charge)', 'Kz 46/47', `${(p.werte.kz46 || 0).toLocaleString('de-DE')} € · USt ${fmtEur(p.werte.kz47)}`) : null}
                                {(p.werte.kz84 || p.werte.kz85) ? kzRow('Andere Leistungen (§ 13b Abs. 2, Reverse Charge)', 'Kz 84/85', `${(p.werte.kz84 || 0).toLocaleString('de-DE')} € · USt ${fmtEur(p.werte.kz85)}`) : null}
                                {kzRow('Abziehbare Vorsteuer', 'Kz 66', `${fmtEur(p.werte.kz66)} (${p.werte.vst_belege} Belege)`)}
                                {p.werte.kz67 ? kzRow('Vorsteuer aus Leistungen nach § 13b', 'Kz 67', fmtEur(p.werte.kz67)) : null}
                                {kzRow('Vorauszahlung / Überschuss', 'Kz 83', fmtEur(p.werte.kz83), true)}
                              </tbody></table>
                              <div>
                                {p.gemeldet ? (
                                  <table style={{ ...S.table, width: 'auto' }}><tbody>
                                    <tr><td style={S.td} colSpan={3}><b>Gemeldet am {dDE(p.gemeldet.uebermittelt_am)}</b>{p.gemeldet.notiz ? ` · ${p.gemeldet.notiz}` : ''}</td></tr>
                                    {kzRow('Kz 81', '', `${(p.gemeldet.werte?.kz81 ?? 0).toLocaleString('de-DE')} €`)}
                                    {kzRow('Kz 66', '', fmtEur(p.gemeldet.werte?.kz66))}
                                    {kzRow('Kz 83', '', fmtEur(p.gemeldet.zahllast), true)}
                                  </tbody></table>
                                ) : p.vor_beginn ? (
                                  <div style={{ fontSize: 12, color: C.muted }}>Zeitraum vor dem Buchführungsbeginn – im Altsystem gemeldet.
                                    {(p.werte.kz46 || p.werte.kz84) ? <div style={{ marginTop: 8, color: C.text }}><b>Reverse Charge aus diesem Zeitraum:</b> Die Steuer nach § 13b entsteht im Monat der Leistung und gehört deshalb in diese Voranmeldung, nicht in ein Quartal im Portal. Prüfen Sie, ob sie im Altsystem enthalten war. Falls nicht, geben Sie für diesen Zeitraum eine berichtigte Voranmeldung ab (Kz 46/47 bzw. 84/85 und Kz 67 – die Zahllast ändert sich dadurch nicht).</div> : null}
                                  </div>
                                ) : <div style={{ fontSize: 12, color: C.muted }}>Noch nicht gemeldet. Werte in „Mein ELSTER“ eintragen und anschließend hier als übermittelt markieren.</div>}
                                {p.werte.nicht_steuerbar > 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Hinweis: {fmtEur(p.werte.nicht_steuerbar)} Einnahmen mit 0 % USt (nicht steuerbar / ohne Konto 8100) sind in keiner Kennzahl enthalten.</div>}
                              </div>
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ ...S.pathInfo, marginTop: 8 }}>{u.hinweis} Vorsteuer wird nach Belegdatum berücksichtigt (auch für erfasste, noch unbezahlte Belege); Umsätze nach {u.ist_versteuerung ? 'Zahlungseingang' : 'Rechnungsdatum'}. Abgabefrist: 10. Tag nach Ablauf des Zeitraums (Wochenende/Feiertag → nächster Werktag).</div>
            </>
          )}
        </div>
      )}

      {sub === 'euer' && e && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {kpiBox('Summe Betriebseinnahmen', fmtEur(e.summe_einnahmen), C.success)}
            {kpiBox('Summe Betriebsausgaben', fmtEur(e.summe_ausgaben), '#b85c00')}
            {kpiBox(`Gewinn / Verlust ${e.jahr}${e.vorlaeufig ? ' (vorläufig)' : ''}`, fmtEur(e.gewinn), e.gewinn >= 0 ? C.success : C.danger, `${e.anzahl_buchungen} Buchungen`)}
            <span style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => dlDatei('/buchhaltung/export', { von: e.von, bis: e.bis, format: 'csv' })}>⬇ Journal {e.jahr} (CSV)</button>
              <button style={{ ...S.btnSmall, background: '#eee', color: C.text }} onClick={() => dlDatei('/buchhaltung/export', { von: e.von, bis: e.bis, format: 'datev' })}>⬇ DATEV {e.jahr}</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={S.card}>
              <h3 style={{ marginTop: 0, color: C.success, fontSize: 15 }}>Betriebseinnahmen</h3>
              <table style={S.table}><tbody>
                {e.einnahmen.filter(z => z.betrag || ['112', '140'].includes(z.kz)).map(z => <tr key={z.kz}><td style={S.td}>{z.label}<div style={{ fontSize: 11, color: C.muted }}>Kz {z.kz}{z.konten.length ? ` · ${z.konten.join(', ')}` : ''}</div></td><td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtEur(z.betrag)}</td></tr>)}
                <tr><td style={{ ...S.td, fontWeight: 700 }}>Summe Betriebseinnahmen</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtEur(e.summe_einnahmen)}</td></tr>
              </tbody></table>
            </div>
            <div style={S.card}>
              <h3 style={{ marginTop: 0, color: '#b85c00', fontSize: 15 }}>Betriebsausgaben</h3>
              <table style={S.table}><tbody>
                {e.ausgaben.filter(z => z.betrag).map(z => <tr key={z.kz}><td style={S.td}>{z.label}<div style={{ fontSize: 11, color: C.muted }}>Kz {z.kz}{z.konten.length ? ` · ${z.konten.join(', ')}` : ''}</div></td><td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtEur(z.betrag)}</td></tr>)}
                {e.ausgaben.every(z => !z.betrag) && <tr><td style={S.td} colSpan={2}><span style={{ color: C.muted }}>keine</span></td></tr>}
                <tr><td style={{ ...S.td, fontWeight: 700 }}>Summe Betriebsausgaben</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtEur(e.summe_ausgaben)}</td></tr>
                {e.nicht_abziehbar.map(z => <tr key={z.kz}><td style={{ ...S.td, color: C.muted }}>{z.label}<div style={{ fontSize: 11 }}>Kz {z.kz} · nicht gewinnmindernd</div></td><td style={{ ...S.td, textAlign: 'right', color: C.muted }}>{fmtEur(z.betrag)}</td></tr>)}
              </tbody></table>
            </div>
          </div>
          <div style={{ ...S.card, textAlign: 'center', background: '#f4f8f6' }}>
            <span style={{ fontSize: 14, color: C.muted }}>Gewinn {e.jahr} (Betriebseinnahmen − Betriebsausgaben): </span>
            <strong style={{ fontSize: 24, color: e.gewinn >= 0 ? C.success : C.danger }}>{fmtEur(e.gewinn)}</strong>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>nachrichtlich: Privatentnahmen {fmtEur(e.privat.entnahmen)} · Privateinlagen {fmtEur(e.privat.einlagen)}{e.festgeschrieben_bis ? ` · festgeschrieben bis ${dDE(e.festgeschrieben_bis)}` : ''}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={S.card}><h3 style={{ ...S.cardTitle, fontSize: 14 }}>Monatsverlauf (netto)</h3><MonatsChart monate={e.monate} /></div>
            <div style={S.card}>
              <h3 style={{ ...S.cardTitle, fontSize: 14 }}>Anlagenverzeichnis {e.jahr} (AVEÜR)</h3>
              {e.anlagen.length === 0 ? <div style={S.empty}>Keine Anlagen im Jahr.</div> : (
                <table style={S.table}>
                  <thead><tr><th style={S.th}>Wirtschaftsgut</th><th style={{ ...S.th, textAlign: 'right' }}>Buchwert 1.1.</th><th style={{ ...S.th, textAlign: 'right' }}>Zugang</th><th style={{ ...S.th, textAlign: 'right' }}>AfA</th><th style={{ ...S.th, textAlign: 'right' }}>Buchwert 31.12.</th></tr></thead>
                  <tbody>{e.anlagen.map(a => <tr key={a.id}><td style={S.td}>{a.bezeichnung}<div style={{ fontSize: 11, color: C.muted }}>{a.konto} · {dDE(a.anschaffung_datum)}{a.gwg ? ' · GWG' : ''}{a.abgang ? ' · Abgang' : ''}</div></td><td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(a.buchwert_anfang - a.zugang)}</td><td style={{ ...S.td, textAlign: 'right' }}>{a.zugang ? fmtEur(a.zugang) : ''}</td><td style={{ ...S.td, textAlign: 'right' }}>{fmtEur(a.afa)}</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{fmtEur(a.buchwert_ende)}</td></tr>)}</tbody>
                </table>
              )}
            </div>
          </div>
          <div style={S.pathInfo}>{e.hinweis}</div>
        </>
      )}
    </div>
  );
}

function FinanzEinstellungen() {
  const [k, setK] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => { try { const { data } = await api.get('/kanzlei'); setK(data); } catch { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  const set = (f, v) => setK(p => ({ ...p, [f]: v }));
  async function speichern() {
    try {
      const fd = new FormData();
      fd.append('ist_versteuerung', k.ist_versteuerung ? 'true' : 'false'); fd.append('ustva_zeitraum', k.ustva_zeitraum || 'quartal');
      fd.append('dauerfrist', k.dauerfrist ? 'true' : 'false'); fd.append('finanzamt', k.finanzamt || '');
      fd.append('datev_beraternr', k.datev_beraternr || ''); fd.append('datev_mandantennr', k.datev_mandantennr || '');
      fd.append('kleinunternehmer', k.kleinunternehmer ? 'true' : 'false'); fd.append('buchfuehrung_ab', k.buchfuehrung_ab || ''); if (k.steuernummer != null) fd.append('steuernummer', k.steuernummer || '');
      const { data } = await api.put('/kanzlei', fd); setK(data); setMsg('Einstellungen gespeichert.');
    } catch (ex) { setMsg(ex.response?.data?.detail || 'Speichern fehlgeschlagen.'); }
  }
  if (!k) return <div style={S.empty}>Lade…</div>;
  return (
    <div style={{ maxWidth: 820 }}>
      {msg && <div style={{ ...S.hint, marginBottom: 10 }}>{msg}</div>}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Buchführung im Portal</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, alignItems: 'end' }}>
          <div><label style={S.label}>Beginn der Buchführung (sauberer Schnitt)</label><input type="date" style={S.input} value={k.buchfuehrung_ab || ''} onChange={e => set('buchfuehrung_ab', e.target.value)} /></div>
          <div style={{ fontSize: 12.5, color: C.muted }}>Alles davor wurde im Altsystem gebucht und gemeldet und erscheint hier nicht. Rechnungen und Belege von davor, die am Stichtag noch offen waren, werden als Altposten übernommen – die spätere Zahlung bildet mit ihnen das Paar Rechnung + Zahlung. Bankumsätze vor dem Stichtag stehen in der Ansicht „Altsystem“.</div>
        </div>
      </div>
      <div style={S.card}>
        <h2 style={S.cardTitle}>Gewinnermittlung & Umsatzsteuer</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={S.label}>Gewinnermittlungsart</label><input style={S.input} value="Einnahmen-Überschuss-Rechnung (§ 4 Abs. 3 EStG)" disabled /></div>
          <div><label style={S.label}>Steuernummer</label><input style={S.input} value={k.steuernummer || ''} onChange={e => set('steuernummer', e.target.value)} /></div>
          <div><label style={S.label}>Zuständiges Finanzamt</label><input style={S.input} value={k.finanzamt || ''} onChange={e => set('finanzamt', e.target.value)} placeholder="z.B. Finanzamt Neukölln" /></div>
          <div><label style={{ ...S.label, marginTop: 26 }}><input type="checkbox" checked={!!k.kleinunternehmer} onChange={e => set('kleinunternehmer', e.target.checked)} /> Kleinunternehmer (§ 19 UStG) – keine USt, keine Voranmeldungen</label></div>
          <div><label style={S.label}>Besteuerung der Umsätze</label>
            <select style={S.select} value={k.ist_versteuerung ? 'ist' : 'soll'} onChange={e => set('ist_versteuerung', e.target.value === 'ist')} disabled={!!k.kleinunternehmer}>
              <option value="ist">Ist-Versteuerung – USt bei Zahlungseingang (§ 20 UStG, Regelfall bei EÜR)</option>
              <option value="soll">Soll-Versteuerung – USt bei Rechnungsstellung</option>
            </select></div>
          <div><label style={S.label}>Voranmeldungszeitraum</label>
            <select style={S.select} value={k.ustva_zeitraum || 'quartal'} onChange={e => set('ustva_zeitraum', e.target.value)} disabled={!!k.kleinunternehmer}>
              <option value="monat">monatlich (Vorjahres-USt über 9.000 €)</option>
              <option value="quartal">vierteljährlich (Regelfall)</option>
              <option value="jahr">nur Jahreserklärung (Vorjahres-USt bis 2.000 €, auf Antrag)</option>
            </select></div>
          <div><label style={{ ...S.label, marginTop: 26 }}><input type="checkbox" checked={!!k.dauerfrist} onChange={e => set('dauerfrist', e.target.checked)} disabled={!!k.kleinunternehmer} /> Dauerfristverlängerung (§ 46 UStDV) – Abgabe einen Monat später</label></div>
        </div>
      </div>
      <div style={S.card}>
        <h2 style={S.cardTitle}>Steuerberater / DATEV-Export</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={S.label}>DATEV-Beraternummer</label><input style={S.input} value={k.datev_beraternr || ''} onChange={e => set('datev_beraternr', e.target.value)} /></div>
          <div><label style={S.label}>DATEV-Mandantennummer</label><input style={S.input} value={k.datev_mandantennr || ''} onChange={e => set('datev_mandantennr', e.target.value)} /></div>
        </div>
        <div style={S.pathInfo}>Werden in den Kopf des DATEV-Buchungsstapels (EXTF, SKR03) geschrieben. Export unter Buchungen bzw. Steuern › EÜR.</div>
      </div>
      <div style={S.card}>
        <h2 style={S.cardTitle}>Festschreibung & ELSTER</h2>
        <div style={{ fontSize: 13 }}>
          <p style={{ margin: '0 0 8px' }}>{k.festgeschrieben_bis ? <>Buchungen sind bis einschließlich <b>{dDE(k.festgeschrieben_bis)}</b> festgeschrieben.</> : 'Noch kein Zeitraum festgeschrieben.'} Festschreiben unter „Buchungen“ oder automatisch beim Markieren einer UStVA als übermittelt.</p>
          <p style={{ margin: 0, color: C.muted }}>ELSTER: Die Übermittlung der UStVA erfolgt derzeit manuell über „Mein ELSTER“ mit den berechneten Kennzahlen. Die direkte Übermittlung aus dem Portal (ERiC) wird freigeschaltet, sobald die Entwicklerzugangsdaten von ELSTER vorliegen.</p>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}><button style={S.btnPrimary} onClick={speichern}>Speichern</button></div>
    </div>
  );
}

function BelegBezahltModal({ beleg, onClose, onDone }) {
  const [datum, setDatum] = useState(isoTag(new Date()));
  const [weg, setWeg] = useState('bank');
  const [err, setErr] = useState('');
  async function ok() {
    try { const fd = new FormData(); fd.append('wert', '1'); fd.append('datum', datum); fd.append('zahlungsweg', weg); await api.post(`/belege/${beleg.id}/bezahlt`, fd); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || 'Fehlgeschlagen.'); }
  }
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalCard, width: 440 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.cardTitle}>Beleg als bezahlt markieren</h2>
        <div style={S.pathInfo}>Das Zahlungsdatum ist das Abflussdatum in der EÜR. Liegt der Bankumsatz vor, ordnen Sie den Beleg besser im Bankabgleich zu.</div>
        {err && <div style={{ ...S.pathInfo, color: C.danger }}>{err}</div>}
        <label style={S.label}>Bezahlt am</label><input type="date" style={S.input} value={datum} onChange={e => setDatum(e.target.value)} />
        <label style={S.label}>Bezahlt per</label>
        <select style={S.select} value={weg} onChange={e => setWeg(e.target.value)}>
          <option value="bank">Bank (ohne Bankabgleich)</option>
          <option value="kasse">Kasse (bar)</option>
          <option value="privat">privat bezahlt (Einlage)</option>
        </select>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={{ ...S.btnSmall, background: '#eee', color: C.text, marginRight: 8 }} onClick={onClose}>Abbrechen</button>
          <button style={S.btnSmall} onClick={ok}>Bezahlt</button>
        </div>
      </div>
    </div>
  );
}

function FinanzHub({ user }) {
  const [sub, setSub] = useState('uebersicht');
  const [jrnFilter, setJrnFilter] = useState(null);
  const [steuernStart, setSteuernStart] = useState(null);
  const [kontoNr, setKontoNr] = useState(null);
  const [maske, setMaske] = useState(null);
  const [ver, setVer] = useState(0);
  const go = (tab, extra) => {
    if (tab === 'buchungen') setJrnFilter(extra || null);
    if (tab === 'steuern') setSteuernStart(extra || null);
    setSub(tab);
  };
  const tabBtn = (k, l) => {
    const a = sub === k;
    return <button key={k} onClick={() => go(k)} style={{
      border: `1px solid ${a ? C.primary : C.border}`, background: a ? C.primary : '#fff', color: a ? '#fff' : C.text,
      cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, padding: '9px 16px', borderRadius: 6, fontWeight: a ? 600 : 500,
    }}>{l}</button>;
  };
  const oeffneKonto = (nr) => setKontoNr(nr0(nr));
  const buchen = (prefill) => setMaske(prefill || {});
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {tabBtn('uebersicht', 'Übersicht')}
        {tabBtn('debitoren', 'Debitoren')}
        {tabBtn('kreditoren', 'Kreditoren')}
        {tabBtn('bank', 'Bank')}
        {tabBtn('belege', 'Belegeingang')}
        {tabBtn('buchungen', 'Buchungen')}
        {tabBtn('anlagen', 'Anlagen')}
        {tabBtn('steuern', 'Steuern (UStVA & EÜR)')}
        {tabBtn('auswertung', 'Kostenstellen')}
        {tabBtn('einstellungen', 'Einstellungen')}
      </div>
      <div key={`${sub}-${ver}`}>
        {sub === 'uebersicht' && <FinanzUebersicht onGo={go} />}
        {sub === 'debitoren' && <DebitorenTab onKonto={oeffneKonto} onBuchen={buchen} />}
        {sub === 'kreditoren' && <KreditorenTab onKonto={oeffneKonto} onBuchen={buchen} />}
        {sub === 'bank' && <Debitoren user={user} only="bank" />}
        {sub === 'belege' && <Belege user={user} />}
        {sub === 'buchungen' && <Buchungen user={user} filter={jrnFilter} onFilterUsed={() => setJrnFilter(null)} onKonto={oeffneKonto} onBuchen={buchen} />}
        {sub === 'anlagen' && <Anlagen />}
        {sub === 'steuern' && <Steuern start={steuernStart} onGo={go} />}
        {sub === 'auswertung' && <Buchhaltung user={user} onKonto={oeffneKonto} />}
        {sub === 'einstellungen' && <FinanzEinstellungen />}
      </div>
      {kontoNr && <KontoblattModal key={`${kontoNr}-${ver}`} nr={kontoNr} onClose={() => setKontoNr(null)} onBuchen={(p) => buchen(p)} />}
      {maske && <BuchungMaske prefill={maske} onClose={() => setMaske(null)} onDone={() => { setMaske(null); setVer(v => v + 1); }} />}
    </div>
  );
}

// Ungelesene Nachrichten der Gegenseite (rote Zahl im Menü / Dropdown / auf der Chat-Kachel), alle 30 s aktualisiert
function useUngelesen(enabled) {
  const [u, setU] = useState({ gesamt: 0, je_mandant: {} });
  const refresh = useCallback(async () => {
    if (!enabled) return;
    try { const { data } = await api.get('/messages/ungelesen'); setU(data || { gesamt: 0, je_mandant: {} }); } catch { /* Zähler ist optional */ }
  }, [enabled]);
  useEffect(() => { refresh(); if (!enabled) return; const t = setInterval(refresh, 30000); return () => clearInterval(t); }, [refresh, enabled]);
  return [u, refresh];
}
const RotBadge = ({ n }) => (n > 0 ? <span style={{ display: 'inline-block', minWidth: 18, padding: '0 6px', marginLeft: 8, borderRadius: 999, background: '#e5484d', color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center', verticalAlign: 'middle' }}>{n}</span> : null);

export default function App() {
  // feste helle Seitenfarbe: im dunklen Browsermodus wäre der Hintergrund sonst schwarz und Text außerhalb der Karten unlesbar
  useEffect(() => { document.documentElement.style.colorScheme = 'light'; document.body.style.background = C.bg; }, []);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  });
  const [tab, setTab] = useState('dashboard');
  const [mandanten, setMandanten] = useState([]);
  const [booting, setBooting] = useState(!!token);
  const [ticketFocus, setTicketFocus] = useState(null);
  const [maFocus, setMaFocus] = useState(null);
  const [secOpen, setSecOpen] = useState(false);
  const [selectedMandant, setSelectedMandant] = useState(() => localStorage.getItem('selectedMandant') || '');
  const [maNew, setMaNew] = useState(false);
  const [ungelesen, refreshUngelesen] = useUngelesen(!!token && !!user);


  function changeMandant(v) { setSelectedMandant(v); localStorage.setItem('selectedMandant', v); }
  function openTicket(id) { setTicketFocus(id); setTab('tickets'); }
  function goMandant(id, t) { changeMandant(String(id)); setTab(t || 'dashboard'); }
  function openMa(maId) { setMaFocus(maId); setTab('mitarbeiter'); }

  useEffect(() => {
    const id = api.interceptors.request.use(cfg => {
      const t = localStorage.getItem('token');
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });
    return () => api.interceptors.request.eject(id);
  }, []);

  useEffect(() => {
    const id = api.interceptors.response.use(r => r, ex => {
      if (ex.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null); setUser(null);
      }
      return Promise.reject(ex);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  const [meinBueroId, setMeinBueroId] = useState(null);
  const loadMandanten = useCallback(async () => {
    try {
      const { data } = await api.get('/mandanten'); setMandanten(data);
      const mb = await api.get('/mein-buero').catch(() => ({ data: null }));
      setMeinBueroId(mb.data && mb.data.vorhanden ? String(mb.data.id) : null);
    }
    catch { /* client roles see 403 — ok */ }
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        setUser(data);
        localStorage.setItem('user', JSON.stringify(data));
        if (data.role === 'admin' || data.role === 'staff') await loadMandanten();
      } catch { /* 401 interceptor handles logout */ }
      finally { setBooting(false); }
    })();
  }, [token, loadMandanten]);

  function onLogin(data) {
    localStorage.setItem('token', data.access_token);
    const u = { username: data.username, role: data.role, mandant_id: data.mandant_id };
    localStorage.setItem('user', JSON.stringify(u));
    setToken(data.access_token); setUser(u); setTab('dashboard');
  }

  function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null); setUser(null);
  }

  const params = new URLSearchParams(window.location.search);
  const pfbToken = params.get('pfb');
  if (pfbToken) return <PersonalfragebogenPage token={pfbToken} />;
  const rvToken = params.get('rv');
  if (rvToken) return <RvBefreiungPage token={rvToken} />;
  const angebotToken = params.get('angebot');
  if (angebotToken) return <AngebotAnnahmePage token={angebotToken} />;
  const onboardingToken = params.get('onboarding');
  if (onboardingToken && (!token || !user)) return <OnboardingPage token={onboardingToken} onLogin={onLogin} />;
  const inviteToken = params.get('invite');
  if (!token || !user) {
    if (inviteToken) return <InvitePage token={inviteToken} onLogin={onLogin} />;
    return <Login onLogin={onLogin} />;
  }
  if (booting) return <div style={S.loginPage}><div style={{ color: C.white }}>Lade…</div></div>;
  if (user.role === 'mitarbeiter') return <EmployeeApp user={user} onLogout={logout} />;
  if ((user.role === 'client_admin' || user.role === 'client_user') && user.onboarding_status && user.onboarding_status !== 'fertig') {
    return <OnboardingWizard user={user} onDone={() => window.location.assign(window.location.pathname)} />;
  }

  const isAdmin = user.role === 'admin';

  const isStaff = user.role === 'admin' || user.role === 'staff';
  // Lohnbüro ohne Mandantenwahl: nur Übersicht (+ Verwaltung) – die übrigen Tabs brauchen einen Mandanten
  const mandantNeeded = isStaff && !selectedMandant;
  const selMandantObj = mandanten.find(m => String(m.id) === String(selectedMandant));
  const isEigenLohnbuero = !!selMandantObj?.ist_eigenes_lohnbuero;
  // In der Übersicht laufen die übrigen Seiten gegen das eigene Büro
  const ownCtx = selectedMandant || meinBueroId || '';
  const navItems = !isStaff
    ? [
        // Mandanten-Nutzer: aufgeräumt – Auswertungen/Rechnungen/Chat/Erfassen laufen über die Dashboard-Kacheln
        ['dashboard', 'Dashboard'],
        ['mitarbeiter', 'Mitarbeiter'],
        ['settings', 'Einstellungen'],
        ['qa', 'Hilfe & FAQ'],
      ]
    : mandantNeeded
      ? (meinBueroId
          ? [
              // Übersicht: links zugleich alle Seiten des eigenen Büros
              ['dashboard', 'Dashboard'],
              ['documents', 'Dokumente'],
              ['mitarbeiter', 'Mitarbeiter'],
              ['tickets', 'Tickets'],
              ['messages', 'Nachrichten'],
              ['fehlzeiten', 'Fehlzeiten'],
              ['extras', 'Extras'],
              ...(user.role === 'admin' ? [['buchfuehrung', 'Buchführung'], ['leads', 'Leads & CRM'], ['ideen', 'Ideen & Roadmap']] : []),
              ['verwaltung', 'Verwaltung'],
              ['qa', 'Hilfe & FAQ'],
            ]
          : [
              ['dashboard', 'Dashboard'],
              ['fehlzeiten', 'Fehlzeiten'],
              ['extras', 'Extras'],
              ...(user.role === 'admin' ? [['buchfuehrung', 'Buchführung'], ['leads', 'Leads & CRM'], ['ideen', 'Ideen & Roadmap']] : []),
              ['verwaltung', 'Verwaltung'],
              ['qa', 'Hilfe & FAQ'],
            ])
      : [
          ['dashboard', 'Dashboard'],
          ['documents', 'Dokumente'],
          ['mitarbeiter', 'Mitarbeiter'],
          ...(selMandantObj?.karte_aktiv ? [['karte', 'Karte']] : []),
          ['tickets', 'Tickets'],
          ['messages', 'Nachrichten'],
          ['fehlzeiten', 'Fehlzeiten'],
          ['arbeitszeiten', 'Zeiten'],
          // „Eigenes Lohnbüro": keine Leistungserfassung/Rechnung
          ...(isEigenLohnbuero ? [] : [['leistungen', 'Leistungen'], ['insights', 'Insights']]),
          ['verwaltung', 'Verwaltung'],
          ['qa', 'Hilfe & FAQ'],
        ];
  // Ziele der Mandanten-Kacheln: nicht in der Sidebar, aber gültige Tabs
  const clientTileTabs = ['documents', 'rechnungen', 'messages', 'erfassen', 'arbeitszeiten'];
  // Auf einen gültigen Tab zurückfallen, wenn der aktuelle ausgeblendet wurde
  if (!navItems.some(([k]) => k === tab) && !(!isStaff && clientTileTabs.includes(tab))) {
    if (tab !== 'dashboard') setTab('dashboard');
  }

  return (
    <div style={S.layout}>
      <aside style={S.sidebar}>
        <div style={{ ...S.sidebarBrand, display: 'flex', alignItems: 'center', gap: 10 }}><img src="/logo.png" alt="" style={{ height: 40, flex: 'none' }} /><span>Die Lohnkanzlei<br /><span style={{ fontWeight: 400, opacity: 0.7 }}>– Portal –</span></span></div>
        {isStaff && (
          <div style={S.sidebarMandant}>
            <label style={S.sidebarLabel}>Mandant<RotBadge n={ungelesen.gesamt} /></label>
            <select style={S.sidebarSelect} value={selectedMandant} onChange={e => changeMandant(e.target.value)}>
              <option value="">Übersicht – Mein Büro</option>
              {mandanten.filter(m => !m.ist_eigenes_lohnbuero).map(m => <option key={m.id} value={m.id}>{m.kundennummer ? `${m.kundennummer} · ` : ''}{m.name}{m.ist_dummy ? ' (Test)' : ''}{(ungelesen.je_mandant[String(m.id)] || 0) > 0 ? ` ● ${ungelesen.je_mandant[String(m.id)]} neu` : ''}</option>)}
            </select>
          </div>
        )}
        <nav style={{ paddingTop: 8 }}>
          {navItems.map(([key, label]) => (
            <button key={key} style={S.navBtn(tab === key)} onClick={() => setTab(key)}>{label}{key === 'messages' ? <RotBadge n={isStaff ? (selectedMandant ? (ungelesen.je_mandant[String(selectedMandant)] || 0) : ungelesen.gesamt) : ungelesen.gesamt} /> : null}</button>
          ))}
        </nav>
        <div style={S.sidebarUser}>
          <div style={{ marginBottom: 8 }}>{user.username}<br /><span style={{ color: 'rgba(255,255,255,0.6)' }}>{user.role}</span></div>
          <button style={{ ...S.btnGhost, marginRight: 6 }} onClick={() => setSecOpen(true)}>🔒 Sicherheit</button>
          <button style={S.btnGhost} onClick={logout}>Abmelden</button>
        </div>
      </aside>
      {secOpen && <SecurityModal onClose={() => setSecOpen(false)} onLoggedOut={() => { setSecOpen(false); logout(); }} />}
      <main style={S.content}>
        {isStaff && selectedMandant && <TimerBar mandantId={selectedMandant} mandantName={selMandantObj?.name} onStopped={() => { if (tab !== 'leistungen') setTab('leistungen'); }} />}
        {!isStaff && clientTileTabs.includes(tab) && (
          <button style={{ ...S.crumbLink, marginBottom: 14, fontSize: 14 }} onClick={() => setTab('dashboard')}>← Zurück zum Dashboard</button>
        )}
        {tab === 'dashboard' && (isStaff
          ? <Dashboard user={user} mandanten={mandanten} mandantId={selectedMandant} onOpenTicket={openTicket} onGoMandant={goMandant} onOpenMa={openMa} onMeinBueroChange={loadMandanten} />
          : <ClientHome user={user} onNav={setTab} onOpenMa={openMa} />)}
        {tab === 'erfassen' && !isStaff && <ErfassenHub user={user} onNeuerMa={() => { setMaNew(true); setTab('mitarbeiter'); }} onNav={setTab} />}
        {tab === 'arbeitszeiten' && <ArbeitszeitenView user={user} mandantId={selectedMandant} />}
        {tab === 'documents' && <Documents user={user} mandanten={mandanten} mandantId={ownCtx} />}
        {tab === 'mitarbeiter' && <Mitarbeiter user={user} mandanten={mandanten} mandantId={ownCtx} focusMaId={maFocus} clearMaFocus={() => setMaFocus(null)} startNewSignal={maNew} onStartNewConsumed={() => setMaNew(false)} />}
        {tab === 'karte' && isStaff && selMandantObj?.karte_aktiv && <MitarbeiterKarte mandantId={ownCtx} onOpenMa={openMa} />}
        {tab === 'tickets' && isStaff && <Tickets user={user} mandanten={mandanten} mandantId={ownCtx} focusId={ticketFocus} clearFocus={() => setTicketFocus(null)} />}
        {tab === 'fehlzeiten' && isStaff && <FehlzeitenBuero user={user} mandanten={mandanten} mandantId={selectedMandant} onOpenMa={openMa} />}
        {tab === 'messages' && <Messages user={user} mandanten={mandanten} mandantId={ownCtx} onOpenTicket={openTicket} onRead={refreshUngelesen} />}
        {tab === 'leistungen' && isStaff && !isEigenLohnbuero && <><Zeitabschnitte mandantId={selectedMandant} /><Leistungen user={user} mandanten={mandanten} mandantId={selectedMandant} /></>}
        {tab === 'insights' && isStaff && !isEigenLohnbuero && <Insights mandantId={selectedMandant} mandantName={selMandantObj?.name} />}
        {tab === 'rechnungen' && !isStaff && <MeineRechnungen user={user} />}
        {tab === 'settings' && !isStaff && <Settings user={user} mandanten={mandanten} mandantId={selectedMandant} />}
        {tab === 'buchfuehrung' && user.role === 'admin' && <FinanzHub user={user} />}
        {tab === 'extras' && isStaff && <Extras user={user} onChanged={loadMandanten} onGoMandant={goMandant} />}
        {tab === 'leads' && user.role === 'admin' && <Leads user={user} reloadMandanten={loadMandanten} />}
        {tab === 'ideen' && user.role === 'admin' && <IdeenBoard user={user} />}
        {tab === 'verwaltung' && isStaff && <Verwaltung user={user} mandanten={mandanten} mandantId={ownCtx} reloadMandanten={loadMandanten} />}
        {tab === 'qa' && <QandA />}
      </main>
    </div>
  );
}
